import { TUTORIAL_MESSAGES } from "./message-tutorial.utils";

/**
 * Le dictionnaire **français** — la référence, et le seul embarqué dans le bundle initial.
 *
 * ⚠️ **Toute clef ajoutée ici doit l'être dans `message-en/es/pt.utils.ts`.** Il n'y a **aucun repli sur
 * le français** : `setCurrentMessages` remplace le dictionnaire entier (`language.service.ts`), et
 * `TranslateUtils.translate` rend la CLEF quand elle manque. Une clef oubliée affiche donc
 * `custom_enum.archiver_message_libre` à l'écran — dans un dialogue de suppression, pour cet exemple.
 *
 * ⚠️ **Les trois autres langues sont chargées en `import()` PARESSEUX** : après une édition, le serveur
 * de dev ne recompile pas toujours leur chunk. Vérifier une traduction demande un redémarrage du service
 * (`./justup stop front-metier && ./justup start front-metier`), sinon on constate un français tenace qui
 * n'existe plus dans le fichier.
 */
export const MESSAGE = {
  button: {
    loading: "...",
    loadMore: "Charger plus",
    filter: "Filtres",
    login: "Connexion",
    confirm: "Confirmer",
    save: "Enregistrer",
    edit: "Editer",
    view: "Voir",
    copy: "Copier",
    add: "Ajouter",
    save_draft: "Enregistrer le brouillon",
    valid: "Valider",
    delete: "Supprimer",
    delete_version: "Supprimer la version",
    modif: "Modifier",
    assign: "Assigner",
    continue: "Continuer",
    import: "Importer",
    print: "Imprimer",
    send: "Envoyer",
    precedent: "Précédent",
    next: "Suivant",
    close: "Fermer",
    done: "Terminé",
    see_all: "Voir tout",
    cancel: "Annuler",
    cancel_modif: "Annuler la modification",
    create: "Créer",
    ordonner: "Ordonner",
    reset: "Réinitialiser",
    searchArticleLot: "Article, lot....",
    select: "Sélectionner",
    selected: "Sélectionné",
    drag_drop:
      "Glissez-déposez vos documents à extraire ici... (seulement aux formats image et PDF)",
    browse_files: "... ou parcourez vos fichiers",
    more_param: "Plus de paramètres",
    less_param: "Moins de paramètres",
    refresh: "Rafraichir",
    invalid: {
      default: "Vous avez des erreurs dans le formulaire",
      stepperError: "Vous avez des etapes en erreur",
      noFiles: "Aucun fichier sélectionné",
      selectOne: "Veuillez sélectionnez au moins un élément",
      selectCommande: "Veuillez sélectionnez une commande",
      selectPalette: "Veuillez sélectionnez au moins une unité d'expédition",
      palettesNotReady:
        "Toutes les unités d'expédition doivent être déclarées « Prête » avant l'expédition",
      commandeCreate: "Veuillez sélectionnez un fournisseur et une adresse",
      noMouvement: "Veuillez renseigner au moins un mouvement",
      receptionInvalid:
        "Vous avez des réceptions incomplètes. Veuillez terminer la saisie ou supprimer les éléments en surplus",
      dechargementInvalid: "Vous avez des déchargements invalides",
      enregistrementInvalid: "Vous avez des enregistrements invalides",
      commentOrDocument:
        "Vous devez renseigner au moins un commentaire ou un document",
      ean13: "Vous pouvez créer un article uniquement avec un EAN13",
      pdcRetour: "Vous avez des retours de PDC invalides",
      noArticle: "Aucun article dans la nomenclature",
      noProcess: "Aucun procédé dans la nomenclature",
      processNotPending:
        "L’annulation est impossible car certaines Opés ont déjà démarré.",
      stepNomenclature: "Veuillez renseigner un lot pour cet article",
      ofWithoutLot: "L'OF n'a pas encore de lot valide.",
      endOpe: "Veuillez renseigner une évaluation",
    },
  },
  system: {
    network_error: "Problème de réseau détecté",
    check_connexion: "Veuillez vérifier votre connexion internet",
    browser_version: "Version du navigateur incompatible",
    browser_version_update: "Veuillez mettre à jour votre navigateur",
  },
  nav_trail: {
    more: "Pages précédentes",
    ofs_list: "Liste des OFs",
    of: "OF",
    clear: "Effacer l'historique",
  },
  sideNav: {
    header: {
      infos: "Données de base",
      daily_todo: "Tâches journalières",
      inventaire: "Logistique",
      production: "Production",
      qualite: "Qualité",
      commandes: "Commandes",
      administration: "Administration",
    },
    menu: {
      tracabilite: "Traçabilité",
      audit_bio: "Audit BIO",
      developpement_recette: "Développement Recette",
      mouvements: "Mouvements de stocks",
      todo: "Listes de tâches",
      inventaire: "Inventaires",
      specifications_to_treat: "Spécifications à traiter",
      dashboard: "Tableau de bord",
      documents: "Documents",
      articles: "Articles",
      production: "Productions",
      stock: "Stock",
      personnel: "Personnel",
      controles: "Contrôles à réaliser",
      ofs: "Ordres de fabrication (OF)",
      models: "Modèles de spécifs.",
      etapes: "Spécifications produit",
      commandes: "Commandes fournisseurs",
      commandes_client: "Commandes clients",
      preparation: "Préparation de commandes",
      of_plannif: "Planification",
      of_plannif_composant: "Planification des composants",
      ligne: "Lignes de production",
      info_journalieres: "Saisie des heures",
      tiers: "Fournisseurs et clients",
      article_param: "Spécification article",
      activite_param: "Paramétrage activité",
      admin_param: "Paramétrage administratif",
      user_param: "Paramétrage utilisateurs",
      support: "Support",
      chat: "Messages",
      administration: "Administration",
      suivi_production: "Suivi des productions",
      stats_prod: "Statistiques",
      dechargements: "Déchargements et Réceptions",
      expeditions: "Expéditions et BL",
      equipes: "Gestion des équipes",
      analyse: "Analyse",
      justup_billing: "Facturation JustUp",
      factures: "Factures et avoirs",
      transferts: "Transferts",
      flux_logistiques: "Flux Logistiques",
      cost_impact: "Simulation de coûts",
      cost_impact_2: "Simulation de coûts (réf.)",
    },
    privacy_policy: "Politique de confidentialité",
    // ── Menu v2 « Espaces + Tout Justup » (2026-08-07) ──
    v2: {
      nouveau_menu: "Nouveau menu",
      ancien_menu: "Ancien menu",
      try_tooltip: "Essayer le nouveau menu",
      back_tooltip: "Revenir à l'ancien menu",
      epingles: "Épinglés",
      epingles_empty: "Épinglez vos écrans (★ au survol)",
      edit_space: "Modifier l'espace",
      space_name: "Nom de l'espace",
      space_color: "Couleur",
      delete_space: "Supprimer l'espace",
      tout_justup: "Tout Justup",
      add_space: "Ajouter un espace",
      pin: "Épingler dans l'espace courant",
      unpin: "Désépingler",
      reorder: "Glisser pour réordonner",
      achats: "Achats",
      ventes: "Ventes",
      stocks: "Stocks",
      equipe: "Équipe",
      reglages: "Réglages",
      space_bureau: "Bureau",
      space_atelier: "Atelier",
      space_quai: "Quai",
    },
  },
  tutorial: TUTORIAL_MESSAGES,
  billing: {
    START_OF: "Démarrage",
    END_OF: "Fin de la production",
    RESTART_OF: "Redémarrage de la production",
    START_OPE: "Démarrage de l'opération de production",
    END_OPE: "Fin de l'opération de production",
    CONTROLE_OPE: "Réalisation d'un contrôle sur l'opération de production",
    PEO_VALID:
      "Validation d'une étape de procédé sur l'opération de production",
    of_of: "de l'OF",
  },
  documents: {
    title: "Documents",
    empty: "Aucun document",
    uploading: "Envoi en cours...",
    initing: "Initialisation...",
    processing: "Préparation de la vidéo...",
    select: "Sélectionner un fichier",
    import: "Importer un fichier",
    remote_photo: "Prendre une photo",
  },
  specification: {
    missing_var_title: "Variables manquantes",
    missing_var_confirm_btn: "Approuver malgré tout",
    missing_var_message: "Des variables du modèle ne sont pas renseignées sur cet article. Les OFs créés devront les renseigner au moment de la création. Approuver quand même ?",
    a_partir_du: "À partir du",
    approbation_activate_title: "Activer cette version ?",
    approbation_activate_msg1: "Cette spécification sera définie comme version en cours.",
    approbation_activate_msg2: "Tous les futurs ordres de fabrication utiliseront cette spécification.",
    approbation_replace_title: "Remplacer la version actuelle ?",
    approbation_replace_msg1: "Cette spécification deviendra la nouvelle version en cours.",
    approbation_replace_msg2: "La version actuellement utilisée sera automatiquement archivée.",
    approbation_replace_orphan_msg2: "La version actuellement utilisée sera remplacée (elle n'a jamais été utilisée).",
    approbation_replace_msg3: "Tous les futurs ordres de fabrication utiliseront cette nouvelle spécification.",
    aside_photo_upload: "Ajouter ou remplacer la photo",
    editing_for_of_banner: "Spécification privée de l'OF",
    editing_for_of_banner_info: "Vos modifications n'affecteront pas les autres OFs.",
    editing_for_of_back: "Retour à l'OF",
    editing_for_of_restore_btn: "Restaurer l'OF ##numero sur la spec courante",
    end_redaction_dialog_title: "Terminer la rédaction ?",
    end_redaction_msg1: "Cette action marquera la spécification comme prête pour validation.",
    end_redaction_msg2: "Vous ne pourrez plus la modifier, sauf si elle est rejetée par un valideur.",
    end_redaction_msg3: "Seuls les utilisateurs habilités pourront poursuivre le processus de validation.",
    stats_menu: "Statistiques & analyses",
    validation_dialog_title: "Valider cette spécification ?",
    validation_msg1: "En validant cette spécification, vous confirmez qu'elle est conforme et prête à être approuvée.",
    validation_msg2: "Elle sera transmise à l'approbateur pour finalisation.",
    create: "Nouvelle version",
    send: "Demander validation",
    validate: "Valider la conformité",
    approuve: "Utiliser cette version",
    duplicate_path: "Dupliquer",
    create_etape: "Créer une spécification",
    master_etapes: "Spécifications produit",
    etats: "États",
    specifs_archivees: "Spécifications archivées",
    editing: "En cours de modification...",
    tips_variables:
      "Un paramètre est une donnée (texte ou nombre) que vous pouvez définir une seule fois et réutiliser ensuite dans vos procédés et vos contrôles via la fonction de texte dynamique.\n" +
      "\n" +
      "👉 Exemple : si plusieurs étapes de votre procédé font référence à un temps de cuisson, créez un paramètre “Temps de cuisson”. Vous pourrez ensuite l’appeler directement sans avoir à le ressaisir à chaque fois.\n" +
      "\n" +
      "✅ Moins de travail répétitif\n" +
      "✅ Moins de risque d’oubli ou d’erreur\n" +
      "✅ Plus de clarté et de cohérence dans vos spécifications",
    demandes_modifications: "Demandes de modifications",
    nouvelles_demandes: "Nouvelles demandes:",
    demandes_traitees: "Demandes traitées:",
    refuser: "Refuser",
    autres_versions: "Autres version",
    version_courante: "Version courante",
    en_redaction: "En rédaction",
    archive_tooltip: "Un article archivé n'apparait plus dans les recherches",
    archive_label: "Archivé",
    importer_specif: "Importer depuis une autre specification",
    archiver: "Archiver",
    desarchiver: "Désarchiver",
    supprimer_article: "Supprimer définitivement",
    archive_or_delete_title: "Archiver ou supprimer ?",
    archive_or_delete_message:
      "Cet article n'a jamais été utilisé. Vous pouvez le supprimer définitivement ou simplement l'archiver.",
    voir_specification: "Voir la spécification",
    consulter_specification: "Consulter la spécification",
    creer_etape_enfant: "Créer une étape enfant",
    demander_modification: "Demander une modification",
    du: "Du",
    au: "au",
    proprietaire: "Propriétaire",
    code_client: "Code client",
    afficher_plannificateur: "Afficher le plannificateur",
    specif_produit: "Spécification produit",
    demander_modif: "Demander une modification",
    voir_specif: "Voir la spécification",
    consulter_specif: "Consulter la spécification",
    duplicate: {
      warningProcesses: "Tous les procédés existantes seront remplacés",
      warningNomenclature:
        "Les articles de nomenclature seront ajouté à la nomenclature existante",
      warningControles: "Tous les contrôles existants seront remplacés",
    },
  },
  cta: {
    fill: "Veuillez la renseigner",
  },
  palette: {
    title: "Gestionnaire d'unités d'expédition",
    add_label: "Créer une unité d'expédition",
    create: "Créer une unité d'expédition",
    edit: "Modifier l'unité d'expédition",
    edit_hint_preparing:
      "Vous pouvez modifier le nom et le type d'unité. Pour modifier le contenu, utilisez le bouton Préparer.",
    edit_hint_ready:
      "L'unité est prête. Vous pouvez modifier le nom et le type, ou reprendre la préparation pour modifier le contenu.",
    for_commande: "Unités d'expédition pour la commande client",
    add_lot_cta:
      "Veuillez sélectionner le lot et la quantité à ajouter au colis",
    add_lot: "Sélection du lot à mettre sur l'unité d'expédition",
    livraison_no_expe: "Livraison directe / gardé en stock",
    preparation: "Préparation de l'unité d'expédition",
    see_other_for_commande: "Voir les autres colis pour cette commande",
    warning_no_lines:
      "Afin de pouvoir préparer une unité d'expédition, veuillez ajouter au moins une ligne de commande",
    palettes_selectionnees: "unités d'expédition sélectionnées",
    palette_selectionnee: "unité d'expédition sélectionnée",
    expedier: "Expédier",
    tare: "Tare",
    entrepot_source: "Entrepôt de départ",
    entrepot_readonly: "Videz l'unité pour changer d'entrepôt",
    entrepot_required: "L'entrepôt de départ est obligatoire",
    empty_confirm_title: "Vider l'unité d'expédition",
    empty_confirm_message:
      "Tous les produits seront réintégrés dans l'entrepôt d'origine. Continuer ?",
    stock_support: "Stock disponible",
    stock_support_unit: "unité(s) en stock",
    stock_support_insufficient:
      "Stock insuffisant pour cet article support dans l'entrepôt sélectionné",
    stock_support_go_to_stock: "Voir les stocks",
    stock_support_lot: "Lot article support",
    stock_support_select_lot: "Sélectionnez un lot pour l'article support",
  },
  palette_finder: {
    title: "Ajouter des unités d'expédition",
    empty: "Aucune unité d'expédition disponible",
    entrepot_filter: "Unités disponibles depuis",
    hidden_no_adresse:
      "1 unité vide masquée — ajoutez du stock dessus avant de la charger",
    hidden_no_adresse_plural:
      "#count unités vides masquées — ajoutez du stock dessus avant de les charger",
    scan_no_adresse:
      "Cette unité d'expédition est vide (sans destination). Ajoutez du stock dessus avant.",
    none_available:
      "Aucune unité d'expédition disponible — préparez une palette avec du stock avant de la charger.",
    added_success: "#count unité(s) d'expédition ajoutée(s) au chargement",
  },
  commandes_client: {
    send_mail: "Envoyer la confirmation au client",
  },
  commandes: {
    command: "Commander",
    send_mail: "Envoyer la commande au fournisseur",
    terminer: "Déclarer comme terminée",
    expedier: "Expédier",
    prete: "Déclarer comme prête",
    preparer: "Préparer la commande",
    creation: "Création d'une commande fournisseur",
    createur: "Créée par",
    passer: "Passer commande",
    solder: "Solder",
    annuler: "Annuler",
    modifier: "Modifier la commande",
    from: "Choisir",
    article: "un article",
    fournisseur: "un fournisseur",
    add_label: "Ajouter une commande",
    for: "Commande pour",
    confirmer: "Confirmer",
    confirmer_the: "Confirmer la commande",
    recevoir: "Réception",
    recevoir_the: "Réception de la commande",
    details: "Mettre à jour la commande",
    add_new_articles: "Ajout de nouveaux articles à la commande",
    dateLivraison: "Date de livraison",
    resume: "Résumé des commandes",
    createCommandes: "Créer #nb commandes",
    noCommand: "Aucune commande à afficher",
    noArticle: "Aucun article",
    aucune_reception: "Aucune réception pour cette commande",
    livreeLe: "Livrée le: ",
    delaiLivraison: "Délai de livraison",
    dateLivraisonAlert:
      "Le délai de livraison pour cet article repousse la date de livraison",
    dateLivraisonManuelleAlert:
      "La date de livraison de la commande est manuelle, cependant le délai de livraison de l'article est de #delai jours. Voulez-vous modifier la date de livraison de la commande ?",
    confirmMulti: "#nb commande crée.",
    confirmMulti_plural: "#nb commandes crées.",
    createMulti: "Créer #nb commande",
    createMulti_plural: "Créer #nb commandes",
    createSingle: "Créer cette commande",
    confirmSingle: "Commande créée avec succès",
    errorSingle: "Erreur lors de la création de la commande",
    lot_obligatoire: "Lot fournisseur requis",
    lot_obligatoire_desc:
      "Cet article est soumis à traçabilité. Veuillez renseigner le numéro de lot.",
    multiple_info: "Multiple de #multiple #unite",
    multiple_ok: "#factor × #multiple #unite",
    multiple_hint: "Multiple de #multiple #unite — arrondir à :",
    multiple_keep: "garder #qty",
  },
  commande_client: {
    creation: "Création d'une commande client",
    createur: "Créée par",
    plus_de_parametres: "Plus de paramètres",
    entrepot_optionnel_hint:
      "Optionnel. Sans entrepôt assigné, la commande est visible par tous et peut puiser dans le stock de n'importe quel entrepôt.",
    eclatement_multi_entrepots:
      "Lignes éclatées sur plusieurs entrepôts (stock insuffisant dans un seul)",
    eclatement_multi_entrepots_hint:
      "L'auto-affectation a réparti les lots sur plusieurs entrepôts car aucun seul entrepôt ne couvrait l'intégralité des lignes. Coordonnez la prépa pour éviter les transferts.",
    eclatement_ligne: "Éclatée multi-entrepôts",
    eclatement_ligne_hint:
      "Cette ligne a été couverte par des lots de plusieurs entrepôts. Vérifie la faisabilité opérationnelle (transferts, équipes).",
    tous_entrepots_assignes:
      "Tous les entrepôts disponibles sont déjà assignés à cette commande.",
    aucun_entrepot_mode_global:
      "Aucun entrepôt assigné — mode global (visible par tous, stock puisé partout).",
    aucun_entrepot_mais_ue:
      "Pas d'entrepôt assigné explicitement. Les entrepôts ci-dessus sont détectés via les unités d'expédition (palettes) ou les réservations actives.",
    date_expedition_auto_hint:
      "Préremplie avec la valeur calculée (livraison − délai logistique). Modifier le champ pour forcer une autre date.",
    date_expedition_overridden_hint:
      "Date saisie manuellement (override). Cliquer sur Réinitialiser pour revenir au calcul automatique.",
    commentaires_et_pj: "Commentaires & pièces jointes",
    entrepots_detectes_ue_label: "Détectés (UE / réservation) :",
    entrepot_ue_tooltip:
      "Entrepôt utilisé par une UE (palette) liée à cette commande ou par une réservation active en zone-prepa, mais pas assigné explicitement.",
    entrepot_ue_tooltip_promote:
      "Entrepôt détecté via une UE liée ou une réservation active. Cliquer pour le promouvoir en entrepôt assigné explicitement.",
    include_sans_entrepot: "Inclure CC sans entrepôt",
    include_sans_entrepot_hint:
      "Les commandes sans entrepôt assigné sont visibles par tous (mode global). Décocher pour ne voir que les CC strictement assignées aux entrepôts sélectionnés.",
    entrepots_assignes: "Entrepôts assignés",
    aucun_entrepot_disponible:
      "Aucun entrepôt disponible. Crée des adresses d'entrepôt dans la fiche société.",
    passer: "Passer commande",
    modifier: "Modifier la commande",
    from: "Choisir",
    article: "un article",
    fournisseur: "un fournisseur",
    add_label: "Ajouter une commande",
    for: "Commande pour",
    qtt_livre: "Quantité livrée",
    join_bill: "Joindre la facture proforma",
    join_confirmation: "Joindre la confirmation de commande",
    lignesDeCommande: "Lignes de commande",
    preparationVrac: "Préparation vrac",
    creerNouvellePalette: "Ajouter une Unité d'Expédition",
    expedierVrac: "Expédier",
    livrerRetrait: "Livrer (retrait)",
    // « articles » et pas « lots » : les prestations (lot unique technique)
    // s'ajoutent aussi par ce bouton, sans choix de lot. 2026-07-30.
    ajouterDesLots: "Ajouter des articles",
    ajouterLignesAvant: "Veuillez d'abord ajouter des lignes à la commande",
    expeditionDu: "Expédition du",
    imprimerBL: "Imprimer le BL",
    envoyerVersChargement: "Envoyer vers chargement",
    preparation: "Préparation",
    avec_expedition: "Avec expédition",
    livraison_directe: "Livré mais gardé en stock",
    livraison_directe_retrait: "Livraison directe (retrait)",
    livraison_directe_retrait_desc:
      "Préparez ici les lots à remettre au client (retrait) : ils sont mis de côté « prévu en livraison directe », sans sortie de stock. La livraison réelle se déclenche ensuite en un geste — « Livrer (retrait) » — qui crée UN seul BL pour l'ensemble. À ne pas confondre avec « Facturé — gardé en stock ».",
    livraison_directe_desc:
      "Facturez la marchandise au client sans expédition physique : les produits restent en stock, seul le propriétaire change. À distinguer de « Livraison directe (retrait) » qui sort réellement le stock de l'entrepôt.",
    stock_autre_entrepot: "Stock disponible dans d'autres entrepôts :",
    prestation_sans_lot:
      "Prestation — livrée sans lot, incluse dans le même bon de livraison",
    palettes: "Unités d'expéditions",
    vrac: "Zone de retrait",
    paletteDu: "Unité d'expédition ·",
    vracDu: "Vrac ·",
    mettreSurPalette: "Mettre sur unité d'expédition",
    choisirArticle: "Cliquez sur un article pour ajouter des lots",
    enZone: "En zone",
    surPalette: "Sur unité d'expédition",
    aide_expedition_maker:
      "Sélectionnez un article pour choisir les lots et quantités à ajouter sur l'unité d'expédition. La barre de progression indique l'avancement par rapport à la quantité demandée.",
    documents_commande: "Documents de commande",
    voir_proforma: "Éditer la facture proforma",
    proforma_rib_title: "Choisir le RIB à afficher sur la proforma",
    voir_confirmation: "Éditer la confirmation de commande",
  },
  search: {
    label: "Rechercher ici ...",
    main_bar: "Rechercher un article, un tiers, une commande...",
  },
  ligne_param: {
    label: "Paramétrage de la ligne",
    add_cta: "Ajouter un paramétrage",
    create_label: "Création d'un nouveau paramétrage",
    edit_label: "Edition du paramétrage",
    no_param: "Aucun paramétrage n'a été configuré.",
  },
  mouvement: {
    pending_to_save: "Mouvements à enregistrer",
    edit_title: "Modifier le mouvement",
  },
  of: {
    voir_of: "Voir l’OF",
    epingle: "Épinglé",
    creer_of: "Créer un OF",
    why_delete:
      "Vous ne pouvez supprimer un OF que s’il n’a aucune opération en cours ni aucune opération déjà terminée.",
    withQuantiteOpe: "Suivi des quantités produites par opé.",
    chose_other: "Sélectionner un autre OF",
    end: "Terminer l'OF",
    list: "Liste des OF",
    add_cta: "Ajouter un OF",
    reinte_and_end: "Réintégrer et finir l'OF",
    confirmation_mouvement: "Confirmer les mouvements pour l'",
    no_ope: "Aucune opé. sur cet OF",
    bilan_matieres_intermediaire: "Avec bilans matières intermédiaires",
    auto_plan: "Planifier automatiquement",
    prepare_opes: "Préparation des opés.",
    quantiteParOpe: "Quantité par opé.",
    missing_precondition_text:
      "Avant de démarrer un ordre de fabrication, vous devez d'abord configurer une ligne de production et un article.",
    missing_precondition_text_article: "Créer votre premier article",
    missing_precondition_text_ligne: "Créer votre première ligne de production",
    no_current_specif1: "La spécification de",
    no_current_specif2: "n'est pas validée",
    select_of: "Veuillez sélectionner un OF",
    specifOutdated1: "La spécification a été modifiée.",
    specifOutdated2: "Voulez-vous mettre à jour l'OF ?",
    regenerateOpe: "Mettre à jour",
    confirmUpdateSpecif: "Mise à jour de la spécification de l'OF",
    selectParam: "Veuillez sélectionner un paramètre de ligne",
    stockOutDated1:
      "Vous avez des articles en pied de chaine qui ne sont plus dans la nomenclature.",
    stockOutDated2: "Cette opération va les réintégrer.",
    stockOutDatedQtt: "Quantité en pied de chaine",
    cancel: "Annuler l'OF",
    cancel_impossible: "Impossible d'annuler l'OF car des opés sont en cours",
    cancel_confirm:
      "Vous êtes sur le point d'annuler l'OF #numero. Confirmez-vous ?",
    prepare_pdc: "Préparer PDC",
    reintegrer_pdc: "Réintégrer PDC",
    du: "Du",
    au: "Au",
    match_date: "Correspond à la date",
    quantity_produced: "Quantité produite :",
    edit: "Modifier l'OF",
    dossier: {
      titre: "Dossier de production — #of",
      format: "Format du dossier",
      personnaliser: "Personnaliser les onglets (#n/#total)",
      btn_pdf: "PDF",
      btn_excel: "Excel",
      gen_pdf: "Génération PDF…",
      gen_excel: "Génération Excel…",
      succes_excel: "Dossier Excel généré — #of",
      succes_pdf: "Dossier PDF généré — #of",
      erreur: "La génération a échoué. Réessayez, ou signalez-le si cela se reproduit.",
      chargement_partiel:
        "Ces données n'ont pas pu être chargées et manqueront au dossier : #quoi.",
      preset_full: "Dossier complet",
      preset_full_tagline: "Tous les onglets",
      preset_full_audience: "Archivage, audit final",
      preset_financier: "Financier",
      preset_financier_tagline: "Récap + statistiques profondes",
      preset_financier_audience: "Direction financière, comptabilité",
      preset_productivite: "Productivité",
      preset_productivite_tagline: "Procédé + heures + statistiques",
      preset_productivite_audience: "Chef d'atelier",
      preset_sf_amont: "Semi-finis et amont",
      preset_sf_amont_tagline: "Traçabilité + mouvements + arbre des coûts",
      preset_sf_amont_audience: "Approvisionnements",
      preset_personnel: "Personnel",
      preset_personnel_tagline: "Procédé + heures",
      preset_personnel_audience: "Ressources humaines production",
      preset_qualite: "Qualité",
      preset_qualite_tagline: "Spécification + contrôles + traçabilité",
      preset_qualite_audience: "Responsable qualité, auditeur IFS",
      section_cover: "Couverture",
      section_recap: "Récapitulatif",
      section_spec: "Spécification",
      section_procede: "Procédé",
      section_controles: "Contrôles",
      section_tracabilite: "Traçabilité",
      section_mouvements: "Mouvements",
      section_stats: "Statistiques",
      section_heures: "Heures",
      section_commentaires: "Commentaires",
    },
  },
  bilan_matiere: {
    why_disabled:
      "Afin de pouvoir saisir le bilan matière, veuillez terminer au moins une opération de production",
  },
  heures: {
    empty: "Aucune heure n'a été saisie sur cet OF",
  },
  users: {
    edit: "Edition",
    activate: "Réactiver",
    deactivate: "Désactiver",
    go_profile: "Profil",
    // QR de connexion rapide (issue justup-realm#42)
    qr_login: "QR de connexion",
    qr_regenerate: "Régénérer",
    qr_regenerated: "Nouveau QR de connexion généré",
    qr_print: "Imprimer",
    qr_error: "Impossible de générer le QR de connexion.",
    qr_hint:
      "À imprimer et coller au poste. Scanner ce code sur l'écran de connexion identifie l'utilisateur sans saisie.",
    showDisabled: "Voir les utilisateurs désactivés",
    status_all: "Tous",
    status_active: "Actifs",
    status_inactive: "Désactivés",
    disabledAt: "Désactivé le",
    language: "Langue",
    tutorial_enabled: "Activer les tutoriels interactifs",
  },
  administration: {
    alerte_qualite_destinataires: "Destinataires des alertes qualité",
    alerte_qualite_hint: "Contrôles non conformes et dérives : alertés en direct dans le chat interne. Liste vide = alertes désactivées.",
    doc_commerciaux: "Documents",
    data_extract: "Exports CSV",
    numerotations: "Numérotations",
    validations: "Validations des Ofs / Spécifications",
    my_business: "Mon entreprise",
    user_management: "Gestion des utilisateurs",
    pass_management: "Gestion des autorisations",
    add_user: "Ajouter un utilisateur",
    devices: "JustUp live",
    advance: "Paramètres avancés",
    planning: "Planning par défaut",
    ip: "Sécurité IP",
    identity: "Identité visuelle",
    infos: "Informations",
    controles_generaux: "Contrôles généraux",
    controles_generaux_chargement: "Contrôles généraux de chargement",
    param_generaux: "Paramétrages généraux",
    validation: "Paramétrages generaux",
    custom_enum: "Enumérations",
    billing: "Facturation JustUp",
    signalements_justy: "Signalements Justy",
    tokens: "Tokens IA",
    chargement: "Chargement / Expédition",
    signature_operateur_obligatoire:
      "Signature de l'opérateur obligatoire au chargement",
  },
  tokenManagement: {
    title: "Facturation JustUp",
    subtitle:
      "Votre consommation, vos tokens IA Justy, vos moyens de paiement et vos factures JustUp.",
    tab_consumption: "Consommation",
    tab_overview: "Tokens IA",
    tab_buy: "Acheter des tokens",
    tab_payment: "Moyens de paiement",
    tab_invoices: "Factures",
    tab_history: "Historique",
    balance: "Solde de tokens",
    balance_remaining: "Tokens restants",
    quota_monthly: "Quota mensuel inclus",
    quota_used: "Consommé",
    quota_consumed_pct: "du quota consommé",
    current_estimate: "Facture du mois en cours (estimation)",
    estimate_hint: "Montant prévisionnel, non encore facturé",
    total_ht: "Total HT",
    total_ttc: "Total TTC",
    subscription: "Abonnement",
    plan: "Formule",
    renews_on: "Renouvellement le",
    monthly_amount: "Montant mensuel",
    no_subscription: "Aucun abonnement actif",
    buy_title: "Recharger des tokens",
    buy_subtitle:
      "Crédités instantanément après paiement. Sans engagement, valables sur toute la durée de votre abonnement.",
    best_value: "Meilleure valeur",
    per_million: "million",
    pack_tokens: "tokens",
    buy_pack: "Acheter",
    services_title: "Formation & accompagnement",
    services_subtitle:
      "Accélérez votre prise en main avec nos experts. Réservez votre créneau en ligne.",
    service_formation_title: "Formation & aide à l'intégration",
    service_formation_desc:
      "Prise en main complète et paramétrage de votre ERP avec un expert JustUp.",
    service_support_title: "Accompagnement sur-mesure",
    service_support_desc:
      "Journée d'accompagnement dédiée (optimisation, formation avancée, support).",
    service_once: "forfait",
    service_per_day: "/ jour",
    service_nb_days: "Jours",
    book_slot: "Réserver un créneau",
    book_pay: "Réserver & payer",
    book_success: "Paiement validé — choisissez votre créneau.",
    book_confirm_title: "Confirmer la réservation",
    book_confirm_days: "#label — #quantity jour",
    book_confirm_days_plural: "#label — #quantity jours",
    book_confirm_once: "#label — forfait",
    book_confirm_amount: "Montant : #ht HT · #ttc TTC (montant débité)",
    book_confirm_debit:
      "Débité immédiatement sur votre moyen de paiement enregistré.",
    book_confirm_slot: "Vous choisirez votre créneau après le paiement.",
    buy_confirm_title: "Confirmer l'achat",
    buy_success: "Achat effectué — tokens crédités",
    // Statut de paiement non finalisé : ne JAMAIS annoncer un achat réussi tant
    // que le PaymentIntent n'est pas `succeeded`.
    buy_pending:
      "Paiement non finalisé — aucun token n'a encore été crédité. Vérifiez l'historique dans quelques instants.",
    buy_error: "Impossible d'ouvrir la page de paiement.",
    excl_vat: "HT",
    no_pack: "Aucun pack disponible pour le moment",
    no_service: "Aucune prestation disponible pour le moment",
    service_generic_title: "Prestation",
    service_generic_desc: "Prestation facturée à la réservation.",
    booking_ready_title: "Paiement validé — réservez votre créneau",
    booking_ready_hint:
      "Si la page ne s'est pas ouverte automatiquement, utilisez ce lien. Il reste disponible tant que vous ne le fermez pas.",
    booking_open: "Choisir un créneau",
    payment_methods_title: "Moyens de paiement",
    add_payment_method: "Ajouter un moyen de paiement",
    card: "Carte bancaire",
    sepa: "Prélèvement SEPA",
    default_badge: "Par défaut",
    set_default: "Définir par défaut",
    delete_pm: "Supprimer",
    no_payment_method: "Aucun moyen de paiement enregistré",
    pm_form_title: "Ajouter une carte ou un mandat SEPA",
    pm_save: "Enregistrer",
    pm_saved: "Moyen de paiement enregistré",
    pm_error: "Échec de l'enregistrement du moyen de paiement",
    invoices_title: "Factures JustUp",
    invoice_number: "N° facture",
    invoice_date: "Date",
    invoice_amount: "Montant TTC",
    invoice_status: "Statut",
    download_invoice: "Télécharger",
    no_invoice: "Aucune facture",
    status_paid: "Réglée",
    status_open: "En attente",
    status_void: "Annulée",
    status_uncollectible: "Impayée",
    status_draft: "Brouillon",
    history_title: "Historique des transactions",
    tx_date: "Date",
    tx_type: "Type",
    tx_amount: "Tokens",
    no_transaction: "Aucune transaction",
    type_subscription_grant: "Quota mensuel",
    type_pack_purchase: "Achat de pack",
    type_chat_consumption: "Consommation chatbot",
    type_refund: "Remboursement",
  },
  activity_infos: {
    logo: "Logo de votre entreprise",
    color: "Couleur de votre entreprise",
    language: "Langue des documents PDF",
    language_section_title: "Langue par défaut des documents",
    language_section_description:
      "Utilisée pour les commandes, bons de livraison, factures et autres documents administratifs.",
    delai_expedition_par_defaut: "Délai expédition par défaut (jours ouvrés)",
    delai_expedition_par_defaut_placeholder: "Ex : 1",
    delai_expedition_par_defaut_hint:
      "Nombre de jours ouvrés entre l'expédition et la livraison client. Sert au calcul automatique de la date d'expédition prévue. Peut être surchargé par adresse de livraison.",
    section_developpement_recette: "Développement recette",
    marge_reference_recette: "Marge de référence",
    marge_reference_recette_placeholder: "40",
    marge_reference_recette_hint:
      "Le taux appliqué aux projets qui n'ont pas de taux propre. Vide : le module retient 40 %.",
    section_qualite_securite: "Qualité & sécurité alimentaire",
    activer_blocage_dlc:
      "Bloquer les expéditions contenant un lot DLC dépassée",
    activer_blocage_dlc_hint:
      "Recommandé pour les sites agro frais et IFS-compliants. Une expédition contenant un lot dont la DLC est dépassée sera refusée (R. 412-21 Code Conso). Ne s'applique qu'aux articles classés DLC ; les DDM dépassées génèrent un simple avertissement.",
  },
  validations: {
    specification: "Validation des spécifications",
    of: "Validation des OFs",
    facture: "Facturation",
  },
  facture: {
    reglement_reste_du: "Reste dû",
    reglement_simple_soldee: "Après ce règlement, la facture sera soldée.",
    reglement_simple_reste: "Après ce règlement, il restera",
    reglement_simple_depasse: "Ce montant dépasse le reste dû de cette facture. Utilisez « Affecter autrement » pour répartir le surplus.",
    reglement_simple_affecter_autrement: "Affecter autrement…",
    search_placeholder: "N° facture, tiers ou montant (HT/TTC)…",
    search_placeholder_reglement: "Tiers ou montant…",
    export_excel_tooltip:
      "Exporter toutes les factures et avoirs filtrés vers un fichier Excel formaté (6 onglets : synthèse, factures, avoirs, règlements, lignes détail, par client)",
    bulk_zip_btn: "Télécharger PDF",
    bulk_zip_tooltip:
      "Télécharge un ZIP contenant un PDF par facture cochée (un fichier nommé par numéro de facture / avoir)",
    bulk_zip_select_first:
      "Sélectionnez au moins une facture dans la liste (cochez la case à gauche) pour activer le téléchargement groupé",
    bulk_zip_success: "{count} factures téléchargées dans le ZIP",
    bulk_zip_error:
      "Échec du téléchargement groupé. Réessayez ou consultez les logs.",
    options_menu: "Options",
    export_excel: "Exporter vers Excel",
    download_factures: "Télécharger des factures",
    download_select_hint: "Sélectionnez les factures à télécharger",
    download_cancel: "Annuler la sélection",
    tab_factures: "Factures",
    tab_avoirs: "Avoirs",
    add_avoir_cta: "Ajouter un avoir",
    download_avoirs: "Télécharger des avoirs",
    download_select_hint_avoirs: "Sélectionnez les avoirs à télécharger",
    transaction_type: "Type de règlement",
    transaction_amount: "Montant",
    transaction_add_cta: "Création d'un règlement",
    transaction_edit_cta: "Modification d'un règlement",
    transaction_link_to_facture: "Imputer à une autre facture",
    transaction_from_ps:
      "Règlement créé automatiquement depuis PrestaShop — non modifiable.",
    transaction_from_ps_tooltip: "Règlement importé depuis PrestaShop",
    reglement_section_infos: "Règlement",
    reglement_section_imputations: "Affectation aux factures",
    reglement_section_facture_en_cours: "Facture en cours",
    reglement_section_autres_factures: "Autres factures du tiers",
    reglement_add_type: "Ajouter un type de règlement",
    reglement_facture_deja_reglee: "Déjà réglée",
    reglement_facture_deja_reglee_help:
      "Cette facture est entièrement réglée. Le règlement peut être affecté à d'autres factures du tiers ou enregistré comme avance non affectée.",
    reglement_factures_du_tiers: "Factures à régler de #tiers",
    reglement_factures_aucune: "Aucune facture en attente de règlement.",
    reglement_jauge_recu: "Montant du règlement",
    reglement_jauge_impute: "Affecté aux factures",
    reglement_jauge_restant_apres: "Restant dû après",
    reglement_jauge_reste: "Reste à affecter",
    reglement_jauge_excedent: "Trop affecté",
    reglement_jauge_impute_complet: "Règlement entièrement affecté",
    reglement_acompte_toggle: "Avance non affectée",
    reglement_acompte_help:
      "Une avance peut être enregistrée sans être affectée à une facture — un client qui verse sur son compte, sans commande identifiée. Si vous l'affectez, le montant doit l'être en totalité. ⚠️ À ne pas confondre avec une facture d'acompte, qui se demande depuis la commande.",
    reglement_acompte_label: "Avance",
    reglement_imputation_zero_error:
      "Une facture cochée a un montant à 0 €. Saisissez un montant ou décochez-la.",
    reglement_select_all: "Tout cocher",
    reglement_unselect_all: "Tout décocher",
    reglement_select_filter_search: "Rechercher une facture…",
    reglement_facture_reste_du: "Reste dû",
    reglement_facture_echeance: "Échéance",
    reglement_facture_echeance_depassee: "Échéance dépassée",
    reglement_facture_total_ttc: "Total TTC",
    reglement_match_exact: "Le montant correspond exactement à cette facture",
    reglement_match_combo: "Le montant correspond à #count factures",
    reglement_apply_match: "Affecter automatiquement",
    reglement_loading_factures: "Chargement des factures…",
    reglement_no_tiers_yet: "Sélectionnez d'abord un tiers",
    send_to_client: "Envoyer au client",
    generate_avoir: "Générer un avoir",
    enter_tiers: "Saisir le tiers",
    attach_bl: "Rattacher un BL",
    no_bl_linked: "Aucun bon de livraison lié",
    linked_avoir: "Avoir lié",
    linked_facture: "Facture liée",
    payment: "Paiement",
    add_payment: "Ajouter un règlement",
    imputer_avoir: "Imputer un avoir",
    affecter_reglement: "Affecter un règlement",
    affecter_reglement_title: "Affecter un règlement",
    affecter_reglement_hint:
      "Rattachez un règlement déjà enregistré à cette facture, ou créez-en un nouveau.",
    affecter_reglement_existing: "Règlements disponibles",
    affecter_reglement_reste: "reste à affecter",
    affecter_reglement_linked: "Lié à la commande",
    affecter_reglement_select: "Sélectionner",
    affecter_reglement_create: "Créer un nouveau règlement",
    imputer_avoir_ligne: "Imputer un avoir",
    reste_apres: "Reste après",
    reglement_pay_cash: "Payer avec ce règlement",
    reglement_pay_cash_help:
      "Part du règlement reçu affectée sur cette facture",
    reglement_couverte_cash:
      "Facture entièrement couverte par le règlement — aucun avoir nécessaire",
    reglement_avoirs_dispo: "disponibles",
    reglement_avoirs_imputes: "Avoirs imputés",
    reglement_avoirs_deja_imputes: "Avoirs déjà imputés",
    reglement_avoir: "Avoir",
    reglement_avoir_a_utiliser: "Avoir à utiliser",
    reglement_avoir_montant: "Montant imputé",
    reglement_avoir_ajouter: "Ajouter un autre avoir",
    reglement_avoir_retirer: "Retirer",
    reglement_deja_regle: "Déjà réglé",
    reglement_dont: "dont",
    reglement_en_avoir: "en avoir",
    imputation_avoir_title: "Imputer un avoir client",
    imputation_avoir_help:
      "Sélectionnez un avoir du même tiers pour l'imputer sur cette facture (règlement sans encaissement).",
    imputation_avoir_select: "Avoir disponible",
    imputation_avoir_solde: "Solde restant de l'avoir",
    imputation_avoir_amount: "Montant à imputer (€)",
    add_line: "Ajouter une ligne",
    unit_price_ht: "PU HT",
    billed: "Facturé",
    unbilled: "Non facturé",
    create: "Créer facture",
    add_cta: "Ajouter une facture",
    return_of: "Retour de",
    to: "à",
    facturation: "Facturation",
    add_ligne: "Ajouter une ligne",
    not_billed: "Non facturé",
    valid_facture: "Valider et figer la facture",
    valid_avoir: "Valider et figer l'avoir",
    valid_facture_warning:
      "Vous allez assigner un numéro à la facture, ceci est irréversible. Confirmez-vous?",
    valid_avoir_warning:
      "Vous allez assigner un numéro à l'avoir, ceci est irréversible. Si des retours clients sont prévus, ils seront réalisé en même temps. Confirmez-vous?",
    en_cours: "En cours",
    a_regler: "À régler",
    reglees: "Réglées",
    en_attente_reglement: "En attente de règlement",
    echeance_depassee: "Échéance dépassée",
    echeances_depassees_detail: "Factures en retard de paiement",
    echeances_voir_tout: "Voir #count autre facture en retard",
    echeances_voir_tout_plural: "Voir les #count autres factures en retard",
    echeances_dialog_title: "Factures en échéance dépassée",
    echeances_search_placeholder: "Rechercher par numéro ou tiers…",
    total_du: "Total dû",
    tout_facture: "Tout est facturé",
    paiements_a_affecter: "Paiements à affecter",
    total_regle: "Total réglé",
    transactions_list: "Liste des règlements",
    etats_facture: "États de la facture",
    filter_amount_trigger: "Montant",
    filter_amount_mode_label: "Mode",
    filter_amount_min: "Min (€)",
    filter_amount_max: "Max (€)",
    filter_amount_clear: "Effacer",
    filter_amount_active: "#mode : #min → #max",
    filter_amount_unbounded: "−",
    adresse_reintegration: "Adresse de réintégration",
    figer_numero: "Figer et affecter un numéro",
    paiement_dialog_title: "Affecter un paiement à une facture",
    affectation: "Affectation",
    affectation_complete: "Le montant du règlement est intégralement affecté",
    affectation_incomplete:
      "Le montant du règlement n'est pas intégralement affecté",
    affectation_exceeded:
      "Le montant du règlement ne peut pas être inférieur à la somme des affectations",
    paiement_label: "#amount € réglé le #date",
    paiement_label_plural: "#amount € réglés le #date",
    paiement_label_avoir: "#amount € imputés via avoir N° #numero",
    paiement_label_avoir_no_num: "#amount € imputés via avoir",
    bl_chooser_title: "Sélection des bons de livraison à associer",
    no_pending_bl_for_tiers:
      "Aucun bon de livraison non facturé disponible pour ce tiers",
    builder_create_facture: "Création d'une facture",
    builder_edit_facture: "Édition d'une facture",
    builder_create_avoir: "Création d'un avoir",
    builder_edit_avoir: "Édition d'un avoir",
    ref_document: "Référence document",
    ref_document_hint: "Référence libre (ex : réf. fournie par le client)",
    ligne_builder_create_facture: "Création d'une ligne de facture",
    ligne_builder_edit_facture: "Édition d'une ligne de facture",
    ligne_builder_create_avoir: "Création d'une ligne de l'avoir",
    ligne_builder_edit_avoir: "Édition d'une ligne de l'avoir",
    ligne_type_commande: "Commandé",
    ligne_type_autre: "Autre",
    mail_dialog_relance: "Envoi d'une relance pour la facture",
    mail_dialog_send: "Envoi de la facture",
    mail_recipient: "Destinataire",
    mail_cc: "En copie",
    mail_message_placeholder: "Votre message ici...",
    mail_show_pdf: "Voir la facture",
    paiement_sync_edit_title: "Règlement issu d'un connecteur",
    paiement_sync_edit_warning:
      "« #label » provient d'un connecteur e-commerce : c'est la boutique qui fait foi. Votre modification sera écrasée à la prochaine synchronisation. Continuer quand même ?",
    paiement_allocated: "#amount € affecté à #numero",
    paiement_allocated_plural: "#amount € affectés à #numero",
    total_paid: "Montant réglé",
    remaining_due: "Reste à payer",
    empty_lignes_draft: "Ajoutez votre première ligne de facture",
    empty_lignes: "Aucune ligne",
    copied_numero: "Numéro copié",
    copy_numero_tooltip: "Copier le numéro",
    pdf_tooltip: "Télécharger en PDF",
    send_mail_tooltip: "Envoyer par mail",
    encours_section: "Règlement",
    encours_remaining: "Reste à payer",
    encours_paid: "Réglé",
    encours_due_today: "Échéance aujourd'hui",
    encours_overdue: "En retard de #count jour(s)",
    encours_no_paiement: "Aucun règlement enregistré",
    encours_settled: "Facture entièrement réglée",
    section_identite: "Informations",
    section_lignes: "Détail",
    section_autres_lignes: "Autres lignes",
    section_totaux: "Totaux",
    rib_inline_label: "Coordonnées de paiement",
    immutable_error: "Cette facture est émise et ne peut plus être modifiée",
    badge_en_redaction: "En rédaction",
    badge_emise_lock: "Émise — non modifiable",
    remise_pied: "Remise pied de facture",
    remise_pied_type: "Type remise pied",
    remise_pied_fixe: "fixe",
    escompte: "Escompte de règlement (%)",
    escompte_hint: "Informatif — mentionné sur la facture",
    sous_total_ht_lignes: "Sous-total HT lignes",
    total_ht_avant_remise: "Total HT avant remise",
    total_remises_ligne: "Total remises",
    pu_brut_ht: "PU brut HT",
    remise: "Remise",
    montant_remise_pied: "Remise pied",
    remises_pied: "Remises pied de facture",
    remise_pied_add_manual: "Ajouter une remise",
    remise_pied_label_placeholder: "Libellé de la remise (optionnel)",
    remise_pied_source_commande: "Commande",
    remise_pied_auto: "Auto-reportée",
    chrono_warning:
      "La date de cette facture (#date) est antérieure à la dernière facture émise #numero (#lastDate).",
    mentions_legales: {
      titre: "Mentions légales",
      penalite_retard: "Pénalités de retard",
      indemnite_forfaitaire: "Indemnité forfaitaire",
      delai_paiement: "Délai de paiement",
      jours: "jours",
      escompte: "Escompte",
      warning_delai_egalim: "Délai de paiement inférieur à 30 jours (EGAlim)",
    },
    ligne_builder_create: "Création d'une ligne de facture",
    ligne_builder_edit: "Édition d'une ligne de facture",
    reglement_simple_avoirs_titre: "Avoirs disponibles",
    reglement_simple_avoir_deja_impute: "Déjà imputé sur cette facture",
    reglement_simple_avoir_lie: "lié à cette facture",
    reglement_simple_avoir_autre: "autre avoir du client",
    reglement_simple_avoir_utilise: "utilisé",
    reglement_simple_avoir_solde: "solde",
    reglement_simple_avoirs_aide:
      "Cocher un avoir l'impute sur cette facture et ajuste le montant à régler.",
    reglement_simple_avoir_non_imputable_titre: "Avoir lié à cette facture, non imputable",
    reglement_simple_avoir_motif_EN_REDACTION:
      "encore en rédaction : il prendra son numéro et deviendra imputable une fois figé.",
    reglement_simple_avoir_motif_CONSOMME: "déjà entièrement consommé.",
    reglement_simple_avoir_motif_SOLDE_EPUISE: "son solde est épuisé.",
    reglement_simple_avoir_motif_ETAT: "son état ne permet pas de l'imputer.",
  },
  chat: {
    nouveau_message: "Nouveau message de #name",
    ouvrir: "Ouvrir",
    title: "Messages",
    subtitle: "Discutez avec vos collègues — citez un OF, un lot, une commande",
    empty: "Aucune conversation",
    pick_conversation: "Choisissez une conversation",
    new: {
      title: "Nouvelle discussion",
      group_name: "Nom du groupe",
      nobody: "Personne à contacter",
      direct_hint: "Un destinataire : discussion directe",
      group_hint: "Plusieurs destinataires : un groupe nommé",
      start: "Démarrer",
    },
    composer: {
      placeholder: "Votre message… @ pour mentionner, # pour citer un OF, un article, une commande…",
      attach: "Joindre un fichier",
      hint: "Entrée : envoyer · Maj + Entrée : retour à la ligne · ↑ : modifier le dernier message",
      shortcuts:
        "Entrée : envoyer\nMaj + Entrée : retour à la ligne\n↑ (zone vide) : modifier mon dernier message\nÉchap : annuler la modification / la réponse\nCtrl (⌘) + Maj + U : joindre un fichier\nCtrl (⌘) + Maj + K : nouvelle discussion\nAlt + ↑/↓ : conversation précédente / suivante\nAlt + Maj + ↑/↓ : conversation non lue précédente / suivante\nPage ↑ / Page ↓ : défiler le fil\n@ : mentionner · # : citer un OF, un lot, une commande",
      send: "Envoyer",
      upload_failed: "Envoi impossible : #name",
    },
    thread: {
      load_older: "Messages précédents",
      empty: "Aucun message pour le moment",
      deleted: "Message supprimé",
      edited: "modifié",
      editing: "Modification du message",
      reply: "Répondre",
      reply_above: "Message plus haut dans le fil",
      edit: "Modifier",
      delete: "Supprimer",
      delete_confirm: "Le message restera visible comme « supprimé », son contenu sera effacé.",
      react: "Réagir",
    },
    search: {
      none: "Aucun message trouvé",
    },
    entity: {
      open: "Ouvrir",
      no_access: "Vous n'avez pas accès au détail de cet élément.",
      line: {
        quantite: "Quantité",
        produite: "Produite",
        client: "Client",
        fournisseur: "Fournisseur",
        dluo: "DLUO",
      },
    },
    mentioned: "#name vous a mentionné",
    group: {
      title: "Réglages du groupe",
      name: "Nom du groupe",
      participants: "Participants",
      add: "Ajouter des personnes",
      leave: "Quitter le groupe",
      leave_confirm:
        "Vous ne verrez plus cette conversation. Les autres participants la conservent.",
    },
    panel: {
      back: "Retour aux conversations",
      close: "Fermer les messages",
      fullpage: "Ouvrir en pleine page",
    },
    conversation_label: "Discutons !",
    deactive: "Fermer",
    label: "Chat",
    message_label: "Votre message ici ...",
    new_chat: "Nouvelle discussion",
  },
  chat_entity_types: {
    NON_CONFORMITE: "Non-conformité",
    ARTICLE: "Article",
    SPECIFICATION: "Spécification",
    TIERS: "Tiers",
    LIGNE: "Ligne",
    OF: "Ordre de fabrication",
    LOT: "Lot",
    COMMANDE: "Commande fournisseur",
    COMMANDE_CLIENT: "Commande client",
    BON_LIVRAISON: "Bon de livraison",
    FACTURE: "Facture",
    USERINFOS: "Personne",
    USER: "Personne",
  },
  auth: {
    login: "Se connecter",
    logout: "Se déconnecter",
    signup: "Créer un compte",
  },
  home: {
    remote_operation: "Opération",
    remote_variable: "à distance",
    remote_wait:
      "Veuillez patienter jusqu'à sa complétion, ou ouvrez un nouvel onglet!",
  },
  chatbot: {
    window_dock_tab: "Ajouter au panneau ancré",
    dock_new_tab: "Nouvelle conversation dans le panneau",
    dock_collapse: "Replier le panneau",
    dock_hint_tab: "Lâche ici pour ajouter un onglet",
    window_dock: "Ancrer à droite (split l'écran)",
    window_undock: "Détacher en fenêtre",
    dock_hint: "Lâche ici pour ancrer à droite",
    dock_resize: "Glisse pour ajuster la largeur",
    window_more: "Autres actions",
    open_window_hint:
      "Ouvrir Justy dans une fenêtre (tu peux en ouvrir plusieurs)",
    notify_toggle: "Me prévenir quand Justy a répondu",
    window_minimize: "Réduire",
    window_maximize: "Agrandir",
    window_restore: "Restaurer",
    window_close: "Fermer la conversation",
    thinking: "Réponse en cours…",
    window_open: "Ouvrir dans une fenêtre",
    answer_ready: "Justy a répondu",
    title: "Justy",
    tooltip: "Ouvrir l'assistant IA",
    // Transparence IA (règlement UE 2024/1689 « AI Act », art. 50) — ne pas
    // supprimer : l'utilisateur doit savoir qu'il s'adresse à une IA.
    ai_subtitle: "Assistant IA",
    ai_disclosure:
      "Vous échangez avec Justy, un assistant IA. Ses réponses sont générées automatiquement et peuvent être inexactes : vérifiez les informations importantes.",
    ai_disclaimer: "Justy est une IA : vérifiez les informations importantes.",
    ai_generated: "Réponse générée par une IA",
    // Jauge de tokens + parcours quota
    tokens_remaining: "Tokens restants : #count",
    tokens_low_short: "Solde bas",
    tokens_exhausted_short: "Épuisé",
    tokens_unavailable: "Solde indisponible",
    tokens_unavailable_hint:
      "Le solde de tokens n'a pas pu être récupéré. L'assistant peut être indisponible.",
    tokens_refill_dev: "Recharger 500k (dev)",
    quota_exhausted_title: "Solde de tokens IA insuffisant",
    quota_exhausted_hint:
      "Justy ne peut plus répondre. Un administrateur peut recharger le solde.",
    quota_exhausted_cta: "Recharger des tokens",
    quota_low_hint:
      "Solde de tokens bas — pensez à recharger pour éviter une interruption.",
    placeholder: "Pose ta question…",
    welcome: "Bonjour ! Comment puis-je t'aider ?",
    welcome_named: "Bonjour #prenom ! Comment puis-je t'aider ?",
    import_commande_client: "Importer une commande client",
    import_commande_client_hint:
      "Joignez un bon de commande (PDF ou photo) et je m'occupe du reste.",
    welcome_hint:
      "Pose-moi une question sur Justup, ou interroge tes données métier.",
    picker_start: "Montrer un élément à Justy (Alt+J)",
    picker_global_hint:
      "Mode interactif : clique un élément de l'écran pour le montrer à Justy (Alt+J)",
    picker_banner: "Clique sur un élément pour le montrer à Justy",
    picker_cancel: "Annuler (Échap)",
    picker_depth_hint: "↑ élargir · ↓ affiner",
    remove_context: "Retirer cet élément",
    picker_suggest_explain: "Explique-moi ça",
    picker_suggest_purpose: "À quoi ça sert ?",
    clear: "Effacer la conversation",
    new_conversation: "Nouvelle conversation",
    no_conversations: "Aucune conversation",
    delete: "Supprimer",
    send: "Envoyer",
    conversations: "Conversations",
    toggle_sidebar: "Afficher/masquer les conversations",
    suggestion_of: "Derniers OF",
    suggestion_help: "Créer un OF",
    suggestion_ca: "Mon CA",
    attach_file: "Joindre un document",
    interrupted: "La réponse a été interrompue",
    retry: "Réessayer",
    cancel: "Arrêter la génération",
    show_capabilities: "Découvrir toutes les capacités",
    hide_capabilities: "Masquer",
    cap_documents: "Traitement de documents",
    cap_documents_desc:
      "Photographiez ou déposez un BL, une facture ou un bon de commande. L'assistant lit le document, identifie le fournisseur, rapproche les articles et propose la réception.",
    cap_data: "Interrogation de données",
    cap_data_desc:
      "Posez vos questions en français : chiffre d'affaires, marges, stocks, production, commandes. L'assistant cherche, calcule et génère des graphiques.",
    cap_quality: "Qualité & Sécurité alimentaire",
    cap_quality_desc:
      "HACCP, traçabilité, rappels produit, audits IFS/BRC, alertes DLC — l'assistant est votre copilote qualité.",
    cap_nav: "Navigation guidée",
    cap_nav_desc:
      "Demandez comment faire quelque chose : l'assistant vous guide visuellement dans l'application en mettant en surbrillance les boutons et zones concernés.",
    cap_actions: "Actions rapides",
    cap_actions_desc:
      "Créez des fournisseurs, passez des commandes, lancez des réceptions — l'assistant exécute pour vous après confirmation.",
    company_rag: {
      title: "Base de connaissance de l'entreprise",
      subtitle:
        "Vos documents internes (procédures, cahiers des charges…) : Justy les consulte en priorité pour répondre avec le contexte de votre entreprise.",
      add: "Ajouter un document",
      uploading: "Envoi…",
      indexing_started: "Indexation lancée — le document sera disponible dans quelques instants.",
      load_error: "Impossible de charger la base de connaissance.",
      upload_error: "L'envoi du document a échoué.",
      reindex_error: "La ré-indexation a échoué.",
      reindex: "Ré-indexer",
      delete: "Retirer",
      delete_title: "Retirer de la base de connaissance",
      delete_message: "« #name » ne sera plus utilisé par Justy. Confirmer ?",
      delete_confirm: "Retirer",
      delete_error: "La suppression a échoué.",
      deleted: "Document retiré de la base de connaissance.",
      too_big: "Fichier trop volumineux (max 15 Mo).",
      empty: "Aucun document dans la base de connaissance",
      empty_hint: "Formats acceptés : PDF (texte), TXT, Markdown, CSV, JSON, XML.",
      stats_docs: "#n document(s) dont #i indexé(s)",
      stats_chunks: "#n extraits indexés",
      stats_tokens: "#n tokens d'indexation consommés",
      chunks_count: "#n extraits",
      status_pending: "En attente",
      status_indexing: "Indexation…",
      status_indexed: "Indexé",
      status_error: "Erreur",
      citation_prefix: "Document interne :",
      forbidden: "Le droit « assistant » est requis pour gérer la base de connaissance.",
      loading: "Chargement…",
    },
  },
  ribs: {
    isDefault: "RIB par défaut",
    setIsDefault: "Utiliser par défaut",
    title: "Coordonnées bancaires",
    add_title: "Ajouter un RIB",
    edit_title: "Modifier le RIB",
    chose_title: "Choisir un RIB de paiement",
    add_cta: "Ajouter un RIB",
    empty: "Aucun RIB enregistré",
    loading: "Chargement...",
    unnamed: "RIB sans nom",
    denomination: "Libellé",
    denomination_placeholder: "Ex: Compte principal, Compte pro...",
    banque: "Code banque",
    banque_tooltip: "5 chiffres identifiant la banque",
    guichet: "Code guichet",
    guichet_tooltip: "5 chiffres identifiant l'agence",
    numero: "N° de compte",
    numero_tooltip: "11 caractères alphanumériques",
    cle: "Clé",
    cle_tooltip: "2 chiffres de vérification",
    IBAN: "IBAN",
    BIC: "BIC / SWIFT",
    BIC_hint: "Optionnel - Code international de la banque",

    show_details: "Afficher les détails RIB",
    hide_details: "Masquer les détails RIB",
    rib_details: "Détails du RIB (remplis automatiquement depuis l'IBAN)",
    copy_iban: "Copier l'IBAN",
    iban_copied: "IBAN copié",

    form_invalid: "Veuillez corriger les erreurs du formulaire",

    validation: {
      iban_required: "L'IBAN est requis",
      iban_format: "Format IBAN invalide",
      iban_key: "Clé IBAN invalide",
      iban_length_fr: "L'IBAN français doit contenir 27 caractères",
      bic_format: "Format BIC invalide (8 ou 11 caractères)",
      bank_code_format: "Code banque : 5 chiffres",
      branch_code_format: "Code guichet : 5 chiffres",
      account_format: "Numéro de compte : 11 caractères",
      rib_key_format: "Clé RIB : 2 chiffres",
    },
  },
  teams: {
    // search: 'Propriétaire de l\'article (si article possédé par un tiers)',
    add_label: "Ajout d'une équipe",
    add_cta: "Ajouter une équipe",
    edit_label: "Modification de l'équipe",
    supervision: "Supervision des équipes",
    supervision_team: "Supervision de l'équipe",
    no_team: "Vous n'êtes affecté à aucune équipe pour le moment",
  },
  ocr: {
    start: "Démarrer l'extraction",
    model_only: "Seulement créer la specif",
    model_article: "Créer un article et y rattacher le modèle",
  },
  data: {
    cancel: "Annuler",
    refSuffix: "Suffixe de référence",
    period: "Période",
    loading: "Chargement…",
    default1: "Entrée 1",
    default2: "Entrée 2",
    default3: "Entrée 3",
    default4: "Entrée 4",
    articles_treated: "Articles traités",
    conso_delta: "Ecart de consommation",
    defineMax: "Définir le maximum possible",
    quantiteProduire: "Quantité à produire",
    quantiteAConsommer: "Quantité à consommer",
    billing: "Facturation",
    theoric_conso: "Consommation théorique",
    manual_conso: "Consommation déclarée",
    consumed: "consommé",
    consumed_plural: "consommés",
    enregistrement: "enregistrement",
    enregistrements: "enregistrements",
    not_specified: "Non précisé",
    last_traced_lot: "Dernier lot tracé",
    users: "Utilisateurs",
    shipped: "Expedié :",
    ready_to_go: "Mis sur unités d'expédition, en attente d'expédition :",
    ordered: "Commandé :",
    client: "Client",
    clients: "Clients",
    fournisseur: "Fournisseur",
    fournisseurs: "Fournisseurs",
    transporteur: "Transporteur",
    details: "Détails",
    dossier_prod: "Dossier de production",
    dashboard: "Tableau de bord",
    confirmation: "Confirmation",
    facture: "Facture",
    avoir: "Avoir",
    factures: "factures",
    client_or_tiers: "Client / Tiers",
    bon_livraisons: "Bons de livraison",
    designation: "Désignation",
    qty_ug_short: "Qté (UG)",
    tva_label: "TVA",
    totalTTC: "Total TTC",
    ttc_short: "TTC",
    quantiteRetourClient: "Quantité à réintégrer",
    facture_avoir: "Factures et avoirs",
    missing_info: "Information manquante",
    validity_date: "Date de validité",
    organisme: "Organisme",
    certificat_bio: "Certificat BIO",
    numero_certificat: "Numéro du certificat",
    costs: "Coûts",
    status: "Statut",
    destinataire_mail: "Destinataire",
    expediteur_mail: "Expéditeur",
    denominationSpecif: "Dénomination de la spécif.",
    denominationArticle: "Dénomination de l'article",
    stayConnected: "Ne pas forcer la reconnexion",
    showLots: "Voir les lots en pied de chaine",
    modifyControleUserDate: "Modifier contrôleur et date de contrôle",
    link: "Lien",
    instructionsAI: "Instruction pour l'IA",
    consigneNC: "Consigne en cas de non conformité",
    tolerance: "Tolérance",
    login: "Identifiant",
    controle_conso: "Combien reste-t-il en PDC du lot",
    bilan_matiere: "Bilan matière",
    billings: "Facturation JustUp",
    delaiReglementJour: "Délai réglement (jours)",
    ip: "IP",
    devise: "Devise",
    checklists: "Checklists",
    documents: "Documents",
    ribs: "RIB",
    models: "Modèles d'articles",
    prixUnitaireAchat: "Prix unitaire (par unité d'achat)",
    stats: "Statistiques",
    quantitePrevue: "Quantité prévue",
    dechargements: "Déchargements",
    receptions: "Réceptions",
    controleParQuantite: "Echantillon dépendant de la quantité à contrôler",
    echantillonParQuantite: "Quantité par échantillon (en unité de gestion)",
    quantite_recue: "Quantité reçue",
    conforme: "Conforme",
    non_conforme: "Non conforme",
    quantite_conforme: "Conforme",
    quantite_non_conforme: "Non conforme",
    quantite_recue_conforme: "Quantité reçue conforme",
    sscc: "SSCC / numéro d'unité d'expédition",
    general_controls: "Contrôles généraux",
    textColor: "Initiales en blanc",
    prixParDefaut: "Prix par défaut",
    prixUnitaire: "Prix unitaire",
    prixCatalogue: "PU Catalogue",
    mailDefault: "Email principal",
    mailPrecision: "Précisions email",
    mailPrecision1: "Emails spécifiques par type d’envoi (optionnel)",
    mailPrecision2: "Si non renseigné, l’email principal sera utilisé.",
    mailCommande: "Commandes envoyées au fournisseur",
    mailCommandeClient: "Commandes passées par le client",
    mailFacturation: "Factures client",
    mailLivraison: "Bons de livraison",
    coutMaterielUnitaire: "Coût matière unitaire",
    coutPersonnelUnitaire: "Coût personnel unitaire",
    desc_commerciale: "Description commerciale",
    microbio: "Critères microbiologiques",
    diametre: "Diamètre",
    densite: "Densité (kg/L)",
    coefficientUnite: "Coefficient",
    device: "Appareil JustUp Live",
    not_applicable: "Non applicable",
    applicable: "Applicable",
    bilanMatiere: "Bilan matière",
    chargements: "Expéditions et BL",
    quantiteAchat: "Quantité en unité d'achat",
    quantiteGestion: "Quantité en unité de gestion",
    uniteClient: "Unité de vente",
    uniteFournisseur: "Unité d'achat",
    uniteGestion: "Unité de gestion",
    quantiteIn: "Quantité en",
    quantiteTotaleIntegree: "Quantité totale intégrée",
    dates: "Dates",
    teams: "Equipes",
    team: "Equipe",
    horaires: "Horaires",
    declared_in_prod_plural: "Déclarés en cours de production",
    declared_in_prod: "Déclarés en cours de production",
    scoreMatiere: "Performance matière premières",
    scorePersonnel: "Performance main d'oeuvre",
    scoreTotal: "Performance économique totale",
    param_mail: "Paramétrage mail",
    signature_placeholder: "Votre signature ici...",
    born_on: "né le",
    entrepot_par_defaut: "Entrepôt par défaut",
    profile_page: "Page de profil",
    lotExpedie: "Quantité expédiée",
    codeGS1: "Code d'entreprise GS1",
    sans_identifiant: "Sans identifiant",
    poste_annule: "Supprimer l'opé de fabrication",
    MAIL_HOST: "Hôte SMTP",
    MAIL_USER: "Nom d'utilisateur",
    MAIL_PASSWORD: "Mot de passe",
    MAIL_FROM: "Adresse par défaut d'envoi",
    subject: "Objet",
    startAdresse: "Adresse de départ",
    endAdresse: "Adresse d'arrivé",
    user: "Utilisateur",
    delivery_adresse: "Adresse de livraison",
    received_by_singular: "Reçu par la commande suivante",
    received_by_plural: "Reçu par les commandes suivantes",
    produced_by_singular: "Produit par l'OF suivant",
    produced_by_plural: "Produit par les OFs suivants",
    receiveConfirmationCommandeClientMail:
      "Est en copie des mails des confirmations de commandes clients",
    receiveCommandeMail: "Est en copie des mails des commandes fournisseurs",
    receivePosteMail: "Est en copie des mails de fin de poste",
    receiveBilanMatiereMail: "Est en copie des mails de bilans matières",
    postes: "Opé. de fab.",
    isPhoto: "Enregistrement d'une photo",
    isValeur: "Enregistrement d'une valeur",
    type_fiche: "Format de fiche imprimable",
    quantiteRefuse: "Quantité refusée",
    unite_simple: "Unité",
    stocks: "Voir les stocks",
    lots: "Voir les stocks",
    info_generales: "Infos générales",
    dureePoste: "Durée de l'opé. de fab. (h)",
    valeurUnitairePonderee: "Valeur unitaire (dernier coût connu)",
    quantiteTotale: "Quantité totale",
    compteur: "Compteur",
    etat: "Etat",
    valeur: "Valeur",
    nb_articles: "Nombre d'articles",
    nb_lots: "Nombre de lots",
    to_compte: "Compter dans cette zone",
    comptage: "Comptages dans cette zone",
    compteRetenu: "Comptage retenu",
    zone: "Zone",
    valeurTotale: "Valeur totale",
    entrepot: "Entrepôt",
    plusieurs_entrepots: "Plusieurs entrepôts",
    sans_stock: "Sans stock",
    compte: "Comptage",
    comptages: "Comptages",
    valeurUnitaire: "Valeur unitaire par défaut",
    valeurUnitaireInventaire: "Valeur unitaire",
    complets: "Nombre de colis complets",
    complets_rouleaux: "Nombre de rouleaux complets",
    inventaire: "Inventaire",
    inventaires: "Inventaires",
    objectif_nominal: "Moyenne des enregistrements",
    evenement: "Evénement",
    bonLivraison: "Bon de livraison",
    bonsLivraison: "Bon de livraison",
    commentaire: "Commentaire",
    prefixArticleRef: "Préfixe référence article",
    prefixLot: "Préfixe numéro de lot",
    prefixCommande: "Préfixe numéro de commande fournisseur",
    prefixFactureCommandeClient: "Préfixe numéro de facture client",
    prefixAvoirClient: "Préfixe numéro d'avoir client",
    prefixOf: "Préfixe numéro d'OF",
    prefixBonLivraison: "Préfixe numéro de bon de livraison client",
    prefixBonRetour: "Préfixe numéro de bon de retour fournisseur",
    prefixCommandeClient: "Préfixe numéro de commande client",
    prefixDechargement: "Préfixe numéro de déchargement",
    prefixReception: "Préfixe numéro de réception",
    prefix_chargement: "Préfixe numéro d'expédition",
    prefix_transfert_entrepot: "Préfixe numéro de transfert entrepôt",
    prefix_bon_transfert: "Préfixe numéro de bon de transfert",
    autres_travaux: "Autres travaux",
    graphs: "Graphiques",
    titre: "Titre",
    resume: "Description",
    type_retour: "Type de remonté",
    pcb: "PCB",
    codeClient: "Code client",
    provisoire: "Provisoire",
    stockDispo: "Stock disponible",
    stockReappro: "Stock de réappro.",
    stockSecurite: "Stock de sécurité",
    quantiteOfDefault: "Quantité de fabrication par défaut",
    quantiteAchatDefault: "Quantité d'achat par défaut",
    stockParams: "Paramètres de stock",
    stockInfos: "Infos stock",
    stockInfosSubtitle: "Seuils de réapprovisionnement et de sécurité",
    commandeInfos: "Infos commande",
    commandeInfosSubtitle: "Paramètres par défaut pour les commandes d'achat",
    proprietaire: "Propriétaire",
    ingredients: "Liste des ingrédients (écrire les allergènes en majuscules)",
    allergenes: "Liste des allergènes",
    nombreFicheToPrint: "Nombre de fiche à imprimer",
    lots_infos: "Informations du lot",
    ean13: "EAN 13",
    ean14: "EAN 14",
    ean14prefix: "Préfixe EAN 14",
    ean128: "EAN 128",
    isDone: "Réalisé / pris en compte",
    gross_weight: "Poids brut",
    gross_weight_uc: "Poids brut / UG (kg)",
    net_weight_uc: "Poids net UC(kg)",
    longueur_uc: "Longeur UC(cm)",
    hauteur_uc: "Hauteur UC(cm)",
    largeur_uc: "Largeur UC(cm)",
    gross_weight_colis: "Poids brut colis(kg)",
    net_weight_colis: "Poids net colis (kg)",
    longueur_colis: "Longeur colis(cm)",
    hauteur_colis: "Hauteur colis(cm)",
    largeur_colis: "Largeur colis(cm)",
    weight_suremballage: "Poids suremballage",
    calculBesoin: "Calcul des besoins",
    quantiteDispo: "Quantité disponible (hors pieds de chaine)",
    version: "Version",
    specification: "Spécification",
    prixHT: "Prix HT",
    total: "Total",
    totalHT: "Total HT",
    recapitulatif: "Récapitulatif",
    dateEcheance: "Date d'échéance",
    transactions: "transactions",
    prixLibre: "Prix libre",
    reduction: "Réduction",
    avecRetour: "Avec retour des produits",
    returnOf: "Retour de",
    at: "à",
    on: "sur",
    numeroBL: "Numéro de BL",
    restant: "Quantité restante",
    compensation: "Compensation",
    avecReintegration: "Réintégration en stock",
    avoir_make: "Gestion des avoirs",
    avoirs: "Avoirs de la commande",
    ca: "Chiffre d'affaire",
    prixCommande: "Prix convenu",
    prixDeVente: "Prix facturé",
    facturation: "Facturation",
    fj: "Forme juridique",
    capital: "Au capital de (€)",
    rcsVille: "RCS (Ville)",
    rcsNumero: "RCS (Numéro)",
    codeAPE: "Code APE",
    tvaIntracom: "Tva Intracommunautaire",
    email: "Email",
    emailFacture: "Destinataires des factures",
    telephone: "Téléphone",
    telephone2: "Téléphone 2",
    adresse: "Adresse",
    adresses: "Adresses",
    rib: "RIB",
    infoBLClient:
      "Informations devant apparaitre sur les bons de livraisons des commandes clients",
    infoConfirmationCommandeClient:
      "Informations devant apparaitre sur les confirmations de commandes clients",
    infoRetourFournisseur:
      "Informations devant apparaitre sur les retours fournisseurs",
    infoBlClient:
      "Informations devant apparaitre sur les bons de livraison clients",
    infoCommandeFournisseur:
      "Informations devant apparaitre sur les commandes fournisseurs",
    infoFactureExpedition:
      "Informations devant apparaitre sur les factures client",
    infoAvoirClient: "Informations devant apparaitre sur les avoirs client",
    quantiteAReintegrer: "Quantité à réintégrer en stock",
    integrateInStock: "Intégrer en stock",
    enExpe: "Sur l'unité d'expédition",
    qttTotalCommande: "Quantité commandé",
    infosprix: "Infos commerciales",
    sousFamille: "Famille technique",
    correctAnswer: "Bonne réponse",
    typePeriode: "Type DDM",
    dluoJours: "DDM (Jours)",
    dluoMois: "DDM (Mois)",
    dluoAnnes: "DDM (Années)",
    historique: "Historique",
    schema: "Schéma",
    nb_schema: "Tolérance",
    mode: "Mode de fonctionnement",
    coutTotalReel: "Coût total",
    coutUnitaireReel: "Coût par unité produite",
    coutHoraireReel: "Coût moyen de l'heure",
    coutTotalTheorique: "Coût total",
    coutUnitaireTheorique: "Coût par unité produite",
    surcoutTotal: "Surcoût total (€)",
    surcoutUnitaire: "Surcoût unitaire (€)",
    surcoutUnitairePourcent: "Surcoût unitaire (%)",
    theorique: "Théorique",
    isEcart: "Calcul d'écart",
    valeur1: "Première valeur",
    valeur2: "Deuxième valeur",
    reel: "Réel",
    analyse: "Analyse",
    operator: "Opérateur",
    heures_saisies: "Heures saisies",
    heuresLigne: "Heures lignes",
    heuresTheorique: "Durée théorique",
    heuresTravailles: "Heures payées",
    heureAffectees: "Heures affectées",
    heuresFormation: "Heures formation",
    ecartHeuresTravailles: "Ecart heures",
    coutTotalPersonnel: "Coût humain total",
    ecartCoutTheorique: "Ecart coût théorique",
    ecartCoutTheoriquePourcent: "Ecart coût théorique (%)",
    productiviteTheorique: "Productivité théorique (u/h)",
    productiviteReel: "Productivité réelle (u/h)",
    productivitePourcent: "Performance productivité (%)",
    ecart: "Ecart",
    coutUnitaire: "Coût unitaire",
    coutTheorique: "Coût total théorique",
    coutReel: "Coût total réel",
    format: "Format",
    productivite: "Productivité",
    heures: "Heures",
    coutTotal: "Coût total",
    coutHoraire: "Coût horaire",
    mouvements: "Mouvements",
    controlesLiberatoires: "Contrôles libératoires",
    quantiteUnitaire: "Quantité unitaire",
    lotsPdc: "Lot démarrage",
    tracabilite: "Traçabilité",
    nouveauLot: "Nouveau lot",
    poste_when: "Opé. de fab. du ",
    dateChangement: "Date de changement",
    changement_lot: "Traçabilité et Consommations",
    quantiteProduite2: "Quantité produite",
    quantiteProduite: "Nombre de produits finis présents dans l'atelier",
    quantiteProduiteTotal: "Quantité restante à intégrer",
    produitsSurLigne: "Produits sur ligne",
    consoTheoriqueUnitaire: "Conso. théorique unitaire",
    consoTheorique: "Conso. théorique",
    consoTheoriqueTotalOf: "Conso. théorique totale OF",
    retourPdc: "Retour pied de chaine",
    piedDeChaine: "Pied de chaine",
    consoProd: "Conso. Prod.",
    pertes: "Pertes",
    pertesPourcent: "Pertes (%)",
    lot: "Lot",
    misEnPdc: "Mis en pied de chaine",
    emplacement: "Emplacement",
    identifiantObligatoire: "Identifiant obligatoire",
    commentairePositif: "Commentaire positif",
    commentaireNegatif: "Commentaire négatif",
    derniereUniteProduite: "Dernière unité produite",
    identifiant: "Identifiant",
    saisie: "Saisie",
    linkedFacture: "Facture liée",
    linkedAvoir: "Avoir lié",
    productions: "Productions",
    cadence: "Cadence nominale (/h)",
    nbPersonnes: "Nombre d'ouvriers",
    bilanProductivite: "Bilan productivité",
    tempsCalage: "Temps de calage (minutes)",
    tempsNettoyage: "Temps de nettoyage (minutes)",
    denomination: "Dénomination",
    profile: "Mon profil",
    infos: "Infos",
    colors: "Couleurs",
    minutes: "Minutes",
    nomenclature: "Nomenclature",
    stock: "Stock",
    detail_stock: "Stocks et lots ",
    of: "OF",
    of_title: "Ordres de fabrication (OF)",
    naf: "NAF",
    dateDebut: "Date de mise en action",
    dateStart: "Date de début",
    startedAt: "Démarrée le",
    fromDate: "du",
    toDate: "au",
    dateEnd: "Date de fin",
    tva_intracom: "TVA Intracom",
    planning: "Planning",
    majorationPercent: "Majoration salaire (%)",
    majorationSalaire: "Majoration salaire (€)",
    panier: "Panier (€)",
    detail_lot: "Détails du lot",
    personnel: "Personnel",
    tauxHoraire: "Taux horaire (Tout compris)",
    dluo: "Date limite",
    quantite: "Quantité",
    part_saisie_aide:
      "Saisir un pourcentage : les autres composants se répartissent le reste au prorata de ce qu'ils pèsent déjà, et la somme reste à 100 %.",
    quantiteAProduire: "Quantité à produire",
    quantiteUniteGestion: "Quantité (unité de gestion)",
    siret: "Siret",
    numero: "Numéro",
    tva: "TVA (%)",
    actions: "Actions",
    mouvement: "Mouvement",
    date_mouvement: "Date du mouvement",
    dateCreation: "Date de passage",
    tailleEchantillon: "Taille de l'échantillon",
    nombreEnregistrement: "Nombre d'enregistrements minimum",
    choix: "Choix possible",
    dateReception: "Date de réception",
    dateLivraison: "Date de livraison prévue",
    lotFournisseur: "Lot fournisseur",
    quantiteReception: "Quantité reçue",
    dateValidation: "Date de validation",
    dateConfirmation: "Date de confirmation",
    dateFermeture: "Date de fermeture",
    type_mouvement: "Type de mouvement",
    francoPrix: "Franco (Prix)",
    francoQuantite: "Informations Franco",
    code: "Code article",
    commandes: "Commandes fournisseur",
    commandes_client: "Commandes client",
    commande_client: "Commande client",
    famille: "Famille",
    debut: "Début",
    fin: "Fin",

    numeroCommandeClient: "Numéro de commande client",

    quantiteAUtiliser: "Quantité à utiliser",
    quantiteARajouterPalette: "Quantité à rajouter sur l'unité d'expédition",
    quantiteReintegrer: "Quantité à réintégrer",

    heuresTravaillees: "Heures travaillées",
    heuresRTT: "Heures en RTT",
    heuresConges: "Heures en congés",
    heuresMaladie: "Heures en Maladie",
    heuresNonJustifiees: "Heures non justifiées",

    sous_famille: "Famille technique",
    delaiReglement: "Délai de réglement",
    typeReglement: "Type de réglement",
    unite: "Unité de gestion",
    unite_achat: "Unité d'achat",
    unite_vente: "Unité de vente",
    unite_base: "unité",
    reference: "Réference",
    reference_fournisseur: "Réf. fournisseur",
    reference_client: "Réf. client",
    coefficientUniteGestion: "Coef. unité de gestion",
    multipleAchat: "Multiple d'achat",
    multipleVente: "Multiple de vente",
    /**
     * ⚠️ « ex: 2045 » ne ressemblait à aucun multiple plausible et brouillait la compréhension du champ :
     * un exemple doit être exemplaire. 6 se lit immédiatement comme « par cartons de six ».
     */
    multiple_placeholder: "ex : 6",
    poste: "Poste",
    role: "Rôle",
    modele: "Utiliser un modèle",
    quantiteMin: "Qtt Min.",
    quantiteMax: "Qtt Max.",
    prix: "Prix",
    prixTTC: "Prix TTC",
    delaiLivraison: "Délai de livraison (jours ouvrés)",
    ddmMiniJours: "Date limite mini contractuelle (jours)",
    ddmMiniJours_placeholder: "Ex : 60",
    ddmMiniJours_hint:
      "Durée minimale entre l'expédition et la date limite (DDM ou DLC selon le type d'article). Un lot est éligible uniquement si dateLimite ≥ date_expé + N jours.",
    ligne: "Ligne de prod.",
    lignes: "Lignes de prod.",
    type: "Type",
    type_list: "Type de liste",
    tiers: "Clients et fournisseurs",
    current: "Courant",
    changePassword: "Changer de mot de passe",
    password: "Mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    passwordMismatch: "Les mots de passe ne correspondent pas",

    articles: "Articles",
    article: "Article",
    duree: "Durée",
    administration: "Administration",
    infos_perso: "Infos perso.",
    colorization: "Couleurs",
    label: "Libellé",
    description: "Description",
    description_variable: "Description du paramètre",
    settings: "Paramètres",
    informations_generales: "Informations générales",
    composants: "Composants",
    procede_article: "Procédé de l'article",
    procede: "Procédé",
    controls: "Contrôles",
    nom: "Nom",
    prenom: "Prénom",
    ddn: "Date de naissance",
    sexe: "Sexe",
    date: "Date",
    qte: "Qté",

    modeOperatoire: "Mode opératoire",

    limiteHaute: "Limite haute",
    limiteBasse: "Limite basse",
    nominal: "Nominal",

    limiteHauteString: "Limite haute",
    limiteBasseString: "Limite basse",
    nominalString: "Nominal",
    delivery_adress: "Adresse de livraison",
    billing_adress: "Adresse de facturation",
    entrepot_depart: "Entrepôt de départ",
    entrepots: "Entrepôts",
    add_entrepot: "Ajouter un entrepôt",
    assigner_entrepot: "Assigner un entrepôt",
    entrepot_stockage: "Entrepôt de stockage",
    quantityProdDefault: "Quantité de référence",
    quantityProdDialogTitle: "Modification de la quantité de référence",
    transfert: "Transfert",
    commande: "Commande",
    dechargement: "Déchargement",
    reception: "Réception",
    palettes: "Unités d'expédition",
    livraisons: "Livraisons",
    contacts: "Contacts",
    commercial: "Commercial",
    procedes: "Procédés",
    params_specification: "Paramètres de spécification",
    logistique: "Logistique",
  },
  models: {
    add_cta: "Ajouter un modèle",
    articles_linked: "Articles liés",
    no_articles_linked: "Aucun articles lié",
    articles_linked_dialog_title: "Articles liés à la spécification",
  },
  onboarding: {
    chapter_prev: "Chapitre précédent",
    chapter_next: "Chapitre suivant",
    watch_videos: "Regarder les vidéos",
    video_playlist: "Toutes les vidéos",
    video_next_in: "Vidéo suivante dans #s s",
    video_play_now: "Lire maintenant",
    video_cancel_auto: "Annuler",
    video_autoplay: "Lecture automatique",
    video_watched: "Déjà vue",
    video_prev: "Vidéo précédente",
    video_next: "Vidéo suivante",
    page_title: "Onboarding",
    page_subtitle: "Configurez et prenez en main JustUp, étape par étape.",
    sidenav_label: "Onboarding",
    guides: "guides",
    status_not_started: "À démarrer",
    status_in_progress: "En cours",
    status_learned: "Guides terminés",
    status_done: "Configuré",
    status_complete: "Terminé",
    action_start_guide: "Démarrer le guide",
    action_continue_guide: "Continuer le guide",
    action_review_guide: "Revoir le guide",
    facet_done: "Données en place",
    facet_done_tip:
      "Détecté automatiquement depuis vos données réelles (articles, commandes…)",
    checks_error: "Vérification incomplète",
    checks_error_tooltip:
      "Certaines vérifications de données ont échoué (réseau/API). Les statuts « fait » peuvent être sous-évalués — relancez la vérification.",
    quickwin_preexisting: "Déjà en place à votre arrivée",
    action_watch_video: "Voir la vidéo",
    action_ask_justy: "Demander à Justy",
    data_check_ok: "Données présentes",
    data_check_missing: "Aucune donnée",
    refresh_checks: "Vérifier",
    see_in_app: "Voir en vrai",
    see_in_app_tip: "Démarre le guide interactif correspondant",
    next_step: "Prochaine étape",
    welcome_title: "Bienvenue sur JustUp !",
    welcome_subtitle:
      "Configurez et apprenez JustUp en suivant votre parcours de mise en route.",
    welcome_description:
      "Le parcours vous guide groupe par groupe, en combinant tutoriels interactifs et saisie de vos premières données.",
    welcome_start: "Commencer l'onboarding",
    welcome_later: "Plus tard",
    justy_prompt_module:
      "Comment faire : #module ? Explique-moi simplement, étape par étape.",
    justy_prompt_general:
      "Aide-moi à démarrer mon intégration sur JustUp : par où commencer ?",
    trophies_title: "Vos trophées",
    parcours_title: "Votre parcours",
    recommended: "Conseillé",
    cta_start: "Commencer",
    cta_resume: "Reprendre",
    level: "Niveau #n",
    xp: "#n XP",
    xp_to_next: "#n XP avant le niveau suivant",
    quickwin_goal: "Objectif",
    quickwin_reached: "Atteint !",
    concepts_ack: "J'ai compris la logique",
    concepts_acked: "Logique comprise",
    phases: {
      comprendre: {
        title: "Comprendre JustUp",
        verb: "Comprendre",
        subtitle:
          "Un quart d'heure pour saisir la logique des données avant de configurer.",
      },
      demarrage: {
        title: "Prenez vos marques",
        verb: "Naviguer",
        subtitle: "Découvrez l'interface, le menu et vos tâches du quotidien.",
      },
      entreprise: {
        title: "Configurez votre entreprise",
        verb: "Configurer",
        subtitle: "Société, activité et lignes de production.",
      },
      catalogue: {
        title: "Construisez votre catalogue",
        verb: "Construire",
        subtitle:
          "Articles, spécifications, nomenclatures et contrôles qualité.",
      },
      approvisionnement: {
        title: "Approvisionnez-vous",
        verb: "Acheter",
        subtitle: "Fournisseurs, commandes d'achat et réceptions en stock.",
      },
      fabrication: {
        title: "Fabriquez",
        verb: "Produire",
        subtitle: "Ordres de fabrication, planification et traçabilité.",
      },
      vente: {
        title: "Vendez & expédiez",
        verb: "Vendre",
        subtitle:
          "Commandes clients, expéditions, bons de livraison et factures.",
      },
      pilotage: {
        title: "Pilotez votre activité",
        verb: "Piloter",
        subtitle: "Stock, traçabilité, tableaux de bord et rentabilité.",
      },
      qualite: {
        title: "Maîtrisez votre qualité",
        verb: "Maîtriser",
        subtitle:
          "Contrôles, non-conformités et tâches périodiques : la preuve de ce que vous produisez.",
      },
    },
    quickwins: {
      logique: {
        title: "Logique comprise",
        desc: "Vous avez compris comment JustUp structure vos données.",
      },
      navigation: {
        title: "Navigation maîtrisée",
        desc: "Vous savez vous repérer et naviguer dans JustUp.",
      },
      usine: {
        title: "Usine configurée",
        desc: "Votre entreprise et vos lignes de production existent dans JustUp.",
      },
      produit: {
        title: "Premier produit fabricable",
        desc: "Un article et sa spécification sont prêts à être produits.",
      },
      matiere: {
        title: "Première matière reçue",
        desc: "Un fournisseur, une commande d'achat : votre première matière entre en stock.",
      },
      of: {
        title: "Première fabrication lancée",
        desc: "Vous avez créé votre premier ordre de fabrication.",
      },
      facture: {
        title: "Première vente facturée",
        desc: "Une commande client transformée en facture.",
      },
      autonome: {
        title: "Pilote autonome",
        desc: "Vous maîtrisez tout le cycle : vous pilotez JustUp en autonomie.",
      },
      maitrise: {
        title: "Qualité maîtrisée",
        desc: "Vous savez déclarer un écart, le clôturer et tenir vos tâches périodiques.",
      },
    },
    justy: {
      welcome:
        "Bienvenue sur JustUp ! Je suis Justy, je vous accompagne. On commence par #phase. 🚀",
      progress: "Beau travail ! Prochaine étape conseillée : #phase. 💪",
      near_done:
        "Bravo, votre intégration est complète. Vous êtes 100 % autonome sur JustUp ! 🎉",
      done: "Bravo, ton intégration est complète. Tu es 100 % autonome sur JustUp ! 🎉",
    },
    celebration: {
      quickwin: "Trophée débloqué !",
      levelup: "Niveau atteint !",
    },
    logic_title: "La logique des données",
    glossary_title: "Glossaire",
    of_cycle: {
      title: "Le cycle de production",
      s1_t: "Article + Ligne",
      s1_c: "nomenclature, procédé et famille technique",
      s2_t: "Ordre de fabrication",
      s2_c: "fabriquer l'article sur la ligne, en puisant si besoin dans le stock existant",
      s3_t: "Production",
      s3_c: "saisie des contrôles + traçabilité",
      s4_t: "Réintégration",
      s4_c: "la quantité produite entre en stock (produit fini)",
      s5_t: "Analyse de performance",
      s5_c: "rendement, coûts réels et écarts de l'OF",
    },
    schema: {
      model: "Modèle de spécif",
      model_sub: "recette + procédé + contrôles + variables",
      inherit: "hérite",
      article_own: "recette & prix propres",
      supplier: "Fournisseur",
      client: "Client",
      buy_price: "prix d'achat",
      sell_price: "prix de vente",
      component_cost: "Coût composant",
      nomenclature: "via nomenclature",
      finished_cost: "Coût produit fini",
      margin: "Marge",
      model_title: "Modèle : Biscuit sablé",
      model_vars: "Variables : parfum · poids",
      one_model: "1 modèle",
      n_articles: "3 articles · 0 re-saisie",
      article_ex1: "Sablé nature · 250 g",
      article_ex2: "Sablé choco · 250 g",
      article_ex3: "Sablé nature · 500 g",
      supplier_ex: "Farine · 0,85 €/kg",
      mat_cost: "Coût matières",
      mat_cost_amount: "2,10 €",
      labor: "+ main d'œuvre (procédé)",
      finished_amount: "3,40 €",
      sell_client: "Prix de vente client",
      sell_amount: "5,00 €",
      margin_amount: "1,60 €",
      margin_calc: "= 5,00 − 3,40",
      spec_sub: "la fiche complète d'un article",
      spec_ex: "ex : le Sablé nature 250 g",
      part_nomenclature_role: "les ingrédients",
      part_nomenclature_ex: "farine, beurre, sucre…",
      part_procede_role: "les étapes",
      part_procede_ex: "pétrir, cuire 12 min",
      part_controle_role: "les vérifications",
      part_controle_ex: "T° four, poids sachet",
      part_variable_role: "les curseurs",
      part_variable_ex: "parfum, poids",
      zoom: "Agrandir le schéma",
      k_model: "MODÈLE",
      k_articles: "ARTICLES",
      k_buy: "ACHAT",
      k_cost: "COÛT",
      k_sale: "VENTE",
      k_margin: "MARGE",
      k_spec: "SPÉCIFICATION",
      model_var1: "parfum",
      model_var2: "poids",
      article_ex1_v1: "nature",
      article_ex1_v2: "250 g",
      article_ex2_v1: "choco",
      article_ex2_v2: "250 g",
      article_ex3_v1: "nature",
      article_ex3_v2: "500 g",
      k_invoice: "FACTURE",
      lot_pill: "LOT",
      circuit_n1: "Fournisseur",
      circuit_n1_s: "matières premières",
      circuit_n2: "Réception",
      circuit_n2_s: "un lot de matière est créé",
      circuit_n3: "Stock",
      circuit_n3_s: "lots et durabilités suivis",
      circuit_n4: "Production",
      circuit_n4_s: "consomme des lots",
      circuit_n5: "Produit fini",
      circuit_n5_s: "son lot entre en stock",
      circuit_n6: "Client",
      circuit_n6_s: "lots expédiés tracés",
      circuit_fil:
        "Le lot suit la matière à chaque étape : la traçabilité est automatique.",
      schema_circuit_caption:
        "La matière circule de l'achat à la vente ; JustUp trace chaque étape par lots.",
      vente_n1: "Commande client",
      vente_n1_s: "exige 90 jours de durabilité",
      vente_n2: "Réservation des lots",
      vente_n2_s: "seuls les lots qui tiennent",
      vente_n3: "Palette",
      vente_n3_s: "lots posés, poids tracé",
      vente_n4: "Bon de livraison",
      vente_n4_s: "ce qui part, lot par lot",
      vente_n5: "Facture",
      vente_n5_s: "générée depuis le BL",
      vente_fil:
        "La contrainte DDM du client guide le choix des lots — automatiquement.",
      schema_vente_caption:
        "De la commande à la facture : les lots sont choisis selon la durabilité exigée par le client.",
      schema_model_caption: "Un modèle, autant d'articles que de variantes.",
      schema_cascade_caption:
        "Le coût remonte la recette ; comparé au prix de vente, il donne la marge.",
      schema_spec_caption:
        "Une spécification = nomenclature + procédé + contrôles, paramétrés par des variables.",
      nomenc_proc: "nomenclature + procédés",
      param_ligne: "paramétrage · famille technique",
      of_fabricate: "fabriquer l'article sur la ligne",
      of_stock_in: "± stock en entrée",
      prod_saisie: "saisie : contrôles + traçabilité",
      reintegration: "Réintégration",
      perf: "Analyse de perf.",
      pf_stock: "Produit fini → stock",
      production: "Production",
      ligne: "Ligne de prod",
      schema_of_caption:
        "Le cycle de production : article + ligne → OF → production → réintégration (stock + analyse).",
    },
    chapters: {
      c1: "Du modèle aux articles",
      c2: "La spécification, fiche d'identité du produit",
      c0: "Le circuit de l'usine, en une image",
      c5: "Le chemin d'une commande client",
      c3: "Des prix d'achat à la marge",
    },
    principles: {
      p1: {
        title: "On décrit une fois, tout se déduit",
        body: "La spécification décrit votre produit (recette, étapes, contrôles). Production, coûts et contrôles qualité en découlent automatiquement — sans re-saisie.",
      },
      p2: {
        title: "Tout se recalcule en cascade",
        body: "Un prix d'achat change ? Le coût des recettes qui l'utilisent, celui des produits finis et les marges se mettent à jour, en chaîne.",
      },
      p3: {
        title: "Le réel corrige le prévu",
        body: "Chaque production compare le théorique (recette) au réel (saisies terrain) : rendements, pertes et coûts réels, lot par lot.",
      },
    },
    chquiz: {
      label: "Quiz éclair",
      ok: "Exact !",
      retry:
        "Pas tout à fait — la réponse est dans le chapitre juste au-dessus.",
      circuit: {
        q: "Un lot de biscuits livré pose problème. Comment retrouver la farine utilisée ?",
        a1: "En fouillant les emails du fournisseur",
        a2: "Par la traçabilité du lot : il relie produit fini, production et matières",
        a3: "Impossible une fois le produit expédié",
      },
      specification: {
        q: "Que contient la spécification d'un article ?",
        a1: "Seulement la recette",
        a2: "La recette, les étapes de fabrication et les contrôles qualité",
        a3: "Son prix de vente",
      },
      catalogue: {
        q: "Je vends le même sablé en 250 g et 500 g. Combien de modèles dois-je créer ?",
        a1: "Deux, un par poids",
        a2: "Un seul — le poids est une variable, chaque valeur donne un article",
        a3: "Aucun, deux articles suffisent sans modèle",
      },
      prix: {
        q: "D'où vient la marge affichée sur un produit fini ?",
        a1: "Je la saisis à la main",
        a2: "Prix de vente client moins coût calculé depuis la nomenclature",
        a3: "Du chiffre d'affaires du mois",
      },
      cycle: {
        q: "Que se passe-t-il à la réintégration d'un OF ?",
        a1: "La quantité produite entre en stock comme lot de produit fini",
        a2: "L'OF est supprimé",
        a3: "Le stock de matières premières remonte",
      },
      vente: {
        q: "Un client exige 90 jours de DDM. Que fait JustUp à la préparation ?",
        a1: "Il bloque la commande",
        a2: "Il ne propose que les lots qui tiennent encore 90 jours à la livraison",
        a3: "Il imprime une alerte à coller sur la palette",
      },
    },
    quiz: {
      title: "Vérifions en 3 questions",
      validate: "Valider mes réponses",
      retry: "Pas tout à fait — corrigez les réponses en rouge et revalidez.",
      q1: {
        q: "Je corrige le prix de la farine sur une commande fournisseur déjà reçue. Que se passe-t-il ?",
        a1: "Rien : l'historique ne bouge jamais",
        a2: "Les coûts et marges de tout ce qui découle de cette commande se recalculent en cascade",
        a3: "Seule la facture fournisseur est corrigée",
      },
      q2: {
        q: "Où décrit-on la façon de fabriquer un article (recette, étapes, contrôles) ?",
        a1: "Dans sa spécification",
        a2: "Dans chaque ordre de fabrication",
        a3: "Dans sa famille",
      },
      q3: {
        q: "Qu'est-ce qui permet de remonter d'un produit livré jusqu'à ses matières premières ?",
        a1: "Le numéro de commande",
        a2: "Les lots, créés et consommés à chaque étape",
        a3: "Le bon de livraison",
      },
    },
    logic: {
      lot_trace: {
        title: "Le lot, fil rouge de la traçabilité",
        body: "À chaque étape, la matière est identifiée par un lot : reçu du fournisseur, consommé par la production, créé en produit fini, expédié au client. Remonter du produit livré jusqu'aux matières premières se fait en un clic — la traçabilité n'est pas un module à part, c'est un sous-produit du flux.",
      },
      vente_ddm: {
        title: "La DDM guide la préparation",
        body: "Un client peut exiger un minimum de jours de durabilité (DDM) à la livraison. À la préparation, JustUp ne propose que les lots qui tiennent cette contrainte et choisit le lot « juste-assez » : les lots longs restent pour les clients exigeants, les pertes diminuent.",
      },
      modele_to_article: {
        title: "Modèles de spécif → articles",
        body: "On crée d'abord des MODÈLES de spécification (recette + procédé + contrôles) avec des VARIABLES pour ce qui change. Chaque article fabriqué s'y rattache et renseigne ses variables : un modèle, autant d'articles que de variantes.",
      },
      familles: {
        title: "Familles & sous-familles",
        body: "Chaque article est classé par famille (et sous-famille) : c'est l'organisation commerciale, utilisée pour les statistiques, les analyses et les regroupements.",
      },
      famille_technique: {
        title: "Famille technique & ligne",
        body: "Un article fabriqué doit avoir une famille technique : elle le relie à une ligne de production. Sans elle, impossible de planifier ni de lancer un ordre de fabrication.",
      },
      lignes: {
        title: "Lignes de production",
        body: "Les lignes représentent vos outils de fabrication. On y rattache les familles techniques pour savoir où et comment chaque produit est fabriqué.",
      },
      tiers_fournis: {
        title: "Tiers & articles fournis",
        body: "Fournisseurs et clients sont des tiers. Pour chaque couple article × tiers, un « article fourni » porte le tarif, l'unité et la référence propres à ce partenaire (achat côté fournisseur, vente côté client).",
      },
      cascade_prix: {
        title: "Cascade des infos prix",
        body: "Les prix d'achat (articles fournis fournisseurs) donnent le coût des composants ; ce coût remonte la nomenclature jusqu'au produit fini, puis se compare au prix de vente (articles fournis clients) pour calculer la marge. Nuance importante : changer le prix catalogue chez le fournisseur ne réécrit pas l'historique — il s'applique aux prochaines commandes. En revanche, corriger le prix sur une commande existante recalcule en cascade tout ce qui en découle (coûts des lots, marges).",
      },
    },
    glossary: {
      ddm: {
        term: "DDM / DLC",
        def: "DDM : date de durabilité minimale — passé la date, le produit reste consommable mais peut perdre en qualité. DLC : date limite de consommation (produits périssables) — dépassée, le produit est invendable. Chaque lot porte sa date ; un client peut exiger un minimum de jours restants à la livraison.",
      },
      stock: {
        term: "Stock & mouvements",
        def: "Le stock n'est jamais saisi : il résulte des mouvements (réception, consommation, production, expédition), tous tracés par lot.",
      },
      palette: {
        term: "Palette (unité d'expédition)",
        def: "Regroupe les lots à expédier sous un code SSCC unique. Contenu, poids et destination sont tracés.",
      },
      bl: {
        term: "Bon de livraison",
        def: "Liste ce qui part chez le client, lot par lot. Il alimente la facture et la traçabilité aval.",
      },
      article: {
        term: "Article",
        def: "Une référence gérée dans JustUp : matière première, emballage, ou produit fabriqué/vendu.",
      },
      mp: {
        term: "Matière première (MP)",
        def: "Ce qu'on achète pour fabriquer : farine, beurre, emballages… Reçue en lots tracés.",
      },
      pf: {
        term: "Produit fini (PF)",
        def: "Ce qui sort de la production, prêt à vendre. Chaque production crée un lot de produit fini tracé.",
      },
      specification: {
        term: "Spécification",
        def: "La fiche technique complète d'un produit fabriqué : recette, procédé et contrôles, versionnée.",
      },
      modele_specif: {
        term: "Modèle de spécif",
        def: "Une spécification partagée et réutilisable : plusieurs articles s'y rattachent au lieu de tout refaire.",
      },
      variable: {
        term: "Variable",
        def: "Un paramètre du modèle qui change d'un article à l'autre (ex : taux de sucre). Chaque article met sa valeur.",
      },
      nomenclature: {
        term: "Nomenclature",
        def: "La recette : liste des composants et quantités pour fabriquer le produit.",
      },
      procede: {
        term: "Procédé",
        def: "Les étapes de fabrication (mélange, cuisson, conditionnement…), certaines optionnelles.",
      },
      controle: {
        term: "Contrôle",
        def: "Un point de contrôle qualité (conforme/non conforme, mesure, CCP…) rattaché à une étape.",
      },
      famille: {
        term: "Famille",
        def: "Le classement commercial principal d'un article (ex : Confitures, Sauces).",
      },
      sous_famille: {
        term: "Sous-famille",
        def: "Une subdivision de la famille pour affiner le classement.",
      },
      famille_technique: {
        term: "Famille technique",
        def: "Le regroupement de production d'un article fabriqué, lié à une ligne — indispensable pour produire.",
      },
      ligne: {
        term: "Ligne de production",
        def: "L'outil/poste où l'on fabrique. Reliée aux familles techniques.",
      },
      tiers: {
        delete_confirm_message: "Vous êtes sur le point de supprimer ce tiers. Confirmez-vous ?",
        delete_sync_link_warning: "Ce tiers est lié à un connecteur e-commerce actif. La suppression dans Justup ne le retire pas de la boutique en ligne, et les futures modifications ne seront plus importées automatiquement.", term: "Tiers", def: "Un partenaire : fournisseur ou client." },
      article_fourni: {
        delete_confirm_message: "Vous êtes sur le point de supprimer ce prix spécifique. Confirmez-vous ?",
        delete_sync_link_warning: "Cet article fourni est lié à un connecteur e-commerce actif. La suppression dans Justup ne le retire pas de la boutique en ligne, et les futures modifications ne seront plus importées automatiquement.",
        term: "Article fourni",
        def: "Le lien article × tiers : tarif négocié, unité et référence propres à ce partenaire (achat ou vente).",
      },
      infos_prix: {
        term: "Infos prix",
        def: "Les tranches de prix d'un article fourni (prix selon la quantité). Elles alimentent la cascade coûts/marges.",
      },
      lot: {
        term: "Lot",
        def: "Un ensemble physique de produit tracé (réception ou production), avec DLC/DDM.",
      },
      of: {
        term: "Ordre de fabrication (OF)",
        def: "Le lancement d'une production d'un article en quantité, sur une ligne.",
      },
    },
  },
  routing: {
    onboarding: "Onboarding",
    support: "Support",
    chat: "Messages",
    back: "Retour",
    auth: "Authentification",
    justup_link: "JustUp Link",
    justup_link_qr: "JustUp Link Qr",
    administration: "Administration",
    entreprise: "Entreprise",
    stats: "Stats.",
    facturation: "Facturation",
    gestion_equipes: "Gestion des équipes",
    tracabilite: "Traçabilité",
    audit_bio: "Audit BIO",
    developpement_recette: "Développement Recette",
    analyse_ca: "Analyse CA",
    checklists: "Checklists",
    expeditions: "Expéditions et BL",
    factures: "Factures",
    inventaire: "Inventaire",
    specification: "Spécification",
    parametrage_admin: "Paramétrage admin.",
    pied_de_chaine: "Pied de chaine",
    articles: "Articles",
    production: "Production",
    commandes: "Commandes",
    transferts_entrepot: "Transferts Entrepôt",
    flux_logistiques: "Flux Logistiques",
    commandes_clients: "Commandes clients",
    lot: "Lot",
    lignes_prod: "Lignes de prod.",
    saisie_heures: "Saisie des heures",
    planif_ofs: "Planif. OFs",
    planif_articles: "Planif. Articles",
    gestion_tiers: "Gestion des tiers",
    parametrage_activite: "Paramétrage activité",
    stocks: "Stocks",
    profil: "Profil",
    cost_impact: "Simulation de coûts",
    cost_impact_2: "Simulation de coûts (réf.)",
    connexion_ecommerce: "Connexion e-commerce",
    synchronisation_ecommerce: "Synchronisation e-commerce",
    ofs: "Ofs",
    suivi_production: "Suivi de production.",
    suivi_fabrication: "Suivi de fabrication.",
    suivi_prod: "Suivi de prod.",
    ope_fab: "Opé de fab.",
    bilan_matiere: "Bilan matière",
    mouvements: "Mouvements",
    statistiques: "Statistiques",
    heures_saisies: "Heures saisies",
    infos_generales: "Infos générales",
    logistique: "Logistique",
    nomenclature: "Nomenclature",
    procede: "Procédé",
    controles_article: "Contrôles de l'article",
    specification_article: "Spécification de l'article",
    parametres_specification: "Paramètres de spécification",
    commercial: "Commercial",
    article_infos: "Article infos.",
    specifications_produit: "Spécifications produit",
    commande: "Commande",
    dechargement: "Déchargement",
    reception: "Réception",
    dechargements: "Déchargements",
    commande_client: "Commande client",
    preparation: "Préparation",
    palettes: "Unités d'expédition",
    livraisons: "Livraison",
    lot_infos: "Lot infos.",
    couts: "Coûts",
    transfert_entrepot: "Transfert Entrepôt",
    reception_transfert: "Réception Transfert",
    comptage: "Comptage",
    mon_entreprise: "Mon entreprise",
    exports_csv: "Exports CSV",
    adresses: "Adresses",
    documents: "Documents",
    numerotations: "Numérotations",
    parametres_avances: "Paramètres avancés",
    facturation_justup: "Facturation JustUp",
    zones_geo: "Zones géo",
    pays: "Pays",
    signalements_justy: "Signalements Justy",
    tokens: "Tokens & Facturation IA",
    tiers: "Tiers",
    groupes: "Groupes",
    groupe: "Groupe",
    infos_generales_tiers: "Infos Générales",
    contacts: "Contacts",
    rib: "RIB",
    infos_perso: "Infos perso.",
    horaires: "Horaires",
    parametrage_mail: "Paramétrage mail",
    gestion_utilisateurs: "Gestion des utilisateurs",
    gestion_autorisations: "Gestion des autorisations",
    planning_defaut: "Planning par défaut",
    enumerations: "Enumérations",
    controles_generaux: "Contrôles généraux",
    parametrages_generaux: "Paramétrages généraux",
    unites_expedition: "Unités d'expédition",
    parametrage_commercial: "Paramétrage commercial",
    territoire_taxes: "Territoire & Taxes",
    base_connaissance: "Base de connaissance",
    pdc: "PDC",
    reintegration_pdc: "Réintégration PDC",
    stock: "Stock",
    stats_of: "Stats. OF.",
  },
  fiche_palette: {
    dialog_title: "Impression d'une étiquette Logistique",
    add: "Ajouter un modèle de fiche",
    colis: "colis",
    nb_colis: "Nb colis",
    uc_label: "Quantité UC",
    colis_label: "Quantité colis",
    qty_required_uc: "Indiquez la quantité en UC",
    qty_required_any: "Indiquez la quantité en UC ou en colis",
    alert: {
      weight_missing: "Poids brut / UG manquant — l'étiquette affichera '—'",
    },
  },
  tooltip: {
    go_commande: "Consulter la commande",
    view_detail: "Voir le détail",
  },
  bl: {
    add_label: "Préparer une nouvelle expédition",
    add_palette_label: "Ajouter une unité d'expédition",
    no_linked_bl: "Aucun bon de livraison lié",
    link_bl: "Rattacher un BL",
    waiting_billing: "Bon de livraison en attente de facturation",
    multiple_waiting_billing: "Bons de livraison en attente de facturation",
    scan_already_scanned: "Vous avez déjà scanné cette unité d'expédition",
    scan_palette_ready: "Unité d'expédition prête pour l'expédition",
    mail_dialog_send: "Envoyer le bon de livraison",
    mail_show_pdf: "Voir le bon de livraison PDF",
    mail_subject_default: "Bon de livraison #numero",
    mail_html_default:
      "<p>Bonjour,</p><p>Vous trouverez ci-joint le bon de livraison <strong>#numero</strong>#ccPart.</p><p>Cordialement.</p>",
    mail_html_cc_part: " correspondant à votre commande #ccNumeros",
    numero_provisoire: "PROVISOIRE",
    send_action: "Envoyer par mail",
    send_all: "Envoyer tous les BL par mail",
    send_one: "Envoyer le BL #numero par mail",
    mail_sent: "Bon de livraison envoyé",
  },
  chargement: {
    expedition_title: "Expédition",
    expedition_finalize: "Expédier le chargement",
    no_controles_generaux: "Aucun contrôle général configuré pour cette étape",
    step2_hint: "Scannez ou sélectionnez les unités d'expédition à charger",
    step3_hint: "Récapitulatif du chargement et lettre de voiture",
    controls_deferred: "Contrôles en attente",
    scan_button: "Scanner",
    ctrl_unite_label: "Contrôles de l'unité d'expédition",
    ctrl_none: "Aucun contrôle",
    ctrl_todo: "Contrôles à faire",
    ctrl_in_progress: "En cours",
    ctrl_ok: "Contrôles OK",
    ctrl_nc: "NC détectées",
    scan_not_sscc: "Code non reconnu comme SSCC",
    scan_not_in_chargement: "Cette unité n'appartient pas à ce chargement",
    scan_already: "Unité déjà scannée",
    manual_confirm_title: "Sélection manuelle",
    manual_confirm_message:
      "Code-barres illisible ? Confirmer la sélection manuelle de cette unité.",
    lettre_voiture_doc: "Lettre de voiture (photo, PDF, scan...)",
    recap_title: "Récapitulatif du chargement",
    recap_palettes: "Unités d'expédition",
    recap_poids: "Poids brut total",
    recap_colis: "Nombre de colis",
    recap_quantite: "Quantité",
    recap_transporteur: "Transporteur",
    nc_detected: "non-conformité(s) détectée(s)",
    observations: "Observations / Réserves",
    observations_placeholder: "Observations, réserves transporteur...",
    signature_operateur: "Signature opérateur",
    signature_chauffeur: "Signature chauffeur",
    signature_clear: "Effacer",
    signature_optional: "Optionnel",
    filter_transporteur: "Transporteurs",
    filter_destinataire: "Destinataires (clients)",
    filter_unit_type: "Types d'unité d'expédition",
    filter_entrepot: "Entrepôts de départ",
  },
  chargement_detail: {
    charged: "Expédié",
    date_expedition: "Date d'expédition",
    entrepot: "Entrepôt de départ",
    controles_generaux: "Contrôles généraux du chargement",
    transfert: "Transfert entrepôt",
    print_bls: "Imprimer les BL",
    print_bts: "Imprimer les BT",
    print_fiches: "Imprimer les fiches",
    controles_palette: "Contrôles réalisés sur cette unité",
  },
  changement_lot: {
    cta: "Saisir la traçabilité",
    article_requis: "Choisissez d'abord un composant pour saisir sa traçabilité.",
    orphan_tooltip:
      "Lot rattaché par déduction (saisi hors d'une opération active). Vérifiez le poste auquel il appartient.",
    label: "Saisie de la traçabilité",
    label_conso: "Saisie de traçabilité et de consommation",
    used_from: "utilisé à partir de",
    take_from_pdc: "Prélever sur le stock de composants d'OF (PDC)",
    take_from_stock: "Prélever sur le stock général (entrepôt:",
    label_poste: "Opé. sur laquelle intervient cette saisie de traçabilité",
    quantite_consommation: "Saisir une quantité consommée",
    quantite_consomme: "Quantité consommée",
    already_declared_consommation: "Consommation déjà déclarée",
    declared_consommation: "Consommation déclarée",
    total_pdc: "Total en PDC",
    reste_disponible: "Quantité disponible en PDC",
    is_traca_obligatoire_missing_message_part1: "L'article",
    is_traca_obligatoire_missing_message_part2:
      "n'a aucun enregistrement de traçabilité. Ajoutez-en un pour continuer.",
    is_traca_obligatoire_missing_header:
      "Traçabilité manquante pour les articles suivants :",
    is_traca_obligatoire_missing_item: "traçabilité manquante",
    missing: "À compléter",
    why_cannot_no_ope:
      "Afin de pouvoir saisir une traçabilité, veuillez au préalable démarrer une opération de fabrication",
  },
  controles: {
    frequence_limit:
      "(la tolérance ne peut pas être supérieure à la moitié de la fréquence)",
    isCCP: "est un CCP (Point critique de contrôle)",
    isPRPO: "est un PrPO (Programme Prérequis Opérationnel)",
    isCalage: "Contrôle de calage",
    isJustupTodo: "Apparait dans dans les listes de tâche liée à l'OF ",
    isProd: "Contrôle de prod.",
    isFinPoste: "Contrôle de fin de poste",
    isFinProd: "Contrôle de fin de prod.",
    add_evenement: "Ajouter un événement",
    add_cta: "Ajouter un contrôle",
    add_choice: "Ajouter un choix",
    add_enregistrement: "Ajouter un enregistrement",
    correct_answer: "Réponse correcte",
    select_answer: "Cliquez sur la bonne réponse",
    reception: "Contrôles à receptions",
    reception_lot: "Contrôles à receptions du lot",
    ecartMinutes: "Ecart (minutes)",
    toleranceMinutes: "Tolérance (minutes)",
    poste_start: "Démarrer l'opé. de fab.",
    poste_end: "Terminer l'opé. de fab.",
    start_rattrapage: "Rattraper les heures",
    stop_rattrapage: "Arrêter le rattrapage",
    with_courbe: "Avec affichage courbe",
    with_dynamic: "Avec valeurs dynamiques",
    infos: "Infos générales",
    param_saisies: "Paramètres de saisie",
    configuration: "Configuration",
    quand: "Quand ?",
    quelles_lignes: "Ligne(s) à exclure",
    aucune_donnee: "Aucune donnée disponible pour ce contrôle",
    aucun_enregistrement: "Aucun enregistrement de contrôles",
    ajouter_reponse: "Ajouter une réponse...",
    familles_categories: "Familles / Sous-familles",
    ajouter_evenement: "Ajouter un évènement...",
    controles_generaux: "Contrôles généraux",
    saisir: "Saisir le contrôle",
    conforme: "Conforme",
    non_conforme: "Non conforme",
    a_faire: "À faire",
    realiser_controle: "Réaliser le contrôle",
    non_realise: "Non réalisé",
    valeur_placeholder: "Valeur",
    commentaire_placeholder: "Commentaire...",
    ajouter_evenement_placeholder: "Ajouter un évènement...",
    tips_form_timing:
      "Vous pouvez définir quand et comment le contrôle doit être réalisé :\n" +
      "\n" +
      "Contrôle à fréquence régulière : définissez un intervalle et une tolérance (ex. : toutes les 10 min ± 3 min).\n" +
      "\n" +
      "Événement : créez un ou plusieurs événements (ex. : changement de rouleau d’étiquette) qui permettront de déclencher manuellement le contrôle lorsqu’ils se produisent.\n" +
      "\n" +
      "👉 Les deux options peuvent être utilisées ensemble ou séparément.\n" +
      "👉 Si vous ne cochez aucune case, le contrôle sera effectué une seule fois.",
  },
  lot: {
    label: "Lot",
    edit: "Modification du lot",
    actions: "Actions sur le lot",
    page: "Page du lot",
    goToOf: "OF de production",
    goToCommande: "Commande fournisseur",
    create: "Créer un lot",
    search: "Numéro de lot...",
    createAndSelect: "Créer un nouveau lot",
    unique: "Lot unique",
    emptyCantCreate:
      "Pouvoir séléctionner un lot, veuillez d'abord l'ajouter au pied de chaine",
    select: "Sélectionner un lot",
    count: "Inventorier",
    transferer: "Transférer",
    quick_transfer_title: "Transfert rapide",
    quantite_a_transferer: "Quantité à transférer",
    adresse_destination: "Entrepôt de destination",
    adresse_depart: "Adresse de départ",
    fusionner: "Fusionner les lots",
    stock_cumule_label: "Évolution du stock cumulé",
    stock_cumule_empty:
      "Sélectionnez un ou plusieurs lots pour afficher l'évolution du stock cumulé.",
    no_stock: "Aucun stock disponible pour ce lot selon les critères.",
    recalculer: "Recalculer la quantité",
    depart_mouvement: "Départ du mouvement",
    arrivee_mouvement: "Arrivée du mouvement",
    quantite_mouvement: "Quantité du mouvement",
    lots_sans_stocks: "Lots sans stocks",
    lot_not_in_pdc: "Ce lot n'est pas présent en pied de chaine",
    type_mouvement_filter: "Type de mouvement",
    date_filter: "Date",
    adresse_filter: "Adresse",
    // Fenêtre de date limite (filtre « Stocks et lots »). Le placeholder du
    // dépôt est `#clef`, pas `{{clef}}`.
    ddm_fenetre_jours: "#jours j",
    ddm_fenetre_toutes: "Toutes",
    comptage_title: "Comptage",
    methode_prefix: "Méthode :",
    compter_packs: "Compter le nombre de packs contenant",
    controles_liberatoires: "Contrôles libératoires",
    inherited_from: "Numéro hérité de",
    stock_edit_title: "Inventaire — #article - #adresse",
  },
  commande: {
    edition_lignes: {
      titre: "Modifier une ligne après confirmation",
      deja_receptionnee: "Cette ligne a déjà été réceptionnée (#quantite). La correction ne touche pas ce qui est entré en stock, seulement la quantité commandée.",
      mail_parti: "Le bon de commande a déjà été envoyé au fournisseur : il ne verra pas cette modification. Préviens-le, ou renvoie-lui le bon après correction.",
      confirmee: "La commande est confirmée et attend sa livraison.",
      terminee: "La commande est TERMINÉE : la comptabilité et le coût de revient s'appuient sur ses lignes. Corrige-les seulement pour les faire coller à la réalité.",
      confirmer: "Modifier quand même",
    },
    ligneEdit: "Edition d'une ligne de commande",
    commandeEdit: "Modifier la commande",
    frais_port: "Frais de port",
    frais_port_montant: "Montant HT",
    frais_port_hint:
      "Répartis au prorata du montant HT dans le coût de revient des lots réceptionnés (composante « Transport achat »).",
    add_frais_port: "Ajouter des frais de port",
    edit_frais_port: "Modifier les frais de port",
    dateManuelle: "Forcer une date de livraison",
    ligneCreate: "Ajouter une ligne de commande",
    missing_delivery_date: "Veuillez saisir la date de livraison prévue",
    consulter_tiers: "Consulter le tiers",
    dechargement: "Déchargement",
    desannuler: "Désannuler",
    fiche_palette: "Fiche Palette",
    declare_nc: "Déclarer une NC",
    total_recu: "Total reçu",
    total_depasse_commande: "Dépasse qté commandée",
    ajouter_lot: "Ajouter un lot",
    qte_superieure_zero: "La quantité reçue doit être supérieure à 0",
    conformite: "Conformité",
    etats_commande: "États de la commande",
    etats_reception: "État réception",
    marquer_terminee: "Marquer la commande terminée",
    reopen: "Remettre la commande en cours",
    desolder: "Désolder",
    facturer: "Facturer",
    ajouter_article_vendu: "Ajouter un article vendu",
    prix_libre: "Prix libre",
    adresse_livraison: "Adresse de livraison:",
    adresse_facturation: "Adresse de facturation:",
    numero: "Numéro",
    voir_bon_commande: "Éditer le bon de commande",
    ref_fournisseur: "Réf. fournisseur",
    receptions_title: "Réceptions",
    hist_numero_dechargement: "N° déchargement",
    no_receptions: "Aucune réception pour cette ligne",
  },
  production: {
    mo_directe: "Main d'œuvre directe",
    mo_semi_finis: "Main-d'œuvre des semi-finis incluse",
    hors_mo_semi_finis: "Hors MO des semi-finis",
    hors_mo_semi_finis_aide:
      "Cette carte ne porte que la main-d'œuvre directe de cet ordre de fabrication. Celle des semi-finis consommés est partie avec eux dans le coût matière, où elle est incluse — c'est le même montant, vu de l'autre côté.",
    mo_semi_finis_aide:
      "Un semi-fini consommé est valorisé à son coût de production complet, main-d'œuvre comprise. Cette main-d'œuvre n'apparaît donc pas sur la carte Main d'œuvre, qui ne porte que celle de cet ordre de fabrication.",
    voir_qte_par_poste: "Voir les quantités par poste",
    matiere: "Matière",
    retour_liste: "Retour à la liste",
    cancelReinte: "Annuler la réintégration",
    cancelReinteWarning:
      "Souhaitez-vous vraiment annuler la réintégration du pied de chaine ?",
    preparePDC: "Préparer PDC",
    reintePDC: "Réintégrer PDC",
    integrationPF: "Intégration anticipée",
    declare_nc: "Déclarer une NC",
    canBeMoreProduit: "Peut-être supérieure car en cours de production",
    dashboard_report: "Tableau de bord machine",
    chose_ope_cta: "Sélectionner une opé. :",
    chose_ope_cta_big: "Veuillez sélectionner une opé. à consulter",
    create_ope_cta_big: "Aucune opé. n'a pour l'instant été créé sur cet OF",
    fill_bilan: "Saisir le bilan matière",
    consult_bilan: "Voir le bilan matière",
    select_ligne: "Sélectionner une ligne",
    select_of: "Sélectionner un of",
    start: "Démarrer la production",
    startNow: "Démarrer maintenant",
    continue: "Continuer la production",
    startNextOpe: "Démarrer l'opé. suivante",
    plan: "Planifier l'OF",
    add_poste: "Nouvelle opé. de fab.",
    end_poste: "Terminer l'opé. de fab.",
    add_prod: "Ajouter une production",
    end_prod: "Terminer la production",
    go_attente_reinte: "Attente réinté. PDC",
    poste_starting: "Démarrage de l'opé. de fab.",
    pdc_retour: "Réintégration pied de chaine",
    pdc: "Pied de chaine",
    restart_prod: "Relancer la production",
    restart_ope: "Repasser en cours",
    pin_ope: "Épingler l'opé",
    unpin_ope: "Détacher l'opé",
    preview_poste: "Prévisualisation des infos de démarrage",
    please_start_ope: "Veuillez démarrer l'opé.",
    close_without_prod: "Clôturer l'opé sans production",
    add_hours: "Ajouter des heures de Main d'Oeuvre",
    ope_quantity_cta:
      "Pour effectuer certains calculs pendant le procédé, la spécification nécessite la quantité prévue.",
    ope_dialog: "Nouvelle opé.",
    create_first_ope: "Créer la première opé. de fab.",
    validation_of: "Validation de l'OF",
    validate_qualite: "Valider l'OF d'un point de vue qualité",
    validate_prod: "Valider l'OF d'un point de vue prod.",
    validate: "Valider l'OF",
    finOpe: {
      title: "Quantité produite",
      integre_cette_ope: "Déjà intégré sur cette opé",
      integrations_note:
        "Ces #qte #unite sont déjà pris en compte dans le calcul.",
      nc_already_declared: "Quantité NC déjà déclarée",
      nc_total_hint:
        "Saisissez ci-dessous la quantité conforme produite. Total fabriqué = #conforme + #nc = #total #unite (conforme + NC).",
      locked_title: "Saisie verrouillée",
      locked_desc_en_attente:
        "L'OF est en attente de validation. Pour modifier ces informations, annulez la réintégration depuis le panneau latéral.",
      locked_desc_valide:
        "L'OF est déjà validé. Pour modifier ces informations, annulez la réintégration depuis le panneau latéral.",
      sur_ligne: "Quantité sur ligne",
      sur_ligne_sub: "Ce que vous comptez",
      saisie_directe: "Saisie directe",
      calcule: "Calculé",
      produit_ope: "Produit sur cette opé",
      produit_ope_sub: "Ce que vous avez fabriqué",
      quantite_negative: "Quantité négative",
      erreur_prod: "Problème : production de",
      erreur_comptage_desc:
        "Vous avez compté #surLigne #unite sur ligne, mais il y avait déjà #avantSaisie #unite avant cette opé.",
      erreur_comptage: "Erreur de comptage sur la ligne",
      pertes_casses: "Pertes ou casses non déclarées",
      integration_double: "Intégration comptée en double",
      resultat: "Soit une quantité produite sur cette opé de :",
      total_apres_ope: "Total produit sur l'OF après cette opé",
      objectif_of: "Objectif de l'OF",
      ope_precedente: "Opé précédente",
      integre_sur_ope: "Intégré sur cette opé",
      produit_cette_ope: "Produit sur cette opé",
      votre_saisie: "votre saisie",
      total_apres: "Total après cette opé",
      reste_a_produire: "Reste à produire",
      of_termine: "OF terminé !",
      represente_objectif: "Cela représente",
      de_objectif: "de l'objectif de l'OF",
      aide_title: "Comment ça fonctionne ?",
      aide_desc:
        "Deux façons de renseigner : saisissez soit ce que vous comptez sur la ligne, soit ce que vous avez produit. L'autre valeur se calcule automatiquement.",
      formule_calcul: "Formule de calcul :",
      detail_of: "Détail pour cet OF",
      avant_saisie: "Avant votre saisie",
      produit_maintenant: "Produit sur cette opé",
      chiffres_collent: "Les chiffres ne collent pas ?",
      chiffres_collent_desc:
        "Vérifiez s'il y a eu des pertes, des casses non déclarées, ou des erreurs sur les opérations précédentes.",
      aide_sur_ligne:
        "Comptez tout ce qui est physiquement présent sur votre ligne de production.",
      formule: "Formule",
      aide_produit:
        "Ce que vous avez fabriqué pendant cette opération (hors intégrations).",
    },
    en_cours_production: "En cours de production",
    acceder_production: "Acceder à la production",
    aucun_of_en_cours: "Aucun OF en cours de production",
    of_suivant: "OF suivant",
    prevu_le: "Prévu le",
    consulter_of: "Consulter l'OF",
    aucun_of_prevu: "Aucun OF prévu",
    demarrer_ope: "Démarrer l'opé",
    quantite_atelier: "Quantité dans l'atelier en fin d'opération",
    quantite_produite_ope: "Quantité produite sur l'opération",
    generer_fiche: "Générer une étiquette logistique",
    etats_of: "États de l'OF",
    lignes_production: "Lignes de production",
    dluo_filter: "DLUO",
    date_filter: "Date",
    lot_pf_choix_title: "Numéro de lot du produit fini",
    lot_pf_choix_description:
      "Au démarrage de la production, un numéro de lot est attribué au produit fini. Vous pouvez en créer un nouveau, ou reproduire un lot déjà existant de ce produit (même numéro) — utile pour poursuivre une série ou un même lot de fabrication.",
    lot_pf_choix_new_title: "Créer un nouveau lot",
    lot_pf_choix_new_subtitle: "Un numéro de lot automatique sera généré.",
    lot_pf_choix_reproduce_title: "Reproduire un lot existant",
    lot_pf_choix_reproduce_subtitle:
      "Réutiliser le numéro d'un lot déjà produit pour ce produit fini.",
    lot_pf_choix_select: "Lot à reproduire",
    lot_heritier_title: "Héritage de lot",
    lot_heritier_description:
      "Une ligne de nomenclature requiert l'héritage d'un numéro de lot. Veuillez sélectionner le lot composant dont le numéro sera attribué au lot produit.",
    lot_heritier_empty: "Aucun lot disponible pour cet article.",
    lot_heritier_select: "Lot à hériter",
    lot_heritier_stock_positif: "Stock positif",
    lot_heritier_filter_adresse: "Adresse",
    lot_heritier_hide_dluo_depassee: "Masquer DDM/DLC dépassées",
  },
  pdc: {
    incomplete: "Pied de chaine incomplet",
    add_lot_cta:
      "Veuillez sélectionner le lot et la quantité à ajouter au pied de chaine",
    no_conso: "Aucune conso.",
  },
  cost: {
    provisoire_tooltip:
      "Coût provisoire : l'ordre de fabrication qui a produit ce lot n'a pas terminé. La valeur affichée est estimée d'après la recette et sera remplacée par le coût réel à la clôture.",
    create: "Création d'un coût",
    total_addends_tooltip:
      "Total = somme des colonnes surlignées : Matière pure + Transport achat + MO amont + MO directe.",
    mo_totale_tooltip:
      "Sous-total informatif = MO amont + MO directe (déjà comptées dans le Total).",
    total_desync_tooltip:
      "La décomposition affichée (#decompose €) ne correspond plus au coût valorisé du lot (#valorise €) — données probablement désynchronisées sur un composant amont. Relancer « Voir les impacts cascade → Lancer le recalcul cascade » pour resynchroniser.",
    dateAlreadyExists: "Un coût est déjà existant pour cette date",
    edit: "Modification d'un coût",
    add: "Ajouter un coût",
    noCosts: "Aucun coût",
    title: "Suivi des coûts",
    locked: "Modifié",
    // Revalorisation (mai 2026)
    dialog_add_revalorisation: "Ajouter une revalorisation",
    dialog_edit_revalorisation: "Modifier la revalorisation",
    btn_add_revalorisation_tooltip:
      "Ajouter un nouveau coût daté pour ce lot (l'historique précédent reste intact)",
    btn_cascade_impact: "Voir les impacts cascade",
    btn_cascade_impact_tooltip:
      "Liste les lots et OFs qui utilisent ce lot — possibilité de relancer le recalcul manuellement",
    source: "Source",
    source_type_of: "OF",
    source_type_reception: "Réception",
    source_type_manuel: "Manuel",
    source_type_revalorisation: "Revalorisation",
    edit_origin_blocked: "Coût d'origine — à modifier via la source",
    delete_origin_blocked:
      "Suppression interdite : ce coût provient d'une source (OF / réception). Modifier ou supprimer la source pour le faire évoluer.",
    date_before_lot_creation:
      "La date ne peut pas être antérieure à la date de création du lot.",
    date_already_exists: "Un coût existe déjà pour cette date.",
    // Snacks contextuels (interpolation #source)
    warn_from_of_title: "Coût d'origine — calculé depuis l'OF #source",
    warn_from_of_action:
      "Pour ajuster ce coût d'origine, modifiez les consommations matière ou les temps de l'OF correspondant.",
    warn_from_reception_title:
      "Coût d'origine — issu de la commande fournisseur #source",
    warn_from_reception_action:
      "Pour ajuster ce coût d'origine, modifiez la commande fournisseur correspondante.",
    warn_alt_revalorisation:
      "Sinon, vous pouvez ajouter une revalorisation à une date ultérieure.",
    warn_no_source_label:
      "Ce coût a une origine système qui ne peut pas être modifiée directement. Vous pouvez ajouter une revalorisation à une date ultérieure.",
    btn_voir_of: "Voir l'OF",
    btn_voir_commande: "Voir la commande",
    // Dialog impacts cascade
    cascade_dialog_title: "Impacts cascade",
    cascade_loading: "Chargement…",
    cascade_error: "Impossible de charger les impacts pour ce lot.",
    cascade_empty:
      "Aucun élément aval. Ce lot n'est consommé nulle part — modifier son coût n'aura pas d'impact en cascade.",
    cascade_explanation:
      "Les éléments suivants utilisent ce lot dans leur chaîne de production. Une modification du coût se propage automatiquement à tous ces éléments. Le bouton ci-dessous permet de forcer le recalcul si vous avez corrigé une donnée source.",
    cascade_ofs: "Ordres de fabrication",
    cascade_lots: "Lots aval",
    cascade_open_of_tooltip: "Ouvrir l'OF",
    cascade_open_lot_tooltip: "Ouvrir le lot",
    cascade_trigger: "Lancer le recalcul cascade",
    cascade_triggered:
      "Recalcul cascade lancé — les éléments aval seront recalculés dans un instant.",
    cascade_trigger_error: "Échec du déclenchement du recalcul.",
    commentaire_label: "Commentaire",
    commentaire_placeholder:
      "Ex : ajustement après inventaire, correction prix matière flambée…",
    commentaire_hint:
      "Optionnel — sert à tracer la raison de la revalorisation pour les audits futurs",
  },
  vn: {
    search: "B12, Calcium...",
    add_cta: "Ajouter une constante",
  },
  entity: {
    articles: "Articles",
    administration: "Administration",
    group: "Société",
  },
  misc: {
    by: "par",
    from_right: "A partir des droits de",
    other: "Autres options",
    choice_scan: "Choix du mode de scan",
    use_camera: "Utiliser la caméra",
    deport_camera: "Déporter le scan",
  },
  adresse: {
    numero: "Numéro",
    numeroComplement: "Numéro complément",
    voie: "Voie",
    complement: "Complément de voie 1",
    complement2: "Complément de voie 2",
    cp: "Code postal",
    commune: "Commune",
    add_cta: "Ajouter une adresse",
    search: "Rechercher une adresse",
    pays: "Pays",
    pays_placeholder: "Sélectionner un pays",
    livraisonDefaut:
      "Adresse de livraison par défaut pour les commandes fournisseur",
    disabled: "Désactivée",
    reactivate: "Réactiver",
    delai_expedition_jours_ouvres:
      "Délai logistique vers cette adresse (jours ouvrés)",
    delai_expedition_jours_ouvres_placeholder: "Ex : 2",
    delai_expedition_jours_ouvres_hint:
      "Surcharge le délai d'expédition par défaut paramétré au niveau de l'entreprise. Si vide, retombe sur la valeur d'entreprise.",
  },
  zone_geo: {
    title: "Zones géographiques",
    add: "Ajouter une zone",
    edit: "Modifier la zone",
    name: "Nom de la zone",
    name_hint: "Ex: Europe, Asie…",
    delete_guard: "Des pays sont rattachés à cette zone. Retirez-les d'abord.",
  },
  pays: {
    title: "Pays",
    add: "Ajouter un pays",
    edit: "Modifier le pays",
    iso_code: "Code ISO (alpha-2)",
    iso_code_hint: "Ex: FR, DE, US",
    nom: "Nom",
    zone_geo: "Zone géographique",
    zone_geo_placeholder: "Sélectionner une zone",
    filter_zone: "Filtrer par zone",
    delete_guard:
      "Ce pays est utilisé par des adresses. Désactivez-le d'abord.",
  },
  pass: {
    label: "Trousseau",
    choose_to_edit: "Choisir un trousseau",
    create: "Ajouter un trousseau",
    specifs: "Accès à la liste des articles et à la consultation des spécifs.",
    lignes: "Accès à la liste des lignes de prod. et leur édition",
    tiers: "Accès à la liste des tiers et leur édition",
    stocks: "Accès à la liste des stocks et leur édition",
    suivi_prod: "Accès au suivi de production",
    infos_journalieres: "Accès à la saisie des heures",
    prod: "Accès à la liste des OFs et leur édition",
    plannif_prod: "Accès à la plannification des productions",
    stats_prod: "Accès aux statistiques de production",
    commandes: "Accès à la liste commandes fournisseurs et leurs opérations",
    commandesClient: "Accès à la liste commandes clients et leurs opérations",
    redactionSpecif: "Peut rédiger des spécifications",
    validationSpecif: "Peut valider des spécifications",
    approbationSpecif: "Peut approuver des spécifications",
    section_modules: "Accès aux modules",
    section_other: "Autres droits",
    // Catégories de widgets
    widgets_section: "Droits par widget",
    cat_production: "Widgets production",
    cat_financier: "Widgets financier",
    cat_commandes: "Widgets commandes",
    cat_stock: "Widgets stock",
    cat_supervision: "Widgets supervision",
    cat_qualite: "Widgets qualité",
    cat_ligne: "Widgets ligne",
    cat_article: "Widgets article",
    cat_tiers: "Widgets tiers",
    cat_user_infos: "Widgets opérateur",
    // Production
    wProductiviteMoyenne: "Productivité moyenne",
    wOfEnCours: "OFs en cours",
    wHeuresTravailles: "Heures travaillées",
    wQuantiteOf: "Quantité d'OFs",
    wOfList: "Liste des OFs",
    wSpecificationList: "Liste des spécifications",
    wQuantiteProduitePeriode: "Quantité produite par période",
    // Financier
    wCaCumule: "CA cumulé",
    wCaParPeriode: "CA par période",
    wCaDelta: "CA delta",
    wCaNMoins1: "CA N-1",
    wRepartitionParClient: "Répartition par client",
    wCommandeFournisseurList: "Liste commandes fournisseur",
    wCommandeClientList: "Liste commandes client",
    wValeurParFamille: "Valeur par famille",
    // Stock
    wLotParEtat: "Lots par état",
    wEvolutionValeur: "Évolution valeur stock",
    wEvolutionMarge: "Évolution de la marge",
    wStockSousSeuil: "Alertes de stock (seuils)",
    // Supervision
    wPlanning: "Planning",
    wLiveProduction: "Production en direct",
    // Qualité
    wNcStats: "Statistiques NC",
    wNcClosureMetrics: "Métriques de clôture NC",
    wNcSupplierCompliance: "Conformité fournisseurs",
    wCertificatBioEcheance: "Certificats bio (échéance)",
    // Ligne
    wQuantiteProduiteLigne: "Quantité produite (ligne)",
    wMainOeuvreLigne: "Main d'œuvre (ligne)",
    wProductiviteLigne: "Productivité (ligne)",
    wCoutsLigne: "Coûts (ligne)",
    wOfsLigne: "OFs (ligne)",
    wArticlesProduitsLigne: "Articles produits (ligne)",
    // Article
    wConsosParArticle: "Consommations par article",
    wProductions: "Productions",
    wAchats: "Achats",
    wAchatsEvolution: "Évolution des achats",
    wVentes: "Ventes",
    wMarge: "Marge",
    wHeuresOfs: "Heures OFs",
    wCaPeriodeArticle: "CA par période (article)",
    wCaCumuleArticle: "CA cumulé (article)",
    wMouvementsArticles: "Mouvements articles",
    wStocksArticle: "Stocks article",
    wQuickWin: "Quick Win",
    // Tiers
    wAchatsEvolutionTiers: "Évolution achats (tiers)",
    wVenteEvolutionTiers: "Évolution ventes (tiers)",
    wCaCumuleTiers: "CA cumulé (tiers)",
    wCaParPeriodeTiers: "CA par période (tiers)",
    wMargeTiers: "Marge (tiers)",
    wAchatsTiers: "Achats (tiers)",
    wVentesTiers: "Ventes (tiers)",
    wCommandesTiers: "Commandes fournisseur (tiers)",
    wCommandesClientsTiers: "Commandes client (tiers)",
    // User Infos
    wHeuresUserInfos: "Heures (opérateur)",
    wRepartitionTravailUserInfos: "Répartition travail (opérateur)",
    wOfsUserInfos: "OFs (opérateur)",
    wProductiviteUserInfos: "Productivité (opérateur)",
    wLignesUserInfos: "Lignes (opérateur)",
    wArticlesProduitsUserInfos: "Articles produits (opérateur)",
    infoFinanciereOfLot: "Accès aux infos financières des OFs et Lots",
    factures: "Accès à la facturation",
    costImpact: "Accès à la simulation de coûts",
    viewCoutArbreContributeurs: "Voir les contributeurs nominatifs sur l'arbre des coûts",
    dossierProd: "Impression du dossier de production (Excel et PDF)",
    assistant: "Accès à l'assistant IA",
  },
  article_fourni: {
    unite_a_normaliser_aide:
      "Cette unité n'existe pas dans le référentiel : elle a été saisie à la main. La valeur est conservée — créez-la pour qu'elle devienne sélectionnable partout.",
    unite_creer: "Créer dans le référentiel",
    /*
     * Les trois blocs du formulaire, nommés par ce qu'ils DÉCIDENT — à qui le prix s'applique, à quelles
     * conditions, combien — c'est-à-dire l'ordre dans lequel on renseigne un tarif.
     */
    bloc_portee: "Portée",
    bloc_conditions: "Conditions",
    bloc_tarifs: "Tarifs",
    /**
     * Le coefficient a été saisi contre une AUTRE unité de gestion que celle de l'article
     * aujourd'hui. Le message nomme l'ancienne unité : sans elle, « à relire » ne dit pas quoi
     * relire ni pourquoi.
     */
    coef_a_relire: "Coefficient d'unité à vérifier — saisi en #ancienne",
    /**
     * ⚠️ **Version COURTE, pour la liste des tarifs.** Le libellé long débordait de la cellule
     * « Unité » (bornée à 100 px, `white-space: nowrap`) et chevauchait la colonne PRIX — constaté
     * en recette. La liste suit la convention de sa voisine « Inexploitable » : un mot, et toute
     * l'explication dans l'infobulle.
     */
    coef_a_relire_court: "Unité changée",
    coef_a_relire_aide:
      "L'unité de gestion de l'article a changé depuis que ce coefficient a été saisi : il exprimait un rapport vers #ancienne. Vérifiez-le, puis enregistrez pour lever cet avertissement. Rien n'est bloqué — la valeur peut être encore juste.",
    /**
     * Même avertissement, mais sur une ligne de commande : le coefficient y est FIGÉ à la
     * saisie, donc ni le tarif d'aujourd'hui ni sa correction ne le changent. Le texte doit
     * dire où se corrige la chose — sur la ligne — sinon on va relire le tarif pour rien.
     */
    coef_ligne_a_verifier:
      "Coefficient d'unité à vérifier — figé en #ancienne",
    coef_ligne_a_verifier_aide:
      "Cette ligne a figé son coefficient à la commande, alors que l'article était géré en #ancienne. L'unité de gestion a changé depuis : la conversion de la quantité, le coût et la valorisation de cette ligne sont à vérifier. Corriger le tarif ne suffit pas — c'est le coefficient de la ligne qu'il faut reprendre.",
    unite_creer_titre: "Créer une unité de gestion",
    /**
     * ⚠️ Le placeholder du dépôt est `#clef`, pas `{{clef}}` — piège déjà rencontré dans ce chantier.
     */
    /**
     * ⚠️ « Calculé » laissait croire que la ligne parlait du coefficient du TARIF. Elle énonce le rapport
     * entre deux unités du référentiel — une définition, pas une valeur de cette ligne.
     */
    coef_calcul: "",
    /**
     * ⚠️ Il NOMME la valeur qu'il pose. « Utiliser ce coefficient » demandait de deviner lequel — et le
     * bouton était placé dans un bloc de lecture, où personne ne le voyait.
     */
    coef_corriger: "Corriger :",
    coef_saisi: "Saisi :",
    unite_heritee: "Hérité du niveau",
    coef_deduit: "déduit des unités :",
    ug_article: "unité de gestion de l'article :",
    ug_inconnue: "unité de gestion de l'article inconnue",
    /**
     * ⚠️ Elle dit ce que le nombre représente, puisqu'il n'est pas déductible : sur ces couples, le
     * coefficient est une mesure de l'article — le contenu réel d'une unité d'achat — et non un rapport
     * entre deux unités du référentiel.
     */
    coef_non_calculable:
      "#achat et #ug ne mesurent pas la même grandeur : saisissez ce qu'une unité d'achat contient réellement pour cet article.",
    coef_non_calculable_aide: "",
    coef_contredit: "",
    list_title: "Tarifs négociés",
    sell: "Vente",
    // Indice de synchro PAR TARIF dans l'onglet Commercial (issue #44)
    sync_synced: "Synchronisé",
    sync_to_push: "À pousser",
    sync_hint_synced:
      "Ce tarif est déjà poussé vers #connecteur (cible : #cible)",
    sync_hint_to_push:
      "Ce tarif n'est pas encore poussé vers #connecteur (cible : #cible)",
    sync_target_default: "tarif par défaut",
    sync_target_unknown: "cible non appairée",
    buy: "Achat",
    buy_sell: "Tous",
    edit: "Modification d'un prix spécifique",
    create: "Création d'un prix spécifique",
    group: "Groupe",
    tiers: "Tiers",
    default: "Par défaut",
    link: "Lier le prix à un tiers ou un groupe",
    achetes: "Articles achetés",
    vendus: "Articles vendus",
    prices: "Prix",
    specific_articles: "Prix spécifiques articles",
    specific_default: "Prix de l'article",
    specific_link: "Prix liés à un tiers ou un groupe",
    add: "Ajouter un prix",
    no_specific_price: "Aucun prix spécifique paramétré pour ce tiers",
    show_prices: "Voir et éditer les prix",
    /**
     * ⚠️ **Réécrit après lecture du code qui l'exploite** (`commande-ligne-form`, `multipleAchatState`), car
     * l'ancien texte décrivait le réglage sans dire ce qu'il produit. Ce que le champ fait réellement :
     * à la saisie d'une commande, la quantité — exprimée dans l'unité d'achat/vente — est comparée à ce
     * multiple. Exacte, elle est validée d'une coche ; sinon l'écran **propose** l'arrondi inférieur et le
     * supérieur, cliquables. Jamais de blocage : `applyMultipleAchat` n'agit que sur un clic.
     *
     * L'exemple porte tout le sens : « 20 propose 18 ou 24 » se comprend sans lire la phrase.
     */
    multiple_hint:
      "Commande par multiples de cette quantité. À la saisie d'une commande, une quantité qui n'en est pas un multiple déclenche une proposition d'arrondi — jamais un blocage. Ex. : multiple 6 → une commande de 20 propose 18 ou 24. Vide = aucune suggestion.",
    prix_par_tranche: "Prix par tranche",
    prix_fixe: "Prix fixe",
    apply: "Appliquer",
    type_prix_label: "Type de prix",
    /**
     * ⚠️ Ces quatre textes forment désormais **une seule phrase** (« … commandes client, pour tous les
     * tiers. »), affichée une fois sous les deux bascules. Séparés, ils occupaient quatre lignes pour deux
     * réglages qu'on lit une fois — et ils ne se comprennent qu'ensemble : le sens et la portée décrivent
     * le même tarif.
     */
    type_prix_vente_hint: "Proposé à la création de commandes client,",
    type_prix_achat_hint: "Proposé à la création de commandes fournisseur,",
    link_label: "Appliquer ce prix à",
    link_default_hint:
      "pour tous les tiers (prix par défaut).",
    link_tiers_hint: "pour le tiers sélectionné uniquement.",
    link_group_hint:
      "pour tous les tiers du groupe sélectionné.",
    warning_prix:
      "Les paramètres du prix ont changé, voulez-vous appliquer les modifications ?",
    inherited_from: "Hérité de",
    inherited_placeholder: "Hérité : #value",
    inherited_prices: "Prix hérités (#level)",
    inherited_tooltip:
      "Ce champ n'est pas défini à ce niveau. La valeur « #value » est héritée du niveau #level. Laissez vide pour conserver l'héritage, ou saisissez une valeur pour surcharger.",
  },
  annonce_fournisseur: {
    section_title: "Annonce tarifaire fournisseur",
    section_hint: "Enregistrer une hausse ou une baisse que ce fournisseur vous a communiquée, avec son justificatif.",
    btn_ouvrir: "Enregistrer une annonce",
    titre: "Annonce tarifaire reçue de #fournisseur",
    avis_aucun_envoi: "Rien ne sera envoyé : ce fournisseur vous a communiqué ce tarif, vous ne faites que l'enregistrer.",
    date_effet: "Date d'effet",
    date_fin: "Date de fin",
    date_fin_hint: "Facultative — laisser vide si le tarif est permanent.",
    motif: "Motif communiqué",
    motif_exemple: "ex. hausse du cours des matières premières",
    fenetre_inversee: "La date de fin ne peut pas précéder la date d'effet.",
    justificatif: "Justificatif reçu",
    justificatif_hint: "PDF ou image — c'est la preuve de ce que le fournisseur a annoncé.",
    articles: "Nouveaux prix d'achat",
    prix_actuel: "actuel",
    soit_ug: "soit #prix € en unité de gestion (coef. #coef)",
    aucun_article: "Aucun article n'est acheté à ce fournisseur pour le moment.",
    creer_si_absent: "Créer le tarif d'achat s'il n'existe pas encore",
    recap: "#n prix seront enregistrés, applicables au #date.",
    btn_enregistrer: "Enregistrer l'annonce",
    snack_ok: "#n tarif(s) d'achat enregistré(s).",
    snack_ignores: "#n article(s) non enregistré(s) : aucun tarif d'achat propre à ce fournisseur.",
    snack_rien: "Aucun tarif enregistré.",
    snack_fichier_illisible: "Ce fichier n'a pas pu être lu. L'annonce peut être enregistrée sans justificatif.",
  },
  tarif_historique: {
    badge_annule: "Annulé",
    btn_annuler: "Annuler",
    btn_annuler_confirmer: "Annuler ce tarif",
    btn_annuler_et_prevenir: "Annuler et prévenir le client",
    annul_titre: "Annuler un tarif annoncé",
    annul_portee_ligne: "Seul le tarif de « #article » sera annulé.",
    annul_portee_acte: "Les #n tarif(s) de cet envoi seront annulés.",
    annul_effet: "Le tarif prévu au #date ne s'appliquera pas, et le tarif précédent reprend sa place.",
    annul_deja_en_vigueur: "Ce tarif s'applique déjà : il a pu servir à chiffrer des commandes et à facturer. Il ne sera pas annulé — corrigez-le en enregistrant un nouveau tarif.",
    annul_prevenir: "Prévenir le client par mail",
    annul_destinataires: "Sera envoyé à : #liste",
    annul_objet: "Objet",
    annul_corps: "Message",
    annul_pas_de_courrier: "Ce tarif n'a pas été communiqué par courrier : il n'y a donc rien à démentir auprès du client.",
    mail_annul_objet: "Annulation de notre évolution tarifaire",
    mail_annul_corps: "Bonjour,\n\nNous vous informons que l'évolution tarifaire annoncée avec effet au #date n'entrera finalement pas en vigueur. Nos conditions actuelles restent applicables.\n\nAvec nos excuses pour la gêne occasionnée.",
    snack_annule: "#n tarif(s) annulé(s), #r tarif(s) précédent(s) rétabli(s).",
    snack_annul_refuse: "#n tarif(s) non annulé(s) : déjà en vigueur.",
    snack_annul_rien: "Aucun tarif à annuler.",
    snack_notif_impossible: "Client non prévenu : ce tarif ne lui avait pas été communiqué par courrier.",
    snack_notif_echec: "Le tarif est annulé, mais le mail au client n'a pas pu partir.",
    section_title: "Suivi tarifaire",
    section_hint:
      "Qui a fixé ces prix, quand, et par quel geste. Le courrier n'est pas toujours produit : la trace, elle, l'est toujours.",
    periode_ouverte: "À compter du #debut",
    periode_bornee: "Du #debut au #fin inclus",
    trace_qui_quand: "Enregistré le #date par #auteur",
    auteur_inconnu: "auteur inconnu",
    badge_a_venir: "À venir",
    canal_mail: "Courrier envoyé",
    canal_sans_envoi: "Communiqué autrement",
    tranches_en_place: "#n prix en place",
    tranches_remplacees: "Remplacés depuis",
    voir_courrier: "Voir le courrier",
    sans_courrier: "sans courrier joint",
    courrier_introuvable:
      "Le courrier n'est plus disponible. L'acte tarifaire reste enregistré.",
    destinataires: "Destinataires : #liste",
    motif: "Motif : #motif",
    qte_min: "dès #n",
    aucun_produit: "Aucun produit détaillé pour cet acte.",
  },
  remise: {
    delete_confirm_message: "Vous êtes sur le point de supprimer cette remise. Confirmez-vous ?",
    delete_sync_link_warning: "Cette remise est liée à un connecteur e-commerce actif. La suppression dans Justup ne le retire pas de la boutique en ligne, et les futures modifications ne seront plus importées automatiquement.",
    title: "Remise",
    add: "Ajouter une remise",
    edit: "Modification d'une remise",
    create: "Création d'une remise",
    tiers: "Tiers",
    group: "Groupe",
    list_title: "Remises",
    valeur: "Valeur",
    type: "Type de remise",
    date_debut: "Date de début",
    date_fin: "Date de fin",
    article: "Article",
    link: "Lier la remise à un tiers ou un groupe",
    default: "Par défaut",
    sans_limite: "Sans limite",
    periode: "Période",
    remises_appliquees: "Remises",
    ajouter_manuelle: "Remise manuelle",
    total_avant_remise: "Total avant remise",
    total_apres_remise: "Total après remise",
    montant_remise: "Montant remise",
    warning_remise: "Des remises disponibles ont changé",
    missing_remise: "Toutes les remises pour ce client ne sont pas appliquées",
    appliquer_remises: "Appliquer les remises",
    pourcentage: "Pourcentage",
    montant_fixe: "Montant fixe",
    manuelle: "Manuelle",
    ajouter_liee: "Remise liée à l'article",
    ajouter: "Ajouter",
    ajouter_remise: "Ajouter une remise",
    creer_liee: "Créer une remise liée",
    creer_tiers: "Remise tiers",
    creer_groupe: "Remise groupe",
    creer_liee_hint:
      "Crée une remise persistante liée au tiers ou groupe du client",
    disponibles: "Remises disponibles",
    appliquees: "Remises appliquées",
    ajouter_manuelle_cta: "Ajouter une remise manuelle",
    manuelle_hint: "Remise non persistée pour les commandes futures",
    montant_unitaire: "€/unité",
    remises: "Remises",
    aucune_remise: "Aucune remise",
    label: "Libellé",
    label_placeholder: "Ex: Remise fidélité, Promotion...",
    label_hint: "Description optionnelle affichée sur les documents",
    existing_group: "Remises existantes pour ce groupe",
    tous_articles: "Tous articles",
    new: "Nouvelle remise",
    sous_total_avant: "Sous-total avant remises",
    total_remises: "Total remises",
    type_label: {
      valeur_pct: "Valeur (%)",
      valeur_unitaire: "Montant par unité (€/u)",
      valeur_montant: "Montant fixe (€)",
      pourcentage_hint: "Ex : 10 pour une remise de 10%",
      montant_unitaire_hint: "Ex : 0.50 pour 0.50 € par unité commandée",
      montant_hint:
        "Ex : 50 pour une remise globale de 50 € (pied de commande)",
      montant_context: "Montant fixe (€) — pied commande/facture",
    },
  },
  contact: {
    contactOf: "Contacts de ",
    add_cta: "Ajouter un contact",
    emailConfirmationCommandeClient:
      "Destinataire lors d'une confirmation de commande client",
    emailBonCommandeFournisseur:
      "Destinataire pour bons de commande fournisseur",
  },
  infoPrix: {
    coef_manquant:
      "Inexploitable",
    coef_manquant_tooltip:
      "Ce tarif porte un prix mais aucun coefficient d'unité de gestion : le prix n'est pas convertible en coût unitaire, ce tarif est donc ignoré dans les calculs de coût de revient. Renseignez le coefficient (1 si le prix est déjà celui d'une unité).",
    coefficient_requis:
      "Renseignez le coefficient d'unité de gestion : c'est le nombre d'unités de l'article contenues dans une unité d'achat (25 pour un sac de 25 kg si l'article se compte en kg). Sans lui, le prix n'est pas convertible en coût. Mettez 1 si le prix est déjà celui d'une unité.",
    origine_saisie: "Saisi à la main",
    origine_acte: "Acte tarifaire",
    origine_sync: "Synchro boutique",
    origine_inconnue: "Origine inconnue",
    origine_voir_suivi: "détail dans le suivi tarifaire du tiers",
    simulation_titre: "Simulation",
    simulation_label: "À la date du",
    simulation_badge: "Lecture seule",
    simulation_qty: "Pour une quantité de",
    simulation_hint:
      "Indique quelle tranche s'appliquerait à cette date pour cette quantité, et à quel niveau (client, groupe ou générique). Rien n'est enregistré.",
    depuis_le: "à partir du",
    jusquau_le: "· jusqu'au",
    a_venir_tooltip: "Des tarifs sont programmés pour ce couple",
    aucun_valide: "Aucun tarif valide",
    aucun_valide_tooltip:
      "Des tranches existent mais aucune n'est valide aujourd'hui : le prix ne sera pas pré-rempli à la saisie d'une commande.",
    valid_from: "Valide à partir du (inclus)",
    fenetre_inversee:
      "La fin de validité précède le début : cette tranche ne s'appliquerait jamais.",
    valid_to: "Valide jusqu'au (inclus)",
    palier: "Tarif #n",
    validite_titre: "Fenêtre de validité (facultative)",
    validite_help_title: "Fenêtre de validité du tarif",
    validite_help:
      "Les deux dates sont INCLUSES : un tarif « jusqu'au 31/08 » s'applique encore aux livraisons du 31 août, son successeur démarre le 01/09. Laisser une date vide = pas de limite de ce côté (illimité).",
    validite_illimitee: "Illimité",
    validite_a_venir: "À venir",
    validite_expire: "Expiré",
    validite_appliquee: "Appliqué",
    validite_supplantee: "Supplanté",
    validite_en_vigueur: "En vigueur",
    add_cta: "Ajouter un prix",
    create_cta: "Créer une grille tarifaire",
    prix_maison: "Il s'agit des prix de base de l'article en question",
  },
  calendar: {
    add_cta: "Créer un planning",
    nameless: "Planning sans nom",
  },
  calendar_event: {
    add_cta: "Ajouter un créneau",
  },
  article: {
    matrice: {
      titre: "Matrice allergènes",
      rechercher: "Rechercher un article…",
      article: "Article",
      contient: "Contient :",
      peut_contenir: "Peut contenir :",
      legende_contient: "contient",
      legende_traces: "peut contenir (traces)",
      legende_vide: "une case vide signifie « non déclaré », jamais « absent »",
      vide: "Aucun article ne déclare d'allergène.",
      vide_recherche: "Aucun article ne correspond à cette recherche.",
      export_echec: "L'export a échoué.",
    },
    allergenes: {
      non_renseigne: "—",
      non_renseigne_aide: "Pas encore renseigné",
      contient: "Contient",
      contient_aide: "Ingrédient du produit — mis en relief sur l'étiquette",
      traces: "Traces",
      traces_aide: "Contamination croisée — mention « peut contenir », à part",
      absent: "Absence garantie",
      absent_aide: "Vérifié, non présent — rien ne s'imprime",
      enfant_renseigne: "Une espèce de cette catégorie est renseignée",
      deduit_aide: "Déduit de la nomenclature : se modifie sur la composition, pas ici",
      recap_titre: "Ce qui partira sur l'étiquette",
      recap_contient: "Contient :",
      recap_peut_contenir: "Peut contenir :",
      recap_aucun: "rien",
      aucun: "Cet article ne contient aucun allergène",
      aucun_impossible: "Impossible : cette fiche déclare déjà un allergène. Retirez-le d'abord.",
      aucun_le: "Déclaré le #date",
      etat_non_renseigne: "Non renseigné — une grille vide ne vaut pas déclaration d'absence",
      titre: "Allergènes",
    },
    /**
     * ⚠️ **COURTE, et c'est le fond.** Deux rédactions précédentes énuméraient les conséquences (valeurs
     * nutritionnelles, rendement, consommations non concernées) : jugées alambiquées en recette. Une aide
     * qu'on n'a pas le temps de lire ne sert à rien, même juste.
     *
     * ⚠️ **Ne pas y remettre le coût ni le rendement.** Mesuré : aucun calcul de coût ne lit la densité,
     * et `rendement.utils` la *déduit* au lieu de l'exiger. `masseEnKg` — la seule fonction qui l'exige —
     * n'est appelée que par `nutrition-essai.service`. « Calculer le poids » couvre exactement cela, sans
     * promettre autre chose.
     */
    densite_encouragee:
      "Cet article se compte en volume : renseigner sa densité est fortement conseillé — c'est elle qui permet d'en calculer le poids, notamment dans le module Développement recette.",
    /**
     * Changement d'unité de gestion sur un article qui porte des tarifs.
     *
     * ⚠️ Le message dit ce qui VA arriver, chiffres à l'appui, et ne juge pas : l'utilisateur
     * a de vraies raisons de changer d'unité de gestion. Ce qu'il ne peut pas savoir, c'est
     * que les coefficients de ses tarifs parlaient de l'ANCIENNE.
     */
    unite_impact: {
      /**
       * ⚠️ Le titre nomme **la raison de l'interruption**, pas le sujet. Le dialogue ne s'ouvre plus
       * que si au moins un coefficient ne se déduit pas des deux unités — sinon le recalcul est
       * silencieux. « Cette unité de gestion commande vos tarifs » décrivait le sujet et laissait
       * croire qu'il y avait toujours quelque chose à arbitrer.
       */
      titre: "Des coefficients ne pourront pas être recalculés",
      /**
       * ⚠️ **La phrase est COMPOSÉE de deux fragments accordés séparément.** Elle porte deux
       * nombres — les coefficients recalculés et ceux à vérifier — et le pipe de traduction ne
       * pluralise qu'une clé à la fois (`translatePlural`). En une seule clé, on obtenait
       * « 1 seront recalculés et 0 resteront à relire » : vu à l'écran en recette. Un texte qui
       * annonce une correction automatique et qui écorche sa propre grammaire n'inspire pas
       * confiance dans le calcul qu'il annonce.
       *
       * ⚠️ `nb <= 1` prend le singulier, donc « 0 coefficient sera recalculé » — la forme juste
       * en français.
       */
      message:
        "Un coefficient dit combien d'unités de gestion contient une unité d'achat — les vôtres parlaient donc de l'ancienne unité. En passant à #nouvelle, #detail. Ceux-là ne se déduisent pas des deux unités : ils mesurent l'article lui-même — le poids d'une pièce, par exemple — et personne ne peut les recalculer à votre place.",
      recalcul: "#n coefficient sera recalculé automatiquement",
      recalcul_plural: "#n coefficients seront recalculés automatiquement",
      relire_n: "#n restera à vérifier",
      relire_n_plural: "#n resteront à vérifier",
      a_relire: "à vérifier",
      bilan: "#detail.",
    },
    qty_in: "Quantité de l'article en",
    actions: "Actions sur l'article",
    select_new: "Sélectionner un article",
    catalogue_chargement: "Chargement du catalogue d'articles…",
    showAll: "Afficher tous les articles",
    toolbox: "Actions rapides",
    rescan: "Scanner un nouvel article",
    scan_no_corresponding_article: "Aucun article correspondant n'a été trouvé",
    make_fiche: "Générer une étiquette logistique",
    bio: "Article BIO",
    ddmAsNumeroDeLot: "Utiliser la date limite pour le numéro de lot",
    ddmAsNumeroDeLot_hint:
      "À la création d'un lot en production (OF), le numéro est généré automatiquement à partir de la date limite formatée (ex : 311226 pour le 31/12/2026 si format JJ/MM/AA). Utile pour les produits où la date sert naturellement d'identifiant lot. Sinon, le numéro est un compteur séquentiel.",
    vn: "Valeurs nutritionnelles",
    vn_ciqual_cta: "Charger depuis Ciqual",
    vn_ajouter_constituant: "Ajouter un autre constituant",
    nom_etiquetage: "Nom d'étiquetage",
    nom_etiquetage_hint: "Le nom qui paraîtra sur une étiquette — court et légal (« huile d'olive »), pas la dénomination d'achat. Sans lui, une recette qui emploie cet article ne peut pas sortir sa fiche technique.",
    vn_ciqual_charge: "Teneurs Ciqual chargées dans les lignes — à relire, puis enregistrer la fiche",
    vn_ciqual_charge_ecartees: "Teneurs Ciqual chargées — #nb non reprise(s) : « traces » et « < 0,1 » ne sont pas des nombres. À relire, puis enregistrer la fiche.",
    vn_ciqual_sans_teneur: "Cet aliment ne porte aucune des teneurs suivies par votre référentiel.",
    vn_origine_ciqual: "Reprises de Ciqual : #aliment (millésime #version)",
    add: "Ajouter un article",
    goToArticle: "Naviguer vers l'article",
    nbUnitePerPack: "Nombre d'unité par colis",
    uniteComptage: "Unité de comptage",
    coefficientComptage: "Coefficient de comptage",
    valeurUnitaire: "Valeur unitaire par défaut",
    quantiteParDimension: "Quantité par dimension",
    weight_suremballage: "Poids surremballage",
    refCalcul: "Référence calcul quantité",
    typeComptage: "Type de calcul",
    tailleEchantillon: "Taille échantillon inventaire",
    secable: "Produit sécable",
    new: "Nouvel article",
    code_auto: "Assignation automatique de la référence",
    ean_title: "Code-barres",
    ddm_title: "Dates limites",
    desc_title: "Descriptions",
    param_title: "Paramètres",
    details_title: "Caractéristiques",
    ean13_auto: "EAN13 automatique",
    ean14_auto: "EAN14 automatique",
    duplication: "Dupliquer un article existant",
    photo: "Photo de l'article",
    etiquette: "Etiquette de l'article",
    details: "Détails",
    article: "Article",
    add_label: "Ajout d'un article",
    add_cta: "Ajouter un article",
    associate_cta: "Associer l'EAN13/14 à un article supplémentaire",
    associate_cta_code: "Créer un article avec cette référence",
    edit_label: "Modification d'un article",
    search: "Dénomination ou code d'un article",
    search_add: "Rechercher ou ajouter un article",
    isPrestation: "Prestation de service pure",
    is_alimentaire: "Produit alimentaire avec valeurs nutritionnelles",
    with_stock: "Article géré en stock",
    withStockPositif: "Assurer un stock disponible pour les mouvements",
    withParallelOpe:
      "Possibilité de réaliser plusieurs opérations de fabrication simultanées",
    with_traca:
      "Traçabilité obligatoire lors des productions, lorsqu'utilisé en composant",
    with_lot: "Détail des quantités en stock pour chaque lot",
    with_lot_unique_warning:
      "Pour cet article, le lot unique a été selectionné par défaut",
    info_gestion_conso:
      "Veuillez sélectionner un mode de consommation lorsque cet article est utilisé en tant que composant:",
    with_pdc:
      "Saisie obligatoire des consommations avec mise à disposition dans le stock de composants de l'OF",
    with_conso_theoric: "Consommation théorique",
    with_conso_volee:
      "Saisie facultative des consommations (sera en consommation théorique si aucune consommation déclarée)",
    with_specif: "Article fabriqué",
    archived: "Article archivé",
    with_emplacement: "Avec emplacement en stock",
    isAchete: "Article achetable",
    isVendu: "Article vendable",
    withValeursNutritionnelles: "Article avec valeurs nutritionnelles",
    stock: "Stock de l'article",
    info_generales: "Infos générales",
    process: "Process",
    nomenclature: "Nomenclature de l'article",
    controles: "Contrôles",
    duplicate: "Dupliquer un article existant",
    model: "Spécification de l'article",
    missing_current_version: "En rédaction",
    select: "Sélectionner un article",
    select_for_traca: "Sélectionner un article pour afficher sa traçabilité",
    type_article: "Type d'article",
    type_article_sub:
      "Optionnel, ne rien sélectionner pour un composant standard",
    article_fabrique: "Article fabriqué",
    article_fabrique_sub: "Produit fini ou semi-fini",
    permet_creation_of:
      "Permet la création d'ordres de fabrication pour cet article",
    prestation_title: "Prestation",
    prestation_sub: "Service sans stock physique",
    pas_gestion_stock: "Pas de gestion de stock",
    facturable_service: "Facturable en tant que service",
    type_periode: "Type de periode",
    format_date: "Format de date",
    format_date_dlc_jour_obligatoire:
      "Une DLC doit être au jour près (obligation légale).",
    type_date_limite_label: "Type de date limite",
    duree_conservation: "Durée",
    type_date_limite: {
      ddm: "DDM — qualité (avertissement)",
      dlc: "DLC — sanitaire (blocage)",
      aucune: "Aucune (produit non périssable)",
      ddm_hint:
        "Date de Durabilité Minimale : après dépassement, qualité dégradée mais consommation possible.",
      dlc_warning:
        "DLC : blocage légal après dépassement (R. 412-21 Code Conso). Durée à justifier scientifiquement.",
      aucune_hint:
        "Pour les produits non périssables (visserie, emballage, fournitures…).",
      bascule_confirm_title: "Changer le type de date limite ?",
      bascule_confirm_message:
        "Vous passez cet article de #ancien à #nouveau.\n\nLes lots déjà en stock restent classés #ancien (cohérence audit IFS — un lot étiqueté physiquement ne peut pas être requalifié rétroactivement). Seuls les nouveaux lots créés à partir de maintenant seront traités comme #nouveau.\n\nConfirmer le changement ?",
    },
    dlc_format_auto_bascule:
      "Format basculé sur JJ/MM/AAAA — la DLC doit s'afficher au jour près.",
    mode_gestion_stock: "Mode de gestion du stock",
    stock_global: "Stock global",
    stock_global_sub: "Un compteur unique pour cet article",
    stock_lot_unique: "Stock géré dans un lot unique",
    lots_enregistres: "Numéros de lots fournisseurs/fabriqués enregistrés",
    traca_amont_aval: "Traçabilité amont/aval disponible",
    suivi_lot_par_lot: "Suivi lot par lot",
    suivi_lot_par_lot_sub: "Un stock distinct pour chaque lot",
    stock_lot_par_lot: "Stock géré lot par lot",
    conso_lot_par_lot: "Consommations lot par lot obligatoires",
    composant_titre: "Quand cet article est utilisé comme composant",
    traca_composant: "Traçabilité composant",
    impose_lots_conso: "Impose de déclarer les lots consommés",
    mode_consommation: "Mode de consommation",
    theorique_title: "Théorique",
    theorique_sub:
      "Consommation calculée automatiquement selon la nomenclature. Aucune saisie requise.",
    declaration_consos: "Déclaration des consommations",
    declaration_consos_sub:
      "L'opérateur déclare les quantités et lots consommés à chaque étape de fabrication.",
    suivi_precis_consos: "Suivi précis des consommations",
    suivi_precis_consos_sub:
      "Calcul par différence de stock : les matières sont transférées au pied de chaîne avant production, puis un inventaire en fin d'OF détermine la consommation réelle.\nIdéal pour détecter les pertes et écarts de rendement.",
    pour_100ml: "Pour 100 ml",
    pour_100g: "Pour 100 g",
    unite_bis: "Unité bis",
    unite_bis_placeholder: "Le sachet de 20g",
    constante: "Constante",
    teneur: "Teneur",
    donnees_article: "Données article",
    donnees_inventaire: "Données inventaire",
    photos_articles: "Photos articles",
    telecharger_modele: "Télécharger le modèle",
    generer_ean13: "Générer un EAN13",
    importation_partielle: "Importation partielles de données",
    selectionner_infos: "Sélectionner les infos",
    a_importer: "à importer",
    info_vn:
      "Afin de pouvoir saisir les valeurs nutritionnelles, veuillez renseigner les informations suivantes",
    diametre_mandrin: "Diamètre extérieur du mandrin",
    diametre_rouleau: "Diamètre d'un rouleau complet",
    epaisseur_produit: "Epaisseur du produit",
    laize: "Laize",
    non_suivi: "Non suivi",
    non_suivi_stock: "Article non suivi en stock",
    qte_unitaire: "Qté unitaire",
    informations_generales: "Informations générales",
    jours: "Jour(s)",
    mois_label: "Mois",
    annees: "Année(s)",
    impose_suivi_precis: "Impose le mode Suivi précis des consommations",
    mouvements_bloques:
      "Les mouvements sont bloqués si le stock est insuffisant",
    stocks_negatifs: "Les stocks négatifs sont autorisés (ex: -5000 kg)",
    mode_incompatible_lot:
      'Mode incompatible avec la traçabilité lot par lot. Choisissez "Déclaration" ou "Suivi précis (PDC)".',
    declarations_modifiables:
      "Les déclarations de consommations restent modifiables après clôture dans les stats OF",
    coefficient: "Coefficient",
    inventaire_heading: "Inventaire",
    prix_catalogue: "Prix catalogue par défaut",
    prix_vente_defaut: "Prix de vente par défaut",
    prix_achat_defaut: "Prix d'achat par défaut",
    prix_catalogue_subtitle:
      "Ces paliers de prix sont utilisés comme valeurs par défaut pour les commandes (client et fournisseur) lorsqu'aucun prix spécifique n'est défini pour un tiers.",
    ajouter_palier_vente: "Ajouter un palier vente",
    ajouter_palier_achat: "Ajouter un palier achat",
    logistique_heading: "Logistique",
    logistique_uc: "Unité de gestion",
    logistique_colis: "Colis",
    edit_weight_title: "Poids de l'article",
    edit_weight_main: "Poids brut par unité de gestion",
    edit_weight_main_subtitle:
      "Poids brut d'une unité de gestion (emballage inclus). Sert au calcul du poids logistique sur les BL et fiches d'expédition. Ne pas inclure le poids de l'unité d'expédition (palette, carton…).",
    edit_weight_more: "Plus de paramètres",
    edit_weight_suspect:
      "Ce poids semble très élevé pour une unité de gestion — vérifier que la saisie est bien en kg (et pas en grammes).",
    enter_weight: "Saisir le poids brut / UG",
    stock_heading: "Stock",
    donc_1_colis: "Donc 1 colis =",
    donc: "Donc",
    comptage_simple_en: "Comptage simple en",
    etats: "États",
    infos: "Informations commerciales",
    denominationCommerciale: "Dénomination commerciale",
    tva: "TVA",
    vn_base_100g: "Teneurs déclarées pour 100 g. C'est la base de Ciqual et celle du module de formulation ; une déclaration par 100 ml exigerait la densité du produit.",
  },
  flux: {
    page_title: "Flux Logistiques",
    beta: "Test",
    from: "Du",
    to: "Au",
    refresh: "Actualiser",
    unit: "Unité",
    no_unit: "Sans unité",
    loading: "Chargement des flux…",
    error: "Erreur lors du chargement des flux.",
    empty: "Aucun flux de stock sur cette période.",
    legend_flows: "Flux",
    hint: "Survolez un flux pour voir le détail. L'épaisseur d'un flux est proportionnelle au volume dans l'unité sélectionnée.",
    view_timeline: "Évolution",
    view_flux: "Flux (Sankey)",
    lot: "Lot",
    lot_search: "Rechercher un lot ou un article…",
    select_lot:
      "Recherchez et sélectionnez un lot pour afficher l'évolution de son stock.",
    current_stock: "Stock actuel",
    over_period: "sur la période",
    stock_trend: "Évolution du stock",
    highlighted_in_list: "mis en évidence dans la liste ci-dessous",
    show_only: "Voir uniquement",
    show_all: "Tout afficher",
    clear: "Effacer",
    warehouse: "Entrepôt",
    zone: "Zone",
    show_graph: "Afficher l'évolution du stock",
    events: "événements",
    entries: "Entrées (+)",
    exits: "Sorties (−)",
    size_hint: "taille du point ∝ volume",
    curve_hint:
      "Courbe = stock du lot sélectionné dans le temps. Chaque point = les mouvements d'un jour (entrées + / sorties −) ; cliquez pour le détail.",
  },
  traca: {
    title_graph: "Visualisation de la traçabilité",
    lastUpdate: "Mis à jour le",
    why_no_graph:
      "Le graphique de traçabilité n'est pas encore disponible. Il sera visible quand le pied de chaine sera réintégré",
    go_graph_of:
      "Pour visualiser la traçabilité de ce lot veuillez vous rendre sur la page de l'OF correspondant",
    node_lot: "Lot",
    node_of: "Ordre de fabrication",
    node_tiers: "Tiers",
    node_drawer_title: "Détail du nœud",
    node_numero: "Numéro",
    node_quantite: "Quantité produite",
    node_date_debut: "Début",
    node_date_fin: "Fin",
    open_fiche: "Ouvrir la fiche",
    recenter_node: "Centrer sur ce nœud",
    close_drawer: "Fermer",
    expand_all: "Tout déployer",
    collapse_all: "Tout replier",
    cluster_hint: "Groupe replié — cliquer pour déployer",
    cluster_legend: "Groupe replié",
    documents_title: "Documents",
    documents_loading: "Chargement des documents…",
    doc_lot_etiquette: "Étiquette logistique",
    doc_of_dossier: "Dossier OF (Excel)",
    doc_of_link: "Voir l'OF",
    doc_bl_client: "BL client",
    doc_facture: "Facture",
    doc_bl_fournisseur: "BL fournisseur",
    // --- Barre d'outils ---
    search: "Rechercher",
    search_placeholder: "Lot, fournisseur, OF, client…",
    dossier: "Dossier traça",
    path_docs: "Docs du chemin (#nb)",
    table: "Tableau",
    reset: "Reset",
    refresh: "Rafraîchir",
    fit: "Ajuster",
    // --- Cartes / panneau de détail ---
    selection: "Sélection",
    collapse_line: "Replier",
    more_lines: "#nb autres",
    detail_in: "Entrées (amont)",
    detail_out: "Sorties (aval)",
    detail_infos: "Infos",
    detail_details: "Détails",
    loading: "Chargement de la traçabilité…",
    load_error: "Erreur de chargement de la traçabilité.",
    empty_lot: "Aucune traçabilité pour ce lot.",
    // --- Tableau des documents ---
    docs_graph: "Documents du graphe",
    export_csv: "Export CSV",
    download_zip: "Tout télécharger (ZIP)",
    blf_not_scanned: "BL fournisseur externe — non numérisé",
    not_scanned: "non numérisé",
    empty_docs: "Aucun document dans le graphe affiché.",
    col_of: "OF",
    // --- Libellés de type de nœud ---
    type_fournisseur: "Fournisseur",
    type_dechargement: "Déchargement",
    type_reception: "Réception",
    type_composant: "Composant",
    type_prestation: "Prestation",
    type_fabrication: "Fabrication",
    type_nonConformite: "NC",
    type_expedition: "Expédition",
    type_client: "Client",
    type_retourClient: "Retour",
    type_transfert: "Transfert",
    type_destruction: "Destruction",
    // --- Organisation / portée ---
    layout_flux: "Par flux",
    layout_type: "Par type",
    layout_temporal: "Temporel",
    scope_lot: "Lié au lot",
    scope_all: "Tout",
    period_filter: "Période",
    period_filter_hint:
      "Filtrer le graphe sur la période : masque les cards dont tous les événements datés sortent de la période (les lots audités et les cards sans date restent visibles).",
    // --- Libellés de colonnes de documents ---
    dock_commande_fournisseur: "Bons de commande",
    dock_blf_info: "BL fournisseur",
    dock_lot_etiquette: "Étiquettes lot",
    dock_of_dossier: "Dossiers OF",
    dock_bl_client: "BL client",
    dock_cc_doc: "Commande client",
    dock_facture: "Factures",
    dock_fiche_nc: "Fiches NC",
    // --- Export CSV ---
    csv_type: "Type de document",
    csv_document: "Document",
    csv_node: "Nœud",
    csv_categorie: "Catégorie",
    // --- Clusters ---
    cluster_of_grouped: "#nb OF regroupés · cliquer pour déplier",
    cluster_lots: "#nb lots",
    // --- Page traçabilité (recherche / critères / picker) ---
    page_title: "Traçabilité",
    search_global_placeholder:
      "Rechercher un lot, OF, article, tiers, commande, facture…",
    searching: "Recherche…",
    no_result: "Aucun résultat traçable",
    criteria_title: "Critères :",
    reset_search: "Réinitialiser la recherche",
    empty_title: "Sélectionnez un élément à tracer",
    empty_hint:
      "Recherchez un <strong>lot</strong>, un <strong>OF</strong>, un <strong>article</strong>, un <strong>tiers</strong>, une <strong>commande</strong> ou une <strong>facture</strong> dans la barre ci-dessus, puis ajoutez-le comme critère.",
    empty_multi:
      "💡 Ajoutez <strong>plusieurs critères</strong> pour obtenir un graphe « multi-filtres ».",
    picker_lots_linked: "lots liés",
    picker_subtitle: "Choisissez le(s) lot(s) à ajouter au graphe",
    picker_filter_placeholder: "Filtrer les lots…",
    select_all: "Tout sélectionner",
    select_none: "Aucun",
    picker_empty: "Aucun lot ne correspond.",
    cancel: "Annuler",
    add: "Ajouter",
    snack_no_lot: "Aucun lot lié à cet élément — rien à tracer.",
    // --- Dialog d'impression ---
    print_title: "Impression du dossier de traçabilité",
    print_subtitle: "Choisissez l'organisation du graphe",
    print_flux_desc: "Chaîne amont → aval (recommandé)",
    print_type_desc: "Colonnes par catégorie (fournisseur, OF, client…)",
    print_temporal_desc: "Frise chronologique par date d'événement",
    // --- Dialog consultation document (scan BL) ---
    doclist_loading: "Chargement du document…",
    doclist_no_scan: "Aucun fichier numérisé pour ce BL.",
    // --- Dialog unité d'expédition ---
    palette_title: "Unité d'expédition",
    // --- PDF dossier (généré) ---
    pdf_edited: "Édité le #date",
    pdf_criteres: "Critères : #list",
    pdf_page: "Page #n / #total",
  },
  nomenclature: {
    title: "Quantités nécessaires pour la fabrication d'une unité",
    empty: "Aucun article dans la nomenclature",
    add: "Ajouter un article",
    add_ligne: "Ajouter un article à la nomenclature",
    edit_ligne: "Edition de nomenclature",
    select_article: "Sélectionner un article",
    select_article_default: "Sélectionner un article par défaut",
    articleDynamic: "Article paramétrable",
    paramDynamic: "Quantité paramétrable",
    optional: "Optionnel",
    do: "Article utilisé",
    dontDo: "Article non utilisé",
    noNomenclature: "Aucun article dans la nomenclature",
    qttUndefined: "Quantité non définie",
    qttUnitaire: "Soit #qtt pour 1",
    perte_normale: "Perte normale",
    perte_maximale: "Perte maximale",
    lotHeritier: "Héritage de lot",
    lotHeritier_tooltip:
      "Au démarrage de l'OF, le numéro de lot du produit fabriqué reprendra automatiquement celui du lot sélectionné pour cet article composant.",
  },
  variable: {
    add_cta: "Ajouter un paramètre",
    edit_cta: "Editer un paramètre",
    name: "Nom du paramètre",
    type: "Type de paramètre",
    payload_default: "Valeur par défaut",
    reset_default: "Réinitialiser",
    payload: "Valeur",
    dontDo: "Ne pas réaliser",
    missing_warning:
      "Des paramètres sont manquantes, veuillez modifier la spécification",
  },
  ij: {
    add_travail: "Ajouter une saisie",
    all_hours_completed: "Toutes les heures ont été saisies",
    hours_mismatch:
      "Les heures payées et les heures saisies ne correspondent pas",
    hour_entry_for: "Saisie d'heure sur",
    type_of: "OF",
    type_conges: "Congés",
    type_rtt: "RTT",
    type_maladie: "Maladie",
    type_accident_travail: "Accident du travail",
    type_retard: "Retard",
    type_autres: "Autres",
    entry_type: "Type de saisie",
    deletion_title: "Suppression",
    deletion_confirm:
      "Vous êtes sur le point de supprimer les heures saisies. Ceci impactera potentiellement des rapports de productivité de différents OF. Confirmez-vous ?",
    unknown_operator: "Opérateur inconnu",
  },
  stock_article: {
    securite: "Stock de sécurité",
    maximum: "Stock maximum",
    reappro: "Seuil de réapprovisionnement",
  },
  inventaires: {
    recalculer_ecarts: "Recalculer les écarts",
    show_pdc: "Voir les pieds de chaine qui contiennent cet article",
    raz_entrepot: "Remettre à 0",
    total_entrepot: "Stock total après inventaire de l'entrepot",
    par_entrepot: "par entrepôt",
    label: "Inventaire",
    add_cta: "Ajouter un inventaire",
    categorie_label: "Type de campagne",
    categorie_standard: "Inventaire standard",
    categorie_audit_bio: "Audit BIO",
    date_label: "Date de l'inventaire physique",
    add_comptage: "Ajouter un comptage",
    total: "Total inventorié",
    search_associate: "Rechercher un article à associer",
    cloture: "Clôture de l'inventaire",
    confirm_raz:
      "Les quantités saisies remplaceront tout le stock de cet article.\n" +
      "Tous les lots et adresses non saisis seront remis à 0.\n" +
      "Voulez-vous continuer ?",
    confirm_only_those:
      "Seuls les stocks des lots saisis à leurs adresses seront mis à jour.\n" +
      "Tous les autres lots et adresses resteront inchangés.\n" +
      "Voulez-vous continuer ?",
    inventaire_confirm_raz:
      "Les quantités saisies remplaceront tout le stock.\n" +
      "Tous les lots et adresses non saisis seront remis à 0.\n" +
      "Voulez-vous continuer ?",
    inventaire_confirm_only_those:
      "Seuls les stocks des lots saisis à leurs adresses seront mis à jour.\n" +
      "Tous les autres lots et adresses resteront inchangés.\n" +
      "Voulez-vous continuer ?",
    inventaire_confirm_only_those_articles:
      "Pour chaque article compté, les lots non saisis de l'entrepôt seront remis à 0.\n" +
      "Les articles sans aucun comptage resteront inchangés.\n" +
      "Voulez-vous continuer ?",
    familles_raz_label: "Familles à remettre à 0",
    familles_raz_placeholder: "Sélectionner des familles",
    familles_raz_hint:
      "Pour chaque famille choisie, tous les articles de cette famille dans l'entrepôt seront remis à 0, sauf ceux comptés dans l'inventaire. Laisser vide pour ne rien remettre à 0 par famille.",
    with_lot: "Inventaire lot par lot",
    with_lot_desc:
      "Le comptage se fait par entrepôt et par lot. Le stock résultant sera exactement celui affiché sur cet écran.",
    with_lot_unique: "Inventaire global",
    with_lot_unique_desc:
      "L'article est inventorié au global pour chaque entrepôt. Le stock ira dans le lot unique de l'article. Le stock résultant sera exactement celui affiché sur cet écran.",
    supervision_parametres: "Supervision et paramètres",
    telecharger_csv: "Télécharger le CSV",
    stock_theorique: "Stock avant inventaire",
    stock_physique: "Stock après inventaire",
    ecart: "Écart",
    entrepots_comptes: "Entrepôts comptés",
    date_inventaire: "Date de l'inventaire",
    date_inventaire_hint: "Date du comptage (une date passée est possible).",
    recap_title: "Récapitulatif de l'inventaire",
    recap_confirm: "Confirmer l'inventaire",
    confirm_raz_detail:
      "Le stock de chaque entrepôt sera remplacé par les quantités saisies. Les entrepôts non comptés seront mis à zéro.",
    inventaire_success: "Inventaire enregistré avec succès",
    comptage_adresse: "Comptage — #name",
    resultat_comptage: "Résultat",
    scroll_to_save: "Enregistrer",
    scan_inventory_title: "Inventaire scan — #name",
    scan_select_adresse: "Sélectionnez un entrepôt",
    scan_start: "Démarrer",
    scan_remote: "Scanner distant",
    scan_camera: "Caméra",
    scan_entrepot: "Scanner",
    scan_inventaire_cta: "Inventaire scan",
    go_to_count: "Compter",
    add_to_session: "Ajouter au comptage",
    session_counted: "Inventaire actuel",
    finish_session: "Terminer",
    close_dirty_title: "Attention",
    close_dirty_message:
      "Vous avez des comptages non sauvegardés, voulez-vous vraiment quitter ?",
    close_dirty_stay: "Rester",
    close_dirty_leave: "Quitter",
    scan_hint: "Scannez un code-barres pour identifier l'article",
    scan_manual_select: "Ou sélectionnez un article",
    scan_lot_auto: "Article et lot identifiés",
    scan_article_found: "Article identifié — sélectionnez le lot",
    scan_not_found: "Article non trouvé",
    scan_ean_mismatch: "Ce code-barres ne correspond pas à cet article",
    scan_no_ean_on_article: "Cet article n'a pas d'EAN associé",
    scan_associate_ean: "Associer cet EAN à l'article",
    scan_or_select_lot: "Ou sélectionnez un lot",
    scan_hint_lot: "Scannez un code-barres pour identifier le lot",
    edit_mouvement_title: "Modifier la quantité",
    add_incomplete_pack: "Ajouter un colis incomplet",
    add_incomplete_roll: "Ajouter un rouleau incomplet",
    add_measurement: "Ajouter une saisie",
    incomplete: "Incomplet",
    sample: "Échantillon",
    measurement: "Saisie",
    total_prefix: "Soit un total de",
    total_gross: "Mesure totale brute",
    total_net: "Mesure totale nette",
    total_count: "Soit un compte de",
    therefore: "Donc",
  },
  stock: {
    show_null_stock: "Afficher les lots à 0",
    createEmplacement: "Créer un nouvel emplacement",
    no_reinte: "Sans réintégration",
    total: "Stock total",
    label: "Stock",
    zoneExpedition: "Zone d'expédition",
    zoneNonConforme: "Zone non conforme",
    zoneQuarantaine: "Zone quarantaine",
    enDechargement: "En déchargement",
    enDechargementInfo:
      "#nb déchargement(s) en cours — non inclus dans le stock total. Réalisez la réception pour intégrer au stock.",
  },
  tiers: {
    deleted: "Tiers supprimé",
    tiers: "Tiers",
    reference: "Référence",
    reference_hint: "Laissez vide pour une attribution automatique",
    reference_format_error:
      "Seuls les lettres et les chiffres sont acceptés (sans espace ni caractère spécial)",
    search: "Propriétaire de l'article (si article possédé par un tiers)",
    add_label: "Ajout d'un tiers",
    add_cta: "Ajouter un tiers",
    edit_label: "Modification du tiers",
    abattement: "Abattement",
    page: "Fiche tiers",
    update_price: "Modifier le prix",
    update_mail: "Mettre à jour l'adresse du tiers",
    appartient: "Appartient à un tiers",
    default: "Défaut",
    tier_group: "Groupe",
    tier_groups: "Groupes",
    show_groups: "Voir les groupes",
    add_group: "Ajouter un groupe",
    edit_group: "Modifier un groupe",
    group_disabled: "Désactivé",
    avecArticlesVendus: "Tiers avec infos d'articles vendus",
    avecArticlesAchetes: "Tiers avec infos d'articles achetés",
    all: "Tous",
    showAll: "Voir tous",
    aucun_groupe: "Aucun groupe",
    est_transporteur: "Est un transporteur",
    est_client: "Est un client",
    est_fournisseur: "Est un fournisseur",
    sans_role: "Sans rôle",
    roles: "Rôles",
    role_required:
      "Au moins un rôle doit être sélectionné (client, fournisseur ou transporteur)",
    rib_paiement_default: "RIB de paiement par défaut",
    rib_paiement_aide:
      "Ce RIB sera automatiquement affecté aux factures client créées pour ce tiers. Il restera modifiable depuis chaque facture.",
    code_label: "Référence",
    code_placeholder: "Ex: T2600042 — laissez vide pour auto-générer",
    code_hint:
      "Lettres et chiffres uniquement (A-Z, 0-9). Auto-générée si laissée vide.",
    code_invalid:
      "Seuls les caractères A-Z et 0-9 sont acceptés (pas d'espaces ni de caractères spéciaux).",
    code_copy: "Copier la référence",
    code_copied: "Référence copiée dans le presse-papiers",
  },
  inventaire: {
    associate: "Associer à un article existant",
    new_article: "Créer un nouvel article",
    useComptage: "Utiliser ce comptage",
  },
  mail: {
    visualize_bon_commande: "Visualiser le bon de commande joint au mail",
  },
  document_list: {
    attachment: "Pièce jointe",
    comments_label: "Commentaires",
    documents_label: "Documents",
    add_cta: "Ajouter un commentaire",
    edit_cta: "Editer un commentaire",
    add_doc: "Ajouter un document",
    new_doc: "Nouveau document",
    edit_doc: "Editer un document",
    element_deleted: "Élément supprimé",
    element_saved: "Enregistré",
    fullwidth_large: "Taille du document: grand",
    fullwidth_small: "Taille du document: petit",
    big_drop_title: "Glissez vos fichiers ici",
    big_drop_subtitle: "ou utilisez les boutons ci-dessous",
    big_browse: "Parcourir les fichiers",
  },
  dechargement: {
    pas_de_bl: "Pas de BL",
    hint_bl:
      "Scannez ou importez le bon de livraison, puis renseignez son numéro. Si vous n'avez pas de BL, passez à l'étape suivante.",
    hint_generaux:
      "Réalisez les contrôles qualité obligatoires liés à ce déchargement avant de saisir les quantités reçues.",
    hint_produits:
      "Saisissez les quantités reçues pour chaque article et réalisez les contrôles qualité associés.",
    previous: "Précédent",
    next: "Suivant",
    save_draft: "Enregistrer en brouillon",
    finalize: "Enregistrer le déchargement",
    edit: "Modifier",
    save_changes: "Enregistrer les modifications",
    articles_title: "Articles à décharger",
    fill_from_commande: "Quantités conformes à la commande",
    fill_conforme: "Quantités conformes",
    no_controles: "Pas de contrôles",
    controles: "Contrôles",
    in_progress: "En cours",
    ctrl_none: "Aucun contrôle",
    ctrl_todo: "Contrôles à réaliser",
    ctrl_in_progress: "Contrôles en cours",
    ctrl_ok: "Contrôles OK",
    ctrl_nc: "NC détectée(s)",
    docs_title: "Commentaires & documents :",
    docs_add: "Ajouter un commentaire ou un document",
    qty_ordered: "Commandé :",
    qty_already: "Déjà déchargé :",
    qty_unloaded: "Déchargé :",
    qty_remaining: "Restant :",
    label: "Déchargement",
    bl_prefix: "BL :",
    new_dechargement: "+ Déchargement",
    add_dechargement: "Ajouter un déchargement",
    etat_termine: "Terminé",
    etat_en_cours: "En cours",
    etat_reception_en_cours: "Réception en cours",
    n_articles: "articles",
    last_dech: "dernier déch. :",
    n_dech: "déch.",
    n_ref: "réf.",
    sort_newest: "Plus récent",
    sort_oldest: "Plus ancien",
    filter_in_progress: "En cours",
    filter_finished: "Terminés",
    filter_all: "Tous",
    snack_created: "Déchargement #numero créé",
    snack_created_hint:
      "Vous pouvez maintenant saisir les réceptions correspondantes.",
    snack_updated: "Déchargement #numero enregistré",
    snack_view: "Voir le déchargement",
    quit_title: "Quitter le déchargement",
    quit_message:
      "Les informations saisies n'ont pas été enregistrées et seront perdues.",
    quit_confirm: "Quitter sans enregistrer",
    quit_cancel: "Rester sur le déchargement",
    partial_delivery_title: "Livraison partielle",
    partial_delivery_message:
      "Les articles suivants n'ont pas de quantité renseignée :",
    partial_delivery_confirm: "Valider sans ces articles",
    delete_title: "Supprimer le déchargement",
    delete_message:
      "Le déchargement et toutes ses réceptions seront supprimés. Les mouvements de stock associés seront annulés. Confirmez-vous ?",
    warn_qty_inferior_title: "Attention — quantités inférieures aux réceptions",
    warn_qty_confirm: "J'ai compris, enregistrer",
    error_no_quantity:
      "Saisissez au moins une quantité pour finaliser le déchargement",
    controles_incomplete_title: "Contrôles incomplets",
    finalize_anyway: "Finaliser malgré tout",
    nc_detected_title: "Non-conformités détectées",
    nc_confirm: "Valider malgré les NC",
    back_to_form: "Revenir à la saisie",
    no_bl_title: "Aucun numéro de BL",
    no_bl_message:
      "Aucun numéro de bon de livraison n'a été renseigné. Voulez-vous continuer sans BL ?",
    no_bl_confirm: "Continuer sans BL",
    no_bl_cancel: "Renseigner un BL",
    save_and_continue: "Enregistrer et continuer",
    save_and_quit: "Enregistrer et quitter",
    step_bl: "Bon de livraison",
    step_generaux: "Contrôles généraux",
    step_produits: "Produits reçus",
    pod_nothing_done: "Rien de réalisé",
    pod_all_ok: "Tout est conforme",
    error_bl_required: "Renseignez un numéro de BL ou cliquez sur Pas de BL",
    error_generaux_required:
      "Réalisez tous les contrôles généraux avant de continuer",
  },
  reception: {
    qty_already: "Déjà réceptionné :",
    qty_remaining: "Reste à réceptionner :",
    qty_received: "Réceptionné :",
    add_lot: "Ajouter un lot",
    print_id_sheet: "Imprimer la fiche d'identification",
    edit_notice:
      "Modification d'une réception enregistrée. Les quantités et le lot seront corrigés, un mouvement de stock correctif sera généré automatiquement.",
    dechargement_nc_warning: "Les contrôles du déchargement sont non conformes",
    lot_required:
      "Le numéro de lot fournisseur est obligatoire pour cet article",
    controles_required:
      "Les contrôles qualité doivent être complétés avant l'enregistrement",
    incomplete: "Renseignez une quantité supérieure à 0",
    saved: "Réception enregistrée",
    deleted: "Réception supprimée",
    delete_title: "Supprimer cette réception",
    delete_message:
      "Les mouvements de stock associés seront annulés. Confirmez-vous la suppression ?",
    nc_title: "Contrôles non conformes",
    nc_message:
      "Des contrôles de réception sont non conformes. Voulez-vous déclarer les produits en non-conforme ?",
    nc_declare: "Déclarer non conforme",
    nc_correct: "Corriger la quantité",
    save_next: "Suivant",
    finalize_and_receive: "Réceptionner",
    tout_conforme: "Conforme",
    renseigner_lot: "Renseigner un lot",
    all_done: "Tous les articles ont été réceptionnés",
    label: "Réception",
    auto: "Auto",
    ddm_prefix: "DDM",
    new_reception: "Réception",
    pending_count: "réception(s) en attente",
    in_progress_count: "réception(s) en cours",
    done_count: "réceptionné(s)",
    received: "Réceptionné",
    to_receive: "À réceptionner",
    change_lot_title: "Changer le lot fournisseur",
    change_lot_confirm: "Changer le lot",
    controles_incomplete_title: "Contrôles incomplets",
    save_anyway: "Enregistrer quand même",
    back_to_form: "Revenir à la saisie",
    fiche_palette: "Fiche palette",
  },
  ligne: {
    deleted: "Ligne de production supprimée",
    add_label: "Ajout d'une ligne de prod.",
    add_cta: "Ajouter une ligne de prod.",
    edit_label: "Modification de la ligne de prod.",
    plannification: "Planification pour la ligne",
    plannification_select: "Choisir une ligne à planifier",
    search: "Ligne de prod.",
    nbOF: "Nombre maximum d'OF en parallèle",
    missing_param_ligne1: "Aucune ligne avec le nom: ",
    missing_param_ligne2: "n'a été paramétré pour la famille technique:",
    show_lignes: "Voir les lignes",
  },
  csv: {
    import: "Importer CSV",
    import_article_fourni: "Importer CSV pour les articles fournis",
  },
  cascade: {
    processing: "Recalcul en cours",
    uptodate: "Infos à jour",
    refresh: "Mettre à jour",
  },
  custom_enum: {
    /**
     * ⚠️ **Ce dialogue existe parce que le bouton MENTAIT.** Il annonçait une suppression, alors que le
     * serveur ARCHIVE dès qu'une clé étrangère référence l'élément. Un utilisateur a donc « supprimé »
     * une famille technique qui restait employée par des fiches article, sans comprendre pourquoi.
     *
     * ⚠️ **Il énonce les DEUX issues, sans prétendre savoir laquelle.** Le front ne connaît l'usage que
     * pour les unités de gestion (où la corbeille est désactivée avec son motif) ; pour les cinq autres
     * types, seule la base tranche, au moment du `DELETE`. Annoncer une issue au hasard remplacerait un
     * mensonge par un autre.
     *
     * ⚠️ **Il nomme le TOGGLE**, parce que c'est la question d'après : un élément archivé n'a pas
     * disparu, il est masqué. Sans cette phrase, on le cherche sans savoir qu'il est sous les yeux.
     */
    /**
     * ⚠️ Générique, parce qu'il sert les modes de règlement ET les types de travail. Le message des
     * unités reste distinct (`unite.native_lecture_seule`) : il parle du système métrique, ce qui n'a
     * aucun sens pour un virement.
     */
    /**
     * ⚠️ Elle dit la CONSÉQUENCE, pas la couleur : « en violet » décrit ce qu'on voit, « fournis par
     * JustUp » explique pourquoi ils ne s'ouvrent pas en modification. La puce d'exemple porte la
     * couleur, le texte porte le sens.
     */
    legende_exemple: "Exemple",
    legende_natifs:
      "Les éléments présentés ainsi sont fournis par JustUp : ils ne se modifient pas et ne peuvent pas être supprimés. Vous pouvez en créer d'autres à côté, librement.",
    natif_lecture_seule:
      "Référentiel standard : ce libellé est fourni par JustUp — il ne se modifie pas, et cet élément ne peut pas être supprimé. Vous pouvez en créer d'autres à côté.",
    /**
     * ⚠️ **Le titre ANNONCE l'issue, il ne la met plus en suspens.** « Archiver ou supprimer ? » était le
     * seul titre possible quand le front ignorait l'usage : il posait la question à laquelle il ne savait
     * pas répondre. Maintenant que le comptage tranche, laisser la question ferait relire le message pour
     * apprendre ce que le titre pouvait dire d'emblée.
     *
     * ⚠️ **`archiver_titre` reste le repli**, le temps que le serveur réponde — c'est le seul moment où
     * l'incertitude est réelle.
     */
    archiver_titre: "Archiver ou supprimer ?",
    titre_archiver: "Archiver",
    titre_supprimer: "Supprimer définitivement",
    /** Le bouton dit l'ACTE, pas « Confirmer » — qui laisse la question « confirmer quoi ? ». */
    action_archiver: "Archiver",
    action_supprimer: "Supprimer",
    archiver_message:
      "Selon son usage, cet élément sera archivé ou supprimé.\n\nS'il est employé quelque part — une fiche article, un lot, un contrôle — il est ARCHIVÉ : il quitte les listes de choix, les fiches qui l'emploient le conservent tel quel, et vous le retrouvez ici en activant « Voir les éléments désactivés ».\n\nS'il n'est employé nulle part, il est supprimé définitivement.\n\nDans les deux cas, aucune fiche existante n'est modifiée.",
    /**
     * Les deux messages **certains**, quand le serveur a compté les emplois.
     *
     * ⚠️ **Ils REMPLACENT le message générique dès que le compte est connu**, pour les six types. Le
     * générique reste le repli tant que la réponse n'est pas là (ou si elle échoue) : énoncer les deux
     * issues est honnête, en annoncer une au hasard remplacerait un mensonge par un autre.
     *
     * ⚠️ **« Employé #n fois » et non « #n fiches »** : le compte additionne des tables de natures
     * différentes, et `article-infos` compte des **versions** de fiche article (1 407 lignes pour 638
     * articles), pas des articles. Écrire « 12 fiches » serait faux d'un facteur deux.
     *
     * ⚠️ **Une phrase, pas un paragraphe** : le générique en fait quatre parce qu'il doit expliquer une
     * incertitude. Ici il n'y en a plus, donc la conséquence tient en une ligne. Le détail des tables
     * entre parenthèses répond à la question suivante — « employé par quoi ? » — sans imposer un
     * inventaire.
     */
    archiver_message_employe:
      "Employé par 1 élément (#quoi) : il ne sera pas supprimé, seulement masqué des listes de choix. Vous le retrouvez ici via « Voir les éléments désactivés ».",
    archiver_message_employe_plural:
      "Employé par #n éléments (#quoi) : il ne sera pas supprimé, seulement masqué des listes de choix. Vous le retrouvez ici via « Voir les éléments désactivés ».",
    archiver_message_libre:
      "Rien ne l'emploie : il sera définitivement supprimé.",
    /**
     * ⚠️ **Le cas où les deux totaux divergent, dit explicitement.** Une clé étrangère portée par une
     * version archivée empêche le `DELETE` autant qu'une version en vigueur : le serveur archivera donc.
     * Mais il n'y a rien à corriger dans une version archivée — annoncer des emplois qu'on ne peut pas
     * défaire découragerait une action légitime, et les taire ferait promettre une suppression qui
     * n'aurait pas lieu.
     */
    archiver_message_archive:
      "Employé uniquement par des éléments archivés : il ne sera pas supprimé, seulement masqué des listes de choix.",
    /**
     * De quoi est fait le compte — nommé par la nature de la donnée, pas par la table.
     *
     * ⚠️ **`article-infos` se dit « versions de fiche article »**, et c'est la seule formulation juste :
     * la table porte une ligne par version de spécification. Un client qui lit « 1 128 fiches article »
     * alors qu'il en a 638 conclut que le logiciel compte mal.
     *
     * ⚠️ **Les libellés sont au PLURIEL sans accord**, parce qu'ils apparaissent comme une liste de
     * catégories entre parenthèses, jamais précédés d'un nombre. C'est ce qui évite un « 1 versions ».
     *
     * ⚠️ **Une table absente d'ici s'affiche telle quelle** (nom technique) : visible et laide, donc
     * corrigée. Un repli silencieux sur « éléments » masquerait l'oubli.
     */
    /**
     * ⚠️ **« Voir où » et non « Voir le détail »** : ce qui manque à l'utilisateur n'est pas un
     * supplément d'information, c'est l'endroit. Le message dit « employé 24 fois (non-conformités…) »,
     * le dépliant répond « dans quoi exactement ».
     */
    usage_detail: "Voir où",
    /** Repli quand l'infobulle s'affiche avant la réponse du serveur — jamais une liste inventée. */
    usage_inconnu: "plusieurs endroits",
    /**
     * ⚠️ **Sur une fiche NATIVE, c'est la seule phrase qui dit l'usage.** Ces entrées n'ont ni
     * « Enregistrer » ni corbeille : leur nombre d'emplois n'était affiché nulle part, donc invérifiable.
     */
    usage_intro: "Employé par 1 élément.",
    usage_intro_plural: "Employé par #n éléments.",
    /**
     * ⚠️ **Le reste est ANNONCÉ, jamais masqué.** Vingt-cinq lignes sur 1 128 sans le dire donnerait
     * l'impression d'un inventaire complet — la pire des deux erreurs, puisqu'elle rassure à tort.
     */
    usage_reste: "… et d'autres au-delà des 25 premiers",
    /**
     * ⚠️ Ces deux libellés étaient **écrits en dur dans le template** — donc absents des fichiers
     * `message-en/es/pt`, donc en français quelle que soit la langue de l'interface. Croisés en
     * posant les `data-testid` du même dialogue : la règle du dépôt est de les migrer au passage.
     */
    reactiver_existant: "Réactiver un élément existant :",
    ou_creer: "Ou créer un nouveau",
    usage_table: {
      "article-infos": "fiches article",
      /**
       * ⚠️ **`article_fourni` = les TARIFS, et c'est la source qui manquait.** Elle n'a pas de clé
       * étrangère vers l'énumération (`uniteGestion` est un `varchar`), donc le comptage par catalogue ne
       * la voyait pas : `CT6` s'annonçait supprimable alors que 130 tarifs la portaient.
       */
      article_fourni: "tarifs",
      commande_ligne: "lignes de commande fournisseur",
      commande_client_ligne: "lignes de commande client",
      /**
       * ⚠️ **`tiers.typeReglement` est un `varchar` sans clé étrangère** : le mode de règlement par défaut
       * d'une fiche tiers échappait au comptage, alors que 79 fiches portent « Virement ». Même piège que
       * les unités dans les tarifs.
       */
      tiers: "fiches tiers",
      custom_enum_unite_ref: "unités rapportées à celle-ci",
      projet_recette: "projets de recette",
      specification_etape: "spécifications",
      specification_etape_master: "spécifications",
      mp_essai: "matières d'essai",
      travail: "saisies d'heures",
      transaction_entity: "règlements",
      non_conformite: "non-conformités",
      ligne_param: "paramètres de ligne",
      ligne_param_essai: "paramètres de ligne d'essai",
      custom_enum: "sous-éléments",
      sync_link: "liens de synchronisation",
      controle_categories: "contrôles",
      controle_familles: "contrôles",
      adresse: "adresses",
      contact: "contacts",
      stock: "lignes de stock",
      lot_mouvement: "mouvements de stock",
      comptage: "comptages",
      integration_pf: "intégrations de produit fini",
      "inventaire-bilan": "bilans d'inventaire",
    },
    add_option: "Ajouter une option",
    add_default: "Ajouter : #label",
  },
  taxRate: {
    title: "Taux de TVA",
    add: "Ajouter un taux",
    denomination: "Dénomination",
    rate: "Taux (%)",
    default: "Par défaut",
  },
  list: {
    empty: "Aucun élément",
    creer_liste: "Créer une liste",
    dupliquer: "Dupliquer",
    lignes_concernees: "Lignes concernées",
    ajouter_tache: "Ajouter une tâche",
    titre: "Listes de tâche",
    add_dialog_title: "Ajouter une liste de tâche",
    acceder: "Accéder à la liste",
    mes_listes: "Mes listes",
    toutes_les_listes: "Toutes les listes",
    taches_count: "Liste de tâches (#count)",
    lignes_hint: "Sélectionnez les lignes de production pour lesquelles la liste de tâche doit être effectuée.",
    reordonner_tache: "Réordonner la tâche",
    duplication_title: "Duplication",
    duplication_message: "Voulez-vous vraiment dupliquer cette tâche ?",
  },
  ordering: {
    valid: "Valider l'ordre",
  },
  process: {
    article: "Procédés de l'article",
    articleSub:
      "Dans une production, les procédés de l'article seront à réaliser à la suite des procédés de la spécification",
    optional: "Procédé optionnel",
    do: "Réaliser le procédé",
    dontDo: "Ne pas réaliser le procédé",
    add_label: "Ajout d'un procédé",
    add_cta: "Ajouter un procédé",
    edit_label: "Modification du procédé",
    noProcess: "Aucun procédé",
    from_template: "Créer à partir d'un modèle",
    from_prompt_ai: "Créer à l'aide de l'IA",
    edit_mode_prompt_ai: "Modifier à l'aide de l'IA",
    edit_prompt_ai: "Editer les instruction à l'IA",
    exclude_lines: "Lignes exclues",
    timer: "Minuteur",
    minute: "minute(s)",
    second: "seconde(s)",
    exclude_lines_exp:
      "Sélectionnez les lignes de production pour lesquelles l'étape n'est pas à effectuer. (L'étape n'apparaîtra pas lors des productions effectuées sur les lignes sélectionnées.)",
  },
  processCopy: {
    quickCreate: "Création rapide",
    processModel: "Modèles de procédé",
    searchSpecif: "Rechercher dans les spécifications",
    processIncorporation: "Incorporation d'article",
    variableUsage: "utilisation",
    variableUsages: "utilisations",
    in: "dans",
    context: "contexte",
    contexts: "contextes",
    control: "Contrôle",
    processDescription: "Déscription du procédé",
    articleRef: "Article de nomenclature",
    articleVarRef: "Article paramétrable de nomenclature",
    varRef: "Variable",
    warn1:
      "Le procédé contient des référence a des variables ou des articles de nomenclature.",
    warn2:
      "Veuillez remplacer les éléments trouvé par des éléments de votre spécification.",
  },
  checklist: {
    instance_non_enregistree: "Impossible d'enregistrer l'ouverture de cette tâche : la case n'est pas conservée, réessayez.",
    all: "Voir toutes les listes",
    who: "Utilisateurs",
    frequence: "Fréquence",
    add_label: "Ajout d'une checklist",
    edit_label: "Modification de la checklist",
    tolerancePlusMois: "Tolérance future (en heures)",
    toleranceMoinsMois: "Tolérance passée (en heures)",
    tolerancePlusSemaine: "Tolérance future (en jours)",
    toleranceMoinsSemaine: "Tolérance passée (en jours)",
    tolerancePlusJours: "Tolérance future (en minutes)",
    toleranceMoinsJours: "Tolérance passée (en minutes)",
    deleted_suffix: "(supprimée)",
    step_of_ope: "Sélection de l'OF et de l'opé.",
    step_of: "Sélection de l'OF",
    step_ope: "Sélection de l'Opé.",
    step_controles_of: "Contrôles liés à l'OF",
    step_controles: "Contrôles",
    controle_saved: "Contrôle sauvegardé",
    dialog_add: "Ajout d'une tâche",
    dialog_edit: "Modification d'une tâche",
    lie_a_of: "Lié à un OF",
    ligne_prod: "Ligne de prod.",
    controles_simplifies: "Réalisation des contrôles simplifiée",
    tolerance: "Tolérance :",
    tolerance_negative: "La tolérance ne peut pas être négative",
    occurences: "Occurrences :",
    heures_precises: "Heures précises",
    interval: "Intervalle",
    ajouter_occurence: "Ajouter une occurrence",
    aucune_occurence: "Aucune occurrence",
    plage_invalide: "L'heure de début doit précéder l'heure de fin",
    grid_header_controles: "Contrôles",
    grid_header_calage: "Calage",
  },
  form: {
    select: "Sélectionner...",
    invalid: "Le formulaire n'est pas valide",
    multiCheckbox: {
      removeAll: "Efacer la sélection",
      select: "Sélectionner",
      selectLigne: "Ligne(s)",
      selectFamille: "Famille(s)",
      selectCategorie: "Sous-famille(s)",
    },
    minZero: "Doit être >= 0",
    validators: {
      required: "Ce champ est obligatoire.",
      minlength: "Ce champ ne peut comporter moins de #min caractères",
      maxlength: "Ce champ ne peut comporter plus de #max caractères",
      year: "L'année est invalide",
      range: "Les intervalles d'émission de CO2 se chevauchent",
      rangeLigne: "L'intervalle d'émission de CO2 est invalide",
      min: "Ce champ doit être supérieur ou égal à ",
      max: "Ce champ doit être inférieur ou égal à ",
      length: "Ce doit être #size #type",
      exactLength: "Ce champ doit comporter #exactLength caractères",
      email: "L'adresse email est invalide",
      adresse: {
        cityPattern: "Ce n'est pas le nom d'une ville",
      },
      min_quantite: "Ce champ doit être supérieur au minimum",
      stockSecuriteMax:
        "Le stock de sécurité ne peut pas être supérieur au stock de réappro.",
    },
  },

  filters: {
    label: "Filtres",
    no_data: "Aucune donnée sur la période / filtres.",
    reset: "Réinitialiser les filtres",
  },
  recette: {
    etat: {
      brouillon: "Brouillon",
      fige: "Figé",
    },
    creation: {
      titre: "Nouveau projet",
      porte_import: "Depuis un produit existant",
      porte_import_sous: "Reprend le process réel comme base de travail, sans y toucher",
      porte_zero: "Partir de zéro",
      porte_zero_sous: "Une recette entièrement nouvelle",
      denomination: "Nom du projet",
      // Ce que le champ SERT, en cinq mots : le seul texte qui suivra le projet partout.
      denomination_hint: "Le nom sous lequel vous le retrouverez.",
      section_produit_fini: "Le produit fini",
      /*
       * ⚠️ **Ce que le bouton gris ne disait pas.** « Créer le projet » restait désactivé sans motif : le
       * lecteur voyait un refus et devait deviner le champ. Le nommer est la seule façon de rendre le
       * refus actionnable — c'est la règle du module (« un refus nommé plutôt qu'un chiffre plausible »).
       */
      manque_nom: "Donnez un nom au projet pour le créer.",
      manque_article: "Choisissez le produit à reformuler.",
      article_source: "Produit à reformuler",
      creer: "Créer le projet",
      cree: "Projet créé",
      descriptif: "Descriptif",
      descriptif_exemple: "Pour quel client, quelle contrainte, quelle piste — facultatif",
      // Les deux étapes du stepper de création. La seconde est aussi le libellé du bouton qui y mène :
      // un bouton qui ne porte pas le nom de sa destination fait douter de ce qu'il ouvre.
      etape_objectifs: "Le taux de marge",
      retour: "Retour",
      // ⚠️ Le dénominateur est DANS le libellé (A51) : un prix sans son unité fausse la marge, et
      // `unitePrixCible` n'est lue par personne — c'est la seule façon honnête de le dire.
      /*
       * ⚠️ **L'unité NOMMÉE quand on la connaît.** « par unité de gestion » est juste et illisible : on
       * saisit 3,40 € en pensant « le bocal » alors que l'unité est le carton de 6, et le projet affiche
       * une marge de −97 % sans que rien ne désigne le dénominateur (constaté le 11/09/2026).
       */
    },
    /**
     * Le **produit fini** d'un essai — la racine de la recette.
     *
     * ⚠️ Ces libellés accompagnent le geste qui manquait : les deux routes d'ajout d'étape exigent une
     * étape parente, donc sur une cascade vide rien ne pouvait être créé. « Partir de zéro » était un
     * cul-de-sac, pas un écran vide.
     */
    produit_fini: {
      poids_net: "Poids net d'une unité (kg)",
      poids_net_hint: "Ce que pèse une unité. Sans lui, rien ne se ramène à 100 g.",
      titre: "Définir le produit fini",
      definir: "Définir le produit fini",
      aide: "C'est la racine de la recette : les composants et les semi-finis s'ajoutent ensuite sous lui, depuis le schéma de fabrication.",
      nom: "Nom du produit fini",
      unite: "Unité de gestion",
      // Ce que le champ EST, pas ce qu'il commande : trois exemples valent mieux qu'une définition.
      unite_hint: "Ce qui se compte : kilo, litre, pièce…",
      quantite: "Quantité obtenue par lot",
      // Dit ce que le chiffre COMMANDE, pas ce qu'il est : c'est le diviseur de tous les coûts unitaires.
      /*
       * ⚠️ **Un exemple plutôt qu'une définition** — la formulation d'avant (« le diviseur de tous les
       * prix à l'unité ») disait le RÔLE du nombre à quelqu'un qui cherche encore QUOI écrire dedans.
       * Deux quantités concrètes lèvent la question en une seconde, et la convention du vide est dite ici
       * plutôt que devinée : depuis le 12/09/2026, une taille de lot absente vaut 1.
       */
      quantite_hint: "Combien une fabrication produit en une fois : 200 pots, 50 kg… Vide, on compte 1.",
      plus_tard: "Tout se complète ou se corrige ensuite, depuis le nœud.",
      defaut_projet: "Vide : #nom",
      sans_nom: "le nom du projet",
      creer: "Créer le produit fini",
      cree: "Produit fini créé",
    },
    liste: {
      vide: "Aucun projet pour le moment.",
      issu_du_reel: "Issu du réel",
      parti_de_zero: "Parti de zéro",
      derniere_activite: "Modifié le",
      supprime: "Projet supprimé",
      // ⚠️ Les deux motifs de refus, nommés. Le bouton reste VISIBLE et désactivé : masquer un geste
      // sans dire pourquoi est ce qui a fait demander une fonction qui existait déjà.
      blocage_promu:
        "Ce projet contient une étape devenue un article de production : le supprimer laisserait cet article sans sa spécification.",
      blocage_reference:
        "Ce projet contient une étape utilisée par une nomenclature de production : le supprimer casserait cette nomenclature.",
      blocage_inconnu: "Ce projet ne peut pas être supprimé.",
    },
    /**
     * Les trois repères d'un projet — prix de vente cible, objectif de marge, échéance.
     *
     * ⚠️ Ils sont facultatifs à la création (§ 4.3) et il n'existait AUCUN moyen de les renseigner
     * ensuite : la carte de marge disait « renseignez un prix de vente cible sur le projet » sans que
     * rien nulle part ne le permette. Ces libellés accompagnent le geste qui manquait.
     */
    /**
     * Le simulateur de prix de la carte de marge — deux curseurs, aucune écriture avant « Retenir ».
     *
     * ⚠️ Le vocabulaire distingue **simuler** de **retenir** : c'est la seule chose qui empêche de repartir
     * avec un prix qu'on a seulement essayé.
     */
    simulation: {
      fermer: "Revenir au prix du projet",
      en_cours: "prix simulé",
      retenir: "Retenir ce prix",
      // ── L'état sans prix retenu : la carte PROPOSE au lieu d'annoncer qu'elle ne peut rien.
      // ⚠️ « proposé », jamais « appliqué » : rien n'est enregistré tant qu'on n'a pas retenu, et
      // « appliqué » est le mot de la branche calculée, où un prix EST en vigueur.
      // ⚠️ La puce d'un prix que personne n'a décidé : il s'affiche, mais il ne se lit pas comme un
      // prix retenu. Sans elle, la carte annoncerait « prix appliqué » sous un aside qui dit « non défini ».
      marge_simple: "Marge",
      // ⚠️ La phrase NOMME la référence (l'objectif du projet) : un prix proposé sans dire d'où il
      // sort se lit comme une valeur par défaut de l'outil, donc ne se retient jamais.
      /*
       * ⚠️ **La SOURCE de la marge est écrite.** « Votre objectif » n'est vrai que si le projet en porte
       * un : sans lui, le prix proposé venait d'un 0 % que personne n'avait décidé, présenté comme une
       * décision de l'utilisateur (corrigé le 08/09/2026). Trois phrases, parce que les trois n'engagent
       * pas la même confiance — et seule la première est une décision prise sur ce projet.
       */
      // Sans objectif, aucune proposition n'est possible : on dit ce qui manque et le geste qui l'obtient.
      sans_objectif: "Pas de coût de revient calculable : sans lui, aucun prix ne peut être déduit du taux de marge.",
      // Noms accessibles des deux curseurs — le libellé voisin n'est pas lié au contrôle.
      aria_prix: "Prix de vente, en euros",
      aria_marge: "Marge, en pourcent",
      // Ce que la simulation ne touche pas — sinon une hypothèse passerait pour un chiffrage.
      /*
       * ⚠️ **« la marge cible » a disparu de la phrase avec la notion elle-même** (12/09/2026). Ce que le
       * réglage ne touche PAS reste vrai et reste dit : le tableau du dessous compare des données
       * enregistrées, et lui faire suivre les curseurs ferait passer une hypothèse pour un chiffrage.
       */
      portee: "Le réglage ne modifie ni les essais, ni le tableau de comparaison.",
    },
    /**
     * L'agencement des cartes du cockpit — déplacer, replier, masquer, remettre.
     *
     * ⚠️ « Masquer » et non « Supprimer » : la carte rejoint un catalogue qui permet de la remettre. Le verbe
     * doit dire que le geste est réversible, sinon personne ne l'essaie.
     */
    agencement: {
      monter: "Monter cette carte d'un rang",
      deplacer: "Glisser pour déplacer cette carte",
      menu: "Agencer cette carte",
      affichage: "Affichage",
      raz: "RAZ",
      descendre: "Descendre cette carte d'un rang",
      replier: "Replier",
      deplier: "Déplier",
      masquer: "Masquer cette carte",
      pleine_largeur: "Sur toute la largeur",
      demi_largeur: "Sur une moitié",
      reinitialiser: "Rétablir l'agencement par défaut",
    },
    cockpit: {
      chargement: "Chargement…",
      /*
       * 🔴 **Ces deux libellés annonçaient une DÉCISION là où il n'y a qu'un repli.** Signalé à l'écran
       * le 12/09/2026 : la carte titrait « Marge cible 40 % » et « par rapport au taux du projet »
       * pendant que la base porte `projet_recette.tauxMargeCible` à NULL **et**
       * `activity_infos.margeReferenceRecettePourcent` à NULL. Les 40 % venaient de
       * `MARGE_REFERENCE_DEFAUT_POURCENT`, une constante en dur que personne n'a décidée.
       *
       * ⚠️ **La source est déjà suivie** (`margeCibleEnVigueur` rend `PROJET | ACTIVITE | DEFAUT`) :
       * seuls les mots ne la suivaient pas. C'est exactement la rechute du défaut corrigé le 08/09/2026
       * sur le prix proposé — « un 0 % que personne n'avait décidé, présenté comme une décision de
       * l'utilisateur ».
       */
      pts: "pts",
      introuvable: "Ce projet n'existe pas ou n'est plus accessible.",
      importe: "Importé",
      /*
       * 🔴 **Le LIBELLÉ suit la source, pas seulement la pastille.** Signalé à l'écran le 12/09/2026 :
       * le bandeau annonçait « Prix de vente cible 4,23 €/Unité · prix proposé » pendant que la carte de
       * main-d'œuvre disait « ce projet n'a pas de prix de vente cible ». Les deux avaient raison — le
       * projet porte bien `prixVenteCible` à NULL — mais le premier titrait une PROPOSITION du nom de la
       * décision, et la pastille en fin de ligne ne suffisait pas à le défaire.
       *
       * ⚠️ Deux écrans qui se contredisent sur le même objet se lisent comme un problème de données, pas
       * d'affichage : c'est la règle que ce module tient déjà sur l'interrupteur nutritionnel.
       */
      prix_simule: "Prix simulé",
      prix: "Prix de vente",
      non_defini: "non défini",
      nouvel_essai: "Nouvel essai",
      nouvel_essai_aide: "Repart de la recette de l'essai affiché, que vous pourrez adapter.",
      reprendre: "Reprendre",
      projet: "Projet",
      essai: "Essai",
      changer_essai: "Changer d'essai",
      issu_de: "issu d'une reprise",
      lot_prod: "Lot de production",
      cascade: "Cascade",
      cascade_hint:
        "Les articles fabriqués, du premier semi-fini au produit fini. Sélectionner un article ne change que la composition ; les coûts restent ceux d'une unité vendue.",
      en_parallele: "en parallèle",
      cascade_vide: "Aucun article fabriqué dans cet essai.",
      semi_fini: "Semi-fini",
      semi_finis: "semi-finis",
      produit_fini: "Produit fini",
      produits_finis: "produit fini",
      rendement_absent: "Rendement non renseigné",
      lignes: "lignes",
      recette: "Recette",
      recette_complete: "Recette complète",
      portee_complete: "tous les semi-finis",
      mode_arbre: "Arbre",
      mode_famille: "Par famille",
      mode_sous_famille: "+ sous-famille",
      cout_total: "Coût total",
      tout_replier: "Tout replier",
      tout_deployer: "Tout déployer",
      part_cout: "Part du coût",
      heures: "Heures / unité",
      composant: "Composant",
      pour_100g: "pour 100 g",
      /*
       * ⚠️ **« Mis en œuvre » et « obtenu » sont les mots d'A29**, ceux de la carte « Données
       * logistiques » : deux vocabulaires pour les mêmes grandeurs feraient chercher la différence.
       */
      bilan_mis_en_oeuvre: "Ingrédients mis en œuvre",
      bilan_obtenu: "Obtenu (poids net)",
      bilan_rendement: "Rendement global",
      total: "Total",
      composition_col_g: "g",
      composition_col_part: "% du mis en œuvre",
      sans_prix: "sans prix connu",
      /*
       * ⚠️ **La phrase donne le SIGNAL, pas le constat.** « Le total ne fait pas 100 g » enverrait
       * chercher une erreur de calcul ; ce qui manque presque toujours, c'est un composant de la recette
       * (l'eau, la plupart du temps) ou une taille de lot surdimensionnée. La phrase nomme les deux
       * causes réelles, dans l'ordre de leur fréquence.
       */
      composition_deficit:
        "#manque g pour 100 g ne sont expliqués par aucun composant. Il en manque probablement un — souvent l'eau — ou la taille de lot est surdimensionnée.",
      composition_concentre:
        "La recette concentre : le total dépasse 100 g, de l'eau s'est évaporée. L'écart ne se rattache à aucune ligne en particulier. Écart :",
      composition_refus:
        "Composition non calculable : le poids d'un ingrédient est inconnu. Sans lui, les pourcentages seraient faux — chacun paraît juste, mais leur somme ne veut rien dire.",
      composition_vide: "Aucun composant.",
      cout_revient: "Coût de revient",
      marge_avec_mo: "matière + MO",
      marge_sans_mo: "matière seule",
      marge_sans_mo_aide:
        "Bascule la lecture entre « matière + main-d'œuvre » et « matière seule ». Réglage d'affichage : rien n'est enregistré, et le coût de revient reste matière + MO.",
      toujours_uc: "pour 1 unité de gestion du produit fini",
      pour_1: "pour 1",
      de_cette_etape: "de ce semi-fini",
      article_fabrique: "Article fabriqué dont reprendre la recette",
      reutiliser_etape: "Réutiliser un semi-fini déjà créé dans cet essai",
      libre: "libre",
      nom_inventee: "Nom du semi-fini (ex. « Base tomate maison »)",
      fabriquee_hint: "Le lot de référence : les composants se dosent pour cette quantité. Facultatif.",
      qte_unitaire: "Quantité pour 1 unité fabriquée",
      qte_sans_lot: "quantité de nomenclature (aucune quantité fabriquée renseignée)",
      unite_sans_nom: "unité fabriquée",
      de_produit_fini: "de produit fini",
      deplier_familles: "Cliquer pour voir la décomposition par famille",
      par_famille: "Par famille",
      par_famille_atelier: "Par famille de l'étape qui produit",
      non_normalise: "taille de lot non déclarée — comptée pour 1",
      matiere: "Matière",
      mo: "MO",
      mo_non_estimee: "non estimée",
      cout_vide: "Sélectionnez un article de la cascade.",
      marge: "Marge du projet",
      deux_reperes: "écart au réel · écart au taux",
      composition_pf: "Composition du produit fini",
      avant_envoyer: "Avant d'envoyer",
      avant_envoyer_hint:
        "Un seul endroit, au moment de sortir un document. La gravité se calcule : elle suit le poids du composant dans le produit fini.",
      rien_a_signaler: "Rien ne s'oppose à la sortie d'un document",
      archive_au_fige:
        "Le rapport sera archivé tel quel : c'est cette photo qui est opposable en audit. Recalculé dans six mois, il ne dirait plus la même chose.",
      /*
       * ⚠️ **Ce qui est TOUJOURS vrai en premier** : le document est figé, donc opposable. La case ne doit
       * pas laisser croire qu'on peut sortir une fiche « pour voir » — c'est l'aperçu qui sert à ça, et il
       * n'écrit rien.
       */
      sortie_document_fige:
        "Le document sera figé : il devient opposable et ne se modifie plus. Pour relire sans rien écrire, utilisez l'aperçu.",
      sortie_figer_essai: "Figer aussi l'essai",
      sortie_figer_essai_oui:
        "son chiffrage et son rapport ne changeront plus — c'est ce qui rend la fiche vérifiable plus tard",
      sortie_figer_essai_non:
        "la formulation reste modifiable : le document restera adossé à un chiffrage qui a pu changer depuis",
      sortie_essai_deja_fige:
        "Cet essai est déjà figé : en resortir une fiche ne le modifie pas — c'est une copie de la même photographie.",
      sortie_deja: "Une fiche de ce profil est déjà sortie le #date",
      sortie_deja_pluriel: "#nb fiches de ce profil sont déjà sorties, la dernière le #date",
      sortie_prochain_rang: "celle-ci portera le n° #rang",
      sortie_ok: "Fiche n° #rang sortie et figée",
      sortie_ok_avec_fige: "Fiche n° #rang sortie — l'essai est figé",
      sortir_fiche: "Sortir la fiche technique",
      envoyer_client: "Envoyer au client",
      sortie_bloquee: "Des points bloquants subsistent — voir « Avant d'envoyer »",
      ajouter: "Ajouter",
      article_referentiel: "Article du référentiel",
      ou_matiere_provisoire: "…ou nom d'un composant provisoire",
      lecture_seule: "Essai figé — lecture seule",
      etiquetage: "Liste d'ingrédients",
      main_oeuvre: "Main-d'œuvre",
      comparaison: "Comparaison des essais",
      fige_lecture_seule: "Cet essai est figé : sa recette ne se modifie plus. Reprenez-le pour en créer une nouvelle version.",
    },
    comparateur: {
      essai: "Essai",
      reel: "Réel",
      courant: "En cours",
      cout_revient: "Coût de revient",
      marge: "Marge",
      sans_prix: "sans prix",
      produit_actuel: "Produit actuel",
      ecart_reel: "Écart au réel",
      marge_horaire: "Marge horaire",
      heures_inconnues: "heures inconnues",
      total_partiel: "total partiel",
      rendement: "Rendement",
      vide: "Aucun essai figé à comparer.",
    },
    source: {
      PRODUCTION_REELLE:
        "Article fabriqué — coût de production observé sur le dernier ordre de fabrication",
      PRODUCTION_THEORIQUE:
        "Article fabriqué — coût théorique, calculé en cascade depuis sa propre nomenclature",
      // ⚠️ **`REEL` a été scindé, parce qu'il MENTAIT.** Il était émis dès qu'un tarif fournisseur
      // existait — c'est-à-dire sur presque tout composant acheté — et annonçait « coût réel constaté
      // sur des achats ». Le tarif est ce qu'on a négocié ; le lot reçu est ce qu'on a payé. Mesuré
      // sur les composants des essais : l'écart entre les deux atteint +315 %.
      ACHAT_REEL: "Coût d'achat réellement constaté",
      TARIF: "Tarif fournisseur — ce qui a été négocié, pas ce qui a été payé",
      // Les compléments de source, assemblés par `vl-prix-provenance`. Ils NOMMENT la source pour
      // qu'un chiffre qui surprend soit vérifiable : sans eux, on ne sait pas quoi aller relire.
      detail_lots: "moyenne pondérée de #nb lots reçus",
      // ⚠️ Une « moyenne » sur un seul lot n'en est pas une : la dire ainsi laisserait croire à une
      // observation répétée là où il n'y a qu'une réception.
      detail_lot_unique: "un seul lot reçu",
      detail_dernier_lot: "le plus récent #lot du #date",
      // ⚠️ Un coût SAISI n'est pas un coût constaté, et rien ne les distingue à l'affichage.
      detail_saisis: "dont #nb à coût saisi à la main",
      detail_tarif: "#fournisseur, tarif du #date",
      detail_fournisseur_inconnu: "fournisseur non renseigné",
      detail_date_inconnue: "date inconnue",
      ESTIME: "Prix estimé — composant provisoire, jamais négocié",
      // ⚠️ La provenance `ESSAI` a été ajoutée côté serveur (prix posé pour un projet de développement)
      // sans son libellé : le survol affichait la clef brute « recette.source.ESSAI ». Une provenance qui
      // ne se lit pas ne sert à rien — c'est précisément ce qu'elle est censée rendre discutable.
      ESSAI:
        "Hypothèse de ce projet de développement — ce n'est pas le tarif du référentiel, et la fiche article n'est pas modifiée",
      AUCUN: "Aucun prix connu pour ce composant",
    },
    graphe: {
      zoom_moins: "Réduire le zoom",
      zoom_plus: "Augmenter le zoom",
      afficher_composants: "Afficher tous les composants",
      deplier_ses_composants: "Voir ses composants",
      replier_ses_composants: "Masquer ses composants",
      masquer_composants: "Masquer tous les composants",
      replacer: "Replacer les cartes",
      par_flux: "Par flux",
      par_type: "Par type",
      groupe_composants: "COMPOSANTS",
      groupe_semi_finis: "SEMI-FINIS",
      groupe_produits_finis: "PRODUIT FINI",
      tout_deployer: "Tout déployer",
      tout_replier: "Tout replier",
      tout_deployer_aide: "Afficher les composants de tous les semi-finis",
      plein_ecran: "Plein écran",
      quitter_plein: "Quitter le plein écran",
      liaison_en_cours: "Relier depuis",
      annuler: "annuler",
      ajouter: "Ajouter un composant",
      ajouter_aide: "Crée une carte non reliée : vous tirerez ses liens ensuite",
      lier: "Relier ce nœud à un autre : il sera consommé par celui que vous cliquerez ensuite",
      disponibles: "Disponibles — à relier",
      modifier_quantite: "Modifier la quantité de ce lien",
      delier: "Détacher cette carte de la recette",
      delier_titre: "Délier la carte",
      // ⚠️ Le message annonce le CHEMIN : délier n'est pas supprimer, et c'est pourtant par là qu'on
      // supprime. Sans ça, on cherche une corbeille qui n'apparaît qu'après le geste.
      delier_message:
        "Détacher « #nom » de la recette qui la consomme ? Elle reste dans l'essai avec sa composition, et rejoint les nœuds disponibles — d'où vous pourrez la relier ailleurs, ou la retirer définitivement.",
      delier_message_n:
        "Détacher « #nom » des #nb recettes qui la consomment ? Elle reste dans l'essai avec sa composition, et rejoint les nœuds disponibles — d'où vous pourrez la relier ailleurs, ou la retirer définitivement.",
      delier_action: "Délier",
      deliee: "Carte déliée : elle est dans les nœuds disponibles.",
      retirer_etape: "Retirer cette étape de l'essai",
      retirer_etape_titre: "Retirer l'étape",
      retirer_etape_action: "Retirer",
      // ⚠️ Le message annonce la descendance sans en donner le compte : le front ne connaît pas l'arbre
      // des masters, et un nombre approché serait pire qu'aucun.
      retirer_etape_message:
        "Retirer « #nom » de cet essai ? Ses sous-étapes éventuelles partiront avec elle. Rien n'est supprimé en production : cette étape n'existe que dans l'essai.",
      retiree: "Étape retirée de l'essai.",
      retiree_avec_sous: "Étape retirée, avec #nb sous-étape(s).",
      par_unite_de: "par unité de",
      a_relier: "à relier",
      lien_impossible:
        "Un composant provisoire appartient à une seule ligne : ajoutez-le dans l'autre carte plutôt que de le relier.",
      cible_impossible:
        "Seule une étape de fabrication peut consommer un composant : choisissez une étape comme destination.",
    },
    etat_nut: {
      renseignees: "Valeurs nutritionnelles renseignées",
      absentes: "Valeurs nutritionnelles inconnues — à renseigner, ou à déclarer sans objet",
      sans_objet: "Déclaré sans valeurs nutritionnelles — exclu du calcul",
      agregees: "Valeurs agrégées depuis les composants de ce semi-fini",
    },
    ajout: {
      deja_disponible: "Déjà créée : elle est dans les nœuds disponibles, à relier depuis le graphe.",
      reutiliser: "Réutiliser",
      densite_aide: "Masse d'un litre, en kg — c'est elle qui permet de peser un composant vendu au volume.",
      prix_aide: "Ce que coûte 1 #unite à l'achat — vide : la matière restera « sans prix connu ».",
      prix_aide_sans_unite:
        "Ce que coûte une unité à l'achat — vide : la matière restera « sans prix connu ».",
      /*
       * ⚠️ **La question se pose à la CRÉATION, pas dans un avertissement plus tard.** Une étiquette ou
       * un carton entrait dans les 100 g alimentaires et paraissait dans les avertissements
       * nutritionnels comme « composant concerné » ; il fallait revenir le déclarer « sans objet ».
       * Une question au bon moment vaut mieux qu'un constat au mauvais.
       */
      comestible: "Ce composant est comestible",
      comestible_aide:
        "Décochez pour un emballage, une étiquette ou un auxiliaire : il ne comptera ni dans les 100 g, ni dans la déclaration nutritionnelle.",
      titre: "Ajouter une carte",
      question: "Qu'ajoutez-vous ?",
      changer: "changer de type",
      type_article: "Composant",
      type_article_aide: "Un article du référentiel. Sa recette reste celle de la production.",
      type_provisoire: "Composant provisoire",
      type_provisoire_aide: "Un composant qui n'existe pas encore : un nom suffit.",
      type_ciqual: "Composant depuis Ciqual",
      type_ciqual_aide:
        "Un composant provisoire créé depuis le référentiel de l'Anses : nom et valeurs nutritionnelles compris.",
      manque_aliment: "Choisissez un aliment dans le référentiel",
      changer_aliment: "changer d'aliment",
      // 🔴 **`ciqual_note` a été SUPPRIMÉE, et pas seulement pour épurer : elle était FAUSSE.** Elle
      // affirmait « c'est la référence à l'aliment qui est enregistrée, pas une copie de ses valeurs :
      // le composant suivra le référentiel, y compris à la prochaine montée de millésime ». C'est
      // exactement ce que le module a cessé de faire le 21/08/2026 — on copie, on ne relie jamais,
      // précisément pour qu'une montée de millésime ne réécrive pas les chiffres d'un essai déjà
      // chiffré. Le texte décrivait donc une règle inversée, sur trois lignes, en tête du seul geste de
      // l'écran. Une aide qui cite une règle vieillit comme le code, et personne ne la relit au moment
      // où la règle change — c'est le même constat que les libellés FVL périmés du Nutri-Score.
      type_etape_article: "Semi-fini (article existant)",
      type_etape_article_aide:
        "Forke la cascade de l'article : sa recette devient modifiable dans cet essai.",
      type_etape_inventee: "Semi-fini provisoire",
      type_etape_inventee_aide:
        "Un semi-fini qui n'existe nulle part. Promouvable en article plus tard.",
      unite_gestion: "Unité de gestion",
      // ⚠️ **Raccourcies pour tenir en légende du bouton**, où elles ont été déplacées : elles étaient
      // deux pavés pleine largeur au-dessus de lui. Le fait qui compte — la carte n'est reliée à rien —
      // survit ; c'est la paraphrase du geste (« à ce nœud, à un autre, ou à plusieurs ») qui part, et
      // elle se lit de toute façon dans le graphe où l'on va relier.
      sans_lien: "Quantité vide : créée sans lien, à relier depuis le graphe.",
      sans_lien_simple: "Créée sans lien, à relier depuis le graphe.",
      quantite_facultative: "Quantité consommée (facultatif)",
      // ⚠️ Nomme le parent : « reliée » ne dit pas à quoi, et le dialogue peut être ouvert depuis
      // n'importe quelle carte du graphe.
      avec_lien:
        "L'étape sera reprise avec toute sa recette et reliée à #parent — elle apparaîtra tout de suite dans la cascade.",
      manque_article: "Choisissez l'article",
      manque_nom: "Donnez un nom",
    },
    lien: {
      titre: "Relier deux nœuds",
      relier: "Relier",
      faite: "lien créé",
      quantite_de: "Quantité de #nom",
      sans_lot: "#cible n'a pas de taille de lot renseignée : la quantité vaut pour l'ensemble fabriqué.",
      /*
       * 🔴 **Le libellé nommait un objet qui n'existe pas pour cette nature.** « sur la fiche article »
       * s'affichait aussi sur une **matière provisoire**, qui n'a pas de fiche — et dont l'unité était
       * pourtant renseignée sur elle-même. Un message qui envoie corriger un endroit inexistant fait
       * chercher une fiche introuvable au lieu du champ qui existe.
       *
       * ⚠️ Et il ne peut pas nommer LE bon endroit non plus : `NoeudCascade` ne porte aucune unité, donc
       * ce cas se déclenche pour toutes les natures. Le libellé se borne donc au constat.
       */
      unite_inconnue: "unité non renseignée",
      pour_le_lot: "Pour les #lot #unite fabriquées",
      pour_ensemble: "Pour l'ensemble fabriqué",
      par_unite_de: "Par unité de #cible",
      pour_le_lot_de: "Pour les #lot #unite de #cible",
      /*
       * ⚠️ **Les DEUX lectures du même nombre, à l'instant où on valide.** Les deux champs se pilotent
       * déjà l'un l'autre, mais personne ne relit celui qu'il n'a pas tapé : 0,2 saisi « par bocal »
       * dans le champ du lot écrit 0,2 kg pour 390 bocaux — un facteur 390, et un coût de revient qui
       * reste parfaitement plausible. La phrase met les deux grandeurs côte à côte, sous le bouton.
       */
      recap: "Sera enregistré : #lot #unite pour le lot — soit #parUnite #unite par unité de #cible.",
    },
    /**
     * Les constituants nutritionnels, par code du référentiel client (`constante_type`).
     *
     * Ils vivent ICI et pas dans le composant : un libellé affiché est une traduction, et le sortir du
     * fichier de traductions le rend invisible au jour où l'application parlera une autre langue.
     */
    /**
     * L'unité de gestion et le nom d'étiquetage d'une matière provisoire.
     *
     * ⚠️ Les libellés de **nature** ne décrivent pas l'unité, ils disent ce qu'elle IMPLIQUE pour le calcul.
     * « Volume » seul n'apprend rien ; « il faudra une densité pour la peser » dit pourquoi la nutrition
     * restera non calculable, et donc quoi faire.
     */
    unite: {
      /*
       * ⚠️ Les titres de BLOC disent la portée une fois, au lieu que chaque champ la répète : les deux
       * champs de ce bloc sont des surcharges d'essai, et c'est la seule chose à retenir avant de les
       * modifier.
       */
      bloc_projet: "Réglages pour ce projet",
      bloc_provisoire: "Identité de cette matière provisoire",
      bloc_consommation: "Consommation",
      depuis_fiche: "vient de la fiche article",
      nature_MASSE:
        "Unité de masse : le composant se pèse directement, ses valeurs nutritionnelles sont calculables.",
      nature_VOLUME: "Unité de volume : la pesée passe par la densité.",
      nature_COMPTAGE: "Unité de comptage : la pesée passe par le poids net d'une unité.",
      /**
       * ⚠️ **« Ce composant ne se pèse pas » était faux** : un bout de tuyau de 50 cm pèse 1 kg. La limite
       * est celle du CALCUL, et c'est tout ce que le texte affirme.
       *
       * ⚠️ **Ne pas y remettre « il faudrait une masse au mètre »** : ce champ n'existe pas, l'utilisateur
       * ne peut pas le créer, et le lui annoncer transforme une aide en énigme. La raison de conception
       * appartient à ce commentaire, pas à l'écran.
       *
       * ⚠️ **Clef OBLIGATOIRE dès que `LONGUEUR` existe côté serveur.** Le dialogue compose sa clef
       * (`"recette.unite.nature_" + nature`) : sans cette entrée, `METRE` affichait la clef brute à
       * l'écran. Une nature ajoutée au serveur sans son libellé ne casse aucun build — elle sort en
       * texte technique dans l'interface.
       */
      nature_LONGUEUR:
        "Unité de longueur : cette quantité ne se convertit pas en poids, donc les valeurs nutritionnelles ne se calculent pas.",
      nature_INCONNUE:
        "Cette unité ne permet pas de peser le composant : ses valeurs nutritionnelles ne se calculent pas. Choisissez une unité de masse, de volume ou de comptage.",
      nom_etiquetage: "Nom d'étiquetage",
      nom_etiquetage_aide:
        "Ce qui paraîtra sur l'étiquette. Sans lui, la fiche technique ne peut pas sortir. Distinct de la dénomination, qui porte le conditionnement et la qualité fournisseur.",
      enregistre: "Modifications enregistrées pour ce projet",
      /* L'état est ÉCRIT : un bouton grisé ne dit pas ce qu'il attend. */
      en_attente: "#nb modification(s) en attente",
      a_jour: "à jour",
      revenir_fiche: "Revenir à l'unité de la fiche",
      /*
       * ⚠️ **Le message le plus important de ce bloc.** Une unité posée ne convertit AUCUNE quantité déjà
       * saisie : les nombres de la recette ont été écrits dans l'unité de la fiche. Sans cette phrase, le
       * coût et les teneurs changent au moment où l'unité change, et rien à l'écran ne relie les deux.
       */
      non_convertie:
        "Les quantités déjà saisies ne sont PAS converties : vérifiez-les. Pour ce projet seulement : la fiche de l'article n'est pas modifiée.",

    },
    nutriments: {
      CALORIE: "Énergie",
      CALORIE_unite: "kcal",
      TOTAL_FAT: "Matières grasses",
      TOTAL_FAT_unite: "g",
      SATURATED: "dont acides gras saturés",
      SATURATED_unite: "g",
      CARB: "Glucides",
      CARB_unite: "g",
      SUGAR: "dont sucres",
      SUGAR_unite: "g",
      DIETARY_FIBRE: "Fibres alimentaires",
      DIETARY_FIBRE_unite: "g",
      PROTEIN: "Protéines",
      PROTEIN_aide: "Ciqual publie deux définitions de la protéine. Nous retenons celle du règlement INCO (azote total × 6,25), qui est la valeur à déclarer sur une étiquette — et non le facteur de Jones, propre à chaque aliment et nutritionnellement plus juste, mais hors définition.\n\nSur le millésime en base, les deux valeurs diffèrent pour 711 aliments, jusqu'à 21 % : l'amande grillée compte 22,5 g avec 6,25 contre 18,6 g avec Jones. Pour 99 aliments, l'écart suffit à changer un palier de protéines du Nutri-Score, donc parfois sa lettre.\n\nSi vous comparez à une table Ciqual, vérifiez laquelle des deux lignes vous lisez.",
      PROTEIN_unite: "g",
      SEL: "Sel",
      SEL_unite: "g",
      SODIUM: "Sodium",
      SODIUM_unite: "mg",
    },
    /**
     * Le tableau « articles × constituants » du cockpit.
     *
     * ⚠️ Aucun libellé n'y qualifie un chiffre de « juste » ou « faux » : quand une masse manque, le
     * texte dit **quelle donnée** manque et **où** elle se saisit. Un module de formulation ne corrige pas
     * le référentiel, donc il envoie vers la fiche article au lieu de proposer de le faire à sa place.
     */
    /**
     * La carte « Mode opératoire & contrôles ».
     *
     * ⚠️ Le vocabulaire distingue **l'opération** (« étape ») du **contrôle**, et nomme le moment du
     * second. Deux mots pour deux natures : une cuisson se fait, une température se vérifie.
     *
     * ⚠️ Aucun libellé ne parle de durée totale ni de coût : cette carte est documentaire, et la
     * main-d'œuvre est calculée ailleurs. Un total ici ferait chercher un lien qui n'existe pas.
     */
    /*
     * La traçabilité DÉCLARATIVE d'une opération d'essai — « qu'ai-je employé ? ».
     *
     * ⚠️ **« Rien n'est réservé ni consommé » n'est pas une précaution de style** : cette section
     * ressemble à une consommation de matière, et sans la phrase quelqu'un finirait par croire que son
     * stock a bougé. C'est l'invariant du module, et l'endroit où il serait le plus tentant de le rompre.
     */
    tracabilite: {
      dialogue_titre: "Déclarer un lot employé",
      ddm: "DDM / DLUO",
      ddm_aide: "La date lue sur l'étiquette. Reprise du lot choisi, corrigez-la si l'emballage dit autre chose.",
      provisoire_saisie_libre: "Cette matière est provisoire : elle n'a pas de fiche article, donc aucun lot du stock ne peut la désigner. Saisissez le numéro lu sur l'étiquette.",
      rien_a_declarer: "Choisissez un lot du stock, ou saisissez le numéro lu sur l'étiquette.",
      titre: "Ce que j'ai employé",
      declaratif: "déclaratif — rien n'est réservé ni sorti du stock",
      lot: "Lot du stock",
      numero_libre: "Numéro lu sur l'étiquette",
      numero_libre_aide: "Pour un échantillon fournisseur qui n'est jamais entré en stock.",
      quantite: "Quantité",
      ajouter: "Déclarer ce lot",
      aucun_lot: "aucun lot déclaré",
      aucun_composant: "Ce nœud n'a aucun composant : rien à déclarer.",
      // ⚠️ Un composant retiré de la recette après la manip ne fait pas disparaître ce qu'on a employé
      // ce jour-là — c'est même le cas où la trace compte le plus.
      orphelines: "Déclarés sur des composants retirés depuis",
      hors_stock: "hors stock",
      retirer_aide: "Retirer cette déclaration",
    },
    methode: {
      operations: "Opérations",
      operation: "Opération",
      lancer_operation: "Lancer une opération",
      clore: "Clore",
      relancer: "Relancer",
      supprimer_operation: "Supprimer cette opération",
      supprimer_operation_titre: "Supprimer cette opération ?",
      supprimer_operation_message: "Ses #nb étape(s) d'exécution seront retirées. Les relevés de contrôles déjà saisis sont CONSERVÉS : ils restent dans le journal de leur contrôle.",
      supprimer_operation_message_sans_etape: "Cette opération et ses étapes d'exécution seront retirées. Les relevés de contrôles déjà saisis sont CONSERVÉS.",
      operation_supprimee: "Opération supprimée",
      op_en_cours: "en cours",
      op_terminee: "terminée",
      aucune_operation: "Aucune opération lancée sur ce nœud.",
      n_operations: "#nb opération(s)",
      etape_faite: "Étape faite",
      operation_a_ouvrir: "Dépliez pour charger les étapes.",
      operation_sans_procede: "Définissez d'abord le mode opératoire : une opération sans étape n'offrirait aucun geste.",
      mode_edition: "Définir",
      mode_saisie: "Saisir",
      rien_a_saisir: "Aucun contrôle à relever sur ce nœud.",
      titre: "Mode opératoire & contrôles",
      documentaire: "documentaire",
      mode_operatoire: "Mode opératoire",
      controles_fabrication: "Contrôles en fabrication",
      controles_liberatoires: "Contrôles libératoires",
      par_nature: "Par nature",
      par_nature_aide: "ce qui paraît sur la fiche technique, et sous quelle rubrique",
      nature_non_classes: "Non classés",
      sur_fiche_courte: "sur la fiche",
      sur_fiche_non: "hors fiche",
      nature_nb_controles: "#nb contrôle(s)",
      nature_tous_sur_fiche: "tous sur la fiche",
      nature_aucun_sur_fiche: "aucun sur la fiche",
      nature_partiel_sur_fiche: "#nb sur la fiche",
      sur_fiche_aide:
        "Ce contrôle paraît-il sur la fiche technique remise au client ? La nature ne fait que proposer : un barème de cuisson est physico-chimique et n'a pas à en sortir.",
      produit_fini: "produit fini",
      semi_fini: "semi-fini",
      reprendre_etape: "Reprendre une étape existante",
      /*
       * ⚠️ **La note NOMME le verrou et son endroit.** « Non modifiable » seul ferait chercher un droit
       * manquant ; dire que ces étapes viennent d'une spécification partagée dit pourquoi, et où aller.
       */
      du_modele:
        "#nb étape(s) de la spécification modèle — partagée par plusieurs articles, elle se corrige depuis la fiche article, pas depuis cet essai.",
      replier: "Replier",
      deplier: "Déplier",
      resume: "#etapes étape(s) · #controles contrôle(s)",
      sans_noeud:
        "Aucun article fabriqué dans cet essai : définissez d'abord le produit fini.",
      vide:
        "Aucune étape de procédé ni contrôle pour l'instant. Ajoutez une étape, ou reprenez-en une déjà écrite sur un autre produit.",
      fige_modifiable:
        "Cet essai est figé : son chiffrage et son rapport sont photographiés, mais le mode opératoire reste modifiable.",
    },
    logistique: {
      mis_en_oeuvre: "Mis en œuvre",
      obtenu: "Obtenu",
      rendement: "Rendement",
      rendement_absent: "non renseigné",
      non_pesable: "non pesable",
      densite: "Densité",
      poids_net: "Poids net par unité",
      emballages: "Emballages par unité",
      poids_brut: "Poids brut par unité",
      ecart_fiche: "fiche article :",
      provenance_deduite: "déduite du rendement",
      /*
       * ⚠️ **Deux déductions distinctes, deux libellés.** Le poids net par unité ne se déduit PAS du
       * rendement : il vaut `masse obtenue ÷ nombre d'unités`. Réutiliser « déduite du rendement »
       * l'affichait à trois lignes d'un rendement « non renseigné » — l'écran se contredisait
       * lui-même. Vu le 07/09/2026, signalé à l'écran.
       */
      provenance_deduite_lot: "déduit de la masse obtenue et du nombre d'unités",
      // ⚠️ L'aide du champ REQUIS dit ce qu'il débloque, pas qu'il est obligatoire : c'est la
      // conséquence qui décide de le remplir, pas l'étiquette.
      densite_requise: "Nécessaire : sans la densité, les valeurs pour 100 g ne se calculent pas.",
      poids_net_requis:
        "Nécessaire : sans le poids d'une unité, les valeurs pour 100 g ne se calculent pas.",
      /*
       * ⚠️ **La phrase dit l'IMPOSSIBILITÉ, pas « à vérifier ».** « Poids incohérent » enverrait relire
       * les deux chiffres sans dire lequel est faux ni pourquoi c'est grave. Un emballage de masse
       * négative, ça se comprend en une lecture — et ça nomme la conséquence : le dénominateur des
       * pourcentages de toute la recette.
       */
      pesee_NET_SUPERIEUR_BRUT:
        "Poids net supérieur au poids brut de la fiche : impossible, l'emballage aurait une masse négative. Ce poids sert de base à tous les pourcentages de la recette.",
      pesee_NET_EGAL_BRUT:
        "Poids net et poids brut identiques sur la fiche : possible en vrac, mais souvent un champ recopié dans l'autre.",
      champ_non_lu: "Non employé pour cette unité : la masse vient de l'autre champ.",
      provenance_essai: "enregistrée pour ce projet",
      // ⚠️ La déduction est AFFICHÉE, jamais préremplie : préremplie, elle serait enregistrée, et le
      // jour où l'autre champ change elle garderait l'ancienne valeur. Le « ≈ » dit qu'elle est
      // calculée, la contenance dit à partir de quoi.
      pesee_liee:
        "Liés : modifier l'un recalcule l'autre (#contenance L par unité). Les deux sont enregistrés.",
      pesee_recalculee_densite:
        "Densité calculée depuis le poids net (#contenance L par unité). Enregistrez pour la conserver.",
      pesee_recalculee_poids:
        "Poids net calculé depuis la densité (#contenance L par unité). Enregistrez pour le conserver.",
      pesee_recalculee:
        "Recalculé depuis l'autre champ (#contenance L par unité). Modifiable si vous connaissez une autre valeur.",
      // ⚠️ Elle NOMME la donnée absente — la contenance d'une unité — au lieu d'annoncer que « ça ne se
      // convertit pas » : une impossibilité ne se corrige pas, une donnée manquante se saisit.
      contenance_inconnue:
        "La densité ne se déduit pas d'un poids net ici : il faudrait la contenance d'une unité. 75 cL pour un bocal, par exemple — la fiche ne la porte pas.",
      provenance_fiche: "vient de la fiche article",
      sens_TAUX: "taux saisi",
      sens_QUANTITE_OBTENUE: "quantité obtenue saisie",
      sens_OBSERVE_HISTORIQUE: "observé sur les fabrications passées",
      rendement_a_poser: "Rendement",
      poser: "Enregistrer",
      effacer: "Effacer le rendement",
      effacer_aide:
        "Retire le rendement : la quantité obtenue redevient celle de la recette, et la densité n'est plus déduite.",
      portee:
        "Pour ce projet seulement : la fiche de l'article n'est pas modifiée. Aucune quantité déjà saisie n'est convertie.",
      constat_ENTREE_NON_PESABLE:
        "Un composant au moins n'est pas pesable : ni le rendement ni la densité ne peuvent être calculés.",
      constat_SUPERIEUR_A_CENT:
        "Il sort plus qu'il n'entre. Vérifiez la quantité obtenue, ou déclarez un procédé d'hydratation.",
      constat_TRES_FAIBLE:
        "Rendement inférieur à 50 %. Possible sur une réduction ou un séchage, mais toutes les teneurs s'en trouvent fortement concentrées — à vérifier.",
      constat_DENSITE_IMPLAUSIBLE:
        "Cette densité n'est pas plausible pour un aliment. Un composant manque probablement dans la recette — souvent l'eau.",
    },
    identification: {
      titre: "Identification de l'essai",
      lot: "Lot (projet)",
      lot_absent: "aucun numéro de projet",
      reference: "Référence de l'essai",
      date_fabrication: "Date de fabrication",
      aujourdhui: "Enregistrer la date du jour",
      duree_vie: "Durée de vie",
      jours: "jours",
      mois: "mois",
      annees: "années",
      n_jours: "#nb jour(s)",
      n_mois: "#nb mois",
      n_annees: "#nb an(s)",
      duree_essai: "enregistrée pour ce projet",
      duree_fiche: "vient de la fiche article",
      type: "Type de date limite",
      type_DDM: "DDM",
      type_DLC: "DLC",
      type_AUCUNE: "Aucune",
      format: "Format imprimé",
      date_limite: "Date limite",
      date_limite_absente: "non calculable — voir ci-dessous",
      manque_NUMERO_ABSENT: "Le projet n'a pas de numéro : sans lui, l'étiquette ne désigne rien et un contrôle de marquage ne peut pas être vérifié.",
      manque_DATE_FABRICATION_ABSENTE: "Date de fabrication à renseigner — c'est elle qui donne la date limite.",
      manque_DUREE_VIE_ABSENTE: "Durée de vie non renseignée, ni ici ni sur la fiche de l'article d'origine.",
      manque_FORMAT_ABSENT: "Format d'impression à choisir : c'est lui qui décide de la chaîne comparée au marquage.",
      imprimer: "Imprimer la fiche d'identification",
      mention: "L'étiquette porte la mention « ESSAI — NE PAS COMMERCIALISER » et aucun code-barres : un code scannable désignerait un lot absent de tout stock.",
    },
    ciqual_suggestion: {
      copie_ratee: "La matière est créée, mais ses teneurs Ciqual n'ont pas pu être copiées — ouvre son détail pour les charger.",
      reference_heritee: "Issue de l'aliment Ciqual #code, dont les teneurs n'ont jamais été copiées.",
      charger_heritee: "Charger ses teneurs",
      chercher: "Chercher cet aliment dans Ciqual",
      changer: "Changer l'aliment Ciqual rattaché",
      pose: "Valeurs Ciqual reprises pour ce projet",
    },
    contributions: {
      titre: "Analyse des contributions",
      forme_pavage: "Pavage",
      forme_flux: "Flux",
      gran_semi_fini: "Par semi-fini",
      gran_feuilles: "Par matière",
      gran_famille: "Par famille",
      gran_famille_aide:
        "Un pavé par famille d'article : l'emballage, les fruits, les épices. C'est la seule vue qui rassemble un bocal, sa capsule et son carton — éparpillés ailleurs, ils ne se somment pas de l'œil.",
      sans_famille: "Sans famille",
      gran_semi_fini_aide:
        "Un pavé par enfant direct du produit fini — la structure de fabrication.",
      gran_feuilles_aide:
        "Un pavé par matière, d'où qu'elle vienne. Une matière employée dans deux étapes est regroupée : c'est la seule vue qui dit ce qu'elle pèse en tout.",
      mesure_cout_total: "Coût total",
      mesure_cout_matiere: "Coût matière",
      mesure_cout_mo: "Coût main-d'œuvre",
      part_mo: "main-d'œuvre",
      mesure_masse: "Masse",
      unite_cout: "€ / unité fabriquée",
      unite_masse: "g / 100 g",
      total: "Total analysé :",
      // ⚠️ Le marqueur de paramètre de ce dépôt est `#nb`, jamais `{{nb}}` : la double accolade
      // n'est PAS reconnue par `TranslateUtils`, donc elle s'affichait telle quelle à l'écran —
      // « — {{nb}} composant(s) sans prix ». Vu le 07/09/2026. L'appelant passait bien `{ nb }`.
      total_incomplet: "— #nb composant(s) sans prix, non représentés",
      non_determine: "non déterminé",
      indetermines: "Non chiffré, faute de donnée :",
      vide: "Rien à représenter : aucune valeur de « #mesure » n'est chiffrée sur cette portée.",
    },
    /**
     * La carte « Nutri-Score estimé » et son pas-à-pas.
     *
     * ⚠️ Aucun libellé n'affirme qu'une note est « bonne » ou « mauvaise » : la carte rend un calcul et
     * ses paliers, l'utilisateur décide quoi changer. Et la mention d'estimation n'est jamais optionnelle
     * — le logo officiel engage l'entreprise.
     */
    historique: {
      titre: "Historique des provisoires",
      note: "Tout ce qui a été inventé en formulant, tous projets confondus : les matières provisoires avec leurs valeurs nutritionnelles, et les semi-finis provisoires avec leur recette. Le plus récent d'abord.",
      rechercher: "Chercher une matière ou un semi-fini",
      vide: "Rien d'inventé pour l'instant. Les matières et semi-finis provisoires créés dans un projet apparaîtront ici.",
      matieres: "Matières provisoires",
      semi_finis: "Semi-finis provisoires",
      emplois: "emploi(s)",
      composants: "composant(s)",
      sans_teneur:
        "Aucune valeur nutritionnelle enregistrée : elles s'enregistrent depuis le projet, sur le composant.",
      teneurs_source: "Valeurs relevées dans le projet #projet, le #date.",
      teneurs_source_supprime:
        "Valeurs relevées le #date dans le projet #projet, depuis supprimé — elles restent lisibles ici.",
      projet_sans_numero: "sans numéro",
      origine: "Inventé dans le projet",
      origine_supprime: "Inventé dans un projet depuis supprimé :",
      copier: "Copier la recette",
      copier_titre: "Copier la recette de",
      copier_note_titre: "Copier cette recette dans un projet",
      copier_note:
        "La copie arrive NON RELIÉE, dans les nœuds disponibles du projet choisi : c'est là que vous choisirez ce qui la consomme et en quelle quantité. Le semi-fini d'origine n'est pas touché.",
      copie_faite: "« #nom » a été copié dans #projet, dans ses nœuds disponibles à relier.",
      aucun_projet: "Aucun projet modifiable : un projet figé est une photographie, on n'y ajoute rien.",
    },
    nutriscore: {
      titre: "Nutri-Score estimé",
      manques_titre: "#nb donnée(s) manquante(s) pour estimer",
      manque_categorie:
        "Catégorie réglementaire à choisir ci-dessous. Sans elle, aucun barème ne s'applique.",
      manque_teneur: "« #nom » : teneur inconnue sur le produit fini.",
      // 🔴 **Le manque qui n'existait pas** : une teneur PARTIELLEMENT connue échappait aux deux
      // autres listes — elle a une valeur (donc pas « manquante »), et les composants en portent
      // d'autres (donc pas « sans valeur »). C'est pourtant elle qui refusait la note, sans rien
      // nommer. La phrase porte le GESTE : un zéro explicite débloque, et il aura été écrit.
      manque_teneur_partielle:
        "« #nom » : #part % de la masse sans valeur connue — #composants. Déclarez 0 s'ils n'en contiennent pas.",
      manque_composant: "« #nom » (#part % de la masse) n'a aucune valeur nutritionnelle connue.",
      manque_noeud:
        "On ne sait pas ce que pèse une unité de « #nom » : aucune part ne se calcule. À renseigner dans « Données logistiques » — densité ou poids net, pour ce projet seulement.",
      manque_masse:
        "On ne sait pas ce que pèse « #nom » : sa part dans le produit fini est incalculable.",
      // ⚠️ **Vulgarisée le 10/09/2026.** L'ancienne phrase parlait de « #part points de masse évaporés
      // sans qu'on sache de quel composant » : trois notions techniques d'affilée (un point de masse, une
      // évaporation non attribuée, une fourchette qui en découle) pour dire une chose simple — la recette
      // perd de l'eau à la cuisson, on ne sait pas laquelle, donc on donne une plage. Le mot « fourchette »
      // reste, c'est celui qu'affiche la note juste au-dessus ; ce qui part, c'est le jargon de la cause.
      avert_concentration:
        "La cuisson fait perdre #part % du poids, et on ne sait pas quel ingrédient a séché. La part de fruits et légumes ne peut donc être donnée qu'en fourchette — aucune saisie ne la resserrera.",
      part_calcul:
        "#part % du produit fini : #masse kg de ce composant pour #obtenue kg obtenus. La masse obtenue est le dénominateur — c'est elle que le rendement fait varier.",
      part_calcul_imbrique:
        "#part % du produit fini : #masse kg pour #obtenue kg obtenus dans « #noeud ». Ce nœud pèse lui-même #facteur % du produit fini ; les deux fractions se multiplient.",
      intervalle_calcul:
        "Une fourchette, parce que deux inconnues subsistent. #nonMarquee % de la masse ne sont pas encore tranchés : les marquer resserre l'intervalle, et la liste ci-dessous les classe du plus lourd au plus léger. Les #concentration points restants viennent de l'eau évaporée, que le module n'attribue à aucun ingrédient — cette part-là ne se referme pas, quelle que soit la saisie.",
      intervalle_calcul_a_trancher:
        "Une fourchette, parce que #nonMarquee % de la masse ne sont pas encore tranchés. Les marquer la resserre, et la liste ci-dessous les classe du plus lourd au plus léger : commencer par le plus lourd rapporte le plus.",
      intervalle_calcul_concentration:
        "Tout est tranché : la fourchette qui reste vient des #concentration points de masse évaporés, que le module n'attribue à aucun ingrédient. Au pire toute la perte venait des fruits et légumes, au mieux aucune. Aucune saisie ne la refermera.",
      intervalle_calcul_ferme:
        "Tout est tranché et toute la masse est connue : la part est une valeur, plus un intervalle.",
      notes_methode: "Sur quoi ce calcul repose",
      critere_SUGAR: "Sucres",
      critere_SATURATED: "Acides gras saturés",
      mention_estime: "Nutri-Score estimé",
      mention_fourchette: "estimé entre #min et #max",
      mention_non_estimable: "non estimable",
      voir_detail: "Détail du calcul",
      detail_titre: "Détail du calcul",
      etape: "Étape",

      etape_categorie: "La catégorie retenue",
      categorie_effet:
        "elle décide du barème entier : les paliers, la règle des protéines, et jusqu'à la base de calcul.",
      categorie_apercu:
        "Les six catégories des textes réglementaires. Chacune décide du barème, pas seulement des seuils :",
      categorie_GENERAL_champ:
        "tout aliment qui n'entre dans aucune des cinq exceptions ci-dessous — soupes et sauces comprises. « Matières grasses ajoutées » désigne les graisses vendues comme produit fini, pas celles employées comme ingrédient.",
      categorie_FROMAGE_champ:
        "les fromages, fondus compris. La frontière avec les produits laitiers frais est fixée par le texte : sur un produit limite, c'est lui qui tranche.",
      categorie_VIANDE_ROUGE_champ:
        "bœuf, veau, porc, agneau, mouton et leurs préparations. La volaille n'en fait pas partie.",
      categorie_MATIERES_GRASSES_champ:
        "huiles et graisses ajoutées, beurres et margarines, fruits à coque et graines.",
      categorie_BOISSON_champ:
        "toute boisson — eaux aromatisées, jus, nectars, sodas, boissons lactées. Une soupe n'est pas une boisson.",
      categorie_EAU_champ:
        "eaux minérales, de table ou de source, SANS aucun autre ingrédient. Une eau aromatisée relève de la catégorie boisson et ne peut pas être notée A.",
      categorie_titre: "Catégorie réglementaire",
      categorie_aide:
        "Elle vient de la fiche article. Pour ce projet seulement : la fiche n'est pas modifiée.",
      categorie_GENERAL: "Général",
      categorie_GENERAL_effet: "Le cas courant, hors des cinq exceptions.",
      categorie_FROMAGE: "Fromage",
      categorie_FROMAGE_effet: "Les protéines comptent toujours, quel que soit le total négatif.",
      categorie_VIANDE_ROUGE: "Viande rouge",
      categorie_VIANDE_ROUGE_effet: "Les protéines sont plafonnées à 2 points.",
      categorie_MATIERES_GRASSES: "Matières grasses, fruits à coque, graines",
      categorie_MATIERES_GRASSES_effet:
        "Les acides gras saturés comptent en ratio aux lipides, et l'énergie est celle des saturés seuls.",
      categorie_BOISSON: "Boisson",
      categorie_BOISSON_effet:
        "Barème par 100 ml : les teneurs sont converties avec la densité du produit fini. Les édulcorants intenses comptent, et le A est réservé à l'eau — une boisson ne peut pas faire mieux que B.",
      categorie_EAU: "Eau",
      categorie_EAU_effet:
        "Notée A, sans calcul. Sauf si le produit comporte plusieurs ingrédients ou porte du sucre, du sel ou de l'énergie : l'estimation refuse alors, plutôt que de rendre un A qu'aucun chiffre ne soutient.",
      /*
       * ⚠️ **« par défaut » n'est PAS « posée pour ce projet ».** `GENERAL` s'applique parce que
       * personne n'a tranché, pas parce que quelqu'un l'a choisi : mesuré, 29 des 30 essais qui
       * déclarent une catégorie sont GENERAL, et un seul est FROMAGE. Le trentième produit doit rester
       * corrigeable — donc la phrase dit ce qu'elle sait, et pas plus.
       */
      provenance_defaut: "catégorie par défaut — à vérifier sur un fromage, une boisson ou une matière grasse",
      provenance_essai: "enregistrée pour ce projet",
      provenance_fiche: "vient de la fiche article",
      revenir_fiche: "Revenir à celle de la fiche article : #categorie",
      /*
       * ⚠️ **L'action tient en un mot, la conséquence passe en infobulle.** La phrase entière était
       * soulignée comme un lien : plus longue que le titre du bloc, elle donnait à une action
       * secondaire le poids visuel le plus fort de la carte, et mélangeait le geste (« Retirer ») avec
       * son effet (« l'estimation refusera de noter »).
       */
      revenir_fiche_absente: "Retirer",
      revenir_fiche_absente_aide:
        "La fiche article ne déclare aucune catégorie : sans elle, l'estimation refusera de noter.",

      points_negatifs: "Points négatifs",
      points_positifs: "Points positifs",
      total_negatif: "Total négatif N",
      total_positif: "Total positif P",
      palier: "palier",
      critere_FVL: "Fruits, légumes, légumineuses",
      critere_RATIO_AGS: "AG saturés / lipides",

      etape_regle: "La règle appliquée",
      regle_RETENUES:
        "Le total négatif reste sous le seuil de la catégorie (#n) : les protéines comptent.",
      regle_ECARTEES_TOTAL_NEGATIF:
        "Le total négatif atteint #n : les protéines ne comptent pas. Sans cette règle, un produit très gras et très salé remonterait sa note grâce à ses protéines.",
      regle_RETENUES_FROMAGE:
        "Catégorie fromage : les protéines comptent quel que soit le total négatif (#n), leur matière grasse étant indissociable de leur apport.",
      regle_PLAFONNEES_VIANDE_ROUGE:
        "Catégorie viande rouge : les protéines comptent, plafonnées à 2 points.",
      formule: "score",
      au_mieux: "au mieux",

      etape_echelle: "Du score à la lettre",
      bascule:
        "Un point de score change la lettre : la note ne varie pas continûment, elle bascule.",

      avert_base_fvl:
        "Le pourcentage porte sur la masse PRÉSENTE dans 100 g de produit fini — la même base que le tableau nutritionnel. Sur la masse mise en œuvre, l'eau évaporée le gonflerait : de quoi gagner une lettre.",
      /*
       * ⚠️ **« Pas marquée » ne dit rien à qui n'a pas écrit le code** — demandé à l'écran le
       * 12/09/2026. Le message nommait un ÉTAT interne (une masse « non marquée ») sans nommer la
       * question posée, l'endroit où l'on y répond, ni ce qu'il en coûte de ne pas répondre.
       *
       * ⚠️ **Le geste est nommé, et il est juste en dessous** : la liste « Fruits, légumes et
       * légumineuses », où chaque composant se déclare « Oui » ou « Non ». Un avertissement qui
       * n'indique pas sa porte se lit comme une fatalité.
       */
      avert_non_marque:
        "#part % de la masse attend une réponse dans « Fruits, légumes et légumineuses » ci-dessous : chaque composant compte, ou ne compte pas. Tant qu'il en reste, la note va de la lettre obtenue si rien de cette masse ne compte à celle obtenue si tout compte.",

      refus_titre: "Aucune note ne peut être calculée",
      motif_CATEGORIE_ABSENTE:
        "La catégorie réglementaire n'est pas déclarée, donc aucun barème ne s'applique. En choisir un par défaut noterait un fromage ou une huile avec les paliers d'un produit courant.",
      motif_TENEUR_ABSENTE:
        "Une des valeurs du barème n'est pas connue. La compter pour zéro fabriquerait une note flatteuse et crédible.",
      motif_MASSE_NON_DETERMINEE:
        "Une part de la masse n'a pas de valeurs connues. Les chiffres sont donc des minimums, et la note serait meilleure que la vraie.",
      motif_DENSITE_BOISSON_ABSENTE:
        "Le barème des boissons se lit par 100 ml, et la densité de ce produit n'est pas connue. Sans elle, la note serait fausse d'un facteur égal à la densité. À renseigner dans « Données logistiques », ou laissez le rendement la déduire.",
      motif_EDULCORANTS_NON_DECLARES:
        "La présence d'édulcorants intenses n'est pas déclarée. Elle vaut 4 points négatifs. Aucun référentiel d'additifs ne permet de la deviner : supposer qu'il n'y en a pas rendrait la boisson meilleure qu'elle n'est.",
      motif_EAU_AROMATISEE_OU_COMPOSEE:
        "La catégorie « eau » ne vaut que pour une eau sans aucun autre ingrédient. Ce produit en comporte d'autres, ou porte du sucre, du sel ou de l'énergie : il relève du barème des boissons, que ce module ne calcule pas encore.",

      edulcorants_titre: "Édulcorants intenses",
      edulcorants_aide:
        "Aspartame, acésulfame K, sucralose, stévia, saccharine… Leur PRÉSENCE — pas leur quantité — ajoute 4 points négatifs, soit une à deux lettres. Le module n'a aucun référentiel d'additifs : la réponse se lit sur la liste d'ingrédients du produit.",
      edulcorants_oui: "Oui",
      edulcorants_non: "Non",
      edulcorants_oui_effet: "4 points négatifs s'ajoutent au total.",
      edulcorants_non_effet: "Aucun point supplémentaire. C'est une réponse, pas une absence de réponse.",
      critere_EDULCORANTS: "Édulcorants intenses",
      fvl_aide_titre: "Ce qui compte dans la part, d'après les textes :",
      fvl_aide_inclus:
        "INCLUS — fruits et légumes sous toutes leurs formes (frais, surgelés, en conserve, séchés) et légumineuses (pois, lentilles, haricots, pois chiches, fèves). Dans la seule catégorie « matières grasses, fruits à coque, graines », s'y ajoutent les huiles tirées de la partie comestible entière : olive, avocat, soja.",
      fvl_aide_exclus:
        "EXCLUS — pomme de terre et autres tubercules amylacés ; farines de légumineuses et de maïs ; quinoa ; épices ; fruits à coque et graines ; toutes les huiles et graisses. Les fruits à coque, les graines et les huiles de colza et de noix comptaient avant 2023 : l'algorithme actualisé les a retirés.",
      fvl_aide_nuances:
        "DEUX PIÈGES — un fruit ou légume séché ou concentré compte pour DEUX FOIS son poids, ce que le module ne modélise pas encore : sa part est sous-estimée. Et un composant seulement EN PARTIE composé de fruits et légumes (une sauce achetée à 70 %) se marque « oui » AVEC son pourcentage — le QUID de son emballage.",
      fvl_aide_source:
        "Le groupe Ciqual ne peut donc que PROPOSER : sa taxonomie et la liste réglementaire ne coïncident pas.",
      fvl_titre: "Fruits, légumes et légumineuses",
      fvl_aide:
        "La liste réglementaire exclut : pomme de terre et tubercules amylacés, farines de légumineuses, fruits à coque, huiles de colza et de noix. Le classement Ciqual ne peut donc que proposer — à vous de trancher.",
      fvl_oui: "Oui",
      fvl_non: "Non",
      fvl_inconnu: "À déterminer",
      fvl_question: "Chaque composant compte-t-il dans cette part ?",
      fvl_part_preciser:
        "Préciser le pourcentage, pour une préparation achetée qui n'est fruit ou légume qu'en partie. C'est le QUID de son emballage. Sans précision, le composant compte pour toute sa masse.",
      fvl_part_label: "dont",
      fvl_part_effet: "#part % de masse, dont #retenu % retenus",
      fvl_oui_effet:
        "Sa masse compte dans le pourcentage de fruits, légumes et légumineuses. Ce pourcentage rapporte des points positifs.",
      fvl_non_effet:
        "Sa masse n'y compte pas. C'est une réponse, pas un manque. La note se resserre autant qu'avec un « oui ».",
      fvl_inconnu_effet:
        "Personne n'a tranché : sa masse élargit la fourchette de la note. Entre le cas où elle compte et celui où elle ne compte pas.",
      fvl_deja_marques: "#nb composant(s) déjà tranché(s)",
      fvl_propose_ciqual: "proposé d'après le groupe Ciqual",
      invite_marquage:
        "#part % de la masse reste à trancher, sur #nb composant(s). Marquer les plus lourds d'abord resserre la note.",
      invite_masse_incomplete:
        "Un composant n'est pas pesable : la borne haute reste à 100 %. Faute de savoir ce qu'il pèse.",

      pied_carte:
        "Estimation — pas une valeur faisant autorité. À faire valider par un expert avant tout usage sur un document, une fiche technique ou un emballage. Elle utilise ce qui est renseigné dans le système : table Ciqual, fiches fournisseurs, valeurs enregistrées pour ce projet. Ni analyse du produit fini, ni effet de la cuisson ou de l'évaporation.",
      pied_estimation:
        "Estimation, pas déclaration : ce calcul ne fait pas autorité et doit être validé par un expert. Apposer le logo Nutri-Score suppose en outre l'enregistrement auprès de Santé publique France. Barèmes de l'algorithme 2023, officialisé en France par l'arrêté du 16 mars 2025 — transition jusqu'en mars 2027, pendant laquelle un produit déjà commercialisé peut encore porter une note calculée avec l'algorithme 2017, que ce module ne produit pas.",
    },
    /**
     * L'aperçu d'une fiche technique — il n'écrit rien, et le dit.
     */
    apercu: {
      titre: "Aperçu de la fiche technique",
      /*
       * ⚠️ **« Télécharger » plutôt que « Enregistrer »** : le second promettrait une écriture côté
       * serveur, ce que l'aperçu ne fait justement pas.
       */
      telecharger: "Télécharger le PDF",
      imprimer: "Imprimer",
      rappel:
        "Cet aperçu n'enregistre rien et n'engage personne. Pour un document opposable, utilisez « Sortir la fiche technique ».",
      rappel_bloquants:
        "Des points bloquants subsistent : la fiche ne pourra pas être sortie tant qu'ils ne sont pas levés.",
    },

    /*
     * Les trois lectures d'une même fiche (A31). ⚠️ **Le libellé n'est jamais la clef** : le popup, la
     * liste des fiches sorties et le bandeau du visualiseur composent `recette.profil.<CLEF>` — un titre
     * reformulé casserait la correspondance sans qu'aucune erreur ne paraisse.
     */
    profil: {
      COMPLET: "Complet",
      CLIENT: "Client",
      AUDIT: "Audit",
      CLIENT_effet: "Ce qui part chez un client : ni coût, ni main-d'œuvre, ni prix, ni limites internes.",
      COMPLET_effet: "Tout ce que le module sait — pour un usage interne.",
      AUDIT_effet: "Comme Complet, plus les sources et versions des données employées.",
    },

    /*
     * ═══ LE POPUP « PARTIES À IMPRIMER » (10/09/2026) ═══
     *
     * ⚠️ **Les clefs des treize parties sont celles de `SectionFicheTechnique`** (contrat `api-pdf`), pas
     * des libellés : elles nomment les cases ET commandent le gabarit. Renommer une clef ici ferait
     * disparaître une section du document, en silence.
     *
     * ⚠️ **`verrou_a31` dit POURQUOI et donne la sortie.** Trois cases sont désactivées sur un profil
     * client parce que le serveur ne compose pas ces données : une case grisée sans phrase fait cliquer,
     * puis chercher. Et elles restent VISIBLES — une case absente fait chercher si on a mal cherché.
     *
     * ⚠️ **La note d'en-tête n'a pas de case à cocher** : elle s'imprime si et seulement si elle porte du
     * texte. Son libellé dit **où ça s'imprime** — sans ce mot, on ne sait pas si c'est un commentaire
     * interne ou du texte qui part chez le client, et on ne l'écrit pas pareil.
     */
    sections: {
      profil_label: "Destinataire",
      voir_apercu: "Voir l'aperçu",
      verrou_a31: "Ces parties ne sont pas composées pour une fiche client. Pour les obtenir, sortez une fiche au profil « Complet ».",
      verrou_a31_court: "Indisponible sur une fiche client",
      note_label: "Note imprimée en tête de la fiche technique",
      note_exemple: "Échantillon de dégustation, lot pilote, ne pas commercialiser…",
      note_hint: "Laissez vide pour n'imprimer aucune note. Elle est conservée pour les prochains aperçus.",
      note_non_gardee: "La note n'a pas pu être enregistrée : elle s'imprimera sur cette fiche, mais ne sera pas préremplie la prochaine fois.",
      INGREDIENTS: "Ingrédients",
      ALLERGENES: "Allergènes",
      DETAIL_INGREDIENTS: "Détail des ingrédients",
      NUTRITION: "Valeurs nutritionnelles",
      NUTRISCORE: "Nutri-Score",
      NUTRISCORE_DETAIL: "Détail du calcul Nutri-Score",
      CONSERVATION: "Conservation",
      CONTROLES: "Contrôles",
      releves_de: "relevés de",
      aucun_releve: "aucun relevé",
      operation_close: "opération du #date (close)",
      operation_en_cours: "opération du #date (en cours)",
      COUT: "Coût de revient",
      MAIN_OEUVRE: "Main-d'œuvre",
      CALCUL_PRIX: "Calcul de prix",
      MARCHE_ANNUEL: "Marché annuel",
      LIMITES: "Limites",
      CONSTATS: "Constats",
      SOURCES: "Sources",
    },

    /*
     * ═══ LES FICHES DÉJÀ SORTIES (10/09/2026) ═══
     *
     * 🔴 Aucun écran ne les listait : une fiche envoyée à un client était **prouvable et non
     * ressortable**. `aucune` est écrite plutôt que masquée — c'est la phrase qui apprend que cette liste
     * existe, sur un projet qui n'a encore rien sorti.
     *
     * ⚠️ **`parties_partielles` nomme ce qui MANQUE**, pas ce qui est inclus : « 10 parties sur 13 —
     * sans : Coût de revient » se vérifie d'un coup d'œil, là où la liste des dix dirait « à peu près
     * tout » en trois lignes.
     *
     * ⚠️ **`rappel_fige` remplace la phrase de l'aperçu dans le MÊME visualiseur.** Un aperçu n'engage
     * rien ; un document figé est ce qui est parti. Garder « cet aperçu n'enregistre rien » sur la copie
     * d'une fiche opposable ferait douter de ce qu'on regarde, au moment où l'on vérifie un envoi.
     */
    fiches_sorties: {
      titre: "Fiches sorties",
      titre_dialogue: "Toutes les fiches sorties",
      titre_visualiseur: "Fiche technique sortie",
      aucune: "Aucune fiche n'est encore sortie de ce projet.",
      rang: "n° #rang",
      voir_autres: "voir les #nb autres →",
      ouvrir: "Rouvrir le PDF de cette fiche",
      depuis_essai: "essai #essai",
      essai_inconnu: "essai retiré du projet",
      parties_toutes: "Toutes les parties (#nb)",
      parties_partielles: "#nb parties sur #total — sans : #retirees",
      rappel_fige: "Fiche n° #rang sortie le #date, profil #profil. Ce document est figé : c'est la copie de ce qui est parti.",
    },

    nutrition_tableau: {
      titre: "Valeurs nutritionnelles par composant",
      composant: "Composant",
      agregat: "Agrégat — #nom",
      aucun: "Aucun composant : rien à agréger.",
      replier: "Tout replier",
      deplier: "Tout déployer",
      legende_cellule:
        "Chaque cellule : ce que le composant apporte aux 100 g du produit fini. Survolez un chiffre pour la valeur exacte, cliquez une ligne pour les teneurs de la matière.",
      poser_pesee:
        "Renseigner le poids",
      pesee_introuvable:
        "Cette étape est introuvable dans l'arbre des coûts. Ouvrez-la depuis la carte « Arbre des coûts ».",
      repli:
        "On ne sait pas ce que pèse « #produitFini ». Les valeurs ci-dessous comptent pour « #noeud », pas pour le produit fini.",
      legende_cellule_repli:
        "Chaque cellule : ce que le composant apporte aux 100 g de « #noeud » — pas du produit fini. Survolez un chiffre pour la valeur exacte.",
      denominateur_DENSITE_ABSENTE:
        "Il manque la densité de « #nom ». Sans elle, pas de valeurs pour 100 g. À saisir sur sa ligne — pour ce projet seulement.",
      denominateur_POIDS_NET_ABSENT:
        "Il manque le poids d'une unité de « #nom ». Sans lui, pas de valeurs pour 100 g. À saisir sur sa ligne — pour ce projet seulement.",
      denominateur_UNITE_INCONNUE:
        "L'unité de « #nom » ne permet pas de le peser : pas de valeurs pour 100 g. Corrigez son unité de gestion.",
      denominateur_NATURE_NON_PESABLE:
        "« #nom » se compte dans une unité qui ne se pèse pas : les valeurs pour 100 g n'ont pas d'objet ici.",
      denominateur_null: "On ne sait pas ce que pèse « #nom » : pas de valeurs pour 100 g.",
      densite_absente: "densité manquante",
      indicatif:
        "Valeurs indicatives : la cuisson, l'évaporation et l'égouttage modifient les teneurs. Seule une analyse laboratoire fait foi.",
      bloquantes:
        "Total impossible : on ne sait pas ce que pèsent #nb composant(s) qui ont des valeurs. Les compter pour rien fausserait le total sans le dire.",
      colonne_vide: "#nom — aucun composant ne le renseigne",
      /*
       * ⚠️ **Une seule clef pour la base, réutilisée à chaque endroit qui l'affiche.** Le tableau l'écrivait
       * en dur dans son template (`/100 g`), ce qui la mettait hors du fichier de traductions ET hors de
       * portée d'une correction globale — c'est précisément ce qui a permis à « 100 g/ml » de survivre
       * ailleurs. Ciqual ne rend que du /100 g et l'agrégation divise une masse par une masse : la base est
       * la même partout, elle doit donc n'avoir qu'un libellé.
       */
      par_100g: "/100 g",
      // ⚠️ Nomme le CONSTITUANT : « aucune valeur connue » était faux sur des composants qui ont des
      // teneurs mais à qui il manque un seul constituant — le manque devient alors une tâche.
      franchise_constituant:
        "#part % du poids n'a pas de valeur connue pour « #constituant ». Le chiffre ci-dessous est donc un minimum.",
      // ⚠️ La base des QUANTITÉS, nommée à côté de celle des cellules : la colonne comptait pour le
      // LOT pendant que tout le reste du tableau était ramené au produit fini — le seul nombre qui
      // sert à vérifier les autres était le seul à ne pas partager leur base.
      legende_quantites:
        "Les quantités sous chaque nom sont celles engagées pour UNE unité de « #noeud ». Le pourcentage est la part de ce composant dans ce qui est mis en œuvre sous le même nœud : la colonne somme à 100 %.",
      legende_quantites_lot:
        "Les quantités sous chaque nom comptent pour le LOT : la quantité obtenue de « #noeud » manque pour les ramener à l'unité.",
      // ⚠️ **UNE phrase, un nombre CUMULÉ, et les composants NOMMÉS.** Le serveur composait cette
      // phrase à chaque niveau de la cascade et la récursion les concatène : la carte affichait
      // « 3.8 % du poids… » puis « 14.3 % du poids… », mot pour mot, chacun juste à son niveau et
      // faux comme description du produit fini. Vu à l'écran le 10/09/2026 : « 3 % ou 14 % ? ».
      franchise:
        "#part % du poids n'a pas de valeurs connues. Les chiffres ci-dessous ne les comptent pas : ce sont des minimums.",
      // Séparée : la liste peut être vide quand l'inconnue vient de fiches partiellement remplies.
      franchise_composants: "Composants concernés : #noms.",
      mention:
        "Mention du référentiel, reprise telle quelle. « traces » n'est pas zéro et « < 0,1 » n'est pas 0,1 : rien n'en est déduit.",
      motif_DENSITE_ABSENTE:
        "Il manque la densité de « #nom ». À saisir sur sa ligne — pour ce projet seulement.",
      motif_POIDS_NET_ABSENT:
        "Il manque le poids d'une unité de « #nom ». À saisir sur sa ligne — pour ce projet seulement.",
      motif_UNITE_INCONNUE: "L'unité de « #nom » ne permet pas de le peser.",
      qualif_NON_DETERMINE: "minimum",
      qualif_TRACES: "traces",
      qualif_INFERIEUR_A: "borne",
    },
    details: {
      image_titre: "Illustration",
      image_aide: "Une image pour ce composant, enregistrée pour ce projet. Elle paraît sur la fiche technique.",
      image_essai: "enregistrée pour ce projet",
      image_fiche: "vient de la fiche article",
      image_retirer: "Retirer l’image",
      allergenes_titre: "Allergènes",
      allergenes_aide: "Ajoutés pour ce projet — ceux de la fiche article restent, ils ne se retirent pas d'ici.",
      allergenes_contient: "Contient",
      allergenes_traces: "Peut contenir (traces)",
      allergenes_fiche_fait_foi: "aucun ajout — la fiche fait foi",
      allergenes_aucune_trace: "aucune trace déclarée",
      allergenes_ajouter: "Ajouter un allergène",
      allergenes_tous_poses: "tous déjà enregistrés",
      // ⚠️ Distinct de « tous déjà posés » : un référentiel vide n'est pas une liste épuisée.
      // Dire « tous déjà posés » sur un composant qui n'en porte aucun affirme l'inverse de la vérité,
      // et envoie chercher le défaut sur le composant au lieu du paramétrage. Vécu le 07/09/2026.
      allergenes_referentiel_vide: "aucun allergène au référentiel — à créer dans Paramétrage activité",
      allergenes_portee: "Pour ce projet seulement : la fiche de l'article n'est pas modifiée.",
      // ⚠️ La fermeture d'un dialogue qui retient des saisies. Le COMPTE est dans la question : un
      // « êtes-vous sûr ? » générique n'aide pas à décider combien on s'apprête à perdre.
      fermer_titre: "Des modifications ne sont pas enregistrées",
      fermer_message:
        "#nb modification(s) en attente sur ce composant. Fermer maintenant les perdrait.",
      fermer_enregistrer: "Enregistrer et fermer",
      /*
       * ⚠️ **La sortie qui ne peut PAS échouer.** « Enregistrer et fermer » conditionnait la seule
       * issue à une écriture que le serveur refuse sur un état incomplet — la fenêtre devenait une
       * prison (signalé le 08/09/2026). Le message annonce « Fermer maintenant les perdrait » : le
       * bouton doit dire la même chose.
       */
      fermer_sans_enregistrer: "Fermer sans enregistrer",
      // Les résumés de section : ils nomment ce qui EST posé, jamais ce qui manque.
      resume_allergenes: "#nb allergène(s)",
      resume_sans_objet: "sans valeurs nutritionnelles",
      resume_teneurs: "#nb teneur(s)",
      resume_aucune_teneur: "aucune teneur",
      sans_source_titre: "Rien à afficher pour ce nœud",
      sans_source:
        "Ce composant n'est relié à rien : ni article, ni matière provisoire. Il n'a donc ni prix, ni valeurs nutritionnelles, ni composition. Reliez-le depuis le graphe.",
      quantite_ligne: "Quantité consommée",
      quantite_pour_lot: "pour un lot de",
      perte_taux: "Perte",
      perte_sens: "Cette quantité est…",
      perte_sens_utilise: "ce qui entre dans le produit",
      perte_sens_engage: "ce qu'on engage",
      perte_aide_titre: "À quoi se rapporte ce pourcentage ?",
      // ⚠️ Nomme les DEUX grandeurs et leur consommateur : c'est ce qui rend le choix décidable sans
      // connaître le modèle de données. La phrase abstraite ne suffisait pas — d'où l'exemple chiffré,
      // qui montre le même ingrédient écrit dans les deux sens et ce que chacun implique.
      perte_aide:
        "La perte est la part de l'ingrédient qui n'arrive pas dans le produit : épluchures, parage, casse. Retirée AVANT le mélange, elle n'apporte ni matière ni valeurs nutritionnelles. Le bouton à côté dit à laquelle des deux quantités votre saisie se rapporte.",
      // ⚠️ La confusion à éviter, et elle est naturelle : « ça perd du poids à la cuisson » ressemble à une
      // perte. Ce n'en est pas une — rien n'est retiré, c'est le lot entier qui se concentre. Saisie ici,
      // elle serait comptée deux fois (une fois sur l'ingrédient, une fois par le rendement).
      perte_aide_concentration:
        "Ne pas confondre avec l'évaporation à la cuisson : là, rien n'est retiré — c'est le lot entier qui perd de l'eau, donc qui se concentre. Cela se déclare sur l'étape, par sa quantité obtenue, jamais ligne par ligne.",
      // ⚠️ **Le MÊME nombre dans les deux branches** : c'est ce qui rend la différence visible. Deux
      // exemples à valeurs différentes (9 ici, 10 là) laissaient croire qu'on décrivait la même recette
      // autrement — alors que le sens change la recette, et c'est tout l'objet du choix.
      // ⚠️ L'impact est chiffré POUR CHAQUE cas, coût et nutrition séparés : c'est la seule conséquence
      // réelle du réglage, et elle était annoncée en fin de texte au lieu d'être montrée.
      perte_aide_exemple:
        "Exemple : vous écrivez 10 kg de pommes et 10 % de perte.\n\n« ce qui entre dans le produit »\n→ 10 kg finissent dans le produit, il faut en mettre 11,1 en œuvre.\n→ le coût porte sur 11,1 kg · les valeurs nutritionnelles et les pourcentages sur 10 kg.\n\n« ce qu'on engage »\n→ 10 kg sont mis en œuvre, il n'en entre que 9.\n→ le coût porte sur 10 kg · les valeurs nutritionnelles et les pourcentages sur 9 kg.\n\nLe même nombre décrit donc deux recettes différentes : c'est ce choix, et lui seul, qui les distingue.",
      perte_engage: "mis en œuvre",
      perte_utilise: "dans le produit",
      // ⚠️ Dit POURQUOI le champ n'est pas là : un bloc qui disparaît sans motif se lit comme un bug.
      perte_multi:
        "Ce composant est employé dans #nb étapes, et la perte appartient à un emploi : des pommes pelées ici, entières ailleurs. Ouvrez-le depuis sa ligne dans le tableau des coûts — l'étape y est désignée, et le réglage apparaît.",
      titre: "Détails",
      voir: "Voir le détail : coût, main-d'œuvre, valeurs nutritionnelles",
      cout: "Coût de revient",
      part_mo: "dont main-d'œuvre",
      fabrication: "Fabrication",
      nature: "Nature",
      /*
       * ⚠️ **« Valeur unitaire », le MÊME mot que la colonne du tableau** (`recette.arbre.col_valeur_unitaire`).
       * Le popup disait « Prix » et le tableau « Valeur unitaire » pour le même nombre : deux mots pour une
       * grandeur font chercher la différence, et sur un écran de chiffrage cette hésitation coûte plus
       * qu'une reformulation. La règle est celle du module — un concept, un mot, partout.
       */
      /* Le dénominateur est NOMMÉ, comme dans l'en-tête du tableau : la valeur affichée porte déjà « €/Kilo »,
       * le libellé dit de quelle unité il s'agit. */
      prix_ug: "Euros par unité de gestion",
      prix_par: "Euros par #unite",
      famille_technique_aide:
        "Elle décide quelles lignes de production savent fabriquer ce produit. Enregistrée ici, elle ne modifie pas la fiche article.",
      famille_technique_matiere_aide:
        "Utile le jour où cette matière sera fabriquée : une matière achetée ne passe sur aucune ligne",
      prix_hint_article: "Ce que coûte 1 #unite à l'achat, enregistré pour ce projet — vide : le tarif du fournisseur s'applique",
      prix_hint_provisoire: "Ce que coûte 1 #unite à l'achat — une matière provisoire n'a pas de tarif, c'est le seul prix qui existe",
      /*
       * ⚠️ **La seconde moitié du piège du facteur 25.** Le tarif d'achat a été divisé par un coefficient
       * (« 1 sac = 25 kg ») ; `uniteGestionReference` dit contre quelle unité ce coefficient a été saisi.
       * Si l'article a changé d'unité depuis, la division reste juste et le résultat plausible — mais le
       * quotient ne dit plus ce qu'il prétend. Cas réel de la base : un coefficient 1 000 figé en « dam »
       * sur un article passé en « KG », qui affiche un « €/kg » qui n'en est pas un.
       *
       * ⚠️ **Le message NOMME l'ancienne unité** : « à vérifier » sans dire par rapport à quoi n'indique
       * ni quoi relire ni pourquoi. Et il envoie au tarif d'achat, qui est le seul endroit où l'on
       * corrige — pas dans l'essai, qui ne fait que lire.
       *
       * ⚠️ **Il n'annonce AUCUNE conversion**, et il ne faut jamais en promettre une : il n'existe pas de
       * facteur entre natures d'unités. Le module signale le doute, il ne le lève pas.
       */
      coefficient_autre_unite:
        "Coefficient d'unité saisi pour « #unite » : ce prix n'est peut-être pas dans la bonne unité. À relire sur le tarif d'achat.",
      consomme: "Consommé ici",
      nutrition: "Valeurs nutritionnelles",
      /*
       * ⚠️ **« pour 100 g », pas « pour 100 g/ml » — et ce n'était pas qu'imprécis, c'était faux.**
       *
       * Trois raisons convergentes :
       * 1. **Ciqual ne donne que du /100 g.** Vérifié sur le millésime 2025 : les 74 constituants portent
       *    leur base dans leur propre libellé (« Sodium (mg/100 g) »), 74 en /100 g, zéro en /100 ml.
       * 2. **L'agrégation divise une masse par une masse** (`masse.utils`) : son résultat est par 100 g du
       *    nœud, quelle que soit l'unité dans laquelle il se compte.
       * 3. Une déclaration réglementaire **par 100 ml** est permise pour un liquide (INCO), mais elle
       *    exigerait la densité du PRODUIT FINI pour convertir sa masse en volume — rien ne le fait
       *    aujourd'hui. Écrire « g/ml » laissait croire que le chiffre valait pour les deux, donc qu'on
       *    pouvait le recopier tel quel sur l'étiquette d'une boisson.
       */
      pour_100: "pour 100 g, agrégées depuis les composants",
      pour_100_saisie: "pour 100 g, saisies à la main",
      pour_100_ciqual: "pour 100 g, reprises de Ciqual",
      // « hors 10,2 » ne disait ni hors QUOI ni que c'était un pourcentage : illisible.
      part_inconnue: "non déterminé",
      /*
       * ⚠️ **La phrase NOMME son nœud.** Identique d'un nœud à l'autre, elle donnait « 7,4 % » sur un
       * semi-fini et « 52,2 % » sur le produit fini, au mot près : deux chiffres qui se contredisent à
       * l'écran se lisent comme une incohérence de données, pas d'affichage.
       */
      part_inconnue_globale:
        "de la masse de « #nom » n'a pas de valeurs nutritionnelles connues — non comptés, ces chiffres sont donc des minimums",
      part_inconnue_aide:
        "#part % de la masse de « #nom » n'a pas de valeur connue pour ce constituant : le chiffre affiché est donc un minimum. Le compléter demande la fiche technique ou une analyse des composants concernés.",
      ce_noeud: "ce nœud",
      saisir: "Saisir les valeurs",
      importer_ciqual: "Importer depuis Ciqual",
      retirer_surcharge: "Revenir à l'agrégation",
      enregistrer: "Enregistrer",
      /*
       * ⚠️ **La garantie d'abord.** Le texte disait la portée dans la recette et jamais que la fiche
       * article restait intacte — alors que « vient de la fiche article » est écrit juste au-dessus de
       * champs modifiables. Vérifié dans le code : la saisie écrit une surcharge d'essai, la fiche n'est
       * jamais touchée. Ce qui est vrai et rassurant doit être lu avant le geste, pas déduit après.
       */
      saisie_aide:
        "Un champ vide n'est pas enregistré : la valeur reste inconnue, elle ne devient pas zéro. Un zéro tapé, lui, est une mesure. Ces valeurs remplacent le calcul pour ce composant et pour tout ce qui le consomme. Pour ce projet seulement : la fiche de l'article n'est pas modifiée.",
      saisie_vide: "Indiquez au moins une teneur, ou annulez.",
      surcharge_saisie: "Valeurs saisies à la main",
      surcharge_ciqual: "Valeurs reprises de Ciqual :",
      renseigner_ciqual: "Renseigner depuis Ciqual",
      /*
       * ⚠️ « **Charger** », pas « renseigner » : le bouton REMPLIT les champs, il n'écrit rien. Le verbe
       * dit ce qui se passe — l'enregistrement, lui, est au pied du dialogue. « Renseigner » laissait
       * croire que le geste était terminé.
       */
      charger_ciqual: "Charger depuis Ciqual",
      /*
       * ⚠️ **Le bouton RESTE, éteint, et dit pourquoi.** Il disparaissait quand l'interrupteur
       * « Valeurs nutritionnelles » était éteint quinze lignes plus haut : on cherchait un bouton, pas
       * un interrupteur. Un geste absent n'apprend rien ; un geste éteint qui se nomme, si.
       */
      charger_ciqual_sans_objet:
        "Ce composant est déclaré sans valeurs nutritionnelles. Rallumez l'interrupteur pour aller chercher des teneurs au référentiel.",
      ciqual_charge: "Valeurs Ciqual chargées dans les champs — à relire, puis enregistrer",
      ciqual_actuel: "Actuellement rattaché :",
      ciqual_apercu_vide: "Survolez un aliment pour voir ses valeurs.",
      ciqual_repli: "Aucun résultat pour « #saisi » — voici ceux pour « #utilise » :",
      ciqual_par_proximite: "Les plus proches par le sens :",
      ciqual_chercher: "Chercher un aliment (tomate, farine, lait…)",
      millesime: "Millésime Ciqual",
      apercu_titre: "Vérifiez ces valeurs avant de les utiliser",
      mention_composant:
        "Valeurs telles qu'elles figurent sur la fiche de ce composant : elles décrivent l'ingrédient reçu, pas ce qu'il devient. La cuisson, la pasteurisation, le séchage et l'oxydation peuvent les modifier sensiblement. Seule une analyse en laboratoire les confirme.",
      origine_ciqual: "Repris de Ciqual :",
      consomme_dans: "Consommé dans",
      consomme_pf: "Par unité de produit fini",
      portee_ce_projet: "pour ce projet uniquement",
      portee_fiche: "valeurs de la fiche article",
      lever_blocage: "Enregistrer",
      portee_essai: "Pour ce projet seulement : la fiche de l'article n'est pas modifiée.",
      /*
       * ⚠️ **La même donnée se saisit à DEUX endroits, et rien ne le disait.** Le poids net se pose ici,
       * en levant un blocage depuis l'étape qui consomme, et dans le détail du composant lui-même —
       * même champ, même portée, deux gestes. Ne pas le dire fait chercher lequel des deux « compte »
       * (constaté le 11/09/2026). Le dire transforme une duplication en deux chemins vers la même
       * chose.
       */
      blocage_meme_champ:
        "C'est la même donnée que « Densité » et « Poids net par unité » dans le détail de ce composant : la saisir ici ou là-bas revient au même.",
      surcharge_essai_posee:
        "Valeurs enregistrées pour ce projet — la fiche de l'article n'est pas modifiée.",
      retirer_composant: "Revenir aux valeurs de la fiche",
      recharger_fiche: "Recharger depuis la fiche",
      recharger_fiche_aide:
        "Relit les valeurs actuelles de la fiche article et remplit les champs. Rien n'est enregistré avant validation. Utile après avoir corrigé la fiche : sans ce geste, le formulaire réaffiche les valeurs saisies ici.",
      fiche_rechargee: "Valeurs de la fiche article rechargées — rien n'est encore enregistré.",
      // ⚠️ Formulation POSITIVE : l'interrupteur allumé montre ce qu'il nomme. Éteint, c'est
      // `sans_objet_declare` qui écrit le constat — le libellé de l'interrupteur ne change pas de sens
      // selon sa position.
      /*
       * ⚠️ **Le MÊME libellé que la fiche article** (« Article BIO ») : c'est le même interrupteur, à la
       * portée d'un projet. Un second vocabulaire pour la même décision ferait apprendre une distinction
       * technique là où l'utilisateur pose la même question.
       */
      bio_toggle: "Article BIO",
      /*
       * ⚠️ **Le titre dit la BASE, pas seulement le champ.** « Origine » seul ferait chercher une
       * provenance géographique ; c'est la question du dénominateur de la mention bio réglementaire.
       */
      origine_agricole: "Origine agricole (mention bio réglementaire)",
      origine_est_agricole: "Ingrédient agricole",
      origine_eau_sel: "Eau ou sel",
      origine_agricole_aide:
        "Compte dans le dénominateur de la mention « X % des ingrédients d'origine agricole sont bio ».",
      origine_eau_sel_aide:
        "Exclu du calcul : le règlement (UE) 2018/848 écarte l'eau et le sel du dénominateur.",
      origine_absente: "Sans réponse, la mention bio réglementaire ne peut pas être écrite.",
      origine_absente_detail:
        "La liste d'ingrédients le signale et n'affiche que la part de masse. Le règlement (UE) 2018/848 impose « X % des ingrédients d'origine agricole sont issus de l'agriculture biologique » : son dénominateur exclut l'eau et le sel.",
      origine_effacer: "Effacer la réponse",
      bio_pose_projet: "enregistré pour ce projet — la fiche de l'article n'est pas modifiée",
      bio_depuis_fiche: "vient de la fiche article",
      bio_sans_fiche: "aucune déclaration — une matière provisoire n'a pas de fiche article",
      bio_revenir_fiche_bio: "Revenir à la fiche article : bio",
      bio_revenir_fiche_non_bio: "Revenir à la fiche article : non bio",
      bio_retirer_sans_fiche: "Retirer la déclaration",
      a_des_valeurs: "Valeurs nutritionnelles",
      sans_objet_declare:
        "Ce composant est déclaré sans valeurs nutritionnelles : il est exclu du calcul, masse de référence comprise. À réserver à ce qui n'est pas comestible — emballage, étiquette, auxiliaire. Pour un ingrédient dont la composition est inconnue, ne rien déclarer : sa masse restera visible en part non déterminée.",
      sans_objet_en_attente:
        "Pas encore enregistré : tant que vous ne l'avez pas fait, ce composant n'est PAS exclu du calcul. Sa masse reste comptée en part non déterminée. À réserver à ce qui n'est pas comestible — emballage, étiquette, auxiliaire.",
      surcharge_retiree: "Valeurs du projet retirées — le composant reprend celles de sa fiche.",
      essai_inconnu:
        "Impossible de rattacher cette valeur à un essai : rouvrez la fiche depuis le projet.",
      origine_ciqual_modifiee: "Repris de Ciqual puis corrigé à la main :",
      apercu_modifie:
        "Vous avez modifié des valeurs : elles seront enregistrées comme saisie manuelle. L'aliment Ciqual n'est plus qu'un point de départ.",
      non_reprise: "non reprise, car ce n'est pas une valeur mesurée",
      utiliser_ces_valeurs: "Utiliser ces valeurs",
      choisir_autre: "choisir un autre aliment",
      aliments: "aliments",
      ciqual_aucun: "Aucun aliment trouvé.",
      ciqual_indisponible:
        "Référentiel Ciqual injoignable pour le moment — réessayez, ou saisissez les valeurs à la main.",
      ciqual_vide:
        "Aucune table Ciqual n'est installée sur cette plateforme. Un administrateur JustUp doit importer les fichiers de l'Anses. D'ici là, saisissez les valeurs à la main.",
      ciqual_sans_teneur: "Cet aliment ne porte aucune teneur exploitable pour ces constituants.",
      // ⚠️ **Un TOTAL, composé ici.** Le serveur concatène les avertissements des enfants : une phrase
      // par niveau annonçait « 3 composants… » puis « 1 composant… » pour quatre. Et écrite côté
      // serveur, elle n'existait qu'en français.
      sans_valeur_un: "1 composant n'a pas encore de valeurs nutritionnelles.",
      sans_valeur_n: "#nb composants n'ont pas encore de valeurs nutritionnelles.",
      // Une RÉPONSE, pas un manque : un emballage n'a rien à déclarer. D'où « rien à corriger », qui
      // évite d'aller chercher une saisie qui n'existe pas.
      sans_objet_un:
        "1 composant est déclaré sans valeurs nutritionnelles — un emballage, un auxiliaire. Rien à corriger. Il ne compte ni dans les valeurs, ni dans le poids qui sert de base.",
      sans_objet_n:
        "#nb composants sont déclarés sans valeurs nutritionnelles — emballages, auxiliaires. Rien à corriger. Ils ne comptent ni dans les valeurs, ni dans le poids qui sert de base.",
      sans_agregat:
        "Aucune valeur agrégée pour l'instant. Ce qui l'empêche est nommé ci-dessous.",
      sans_agregat_sans_cause:
        "Aucune valeur agrégée : aucun composant ne porte de valeur nutritionnelle.",
      mention_indicative:
        "Valeurs purement indicatives : addition des composants au prorata de leur part, sans tenir compte des procédés. Cuisson, pasteurisation, séchage, oxydation peuvent les modifier sensiblement. Seule une analyse en laboratoire les confirme.",
    },
    promotion: {
      titre: "Créer l'article",
      note:
        "Ce semi-fini n'existe que dans l'essai. Le promouvoir crée un article réel avec sa spécification (recette copiée), puis remplace le semi-fini par cet article dans les essais ouverts. Les essais figés ne bougent pas.",
      impossible: "Promotion impossible en l'état",
      composants: "composant(s) dans la recette",
      essais_ouverts: "essai(s) ouvert(s) basculeront sur l'article",
      essais_figes: "essai(s) figé(s) garderont le semi-fini",
      denomination: "Dénomination de l'article",
      code: "Code article (facultatif)",
      code_aide:
        "Laissé vide, le code suit la numérotation maison — c'est une convention d'entreprise, pas une décision de formulation.",
      creer: "Créer l'article",
      confirmer_titre: "Créer un article dans le référentiel",
      confirmer_message:
        "Cette action crée un article réel et sa spécification, en dehors de l'essai. Elle ne s'annule pas depuis le module de formulation. Confirmez-vous ?",
      faite: "Article créé",
      lignes_substituees: "ligne(s) substituée(s)",
    },
    mini: {
      produit_fini: "Produit fini",
    },
    coef: {
      /*
       * ⚠️ **« Quantité obtenue », et non « Composition nécessaire pour fabriquer ».**
       *
       * C'est le MÊME champ (`quantityProdDefault`) que celui de « Données logistiques », où le sens de
       * saisie A29 s'appelle `QUANTITE_OBTENUE`. Le libellé d'origine venait de l'écran Spécification, où
       * il est juste : on y décrit une nomenclature existante. Dans un outil de formulation, il se lit
       * comme un **dimensionnement de lot** — « je veux en faire 200 » — alors que le champ dit un
       * **rendement** : « ces mêmes ingrédients en donnent 200 ». D'où l'impression, vécue, que l'effet
       * est inversé : on attend que la recette se recalcule, et c'est le par-unité qui bouge.
       *
       * Un même nombre éditable à deux endroits sous deux noms qui ne racontent pas la même chose : le
       * défaut était dans le NOM de la donnée, pas dans le calcul (A72).
       */
      titre: "Quantité obtenue",
      aide:
        "Ce que la recette ci-dessous produit — et le diviseur qui ramène chaque composant à une unité. La modifier ne recalcule pas les quantités : elle déclare que les mêmes ingrédients donnent cette quantité-là. Le rendement se saisit dans « Données logistiques ».",
      pour_fabriquer: "Quantité obtenue",
      rendement: "Rendement",
      // Le motif est nommé : « non totalisable » sans cause ne dit pas quoi corriger. L'unité manquante
      // sur l'étape se règle dans la spécification ; des unités mêlées ne se règlent pas du tout.
      sans_unite_etape: "l'unité fabriquée n'est pas renseignée",
      unites_melees: "unités des composants :",
      mo_absente: "Main-d'œuvre à estimer",
      deduit: "déduit",
      rendement_deduit_aide: "Aucun rendement n'est saisi sur cette étape : celui-ci est déduit de la recette — quantité obtenue ÷ somme des quantités engagées. Si toutes les unités se valent, le rapport des quantités suffit ; sinon il porte sur les masses, ce qui demande la densité ou le poids net de chaque composant et du produit obtenu.",
      inventee: "Inventée",
      inventee_tip:
        "Ce semi-fini ne correspond à aucun article : il n'existe que dans cet essai. Cliquer pour le créer en article réel.",
    },
    decomposition: {
      matiere: "Matière (composants)",
      mo: "Main-d'œuvre",
      prix_unitaire: "Prix unitaire",
      matiere_u: "Matière €/u PF",
      total_mat: "Total mat. €",
      totaux_pour_lot: "Totaux pour un lot de",
      mo_u: "MO €/u PF",
      total_mo: "Total MO €",
      grand_total: "Grand total",
      sans_famille: "Sans famille",
      sans_sous_famille: "Sans sous-famille",
    },
    arbre: {
      part_saisie_aide: "Saisir une part renormalise les autres composants au prorata de leur poids ; la somme reste à 100 %.",
      valider_qte: "Valider la quantité",
      annuler_qte: "Annuler la modification",
      produit_fini: "Produit fini",
      semi_fini: "Semi-fini",
      matiere_premiere: "Matière première",
      matiere_provisoire: "Composant provisoire",
      /*
       * ⚠️ **L'en-tête d'une colonne chiffrée porte DEUX niveaux** : le nom de la grandeur, puis son
       * dénominateur. Les deux étaient auparavant concaténés (« Quantité pour 1 unité fabriquée → par
       * unité PF »), ce qui tenait sur trois lignes et imposait à lui seul la hauteur de l'en-tête, alors
       * que les quatre autres intitulés en tenaient une. Séparés, le nom se balaye et l'échelle se lit une
       * fois — c'est déjà le patron des cellules du tableau, remonté d'un cran.
       *
       * ⚠️ **L'échelle CHANGE avec la portée**, et ce n'est pas cosmétique : en portée « un Semi-Fini »
       * tout est ramené à une unité de CE Semi-Fini, pas du Produit Fini. Les deux peuvent différer d'un
       * facteur 1 000, et c'est ce facteur qui fait commander un sac ou une palette.
       */
      col_quantite: "Quantité",
      col_valeur_unitaire: "Valeur unitaire",
      /*
       * ⚠️ « Coût **matière** », pas « Coût » tout court : la colonne voisine « Main-d'œuvre €/u » est
       * elle aussi un coût, et les deux se lisent côte à côte dès qu'un tier fournit des heures. Un
       * intitulé qui ne dit pas de quel coût il parle rend la ligne ambiguë au moment précis où elle
       * porte les deux.
       */
      col_cout_matiere: "Coût matière",
      /*
       * ⚠️ **« Part du poids » et non « Part ».** À côté de « Part du coût », le mot seul ne dit pas de
       * quoi il est la part — et les deux colonnes sont voisines, toutes deux en pourcent. Nommer la
       * grandeur des deux côtés les rend impossibles à confondre : l'une pèse, l'autre partage l'argent.
       */
      col_part_massique: "Part du poids",
      /*
       * Les aides d'en-tête. Une phrase par colonne, qui dit CE QUE C'EST — le sous-titre sous le
       * titre dit contre quoi le chiffre se calcule, ce qui ne tient pas dans les deux.
       *
       * ⚠️ Les deux colonnes de pourcentage sont les plus proches à l'œil et les plus éloignées par le
       * sens : l'une pèse de la matière, l'autre partage de l'argent. Leurs aides le disent chacune en
       * nommant l'autre, parce que c'est en les comparant qu'on les confond.
       */
      aide_col_nutrition:
        "L'état nutritionnel de chaque composant : valeurs connues, à renseigner, déclaré sans valeurs, ou calculé depuis ses propres composants.",
      aide_col_etiquetage:
        "Ce que le composant devient sur l'étiquette : listé, nommé avec sa décomposition, exclu, ou porteur d'un pourcentage réglementaire.",
      /*
       * ⚠️ **« Échantillon » et non « Cible ».** « Cible » est le vocabulaire des objectifs — le module
       * s'en sert déjà pour le prix de vente cible et l'objectif de marge, qui sont des intentions
       * commerciales. Ici on ne vise rien : on fabrique une petite quantité pour de vrai. Deux sens du
       * même mot sur le même écran, c'est un mot de moins qui veut dire quelque chose.
       */
      col_echantillon: "Échantillon",
      echantillon_vide: "aucun",
      echantillon_sur: "de #etape",
      echantillon_total_avant: "Matière pour",
      echantillon_total_apres: "de #etape",
      echantillon_retirer: "Retirer",
      echantillon_retirer_aide: "Le tableau revient au seul lot de la recette.",
      // ⚠️ Dit les deux choses : ce que la colonne calcule, ET que la recette ne bouge pas. Sans le
      // second, une colonne de quantités à côté d'une autre se lit comme une modification en cours.
      aide_echantillon:
        "Tapez la quantité que vous voulez fabriquer : toute la recette se met à cette échelle. Rien n'est enregistré. Videz le champ pour revenir au lot.",
      aide_composant:
        "Les composants de la recette, du produit fini vers ses matières. Un chevron indique un article fabriqué : il se déplie sur sa propre composition.",
      aide_quantite:
        "Ce qu'il faut de ce composant pour le lot indiqué. Modifiable : la valeur s'enregistre en quittant le champ.",
      aide_valeur_unitaire:
        "Le prix d'achat d'une unité de ce composant. La pastille R signale un prix repris de la fiche ; une valeur saisie ici ne vaut que pour cet essai.",
      aide_cout_matiere:
        "Ce que ce composant coûte pour une unité de produit fini : sa quantité × sa valeur unitaire. Main-d'œuvre non comprise.",
      aide_part_massique:
        "La part de ce composant dans son étape, en poids — « 42 % de tomate dans la sauce ». Différent de « Part du coût », qui partage l'argent : un ingrédient léger peut coûter cher. Vide si un composant de l'étape n'est pas pesable.",
      aide_mo:
        "Le coût de main-d'œuvre de cette étape, pour une unité de produit fini. Le survol de la valeur dit d'où elle vient : ligne réelle, moyenne observée, forfait.",
      aide_contribution:
        "Ce que ce composant représente dans le coût du produit fini, en pourcent. Différent de « Part du poids » : un ingrédient minoritaire peut coûter cher.",
      // ⚠️ Dit CONTRE QUOI la part se calcule. Sans ce rappel, « 42 % » se lirait comme une part de
      // l'étiquette (QUID), qui est une autre grandeur et vit sur la carte Étiquetage.
      echelle_part: "dans son étape",
      // ⚠️ Chaque motif NOMME la donnée à saisir, jamais « non calculable » : le premier envoie la
      // renseigner sur la fiche article, le second envoie chercher. Le composant fautif est ajouté
      // après le texte, par le gabarit.
      part_motif_DENSITE_ABSENTE: "Part incalculable : densité manquante sur",
      part_motif_POIDS_NET_ABSENT: "Part incalculable : poids net manquant sur",
      part_motif_UNITE_INCONNUE: "Part incalculable : unité hors référentiel sur",
      part_motif_NATURE_NON_PESABLE: "Part incalculable : cet article ne se pèse pas —",
      echelle_pf: "par unité de Produit Fini fabriquée",
      echelle_sf: "par unité de Semi-Fini fabriquée",
      par_unite_pf: "par #unite de PF fabriqué",
      // ⚠️ Sous la quantité obtenue d'un nœud fabriqué : « = 540,00 kg engagés ». Le mot dit que
      // c'est ce qui ENTRE, par opposition à la quantité obtenue juste au-dessus.
      engages: "engagés",
      par_unite_sf: "par #unite de SF fabriqué",
      unite_inconnue: "unité",
      mo: "Main-d'œuvre €/u",
      contribution: "Part du coût",
      pour_lot: "pour un lot de",
      // Le MÊME mot que la colonne et que le popup : un concept, un mot.
      prix_composant_col: "Prix composant",
      // ⚠️ Plus de libellé écrit : le geste est porté par une CROIX contre la pastille « H », qui dit déjà
      // l'état. Une phrase sous le champ disait les deux à la fois et gonflait la ligne. Il ne reste que
      // l'explication, en survol — une croix seule dirait quoi faire, pas ce qu'on annule.
      // ⚠️ L'aide NOMME la valeur rétablie : « revenir au prix des achats » demandait d'annuler pour
      // savoir de combien ça bouge, puis de resaisir de mémoire si l'écart déplaisait.
      prix_essai_aide_valeur:
        "Prix enregistré pour ce projet — ce n'est pas le tarif de la fiche. Cliquer pour revenir au tarif : #prix € / #unite.",
      // Le référentiel n'a aucun prix : le retrait laisse la ligne sans prix. Le dire, plutôt que de
      // promettre un retour qui n'aura pas lieu.
      prix_essai_aide_sans_ref:
        "Prix enregistré pour ce projet. Aucun tarif connu ailleurs pour ce composant. Le retirer laissera la ligne sans prix, donc sans coût.",
      // ⚠️ **La croix « revenir à la prod ».** La quantité, la perte, le rendement et la taille de lot
      // s'écrivent en dur sur la ligne du fork : une fois la saisie validée, rien ne disait plus ce que
      // la prod avait donné. La pastille signale l'écart, l'aide NOMME la valeur rétablie — sans quoi
      // il faut cliquer pour savoir de combien ça bouge, puis resaisir de mémoire si l'écart déplaît.
      origine_pastille:
        "Cette valeur a été modifiée pour cet essai : ce n'est pas celle du process importé.",
      origine_aide_valeur:
        "Revenir à la valeur du process importé : #valeur #unite.",
      // L'import ne donnait rien pour ce champ : le retour le VIDE, il ne le remet pas à zéro. Un zéro
      // se lirait comme une valeur décidée.
      origine_aide_vide:
        "Ce champ était vide dans le process importé. Cliquer pour le vider à nouveau.",
      // ⚠️ L'aide dit ce que la ligne AFFICHE, pas ce que le champ veut dire : le nombre montré est
      // toujours l'autre grandeur, et sans ça on croit lire deux fois la même.
      perte_aide_utilise:
        "Perte de mise en œuvre. La quantité saisie est celle qui entre dans le produit ; la valeur affichée ici est ce qu'il faut engager.",
      perte_aide_engage:
        "Perte de mise en œuvre. La quantité saisie est celle qu'on engage ; la valeur affichée ici est ce qui entre réellement dans le produit.",
      colonnes: "Colonnes",
      colonnes_aide: "Choisir les colonnes affichées — le réglage est conservé.",
      col_reperes: "Repères (nutrition, étiquetage)",
      // ⚠️ Dit pourquoi la case ne répond pas : une case inerte sans explication se lit comme un bug.
      colonnes_mo_absente: "Aucune main-d'œuvre chiffrée sur cette recette.",
      du_modele: "du modèle",
      du_modele_aide:
        "Ce composant vient de la recette partagée de l'article : il compte, mais ne se modifie pas ici. D'autres articles l'emploient — le changer ici changerait leur recette.",
      etiquetage_inclus: "Figure dans la liste des ingrédients — cliquer pour l'en retirer",
      etiquetage_exclu:
        "Hors liste des ingrédients (emballage, auxiliaire) — cliquer pour l'y remettre",
      etiquetage_decompose:
        "Décomposé : ses composants figurent directement dans la liste — cliquer pour le nommer comme ingrédient composé",
      etiquetage_nomme:
        "Nommé comme ingrédient composé : il paraît suivi de sa décomposition entre parenthèses — cliquer pour le décomposer",
      retirer: "Retirer ce composant",
      retirer_titre: "Retirer ce composant ?",
      retirer_message:
        "Le composant #nom quittera la recette. Sa quantité, sa perte et son unité ne sont pas conservées. Pour le remettre, il faudra le ressaisir.",
      retirer_message_sans_nom:
        "Ce composant quittera la recette. Sa quantité, sa perte et son unité ne sont pas conservées. Pour le remettre, il faudra le ressaisir.",
      definir_mo: "Rattacher un calcul de main-d'œuvre à cet article fabriqué",
      /*
       * ⚠️ **Le libellé dit l'ACTE, et il diffère selon l'état.** Les deux clefs portaient le même mot,
       * « Main-d'œuvre » — un nom de grandeur, pas un geste. Posé au milieu des badges (« Produit fini »),
       * le bouton se lisait comme une étiquette d'état : on cherchait un bouton, on voyait une mention.
       * C'est la règle déjà appliquée au fige (A41) : un bouton porte son résultat.
       */
      ajouter_mo: "Estimer la main-d'œuvre",
      modifier_mo: "Modifier la main-d'œuvre",
      mo_absente:
        "Aucune main-d'œuvre estimée : renseignez une ligne de production, une famille technique ou des paramètres provisoires.",
      dont_directe: "dont directe",
      vide: "Aucune cascade : importez un produit existant ou ajoutez des composants.",
    },
    // ⚠️ **Ce que le Ctrl+Z vient de DÉFAIRE**, pas ce qu'il va faire : le message paraît après coup.
    // Un raccourci silencieux sur un écran qui recharge se lit comme un clic raté — on le represse,
    // et on défait un second geste qu'on ne voulait pas.
    annulation: {
      retrait: "Composant remis :",
      // ⚠️ Un raccourci qui ne répond rien se lit comme un raccourci cassé : on ne peut pas
      // distinguer « rien à défaire » de « la touche n'est pas écoutée ».
      rien: "Rien à annuler pour cet essai.",
      bouton: "Annuler",
      aide: "Annule le dernier geste de cette session (Ctrl+Z). Le retour au process importé, lui, passe par la croix de chaque valeur.",
    },
    mo_dialog: {
      titre: "Main-d'œuvre",
      note:
        "La main-d'œuvre se rattache à l'article fabriqué : c'est lui qui correspond à un ordre de fabrication. Donc à une ligne et à des heures.",
      tier_ligne: "Une ligne de production",
      tier_ligne_sous: "Ses paramètres réels : cadence, personnes, calage, nettoyage — rien à saisir",
      choisir_ligne: "CHOISIR LES PARAMÈTRES D'UNE LIGNE",
      champ_ligne: "Ligne de production",
      champ_ligne_vide: "Choisir une ligne",
      champ_parametrage: "Paramétrage de cette ligne",
      champ_parametrage_vide: "Choisir un paramétrage",
      famille_filtre:
        "Seuls les paramètres de la famille technique « #famille » sont proposés — celle de ce produit.",
      famille_sans_jeu:
        "Aucun jeu de paramètres ne porte la famille « #famille » de ce produit : tous les jeux de l'atelier sont proposés.",
      famille_absente:
        "Ce produit n'a pas de famille technique : tous les jeux de paramètres de l'atelier sont proposés.",
      ligne_aide:
        "Une ligne a des paramètres différents selon la famille technique qu'elle traite : choisissez la paire, pas seulement la ligne.",
      aucune_ligne:
        "Aucune ligne de production n'a de cadence renseignée. Renseignez-les dans le paramétrage des lignes, ou utilisez les paramètres de votre atelier ci-dessus.",
      /*
       * 🔴 **Une liste vide DISAIT que l'atelier n'avait aucun paramètre.** L'appel qui la remplit
       * n'avait pas de branche d'erreur : sur une requête en échec, le signal restait vide et l'écran
       * affirmait « aucune ligne n'a de paramètres renseignés » — un diagnostic faux, qui envoie
       * paramétrer des lignes déjà paramétrées. Les deux causes ont donc deux phrases.
       */
      liste_indisponible:
        "Les paramètres de l'atelier n'ont pas pu être chargés : fermez cette fenêtre et réouvrez-la. La liste est vide pour cette raison, pas parce que l'atelier n'en aurait aucun.",
      manque_ligne: "Choisissez la ligne et la famille technique",
      u_h: "u/h",
      pers: "pers.",
      tier_params: "Paramètres de mon atelier",
      tier_params_sous: "Cadence, personnes, calage, nettoyage — réutilisables sur d'autres essais",
      tier_forfait: "Forfait",
      tier_forfait_sous: "Un taux horaire et un nombre d'heures, sans détail",
      reutiliser: "Réutiliser des paramètres existants",
      nouveaux_params: "Nouveaux paramètres",
      modifier_params: "Modifier ces paramètres",
      modifier_params_portee:
        "La correction s'appliquera aux essais ouverts qui emploient ces paramètres : leur coût de main-d'œuvre sera recalculé. Les essais figés gardent leur chiffrage.",
      enregistrer_params: "Enregistrer les modifications",
      annuler_edition: "Annuler",
      retirer_params: "Retirer ces paramètres ?",
      retirer_params_consequence:
        "Ils quittent la liste réutilisable. Les #nb essai(s) qui les emploient continuent de chiffrer. Rien n'est perdu, et vous pourrez en recréer.",
      params_modifies: "Paramètres modifiés",
      params_retires: "Paramètres retirés de la liste",
      usages_aide: "Employés dans #nb essai(s)",
      cout_jeu: "Coût horaire",
      // ⚠️ « chargé » seul ne dit pas de QUI : le taux est celui d'UN opérateur, et la cadence
      // multiplie ensuite par l'effectif. Sans le mot, on saisit le coût de l'équipe entière.
      cout_jeu_hint: "€/heure — coût chargé moyen d'un opérateur",
      nom: "Nom (ex. « Ligne conditionnement »)",
      cadence: "Cadence (unités/heure)",
      personnes: "Personnes",
      duree_poste: "Durée d'un poste (heures)",
      duree_poste_hint: "Heures — sert à compter les calages",
      calage: "Calage",
      nettoyage: "Nettoyage",
      minutes: "Minutes",
      heures: "Heures estimées",
      taux_horaire: "Coût horaire",
      reutiliser_vide: "Choisir un paramétrage enregistré",
      // ⚠️ Nomme le TOTAL, pas seulement l'affiché : c'est le total qui dit si la médiane est solide.
      lots_tronques: "Les #n lots les plus récents, sur #total pris en compte dans le calcul.",
      of_tronques: "Les #n OF les plus récents, sur #total pris en compte dans le calcul.",
      voir_lots: "Voir les lots qui donnent cette valeur",
      masquer_lots: "Masquer les lots",
      lot_quantite: "Quantité",
      lot_cout: "MO",
      lot_heures: "Heures",
      tier_article: "Observé sur ce produit",
      // ⚠️ Les heures figurent dans le sous-titre : c'est ce qui distingue ce tier de la moyenne de
      // famille, et ce qui décide de la marge horaire.
      tier_article_sous: "#cout €/unité · #n lot(s) fabriqués · #heures h/unité",
      aucune_heure: "aucune",
      article_titre: "Ce que la reprise apporte",
      article_aide:
        "Ces valeurs viennent des lots déjà fabriqués de ce produit : le mode le plus proche du réel. C'est aussi le seul mode observé qui donne des heures — la recette entrera donc dans la marge par heure de production.",
      tier_sans_objet: "Sans main-d'œuvre",
      tier_sans_objet_sous: "Cette étape ne mobilise personne — rien à saisir",
      sans_objet_aide:
        "Aucun coût ni aucune heure ne seront comptés pour cette étape. C'est une réponse, pas un manque : « Avant d'envoyer » cessera de la signaler.",
      tier_famille: "Moyenne observée de la famille",
      // ⚠️ « aucune heure » AVANT le clic : c'est ce qui sort la recette de la marge horaire.
      tier_famille_sous: "#cout €/unité · #n OF observés · aucune heure",
      taux_observe: "Taux horaire observé",
      taux_observe_effectif: "#n OF",
      // ⚠️ Une PLAGE, pas une largeur : « tiennent dans 8,55 € » demandait « autour de quoi ? ».
      taux_observe_plage: "la plupart entre",
      et: "et",
      // ⚠️ Le mot dit la fragilité sans retirer la proposition : un taux fragile vaut mieux qu'un taux
      // inventé, mais il ne doit pas se lire comme un fait.
      taux_observe_fragile: "trop peu d'OF pour être une référence",
      // ── L'aide du taux observé ────────────────────────────────────────────────────────────────
      aide_taux_titre: "D'où vient ce taux ?",
      aide_taux:
        "Calculé sur les OF terminés de cette famille : pour chacun, le coût de personnel pointé divisé par les heures travaillées. C'est la valeur médiane, pas une moyenne — un OF exceptionnel ne tire pas le chiffre. Attention à ce que « par heure » recouvre : les heures d'un OF additionnent celles de TOUTES les personnes présentes. À trois personnes, la ligne coûte trois fois ce montant par heure d'horloge.",
      // ⚠️ L'étendue est le chiffre le moins lisible des trois : il faut dire ce qu'il autorise à conclure.
      aide_taux_etendue:
        "« La plupart entre A et B » : l'intervalle où se situent 80 % des OF, les 10 % les moins chers et les 10 % les plus chers écartés. Resserré, l'atelier est régulier. Largement étalé, la famille mélange des postes différents — la ventilation par ligne montre lesquels.",
      aide_taux_exemple:
        "Exemple : 38,86 €/h · 70 OF · la plupart entre 35,10 et 43,65 €/h. La moitié des OF sont au-dessus de 38,86, l'autre en dessous ; huit sur dix entre 35,10 et 43,65. Ce taux pré-remplit le champ ci-dessus. Il reste modifiable : il décrit ce qui s'est passé, pas ce que vous voulez chiffrer.",
      // ── L'historique ──────────────────────────────────────────────────────────────────────────
      voir_historique: "Voir les OF qui donnent ce taux",
      masquer_historique: "Masquer les OF",
      par_ligne: "Par ligne de production",
      toutes_lignes: "Toutes lignes",
      sans_ligne: "Sans ligne",
      // ── Les conditions de production, sans lesquelles un €/h ne s'interprète pas ──────────────
      par_heure_personne: "€ par heure et par personne",
      par_heure_ligne: "€ par heure de ligne",
      assiette: "Observé sur",
      cond_personnes: "Personnes sur la ligne",
      cond_ligne_heure: "soit",
      cond_cadence: "Cadence",
      cond_unites_heure: "u/h",
      of_pers: "Pers.",
      of_cadence: "Cad. u/h",
      of_numero: "OF",
      of_date: "Date",
      of_ligne: "Ligne",
      of_heures: "Heures",
      of_cout: "Coût MO",
      of_taux: "€/h",
      aucun_of: "Aucun OF exploitable sur cette sélection.",
      taux_horaire_hint: "€/heure — coût chargé moyen d'un opérateur",
      lot_production: "Taille de lot de production",
      /*
       * ⚠️ **L'aide dit ce que le nombre COMMANDE, pas ce qu'il est** : c'est lui qui dilue le calage et
       * le nettoyage. Et elle dit ce que le vide vaut — sans quoi un champ facultatif se lit comme un
       * champ oublié.
       */
      lot_production_hint:
        "Combien on en lance à la fois. Le calage et le nettoyage sont comptés une fois par lancement, puis répartis sur cette quantité. Vide : sur la quantité obtenue de la recette.",
      apercu_titre: "Ce que ça donne",
      apercu_unitaire: "par unité",
      /*
       * ⚠️ **« pour le lot » était FAUX, et dangereusement plausible.** `EstimationMo.cout` porte sur la
       * QUANTITÉ OBTENUE du nœud, pas sur le lot de production qu'on vient de saisir juste au-dessus :
       * les deux sont à l'écran en même temps, et « 0,04 € pour le lot » se lisait comme le coût des
       * 3 000 unités lancées — faux d'un facteur 3 000.
       */
      apercu_lot: "pour la quantité obtenue",
      apercu_heures: "heures",
      apercu_impossible: "Le calcul ne peut pas encore aboutir : il manque une donnée ci-dessus.",
      // ⚠️ Le TITRE porte l'icône d'aide ; le mécanisme est passé au survol le 14/09/2026.
      calage_titre: "Calage et nettoyage, par poste",
      calage_aide:
        "Le calage et le nettoyage se répètent PAR POSTE, pas par lot : trois jours de production paient trois calages. C'est l'erreur de chiffrage la plus fréquente, et elle sous-estime toujours.",
      forfait_aide: "Le plancher assumé : deux chiffres, aucune prétention à la précision. Mieux qu'un coût de revient sans main-d'œuvre.",
      /*
       * ⚠️ **Rapprocher les DEUX durées de la même chose.** Les minuteries du mode opératoire et les
       * heures du forfait vivaient chacune de leur côté : on saisissait deux fois, et les deux
       * pouvaient diverger sans un mot. La phrase annonce ce que le mode opératoire dit déjà ; le
       * bouton le reprend, sans jamais l'imposer — un forfait d'atelier couvre aussi le calage, le
       * nettoyage et les attentes, que personne ne minute.
       */
      heures_procedes: "#h h d'après le mode opératoire",
      heures_procedes_reprendre: "Reprendre",
      manque_params: "Donnez un nom à ces paramètres",
      manque_cadence: "Indiquez une cadence (unités/heure)",
      manque_taux: "Indiquez un coût horaire",
      manque_heures: "Indiquez un nombre d'heures",
      retirer: "Retirer la main-d'œuvre",
      enregistrer: "Enregistrer",
    },
    etiquetage: {
      detacher:
        "Reprendre cette liste à la main",
      detacher_aide:
        "Le texte devient modifiable. Le module continue de calculer sa version et signale ce qui diverge : un allergène absent, un ordre qui ne suit plus le poids.",
      detachee_active:
        "Liste reprise à la main. Le contrôle continue.",
      detachee_rattacher:
        "Revenir à la liste calculée",
      ecart_allergene:
        "« #objet » est présent dans la recette mais absent de ce texte",
      ecart_ordre:
        "« #objet » n'est plus à sa place dans l'ordre décroissant (INCO art. 18)",
      fusion: "regroupe #nb composants",
      fusion_aide:
        "Deux composants ont le même nom d'étiquetage : ils paraissent une seule fois, quantités additionnées. C'est ce qu'impose la déclaration d'un ingrédient. Pour les séparer, donnez-leur des noms différents.",
      fusion_membres: "Regroupés : #liste",
      edition_cible: "Nom d'étiquetage de #article",
      edition_cible_fusion:
        "Renomme #article seul — les #nb composants regroupés se sépareront.",
      // ── Le bandeau de blocage, groupé par NATURE et comptant les COMPOSANTS.
      // ⚠️ Il annonçait « #nb composant(s) sans nom d'étiquetage » quel que soit le constat, et comptait
      // les constats au lieu des composants — trois erreurs dans une phrase de sept mots, dont la
      // conséquence : la liste EST écrite, elle s'affiche juste en dessous. C'est le DOCUMENT qui est
      // retenu, pas la liste.
      /*
       * ⚠️ **Deux titres, deux conséquences.** Le premier arrête la sortie du document, le second non — et
       * c'est la seule chose que l'utilisateur a besoin de savoir pour décider s'il continue. Les ranger
       * sous un titre unique faisait annoncer un refus qui n'arrivait pas.
       */
      avertissements_titre: "À vérifier — la fiche technique peut sortir :",
      bloquants_titre: "La fiche technique ne peut pas sortir :",
      ligne_aucun_allergene: "aucun allergène",
      /*
       * ⚠️ **L'origine d'une trace se dit par la POSITION, plus par une phrase.** Elle a vécu sous la
       * mention imprimable (« MOUTARDE : déclaré par HARICOTS BLANCS FRANCE BIO ») : la bonne
       * information au mauvais endroit — cette zone est celle qui s'imprime, et une étiquette nomme la
       * substance, jamais le fournisseur. Portée par la ligne du composant, à côté de ses allergènes,
       * la puce n'a plus qu'à nommer le terme.
       *
       * ⚠️ **Le mot « traces » est DANS la puce**, il ne se déduit pas du contour en pointillés : le
       * règlement distingue l'ingrédient du contaminant, et une puce qui ne dirait que « MOUTARDE » se
       * lirait comme l'allergène d'à côté.
       */
      ligne_trace: "traces : #terme",
      ligne_trace_aide:
        "Contamination croisée déclarée sur ce composant — ce n'est pas un ingrédient. Elle se lit « peut contenir » sous la liste, jamais dedans.",
      membre_renommer: "Renommer ce composant — il sortira du regroupement",
      membre_aide: "Deux composants qui portent le même nom d'étiquetage n'en font qu'un sur l'étiquette. Renommez-en un pour les séparer.",
      bloquant_nom_etiquetage: "#nb composant sans nom d'étiquetage",
      bloquant_nom_etiquetage_pluriel: "#nb composants sans nom d'étiquetage",
      bloquant_non_pesable: "#nb composant qu'on ne sait pas peser",
      bloquant_non_pesable_pluriel: "#nb composants qu'on ne sait pas peser",
      bloquant_allergene_divergent: "allergènes de la composition absents de la déclaration",
      /*
       * ⚠️ **La note dit la RÈGLE et le GESTE, jamais « écarté » tout seul.** Un composant qui disparaît
       * sans raison se lit comme une perte de données ; nommer les deux conditions (aucune teneur, aucun
       * nom d'étiquetage) dit du même coup comment le faire revenir — et qu'il revient tout seul, sans
       * rien à défaire.
       */
      ecartes_deduits:
        "#noms n'a ni valeur nutritionnelle ni nom d'étiquetage : il reste hors de la liste. Renseigner l'un des deux le fait revenir.",
      ecartes_deduits_pluriel:
        "#nb composants n'ont ni valeur nutritionnelle ni nom d'étiquetage : #noms. Ils restent hors de la liste. Renseigner l'un des deux les fait revenir.",
      nom_portee_essai:
        "Pour ce projet seulement : la fiche de l'article n'est pas modifiée. Vide, c'est le nom de la fiche qui s'applique.",
      exclure_tooltip: "Retirer de la liste d'étiquetage — se remet depuis l'arbre des coûts",
      pct_decimales: "Décimales",
      pct_arrondi: "Arrondi",
      pct_decimales_0: "entier",
      pct_decimales_1: "1 décimale",
      pct_decimales_2: "2 décimales",
      pct_regle_PROCHE: "au plus proche",
      pct_regle_INFERIEUR: "vers le bas",
      pct_regle_SUPERIEUR: "vers le haut",
      pct_regle_PROCHE_aide:
        "Arrondi au plus proche. La réglementation ne fixe aucun arrondi pour le pourcentage QUID : c'est une décision d'entreprise.",
      pct_regle_INFERIEUR_aide:
        "Arrondi vers le bas — le choix prudent. Déclarer plus d'un ingrédient mis en avant est une allégation qu'un contrôle peut contredire ; en déclarer moins reste défendable.",
      pct_regle_SUPERIEUR_aide:
        "Arrondi vers le haut. À réserver aux cas où l'on veut ne jamais sous-déclarer un ingrédient.",
      allergenes: "Allergènes",
      aucun_allergene: "aucun",
      peut_contenir: "Peut contenir :",
      // ⚠️ **Ne dit plus « pour ce projet ».** Les traces viennent des FICHES des composants autant que
      // des surcharges d'essai (correctif du 03/09/2026) ; attribuer au projet une déclaration portée par
      // une fiche article ferait chercher, dans l'essai, une saisie qui n'y est pas.
      /*
       * ⚠️ **La BASE est dans la phrase, pas en note de bas de page.** Le règlement (UE) 2018/848 impose
       * « X % des ingrédients d'origine agricole sont issus de l'agriculture biologique » — dénominateur
       * qui exclut l'eau et le sel. Le référentiel ne porte pas cette distinction : écrire « 48,1 % bio »
       * sans dire de quoi ferait recopier ce chiffre comme s'il était la mention réglementaire.
       */
      bio_part: "#pct % de la masse mise en œuvre est déclarée bio",
      bio_part_plancher: "au moins #pct % de la masse mise en œuvre est déclarée bio",
      bio_part_refusee:
        "part bio non calculable : il manque la masse d'au moins un composant",
      bio_statut_inconnu: "— sans déclaration : #noms",
      /*
       * ⚠️ **Le refus NOMME ce qui manque, et il vit hors du texte imprimable** : c'est une tâche, pas
       * une mention d'étiquette. Aucun pourcentage prudent en attendant — il n'y a pas de côté sûr, les
       * deux erreurs possibles s'impriment.
       */
      bio_reg_origine_non_tranchee:
        "Mention réglementaire indisponible : l'origine agricole n'est pas déclarée pour #noms. Ouvrez leur détail pour la déclarer.",
      bio_reg_statut_inconnu:
        "Mention réglementaire indisponible : un ingrédient d'origine agricole ne déclare pas s'il est bio.",
      bio_reg_aucun_agricole:
        "Mention réglementaire sans objet : aucun ingrédient n'est déclaré d'origine agricole.",
      bio_reg_masses:
        "Mention réglementaire indisponible : il manque la masse d'au moins un composant.",
      traces_mention: "contamination croisée déclarée sur les composants",
      deduits: "déduits de la composition — ajoutez-en si besoin, ils ne se retirent pas",
      nom_a_poser: "Nom d'étiquetage",
      poser_nom: "Enregistrer",
      nom_pose: "Nom d'étiquetage enregistré sur la fiche article",
      editer_nom: "Corriger le nom d'étiquetage",
      annuler: "Annuler",
      pct_afficher: "Afficher le pourcentage de cet ingrédient sur l'étiquette",
      pct_masquer: "Ne plus afficher ce pourcentage",
      pct_incalculable:
        "Pourcentage demandé mais pas encore calculable : un composant n'est pas pesable. Cliquer pour ne plus le demander.",
      /*
       * ⚠️ Répété au moment de l'ÉCRASEMENT, et seulement là : compléter un vide est une correction,
       * remplacer un nom existant change ce que lisent toutes les recettes.
       */
      /*
       * ⚠️ La seconde phrase disait « un nom déjà renseigné ne se change pas d'ici » — c'était vrai, ce ne
       * l'est plus : le stylo de chaque ligne permet la correction. Un libellé qui décrit une règle levée
       * est pire qu'une absence de libellé, parce qu'on le croit.
       */
      vide: "Aucun composant dans cet essai.",
    },
    /**
     * **La carte « Marché annuel »** — projeter le projet sur un volume.
     *
     * ⚠️ **Aucun verdict nulle part** : pas de « marché rentable », pas de seuil. Des chiffres et une
     * légende ; la décision appartient à qui lit, d'autant que le chiffre part en négociation.
     */
    marche: {
      titre: "Marché annuel",
      volume: "Volume annuel",
      par_an: "par an",
      // ⚠️ L'écho n'est pas décoratif : c'est la grandeur contre laquelle on vérifie qu'on n'a pas tapé
      // un zéro de trop — et celle qui parle au logisticien et au certificateur bio.
      echo_tonnes: "soit #t t de produit fini",
      echo_kilos: "soit #kg kg de produit fini",
      vide: "Renseignez un volume annuel pour projeter ce projet : ce qu'il rapporte, ce qu'il demande à l'atelier, ce qu'il faut acheter.",
      chargement: "Projection en cours…",
      pese_titre: "Ce que ça pèse",
      de_marge: "de marge",
      // ⚠️ Un LIBELLÉ de légende, pas la queue de phrase qui suit la pastille : « de marge 36 507 € »
      // dans une colonne de noms se lit comme une phrase coupée.
      marge_libelle: "Marge",
      ca: "Chiffre d'affaires",
      matiere: "Matière",
      mo: "Main-d'œuvre",
      sans_prix: "Pas de marge calculable : aucun prix de vente n'est disponible, faute de coût de revient complet.",
      atelier_titre: "Ce que ça demande à l'atelier",
      // ⚠️ Le lancement porte SA taille et SON étape : un nombre de lancements sans son assiette ne se lit pas.
      lancements: "#n lancements de #lot #unite — #etape",
      /*
       * ⚠️ **Le TOTAL, distinct des heures par ligne.** Le bloc restait muet sur les projets dont
       * aucune étape ne déclare de ligne — alors que le module connaît les heures et les affiche déjà
       * à l'unité dans l'aside, où elles sont illisibles (« 0,086308 h »).
       */
      heures_total: "de main-d'œuvre sur l'année, toutes étapes confondues",
      /*
       * ⚠️ **Deux GRANDEURS, pas deux façons de dire la même.** L'occupation est la durée pendant
       * laquelle la ligne est prise — elle ne peut rien faire d'autre ; le travail cumulé est cette
       * durée multipliée par l'effectif. Elles ne diffèrent qu'à partir de deux opérateurs, et c'est
       * pourquoi la seconde ne s'affiche QUE si elle diffère : « 165 h machine · 165 h atelier » posait
       * une question au lieu d'y répondre (signalé à l'écran le 14/09/2026).
       */
      occupation: "d'occupation de la ligne",
      // ⚠️ « main-d'œuvre », pas « travail » : c'est le mot du module, abrégé MO dans les colonnes.
      travail: "de main-d'œuvre — l'occupation ci-dessus, multipliée par l'effectif de la ligne",
      /*
       * ⚠️ **La ligne la plus utile de la carte.** C'est la seule part du coût que le volume ne dilue
       * pas : elle se répète à chaque lancement, et c'est elle qui répond à « et si on lançait par
       * 5 000 au lieu de 3 000 ? ».
       */
      dont_fixes: "dont #h h de calage et nettoyage — payées à chaque lancement",
      aucune_ligne: "Aucune étape ne déclare d'occupation de ligne : choisissez une ligne de production ou des paramètres d'atelier sur au moins une étape fabriquée.",
      // ⚠️ NOMMÉES, jamais tues : elles pèsent dans le bloc du dessus et n'occupent rien ici.
      /*
        * ⚠️ **`#nb`, surtout pas `#n`** : `#n` est un préfixe de `#noms`, et le remplacement du premier
        * mange le second — l'écran affichait « 3oms » là où il fallait la liste des étapes. Deux jetons
        * dont l'un préfixe l'autre ne cohabitent pas dans la même phrase.
        */
      sans_ligne: "#nb étape(s) pèsent sans occuper de ligne — leur mode de calcul donne un coût, pas des heures : #noms",
      achats_titre: "Ce qu'il faut acheter",
      // Deux achats qui ne se négocient ni aux mêmes délais, ni aux mêmes minima, ni avec les mêmes interlocuteurs.
      /*
        * ⚠️ **« Composants », le mot du MODULE.** Le premier jet disait « denrées » — un mot que ce
        * produit n'emploie nulle part. Un vocabulaire inventé dans un coin oblige le lecteur à faire la
        * traduction lui-même, et il la fait mal : il cherche ailleurs un écran qui parlerait de denrées.
        */
      denrees: "Composants alimentaires",
      /*
        * ⚠️ **Nommé par le CRITÈRE, pas par ce qu'on y trouve d'habitude.** Le partage se fait sur la
        * déclaration « sans valeurs nutritionnelles » (`isAlimentaire`) : ce sont donc des composants
        * NON ALIMENTAIRES, et pas seulement des emballages. Une denrée dont la fiche est fausse — les
        * graines de moutarde du chutney — atterrit ici, et le titre ne ment alors pas : il dit
        * exactement ce que le module a lu.
        */
      emballages: "Composants non alimentaires",
      et_autres: "+ #n autres composants",
      et_autre: "+ 1 autre composant",
      /*
        * ⚠️ **Le CRITÈRE est écrit, parce qu'il se trompe quand la fiche se trompe.** Le classement lit
        * la déclaration « sans valeurs nutritionnelles » — le seul signal que cette base renseigne
        * (`typeComposant` vaut `INDETERMINE` presque partout). Une denrée dont la fiche porte
        * `isAlimentaire = false` atterrit donc ici : mesuré sur le chutney, les graines de moutarde.
        * Nommer le critère envoie corriger la fiche, plutôt que chercher une erreur de calcul.
        */
      emballages_critere: "Reconnus à leur déclaration « sans valeurs nutritionnelles » sur la fiche article — c'est là que se corrige un composant mal classé.",
      replier: "Replier",
    },
    mo: {
      etape: "Étape",
      col_cout: "MO €/u PF",
      col_heures: "Heures",
      col_pers: "Pers.",
      col_cadence: "Cadence",
      total: "Total",
      // ⚠️ « À chiffrer » et non « 0 € » : une main-d'œuvre non chiffrée n'est pas une main-d'œuvre nulle.
      a_chiffrer: "à chiffrer",
      cout_unitaire: "Coût",
      par_unite: "par unité de produit fini",
      /*
       * ⚠️ **« Pour le lot » était FAUX** : ce montant vaut `coût unitaire × quantité obtenue`, et la
       * carte affiche par ailleurs un LOT DE PRODUCTION qui n'a rien à voir. Deux « lots » à l'écran,
       * dont l'un peut valoir 1 et l'autre 3 000 : le chiffre se lisait comme le coût du lancement.
       */
      cout_lot: "Pour la quantité obtenue",
      /*
       * ⚠️ **Le temps d'OCCUPATION de la ligne** — la grandeur qui décide d'un planning, et la seule que
       * « 0,0015765 h par unité » ne permettait pas de lire. Les deux durées sont dites parce qu'elles
       * diffèrent dès qu'un jeu déclare plus d'un opérateur.
       */
      temps_ligne: "Temps de production prévu",
      temps_ligne_machine: "sur la ligne",
      temps_ligne_lot: "pour un lot de #lot",
      col_temps_ligne: "Temps ligne",
      par_ligne_titre: "Occupation par ligne de production",
      /*
       * ⚠️ **La phrase dit pourquoi le total du bandeau n'est PAS une durée.** Sans elle, deux chiffres
       * cohabitent — 12,98 h en tête, 8,25 + 4,73 ici — et rien n'explique qu'ils répondent à deux
       * questions différentes : ce que ça coûte en atelier, et combien de temps chaque ligne est prise.
       */
      par_ligne_aide:
        "Deux étapes sur la même ligne s'additionnent — elle n'en fait qu'une à la fois. Sur deux lignes, elles tournent en parallèle.",
      /*
       * ⚠️ **Le nombre de LIGNES est dans le libellé** (A51) : « 5,53 h sur la ligne » sur un projet à
       * deux étapes se lisait comme le temps d'une seule — c'est le défaut signalé le 12/09/2026.
       */
      temps_ligne_total: "au total sur #nb lignes",
      /*
       * ⚠️ **Un total partiel ne s'affiche pas** : il serait plus petit que le vrai et se lirait comme
       * un fait. On nomme ce qui l'empêche, et le tableau ci-dessous montre étape par étape ce qui est
       * connu.
       */
      temps_ligne_partiel: "#nb étape(s) n'annoncent pas de temps de ligne — voir le détail",
      /*
       * ⚠️ **Le dénominateur est DANS le libellé** (A51) : juste sous cette ligne s'affiche désormais un
       * temps de production « sur la ligne », en heures lui aussi. « Heures 0,0015765 h » et « 4,73 h »
       * l'un au-dessus de l'autre, sans dire de quoi chacun est l'heure, se lisaient comme une
       * incohérence.
       */
      heures: "Heures par unité",
      calcul: "Mode de calcul",
      calcul_multiple: "#nb modes différents — voir le détail par étape",
      // ⚠️ Dit la CONSÉQUENCE, pas seulement le manque : c'est elle qui explique la ligne du dessous.
      sans_heures: "aucune — cette recette n'entre pas dans la marge horaire",
      marge_titre: "Marge par heure de production",
      /*
       * ⚠️ **Les deux TERMES de la marge horaire**, demandés à l'écran : 186 €/h peut sortir d'un CA de
       * 200 € sur 14 € de coût, ou de 460 € sur 274 €. Deux ateliers très différents, et c'est le second
       * chiffre qui dit s'il reste de la place pour négocier.
       */
      ca_horaire: "Chiffre d'affaires",
      cout_horaire: "Coût complet (matière + MO)",
      // ⚠️ Dire CE QUI MANQUE : « non calculable » n'indique pas quoi faire, et les deux causes ne se
      // corrigent pas au même endroit.
      /*
       * ⚠️ **Le message NOMME le geste et son emplacement.** « Renseignez-le » n'indiquait pas où : le
       * prix de vente cible ne se saisit pas sur cette carte, et le bandeau affichait par ailleurs un
       * prix PROPOSÉ — d'où la contradiction lue à l'écran le 12/09/2026 (« on a bien un prix de vente
       * et une marge »). Le bouton « Retenir ce prix », sous la simulation, est exactement ce qui
       * transforme la proposition en cible.
       */
      /*
       * ⚠️ **Le geste a changé avec la disparition de l'objectif.** Le message envoyait « Retenir ce
       * prix » — le bouton n'existe plus. La marge par heure se calcule désormais sur le prix en vigueur,
       * donc cette branche ne se lève plus que si AUCUN prix n'est calculable : il manque le coût de
       * revient, et c'est lui qu'il faut nommer.
       */
      marge_sans_prix:
        "Pas encore calculable : aucun prix de vente n'est disponible, faute de coût de revient complet.",
      marge_sans_heures:
        "Pas encore calculable : aucune étape ne déclare d'heures. Choisissez une ligne de production ou des paramètres d'atelier sur au moins une étape fabriquée.",
      exclus_resume: "#n étape(s) n'entrent pas dans ce calcul",
      // ⚠️ Les deux motifs ne se corrigent pas pareil : l'un se remplace, l'autre se chiffre.
      motif_TIER_SANS_HEURES: "Leur mode de calcul donne un coût, pas des heures :",
      motif_NON_CHIFFRE: "Leur main-d'œuvre n'est pas chiffrée :",
      // ⚠️ L'ancien message — « Renseignez une quantité de production » — désignait un champ qui n'existe
      // sur aucun écran (0 des 24 essais en base en portait une). Il nomme désormais le geste réel :
      // rattacher un calcul de main-d'œuvre au produit fabriqué, depuis l'arbre ou le graphe.
      vide: "Aucune main-d'œuvre chiffrée : utilisez « Modifier la main-d'œuvre » sur le produit fabriqué, dans l'arbre des coûts.",
      // ⚠️ La règle est écrite SUR le bouton, pas seulement en infobulle : c'est la seule raison pour
      // laquelle on ose cliquer sans aller relire ensuite les étapes déjà décidées.
      //
      // ⚠️ **« peut-être » est parti, et ce n'est pas un allègement de style.** L'intro disait « l'atelier
      // fabrique PEUT-ÊTRE déjà ces produits » parce que l'écran l'ignorait : il comptait les étapes
      // vierges sans savoir si la production en portait le coût. Le coup d'essai serveur répond, donc la
      // phrase affirme — et le compte est celui des étapes réellement reprenables, pas des étapes vides.
      rattrapage_intro:
        "#n étape(s) sans main-d'œuvre ont déjà été fabriquée(s) : leur coût observé est disponible.",
      rattrapage_cta: "Reprendre celle observée en production",
      // ⚠️ Le CONSTAT qui remplace le bouton quand il n'y a rien à reprendre. Il était un refus au clic
      // (`rattrapage_rien`, ci-dessous) : le geste était offert, on cliquait, et on apprenait qu'il ne
      // servait à rien. Il nomme donc ce qui manque et où le saisir, à la place du bouton.
      rattrapage_aucune_observation:
        "#n étape(s) n'ont aucune main-d'œuvre, et n'ont jamais été fabriquée(s) : rien à reprendre de la production. Chiffrez-les depuis « Modifier la main-d'œuvre ».",
      // Les trois nombres se disent ensemble : « 3 posées » seul laisserait croire que le reste va bien.
      rattrapage_fait: "Main-d'œuvre observée enregistrée sur #n étape(s).",
      rattrapage_reste: "#n étape(s) restent sans observation : elles n'ont jamais été fabriquées.",
      // ⚠️ CONSERVÉ, et ce n'est pas un doublon du constat ci-dessus : le coup d'essai n'est pas un
      // verrou. De nouveaux lots — ou une étape chiffrée dans un autre onglet — peuvent changer la
      // réponse entre l'affichage du bouton et le clic. Dernier recours, jamais le chemin normal.
      rattrapage_rien:
        "Aucune de ces étapes n'a été fabriquée : rien à reprendre. Chiffrez-les depuis « Modifier la main-d'œuvre ».",
    },
    rapport: {
      retenu: "Rendement enregistré sur l’étape.",
      retenir: "Retenir cette valeur",
      export_refuse: "À trancher avant de sortir une fiche technique : un point bloquant subsiste",
      cible_introuvable: "Ce point ne renvoie plus à rien dans cet essai : la ligne ou l'étape visée a été retirée depuis le calcul du rapport. Recalculez-le.",
      escalade: "Sévérité relevée : poids du composant",
      ouvrir: "Ouvrir",
      vide: "Rien à signaler.",
    },
  },
  audit_bio: {
    title: "Audit BIO",
    run: "Lancer l'audit",
    empty: "Sélectionnez un article et une période, puis lancez l'audit.",
  },
  tracabilite: {
    lot_unique_display:
      "Attention : le stock de cet article n’étant pas suivi par lot, le graphique ci-dessous est construit uniquement à partir des traçabilités saisies sur les différents OF liés à ce lot. Il donne une vue des liens entre lots et OF, mais ne reflète pas précisément les mouvements réels de consommation ni les quantités exactes utilisées.",
    tips_form:
      "Vous pouvez sélectionner dès maintenant l’article dont vous souhaitez assurer la traçabilité.\n" +
      "\n" +
      "Si vous choisissez un article ici : il sera automatiquement utilisé lors de la saisie de la traçabilité.\n" +
      "\n" +
      "Si vous ne sélectionnez rien : vous devrez choisir l’article à tracer au moment de la saisie.\n" +
      "\n" +
      "👉 Cela vous laisse la flexibilité d’anticiper votre choix ou de le faire plus tard, selon vos besoins.",
  },
  planifComposant: {
    composants: "Composants",
    stockActuel: "Stock à date",
    avecProvisoire: "Avec commandes provisoires",
    weekEvents: "Événements de la semaine",
    dayEvents: "Événements du jour",
    commandes: "Commandes",
    commandesProvisoires: "Commandes provisoires",
    missingSeuil: "Seuil(s) non renseignés",
    displayModeFlux: "Mode d'affichage en flux",
    displayModeChart: "Mode d'affichage en graphique",
    editStock: "Editer les paramètres de stock",
    showStock: "Voir les stocks",
    createCommande: "Commander",
    showCommandes: "Voir les commandes",
    articlesWithStock: "Articles gérés en stock",
    articlesWithNoStock: "Articles non gérés en stock",
    articlesWithoutEvent: "Articles sans mouvements à venir",
    articlesWithEvent: "Articles avec mouvements à venir",
    articlesOnlyReappro: "Articles à réapprovisionner",
    triAlpha: "A-Z",
    triEvent: `Évenement`,
    priority: "Priorité",
    modeCommande: "Commande",
    modePlanif: "Planification",
    noStock: "Pas de stock",
    withoutStock: "Article non géré en stock",
    stock: "Stock",
    cumul: "Solde des mouvements prévues au",
    enRetard: "En retard",
    enRetardTooltip:
      "Livraison prévue dépassée — commande toujours ouverte, entrée attendue au plus tôt maintenant",
    sansDate: "#nb cmd sans date",
    sansDateTooltip:
      "Commande(s) sans date de livraison prévue — non positionnable(s) sur la timeline et hors calcul du stock prévisionnel",
    masques: "#nb masqués par les filtres",
    masquesTooltip:
      "Des articles sont écartés par les filtres actifs (par défaut : seuls les articles gérés en stock AVEC mouvements à venir s'affichent). Cliquer pour tout afficher.",
    noEventWeek: "Aucun événement prévu pour cette semaine",
    noEventDay: "Aucun événement prévu pour ce jour",
    cancelCommandes:
      "Vous êtes sur le point supprimer votre brouillon de commande. Confirmez-vous ?",
    cancelCommandes_plural:
      "Vous êtes sur le point supprimer vos #nb brouillons de commande. Confirmez-vous ?",
    events: {
      expedition: "Expédition",
      commandesClient: "Commandes client:",
      commandes: "Commandes fournisseur:",
      ofsConso: "Consommé dans:",
      ofsProd: "Produit par:",
      reception: "Réception",
      prod: "Prod.",
      conso: "Conso.",
      expeditionShort: "E",
      receptionShort: "R",
      prodShort: "P",
      consoShort: "C",
      prevu: "Réception",
      prevuShort: "R",
    },
  },
  planif: {
    performance: "Coefficient de performance",
    addTacheCustom: "Ajouter une tâche",
    addCloseCreneau: "Ajouter une fermeture",
    dateDebut: "Date de début",
    dateFin: "Date de fin",
    customLigneName: "Catégorie",
    tacheLibelle: "Libellé",
    affichage: "Affichage",
    eventTypes: "Type d'évènements",
    others: "Autres filtres",
    ligneIncompatible: "Paramétrage de ligne incompatible",
    deployLigne: "Veuillez déployer cette ligne pour déposer le créneau",
    of: {
      enCours: "OF en cours",
      planify: "Planifier un OF",
      start: "Démarrer un OF",
      startButton: "Démarrer",
      planifyButton: "Planifier",
      prevu: "Prochain OF prévu",
      suivants: "OF(s) suivant(s)",
      select: "Sélectionner un OF",
      quantity_needed:
        "Pour effectuer certains calculs pendant le procédé, la spécification nécessite la quantité prévue.",
    },
    filter: {
      afficherHoraires: "Horaires d'ouverture",
      afficherFermeture: "Fermetures ponctuelles",
      hideLigneInactive: "Masquer les lignes inactive",
      ligne: "Lignes",
      famille: "Famille technique",
      displayDays: "Echelle",
      cacheWeeks: "Prévisionnel",
      articleOrder: "Articles commandés",
      articleConso: "Articles consommés",
      articleProd: "Articles produits",
      articleExpe: "Article expédiés",
      fab: "Articles fabriqués",
      nonFab: "Articles non fabriqués",
      fabNonFab: "Articles fabriqués et non fabriqués",
      yes: "Oui",
      no: "Non",
      all: "Tous",
      fabLabel: "Article fabriqué",
      stockLabel: "Géré en stock",
      eventLabel: "Mouvements prévus",
    },
  },
  peo: {
    start: "Démarrer les contrôles récurrents",
    stop: "Terminer les contrôles récurrents",
    restart: "Relancer les contrôles récurrents",
  },
  commandeClient: {
    acompte_facture: "Facture d'acompte",
    acompte_en_redaction: "en rédaction",
    acompte_regle_le: "Acompte réglé le",
    acompte_reglement: "Règlement",
    acompte_non_regle: "en attente",
    acompte_partiellement_regle: "partiel",
    acompte_aide_creation: "Le montant s'affichera dans le récapitulatif dès la première ligne saisie.",
    acompte_emettre_facture: "Émettre la facture d'acompte",
    acompte_facture_creee: "Facture d'acompte créée en rédaction — relisez-la puis figez-la depuis les factures.",
    acompte: "Acompte",
    acompte_valeur_pourcentage: "Acompte (% du total TTC)",
    acompte_valeur_montant: "Acompte (€ TTC)",
    acompte_echeance: "À régler avant le",
    acompte_a_verser: "Acompte à verser",
    acompte_solde: "Solde à la livraison",
    acompte_reste: "Reste à payer",
    acompte_divergence: "La commande a changé depuis l'émission : la condition vaudrait #montant aujourd'hui",
    acompte_reliquat_sous_livraison: "⚠ Commande clôturée avec #montant € TTC d'acompte jamais déduits. Le client a payé d'avance une marchandise qu'il ne recevra pas.",
    acompte_solde_sans_deduction: "⚠ Une facture de solde a été émise sans déduire cet acompte. Le client le paierait deux fois : régularisez par un avoir.",
    acompte_reste_du_annulee: "Cette commande est annulée mais sa facture d'acompte reste due.",
    acompte_creer_avoir: "Créer l'avoir de régularisation",
    annulation_acompte_reste: "⚠ La facture d'acompte #numero (#montant € TTC) restera due. Vous pourrez en créer l'avoir juste après.",
    avoir_acompte_titre: "Régulariser l'acompte",
    avoir_acompte_message: "La facture d'acompte #numero (#montant € TTC) reste due sur une commande annulée. Créer l'avoir de régularisation ?",
    avoir_acompte_cree: "Avoir d'acompte créé, en rédaction — il reste à le figer.",
    acompte_assiette: "Sur un total de",
    acompte_superieur_total: "L'acompte dépasse le total de la commande.",
    acompte_aide: "L'acompte est annoncé sur la proforma et la confirmation de commande. La facture d'acompte s'émet ensuite, séparément.",
    add_acompte: "Demander un acompte",
    edit_acompte: "Modifier l'acompte",
    associate_of: "Lier un OF",
    ref_client: "Réf. client",
    ligne_pending_chip: "Article non synchronisé",
    ligne_pending_tooltip:
      "Cet article de la boutique n'est pas encore rapproché — la ligne sera complétée automatiquement dès qu'il le sera.",
    plus_de_parametres:
      "Plus de paramètres (entrepôt, date d'expédition, créneau)",
    entrepot_optionnel_hint:
      "Optionnel. Sans entrepôt assigné, la commande est visible par tous et peut puiser dans le stock de n'importe quel entrepôt.",
    eclatement_multi_entrepots:
      "Lignes éclatées sur plusieurs entrepôts (stock insuffisant dans un seul)",
    eclatement_multi_entrepots_hint:
      "L'auto-affectation a réparti les lots sur plusieurs entrepôts car aucun seul entrepôt ne couvrait l'intégralité des lignes. Coordonnez la prépa pour éviter les transferts.",
    eclatement_ligne: "Éclatée multi-entrepôts",
    eclatement_ligne_hint:
      "Cette ligne a été couverte par des lots de plusieurs entrepôts. Vérifie la faisabilité opérationnelle (transferts, équipes).",
    tous_entrepots_assignes:
      "Tous les entrepôts disponibles sont déjà assignés à cette commande.",
    aucun_entrepot_mode_global:
      "Aucun entrepôt assigné — mode global (visible par tous, stock puisé partout).",
    aucun_entrepot_mais_ue:
      "Pas d'entrepôt assigné explicitement. Les entrepôts ci-dessus sont détectés via les unités d'expédition (palettes) ou les réservations actives.",
    date_expedition_auto_hint:
      "Préremplie avec la valeur calculée (livraison − délai logistique). Modifier le champ pour forcer une autre date.",
    date_expedition_overridden_hint:
      "Date saisie manuellement (override). Cliquer sur Réinitialiser pour revenir au calcul automatique.",
    commentaires_et_pj: "Commentaires & pièces jointes",
    entrepots_detectes_ue_label: "Détectés (UE / réservation) :",
    entrepot_ue_tooltip:
      "Entrepôt utilisé par une UE (palette) liée à cette commande ou par une réservation active en zone-prepa, mais pas assigné explicitement.",
    entrepot_ue_tooltip_promote:
      "Entrepôt détecté via une UE liée ou une réservation active. Cliquer pour le promouvoir en entrepôt assigné explicitement.",
    include_sans_entrepot: "Inclure CC sans entrepôt",
    include_sans_entrepot_hint:
      "Les commandes sans entrepôt assigné sont visibles par tous (mode global). Décocher pour ne voir que les CC strictement assignées aux entrepôts sélectionnés.",
    entrepots_assignes: "Entrepôts assignés",
    aucun_entrepot_disponible:
      "Aucun entrepôt disponible. Crée des adresses d'entrepôt dans la fiche société.",
    livraison_sans_expedition: "Gardé en stock",
    livraison_directe_desc:
      "Facturez la marchandise au client sans expédition physique : les produits restent en stock, seul le propriétaire change. À distinguer de « Livraison directe (retrait) » qui sort réellement le stock de l'entrepôt.",
    desannuler: "Désannuler",
    confirmer: "Confirmer la commande",
    marquer_terminee: "Marquer la commande terminée",
    reopen: "Remettre la commande en cours",
    etat_actions_tooltip: "Actions sur l'état de la commande",
    confirmer_commande: "Confirmer la commande",
    confirmer_sans_envoi: "Confirmer sans envoi",
    confirmer_avec_mail: "Confirmer avec un mail client",
    envoyer_confirmation: "Envoyer la confirmation au client",
    renvoyer_confirmation: "Renvoyer la confirmation",
    confirmation_envoyee_le: "Confirmation envoyée le {date}",
    confirmation_envoyee_sans_date: "Confirmation déjà envoyée au client",
    envoyer: "Envoyer",
    etats_commande: "États de la commande",
    etats_expedition: "État expédition",
    etats_facturation: "État facturation",
    etats_preparation: "Préparation",
    etats_acompte: "Acompte",
    mode_livraison: "Mode de livraison par défaut",
    mode_livraison_hint:
      "Pré-sélectionne le mode dans le préparateur. Modifiable par lot lors de la préparation.",
    mode_expedition: "Avec expédition",
    mode_directe: "Livraison directe",
    mode_garde_stock: "Gardé en stock",
    slot_horaire_enlevement: "Heure de passage du transporteur",
    slot_horaire_hint:
      "Heure à laquelle le transporteur vient récupérer la marchandise (optionnel)",
    slot_horaire_format_error: "Format invalide (attendu : HH:mm)",
    slot_horaire_tooltip_title: "À quoi sert ce champ ?",
    slot_horaire_tooltip_content:
      "Heure à laquelle le transporteur passe récupérer les marchandises. Trois usages : trier les CC du jour par ordre d'enlèvement dans la vue Pilotage et Supervision Prépa, et imprimer l'heure cible sur la fiche picking remise au préparateur.",
    slot_horaire_tooltip_example:
      "Exemple : une CC à 08:00 sera affichée avant une CC à 14:00 dans la liste du jour. Si le champ est vide, la CC est triée en fin de liste.",
    date_expedition_tooltip_title: "Date d'expédition prévue",
    date_expedition_tooltip_content:
      "Date à laquelle les marchandises doivent partir de l'entrepôt pour être livrées à temps. Préremplie avec la valeur calculée automatiquement (date de livraison − délai logistique en jours ouvrés). Tu peux la modifier pour forcer une autre date — elle sera marquée comme override manuel. Vide la case pour revenir au calcul automatique.",
    date_expedition_tooltip_example:
      "Exemple : livraison prévue le 20/06, délai logistique = 3 jours ouvrés → date d'expédition calculée = 17/06.",
    remise_pied: "Remise pied de commande",
    remise_pied_type: "Type de remise pied",
    remise_pied_label: "Libellé de la remise",
    remise_pied_label_placeholder: "Ex: Remise fidélité, Promo été...",
    total_avant_remise_pied: "Total HT avant remise pied",
    montant_remise_pied: "Montant remise pied",
    total_apres_remise_pied: "Total HT après remise pied",
    delivery_overdue: "En retard",
    delivery_urgent: "Urgent",
    delivery_on_time: "À l'heure",
    delivery_planned_date: "Livraison prévue",
    delivery_first_date: "Première livraison",
    delivery_delta: "Écart",
    delivery_remaining: "Reste",
    delivery_days: "jours",
    preparation_commande: "Préparation de commande",
    date_livraison_ligne: "Date de livraison de cette ligne",
    date_livraison_ligne_heritee:
      "Hérite de la date commande : #date. Saisir une date pour cette ligne uniquement si elle diffère.",
    date_livraison_ligne_specifique:
      "Date spécifique à cette ligne (override de la date commande).",
    date_livraison_ligne_hint:
      "Laisser vide pour hériter de la date commande, ou saisir une date spécifique (cas frais + secs dans une même CC).",
    date_livraison_ligne_reset: "Réinitialiser",
    date_livraison_ligne_reset_tooltip:
      "Réinitialiser la date à celle de la commande (héritage)",
    date_expedition_prevue: "Date d'expédition prévue",
    date_expedition_prevue_info:
      "Date à laquelle les marchandises doivent partir. Calculée automatiquement à partir de la date de livraison et du délai logistique en jours ouvrés.",
    date_expedition_prevue_overridden:
      "Date d'expédition modifiée manuellement",
    date_expedition_prevue_reset_auto:
      "Recalculer automatiquement (livraison − délai)",
    date_expedition_prevue_lignes_heterogenes:
      "Plusieurs dates expé selon les lignes. Première à partir : #date.",
    date_livraison_ligne_badge: "📅 Date spécifique",
    frais_port: "Frais de port",
    frais_port_fige: "Frais de port figé (synchronisé depuis le connecteur)",
    frais_port_montant: "Montant HT",
    frais_port_tva: "TVA appliquée",
    frais_port_tva_none: "Aucune TVA",
    frais_port_mode: "Mode de calcul",
    frais_port_auto:
      "Les frais de port seront calculés automatiquement selon le barème du transporteur.",
    bareme_aucun_transporteur: "Aucun transporteur assigné à cette commande.",
    bareme_aucun_bareme: "Aucun barème configuré pour ce transporteur.",
    bareme_hors_tranches:
      "Le montant de la commande est hors des tranches du barème — aucun tarif applicable.",
    bareme_surcharge_zone:
      "Des surcharges par zone géographique peuvent s'appliquer.",
    add_frais_port: "Ajouter des frais de port",
    edit_frais_port: "Modifier les frais de port",
    prix_source_negocie: "Tarif négocié",
    prix_source_catalogue: "Catalogue",
    prix_source_manuel: "Manuel",
    multiple_info: "Multiple de #multiple #unite",
    multiple_ok: "#factor × #multiple #unite",
    multiple_hint: "Multiple de #multiple #unite — arrondir à :",
    multiple_keep: "garder #qty",
    pu_brut_ht: "PU brut HT",
    pu_net_ht: "PU net HT",
    pu_ht: "PU HT",
    poids_col: "Poids",
    poids_brut_prevu: "Poids brut prévu",
    poids_edit_tooltip:
      "Poids brut prévu = quantité × poids brut unitaire de la fiche article. Cliquer pour modifier le poids de l'article.",
    poids_manquant_tooltip:
      "Aucun poids brut renseigné sur la fiche article — cliquer pour le renseigner.",
    poids_total_tooltip:
      "Poids brut marchandise prévu (quantités × poids brut unitaire des articles), hors tare des unités d'expédition.",
    poids_total_incomplet: "#count article(s) sans poids renseigné",
    poids_hint_brut: "≈ #poids kg brut",
    remise_col: "Remise",
    montant_ht: "Montant HT",
    add_remise_pied: "Ajouter une remise",
    edit_remise_pied: "Modifier la remise",
    remise_pied_pct_label: "Pourcentage de remise",
    remise_pied_eur_label: "Montant de remise (€ HT)",
    remise_pied_pct_hint: "Appliqué sur le total HT",
    remise_pied_eur_hint: "Déduit du total HT",
    expeditions_title: "Expéditions",
    no_expeditions: "Aucune expédition pour cette ligne",
    aide_table:
      "Chaque ligne représente un article commandé. La quantité s'affiche en unité client et unité article. Le total tient compte des remises appliquées.",
    aide_prix:
      "Prix unitaire HT affiché en unité de gestion et unité article. Si des remises s'appliquent, le prix catalogue est barré et le prix remisé en gras.",
    aide_recap:
      "Total HT = somme des lignes. TVA ventilée par taux. TTC = HT + TVA.",
    aide_remise_pied:
      "Remise globale sur le total HT de la commande. Peut être en pourcentage ou en montant fixe.",
    prix_libre: "Prix libre",
    tarif_repli_aujourdhui:
      "Tarifé au jour même (aucune date de livraison). Au #date, le tarif passe à #prix €.",
    tarif_divergent:
      "La grille indique #prix € au #date. Le prix de la ligne n'a pas été recalculé.",
    tarif_appliquer: "Appliquer ce tarif",
    select_article_hint: "Recherchez et sélectionnez un article pour commencer",
    article_filter_tiers: "Articles du tiers",
    article_filter_all: "Tous les articles",
    article_filter_hint: "⋯ pour voir tous les articles",
    article_label_tiers: "Article (prix du tiers)",
    article_label_all: "Article (tous)",
    // Historique livraisons
    hist_no_deliveries: "Aucune livraison pour cette commande",
    hist_bl_singular: "bon de livraison",
    hist_bl_plural: "bons de livraison",
    hist_expe_singular: "expédition",
    hist_expe_plural: "expéditions",
    hist_article: "Article",
    hist_lot: "Lot",
    hist_dluo: "DDM",
    hist_quantite: "Quantité",
    hist_expediteur: "Expéditeur",
    hist_palette: "Unité d'expédition",
    hist_palette_singular: "unité d'expédition",
    hist_palette_plural: "unités d'expédition",
    hist_print_bl: "BL",
    hist_facture: "Facture",
    hist_no_lot: "Sans lot",
    hist_numero_expedition: "N° Expé",
    hist_poids: "Poids brut",
    hist_total_bl: "Total BL :",
    hist_articles_total: "articles",
    hist_vrac: "Expéditions vrac",
    units_section_title: "Unités d'expédition",
    enlevement_section_title: "En attente d'enlèvement",
    enlevement_hint:
      "Marchandises facturées et gardées en stock (déjà vendues, en dépôt). Sélectionnez-les pour générer un bon d'enlèvement lors de leur sortie physique (aucune nouvelle facture).",
    enlevement_item_sans_lot: "Marchandise gardée en stock",
    btn_generer_bon_enlevement: "Générer le bon d'enlèvement",
    adresse_livraison: "Adresse de livraison",
    adresse_facturation: "Adresse de facturation",
    adresse_livraison_facturation: "Adresse de livraison & facturation",
    garde_stock_staged_ok:
      "Lot(s) mis en attente de facturation (gardé en stock)",
    garde_stock_valide_ok: "Gardé en stock facturé et validé",
    garde_stock_retire_ok: "Lot retiré de l'attente",
  },
  widget: {
    add: "Ajouter une widget",
    saveDashboard: "Sauvegarder le tableau de bord",
    production: "Production",
    financier: "Financier",
    of: "OF",
    commandeFournisseur: "Commande Fournisseur",
    commandeClient: "Commande Client",
    specif: "Specification",
    list: "Listes",
    stopEdition: "Quitter le mode edition",
    stock: "Stock",
    select: "Sélectionner un Widget",
    dluoDepassee: "DDM dépassée",
    dateValidation: "Date de validation",
    dateRedaction: "Date de rédaction",

    PRODUCTIVITE_MOYENNE: "Productivité moyenne",
    OF_EN_COURS: "OFs en cours",
    HEURES_TRAVAILLES: "Heures travaillées",
    ARTICLES_PAR_FAMILLE: "Articles par famille",
    QUANTITE_OF: "Quantité OF",
    CADENCE_REELLE: "Cadence réelle",
    //financier
    CA_CUMULE: "CA cumulé",
    CA_PAR_PERIODE: "CA par période",
    CA_DELTA: "CA delta",
    REPARTITION_PAR_CLIENT: "Répartition par client",
    REPARTITION_PAR_FAMILLE: "Répartition par famille",
    CA_N_MOINS_1: "CA année précédente",
    //stocks
    VALEUR_STOCK: "Valeur du stock",
    LOT_PAR_ETAT: "Lot par état",
    VALEUR_PAR_FAMILLE: "Valeur par famille",
    ETAT_STOCK: "État du stock",
    NB_LOT_DLC: "Nombre de lot en DLC",
    NB_COMMANDE_FOURNISSEUR: "Nombre de commande fournisseur",
    NB_COMMANDE_CLIENT: "Nombre de commande client",
    MOUVEMENTS: "Mouvements de stock",

    //subType
    OF_ATTENTE_REINTEGRATION: "Attente réintégration",
    OF_ATTENTE_PDC: "Attente pied de chaine",
    //commandeClient
    COMMANDE_CLIENT_EN_COURS_DE_TRAITEMENT:
      "Commandes client en cours de traitement",
    COMMANDE_CLIENT_EN_COURS_DE_PREPARATION:
      "Commandes client en cours de préparation",
    COMMANDE_CLIENT_PRETE: "Prête",
    COMMANDE_FOURNISSEUR_EN_ATTENTE_ENVOI:
      "Commande fournisseur en attente envoi",
    COMMANDE_FOURNISSEUR_EN_ATTENTE_CONFIRMATION:
      "Commandes fournisseur en attente confirmation",
    COMMANDE_FOURNISSEUR_EN_ATTENTE_LIVRAISON:
      "Commandes fournisseur en attente de livraison",
    COMMANDE_FOURNISSEUR_LIVREE_PARTIELLEMENT:
      "Commandes fournisseur livrée partiellement",
    SPECIFICATION_REDIGEE: "Spécifications rédigées",
    SPECIFICATION_VALIDEE: "Sécifications validées",
  },
  reinte: {
    recap: "Récap.",
    edit_pdc: "Modifier le PDC",

    goPageReinte: "Affiner le bilan matière",
    finProduction: "Fin de production",
    saisirQuantitePourVoirBilan:
      "Saisissez une quantité pour afficher le bilan matière",

    articlesConsommationManuelle: "Articles en consommation déclarée",
    articlesConsommationTheorique: "Articles en consommation théorique",
    consoTheoriqueUnitaire: "Conso. théorique unitaire",
    consoTheorique: "Conso. théorique",
    consoReelle: "Conso. réelle",
    ecart: "Écart",

    bilanOk: "Bilan OK",
    ecartDetecte: "Écart détecté",
    ecartImportant: "Écart important",

    bilanPrecis: "Consommations précises",
    bilanPrecisDescription:
      "Les consommations déclarées correspondent aux quantités théoriques. Aucune action requise.",

    ecartAVerifier: "Consommations à vérifier",
    ecartAVerifierDescription:
      "Certaines consommations s'écartent légèrement des quantités théoriques. Vous pouvez les corriger si nécessaire.",

    ecartImportantTitre: "Écarts importants détectés",
    ecartImportantDescription:
      "Les consommations déclarées sont significativement différentes des quantités théoriques. Vérifiez les saisies consommations.",

    corrigerConsommations: "Corriger les consommations",

    calculAuto: "Calcul auto",
    articlesTheoriquesInfo:
      "Ces articles sont consommés automatiquement selon la nomenclature. Aucune déclaration de saisie n'est requise.",
    consommationTotaleTheorique: "Consommation totale théorique",

    resumeConsommations: "Résumé des consommations",
    imprimerBilan: "Imprimer le bilan",
    quantiteRestanteAIntegrer: "Quantité restante à intégrer",
    quantiteIntegreeEnCours: "Quantité intégrée en cours de fabrication",
    quantiteTotaleProduiteSurOf: "Quantité totale produite sur l'OF",
    xEnregistrements: "enregistrement(s)",
    modifierInfosReinte: "Modifier les informations",
    quantiteDepasse:
      "La quantité saisie dépasse la quantité disponible sur la ligne",
    quantiteIncoherente: "Quantité incohérente",
    pfSurLigneCalcule: "PF disponible sur la ligne",
    aideQuantiteDepassee:
      "La quantité saisie est supérieure à la quantité de produits finis réellement présents sur la ligne. Veuillez corriger la valeur pour pouvoir valider.",
    aideQuantiteRestante:
      "Quantité produite restant sur la ligne de production, en attente d'être réintégrée en stock.",
    aideQuantiteIntegree:
      "Somme des quantités déjà transférées du pied de chaîne vers le stock au cours de la fabrication de cet OF.",
    aideQuantiteTotale:
      "Total de la production sur l'OF : quantité restante à intégrer + quantité déjà intégrée en cours de fabrication.",
    dateFinProduction: "Date de fin de production",
    quantite: "Quantité",
    date: "Date",
    article: "Article",
    articles: "articles",
    etat: "État",
    total: "Total",
    consommesAuTotal: "consommés au total",
    validerFinProduction: "Valider fin production",
    aideSaisieConsos: "Aide à la saisie des consommations",
    saisieConso: "Saisie de la consommation",
    saisirConsos: "Saisir consos",
    lotsSaisis: "lots saisis",
    saisi: "Saisi",
    aSaisir: "À saisir",
    // aSaisirOpe: "",
    // aSaisirReinte: "Veuillez saisir l",
    aucunLotPourCetArticle:
      "Aucun lot trouvé pour cet article dans le bilan de réintégration.",
    sauvegarderProgression: "Sauvegarder la progression",
    validerEtFermer: "Valider et fermer",
  },
  help: {
    stockReappro:
      "Seuil en dessous duquel une commande d'achat est recommandée. Doit être supérieur ou égal au stock de sécurité.",
    stockSecurite:
      "Stock minimum à toujours conserver. Ne peut pas être supérieur au stock de réapprovisionnement.",
    quantiteAchat:
      "Quantité par défaut proposée lors de la création d'une commande d'achat pour cet article.",
    net_weight_uc:
      "Poids net d'une unité de consommation (UC) en kg. Cette valeur est utilisée pour calculer automatiquement le poids net total des unités d'expédition.",
    gross_weight_uc_etiq:
      "Poids brut d'une unité de gestion (emballage inclus) en kg, tel que renseigné dans la fiche article. " +
      "Il est multiplié par la quantité en unités de gestion pour calculer le poids brut total affiché sur l'étiquette et encodé dans le code-barres. " +
      "Ne pas inclure le poids de l'unité d'expédition (palette, carton…).",
    weight_edit_article_warning:
      "Cette modification sera enregistrée définitivement dans la fiche article et s'appliquera à toutes les prochaines impressions d'étiquettes.",

    article:
      "Produit ou composant référencé dans l'entreprise, pouvant être fabriqué, acheté ou stocké. Il possède des caractéristiques précises (nom, code, unité de gestion, EAN, etc.).",

    tiers:
      "Entité extérieure avec laquelle l’entreprise interagit, comme un client, un fournisseur ou un sous-traitant.",

    specification:
      "Ensemble de critères définissant les caractéristiques attendues d’un article (nomenclature, procédé de fabrication, contrôles à réaliser, etc.). Sert de référence pour la fabrication et le contrôle qualité.",

    FAMILLE_ARTICLE:
      "Catégorie regroupant plusieurs articles aux caractéristiques similaires ou ayant une même finalité (ex : biscuits, sauces, emballages). Facilite l’organisation et la recherche des articles.",

    SOUS_FAMILLE_ARTICLE:
      "Catégorie regroupant les articles selon leurs spécificités de fabrication ou d’utilisation technique (ex : types de matières premières, procédés de transformation spécifiques). Permet de structurer les articles en fonction de leur compatibilité avec une ou plusieurs lignes de fabrication (cadence, nombre d'opérateurs, etc.).",

    TRAVAIL:
      "Libellé personnalisé permettant d’assigner des heures de travail à des opérations spécifiques (ex : nettoyage d’entrepôt, soutien administratif).",

    UNITE:
      "Unité de gestion personnalisée (ex : carton de 24 unités, bidon de 6L), basée sur une unité de base (Kg, Unitaire, Litre…) avec un coefficient de conversion associé.",

    OF: "Commande interne déclenchant la production d’un article, en précisant la quantité à fabriquer et la ligne de production assignée. Permet d’organiser, suivre et optimiser la fabrication.",

    ope: "Subdivision d’un Ordre de Fabrication (OF), permettant d’exécuter la production en plusieurs étapes. Correspond à une quantité spécifique fabriquée ou à une période définie de production.",

    controles_simplifie:
      "En mode classique, les contrôles sont regroupés par étapes de process (un step par étape, avec plusieurs contrôles dedans).\n" +
      "\n" +
      "En mode simplifié, chaque contrôle devient directement un step du stepper.\n" +
      "👉 L’opérateur enchaîne les contrôles un par un, sans passer par la notion d’étape.\n" +
      "\n" +
      "✅ Avantages : plus simple, plus fluide, moins de risque d’oubli.",
    customEnum: {
      FAMILLE_ARTICLE:
        "Permet de catégoriser les articles par famille (ex : composants, produits finis, emballages…).",
      SOUS_FAMILLE_ARTICLE:
        "Détermine sur quelle ligne un article peut être produit, chaque ligne ayant ses propres paramétrages adaptés aux sous-familles.",
      UNITE:
        "Désigne l’unité dans laquelle l’article est exprimé (ex : pièce, kilogramme, litre).",
      UNITE_PROD:
        "Représente l’unité spécifique à la production de l’article (ex : mètre pour des câbles).",
      EMPLACEMENT_STOCK:
        "Indique l’emplacement de stockage de l’article (ex : rayon A1, entrepôt B).",
      POSTE:
        "Représente le poste de l'utilisateur (ex : manager, opérateur, technicien).",
      TITRE:
        "Indique le titre ou la civilité de l'utilisateur (ex : Monsieur, Madame, Monsieur le Directeur).",
      TRAVAIL: "",
      ALLERGENE:
        "Les 14 allergènes à déclaration obligatoire (règlement INCO) et leurs espèces. Cochez-les sur la fiche article : l’étiquette les met en relief.",
    },

    mode_conso:
      "Ces règles s’appliquent au moment de clôturer un OF.\n" +
      "Ensuite, les consommations restent toujours modifiables dans la page de statistiques de l’OF.\n" +
      "\n" +
      "───────────────────────────\n" +
      "1) Article géré lot par lot\n" +
      "───────────────────────────\n" +
      "Si l’article est configuré avec « Détail des quantités en stock pour chaque lot »,\n" +
      "alors le mode de consommation est imposé :\n" +
      "\n" +
      "« Analyse précise des consommations (bilans matières / article suivi en pied de chaîne) »\n" +
      "\n" +
      "Pourquoi ?\n" +
      "Pour maintenir un stock exact lot par lot, il faut pouvoir rattacher chaque consommation à un lot.\n" +
      "\n" +
      "Exemple :\n" +
      "Sucre (Lot S-001, Lot S-002) → consommation : 30 kg sur S-001 + 20 kg sur S-002.\n" +
      "\n" +
      "───────────────────────────\n" +
      "2) Article non géré par lot\n" +
      "───────────────────────────\n" +
      "Deux modes sont possibles :\n" +
      "\n" +
      "A. Consommation théorique\n" +
      "→ JustUp enregistre la consommation calculée automatiquement.\n" +
      "Exemple : théorique = 100 kg → consommation retenue = 100 kg.\n" +
      "\n" +
      "B. Saisie facultative des consommations\n" +
      "→ Si rien n’est saisi : JustUp applique le théorique.\n" +
      "→ Si une consommation est saisie : elle devient prioritaire et remplace le théorique.\n" +
      "\n" +
      "Exemples :\n" +
      "• Théorique = 100 kg, saisi = 96 kg → consommation retenue = 96 kg\n" +
      "• Théorique = 100 kg, rien saisi → consommation retenue = 100 kg\n",
  },
  variables: {
    modelRedaction: "Modèle non validé",
    operator: "Operateur",
    text: "Texte",
    number: "Nombre",
    document: "Document",
    nomenclatureArticle: "Nomenclature",
    processOptional: "Process optionnel",
    optional: "Optionnel",
    articleDynamic: "Article paramétrable",
    paramDynamic: "Quantité paramétrable",
    do: "Réaliser",
    dontDo: "Ne pas réaliser",
    math_formula: "Formule numérique",
    text_formula: "Formule textuelle",
  },
  nutrient: {
    calories: "Calories",
    total_fat: "Lipides",
    saturated_fats_sub: "– dont acides gras saturés",
    trans_fats_sub: "– dont acides gras trans",
    omega3_sub: "– dont Oméga-3",
    omega6_sub: "– dont Oméga-6",
    monounsaturated_fats_sub: "– dont acides gras monoinsaturés",
    carbohydrates: "Glucides",
    dietary_fibre_sub: "– dont fibres alimentaires",
    soluble_fibre_sub: "– dont fibres solubles",
    insoluble_fibre_sub: "– dont fibres insolubles",
    sugars_sub: "– dont sucres",
    polyalcohols_sub: "– dont polyalcools",
    starch_sub: "– dont amidon",
    protein: "Protéines",
    cholesterol: "Cholestérol",
    sodium: "Sodium",
    /*
     * 🔴 **`nutrient.salt` manquait, et le SEL s'affichait « nutrient.salt (g) »** sur toute fiche
     * article alimentaire — à côté de trente-neuf constituants correctement traduits.
     *
     * ⚠️ **Le gate i18n ne pouvait pas l'attraper** : la clé est composée à l'exécution
     * (`'nutrient.' + code`), donc rien de littéral n'apparaît dans le source. C'est la limite
     * documentée de ce contrôle, et elle a un coût réel — ce défaut a été trouvé à l'œil.
     *
     * ⚠️ **Ce n'est pas un constituant secondaire** : c'est le SEL que l'étiquetage européen impose
     * de déclarer (INCO annexe XV), pas le sodium. Il figure sur presque toutes les étiquettes.
     */
    salt: "Sel",
    potassium: "Potassium",
    calcium: "Calcium",
    iron: "Fer",
    vitamin_a: "Vitamine A",
    vitamin_c: "Vitamine C",
    vitamin_d: "Vitamine D",
    vitamin_e: "Vitamine E",
    vitamin_k: "Vitamine K",
    thiamine: "Thiamine",
    riboflavin: "Riboflavine",
    niacin: "Niacine",
    vitamin_b6: "Vitamine B6",
    folate: "Folate",
    vitamin_b12: "Vitamine B12",
    biotin: "Biotine",
    pantothenate: "Pantothénate",
    choline: "Choline",
    phosphorus: "Phosphore",
    iodide: "Iode",
    magnesium: "Magnésium",
    zinc: "Zinc",
    selenium: "Sélénium",
    copper: "Cuivre",
    manganese: "Manganèse",
    chromium: "Chrome",
    molybdenum: "Molybdène",
    chloride: "Chlorure",
  },
  avoir: {
    add_cta: "Créer un avoir",
    imputations_title: "Imputé en règlement sur",
    imputations_empty:
      "Aucune imputation — cet avoir n'a pas encore réglé de facture.",
    imputation_montant: "#montant € imputés",
  },
  tts: {
    regenerate: "Regénérer la synthèse vocale",
    autoplay: "Lecture automatique",
    autoplayLabel: "Lecture automatique de la synthèse vocale",
  },
  connector: {
    title: "Connecteur e-commerce",
    manageSync: "Gérer la synchronisation",
    active: "Activé",
    endpoint: "Endpoint",
    apiKey: "Clé API",
    typeConnector: "Type de connecteur",
    clientId: "Client ID",
    state: "État de synchronisation",
    sync: "Synchroniser",
    loadingFrom: "Chargement des données depuis",
    params_title: "Paramètres de synchronisation",
    params_description:
      "Pour chaque type d'entité, configurez la réception (PS → Justup) et l'envoi (Justup → PS) indépendamment.",
    params_recap_description:
      "Récapitulatif des entités synchronisées. La configuration par défaut est appliquée à la création du connecteur.",
    direction_both: "Deux sens",
    direction_both_desc:
      "Les modifications dans Justup sont envoyées vers la boutique, et les modifications dans la boutique sont importées dans Justup. Le rapprochement initial se fait dans les étapes suivantes.",
    direction_pull: "Réception",
    direction_pull_desc:
      "Seules les modifications de la boutique sont importées dans Justup. Justup ne modifie rien sur la boutique.",
    direction_push: "Envoi",
    direction_push_desc:
      "Seules les modifications de Justup sont envoyées vers la boutique. Les changements faits sur la boutique ne sont pas importés.",
    direction_none: "Aucune",
    direction_none_desc: "Aucune synchronisation pour ce type d'entité.",
    entity: {
      incoming: "Réception (PS → JustUp)",
      outgoing: "Envoi (JustUp → PS)",
      incoming_short: "↓ Réception",
      outgoing_short: "↑ Envoi",
      creation: "Création",
      update: "Mise à jour",
      deletion: "Suppression",
      mode_enabled: "Activé",
      mode_disabled: "Désactivé",
      mode_manual: "Manuel",
      advanced_mode: "Mode avancé",
      clients: "Clients, groupes & adresses",
      products: "Articles",
      specificPrices: "Prix spécifiques",
      taxes: "TVA",
      orders: "Commandes",
      invoices: "Factures & Avoirs",
      payments: "Paiements",
      carriers: "Transporteurs",
      config_missing:
        "La configuration de synchronisation n'est pas encore initialisée. Cliquez pour appliquer les valeurs par défaut.",
      config_init: "Initialiser la configuration",
    },
    monitoring: "Monitoring",
    mon: {
      title: "Suivi de la synchronisation",
      refresh: "Actualiser",
      empty_ok: "Rien à signaler",
      // Onglets (par direction)
      tab_overview: "Vue d'ensemble",
      tab_outgoing: "JustUp → Boutique",
      tab_incoming: "Boutique → JustUp",
      tab_stats: "Statistiques",
      // Statut connecteur (header + carte envoi)
      connector_disabled: "Connecteur désactivé",
      reactivate: "Réactiver",
      relaunch: "Relancer",
      reactivated: "Connecteur réactivé",
      auth_error: "Erreur d'authentification",
      auth_error_hint:
        "Identifiants refusés par la boutique — re-test automatique toutes les 15 min. « Relancer » force un nouvel essai immédiat.",
      // Vue d'ensemble — bandeau de statut global
      banner_ok: "La synchronisation fonctionne normalement",
      banner_errors: "#count élément(s) nécessitent votre attention",
      banner_see: "Voir",
      // Vue d'ensemble — cartes par direction
      incoming_title: "Boutique → JustUp",
      incoming_subtitle: "Réception des modifications de la boutique",
      outgoing_title: "JustUp → Boutique",
      outgoing_subtitle: "Envoi des modifications JustUp",
      errors_count: "#count en erreur",
      pending_count: "#count en attente",
      exhausted_count: "dont #count sans nouvel essai prévu",
      ps_queue: "En attente côté boutique",
      ps_queue_hint:
        "Modifications de la boutique qui n'ont pas pu être transmises à Justup",
      ps_backlog: "En attente d'envoi",
      ps_backlog_hint:
        "Éléments de la boutique en file d'attente, pas encore transmis à Justup (envoi en cours / nouvelle tentative)",
      ps_backlog_stuck:
        "Un élément attend depuis plus de 15 min — l'envoi ne passe plus",
      tick_label: "Contact boutique",
      tick_stale: "Injoignable",
      tick_stale_hint:
        "Aucun contact avec la boutique depuis plus de 5 min — la boutique ou le tunnel est peut-être hors ligne",
      next_retry_hint: "Heure de la prochaine tentative d'envoi",
      deferred_queue: "En attente d'un autre élément",
      deferred_hint:
        "Reçus de la boutique, en attente qu'un élément lié soit synchronisé (client, article…)",
      dead_letter: "Abandonnés",
      dead_letter_hint:
        "Éléments mis de côté après plusieurs échecs — relance manuelle possible",
      push_pending: "Envois en attente",
      push_pending_hint: "Modifications planifiées vers la boutique",
      push_error: "Envois en erreur",
      push_error_hint:
        "Échecs après plusieurs tentatives — relançables dans le détail",
      circuit: "Connexion à la boutique",
      circuit_hint:
        "Les envois sont suspendus automatiquement quand la boutique ne répond plus",
      circuit_closed: "Opérationnelle",
      circuit_open: "Suspendue (boutique en erreur)",
      circuit_half_open: "Reprise en cours de test",
      until: "jusqu'à",
      oldest_pending: "Envoi le plus ancien en attente",
      last_checked: "Dernière vérification :",
      // Onglet Justup → PS (push jobs)
      outgoing_tab_title: "Envois vers la boutique",
      filter_all: "Tous",
      filter_pending: "En attente",
      filter_error: "En erreur",
      filter_done: "Réussis",
      attempts_tooltip: "#count tentative(s)",
      view_history: "Voir l'historique",
      retry_now: "Relancer maintenant",
      cancel_job: "Annuler",
      job_requeued: "Envoi replanifié",
      job_cancelled: "Envoi annulé",
      push_job_detail_title: "Détail de l'envoi #id",
      push_job_details:
        "Type : #type / #operation\nStatut : #status (#attempts tentatives)\nProchaine exécution : #nextRun\n\nHistorique :\n#history",
      no_failures: "aucun échec enregistré",
      // Onglet PS → Justup
      section_ps_queue: "En attente côté boutique",
      section_ps_queue_hint:
        "Modifications de la boutique qui n'arrivent pas à être transmises à Justup",
      section_deferred: "En attente d'un autre élément",
      section_deferred_hint:
        "En attente qu'un élément lié soit synchronisé — nouvel essai automatique",
      section_dead_letter: "Abandonnés",
      section_dead_letter_hint:
        "En échec définitif — à relancer ou supprimer manuellement",
      queue_filter_active: "En erreur",
      queue_filter_resolved: "Résolus",
      queue_filter_all: "Tous",
      first_error: "1ère erreur :",
      last_seen: "Dernier :",
      requeue_item: "Remettre en file",
      delete_item: "Supprimer de la file",
      item_requeued: "Élément remis en file",
      item_deleted: "Élément supprimé",
      clear_queue: "Supprimer tous",
      clear_queue_hint:
        "Supprime tous les éléments en attente/erreur de la file boutique (côté PrestaShop et ici). Action définitive.",
      clear_queue_title: "Vider la file boutique",
      clear_queue_confirm:
        "Supprimer définitivement les #count élément(s) de la file boutique ? Ils ne seront pas synchronisés.",
      queue_cleared: "#count élément(s) supprimé(s) de la file",
      next_run: "Prochain essai",
      deferred_requeued: "Nouvel essai programmé",
      view_payload: "Voir le contenu",
      delete_dead_letter: "Supprimer",
      dl_requeued: "Nouvel essai programmé",
      dl_deleted: "Entrée supprimée",
      dead_letter_detail_title: "Élément abandonné — #entity / #eventType",
      dead_letter_details:
        "eventId : #eventId\nReçu le : #receivedAt\nErreur : #error\n\nPayload :\n#payload",
      // Onglet Statistiques
      stats_by_entity: "Activité sur 30 jours — par entité",
      stats_daily: "Détail quotidien",
      // En-têtes de colonnes (tables des onglets de détail + stats)
      col_item: "Élément",
      col_status: "Statut",
      col_attempts: "Essais",
      col_date: "Date",
      col_error: "Erreur",
      col_entity: "Entité",
      col_received: "Reçus",
      col_succeeded: "Réussis",
      col_failed: "Échecs",
      col_p50: "Durée médiane",
      col_p99: "Durée max (p99)",
      // Statuts (push jobs + files de réception)
      status: {
        pending: "En attente",
        running: "En cours",
        done: "Réussi",
        error: "Erreur",
        cancelled: "Annulé",
        annule: "Annulé",
        resolved: "Résolu",
      },
      // État plateforme d'un item de la file boutique (queue-log)
      qstatus: {
        queued: "En attente d'envoi",
        retrying: "Nouvelle tentative",
        sending: "Envoi en cours",
        failed: "Échec",
      },
      // Types d'objets synchronisés
      objtype: {
        tier_group: "Groupe",
        tiers: "Client",
        adresse: "Adresse",
        article: "Article",
        article_fourni: "Tarif",
        commande: "Commande",
        commande_ligne: "Ligne",
        facture: "Facture",
        avoir: "Avoir",
        paiement: "Paiement",
        carrier: "Transporteur",
        tax: "TVA",
        remise: "Remise",
      },
    },
    inactive: "Inactif",
    bareme: {
      poids: "Poids",
      prix: "Prix",
      tranche_count: "tranche(s)",
      ps_chip: "Barème PS",
      justup_chip: "Barème JustUp",
      diff_chip: "Diff barème",
      none: "Aucun barème",
      field_label: "Barème",
    },
    errors: {
      necessite: "nécessite",
      compatible: "compatible",
      current: "actuel",
    },
    status: "Status",
    updated: "Dernière mise à jour",
    lastUpdateSource: "Par",
    webhooks: {
      title: "Module PrestaShop — installation & connexion",
      description1Prestashop:
        "Si vous souhaitez synchroniser automatiquement les éléments lors d'une modification, création ou suppression sur Prestashop.",
      description2Prestashop:
        'Veuillez configurer le module "JustUp Webhooks" pour Prestashop.',
      download: "Télécharger le module Prestashop",
      generateApiClient: "Générer un client API",
      reInitApiClient: "Réinitialiser le client API",
      reInitApiClientMessage:
        "Cette action va générer un nouvel identifiant et désactiver immédiatement les anciens.\n" +
        "Vous devrez copier le nouveau secret et le coller dans le module MODULE pour rétablir la synchronisation.\n" +
        "L’ancien ne pourra pas être récupéré.",
      clientId: "Client ID",
      clientSecret: "Client Secret",
    },
    type: {
      customer: "Client",
      customer_plural: "Clients",
      address: "Adresse",
      address_plural: "Adresses",
      product: "Article",
      product_plural: "Articles",
      order: "Commande",
      order_plural: "Commandes",
      group: "Groupe",
      group_plural: "Groupes",
      tax: "Taux TVA",
      tax_plural: "Taux de TVA",
      carrier: "Transporteur",
      carrier_plural: "Transporteurs",
      zone: "Zone",
      zone_plural: "Zones",
      country: "Pays",
      country_plural: "Pays",
      payment: "Paiement",
      payment_plural: "Paiements",
      toCreate: "à créer",
      toReconcile: "à rapprocher",
      diff: "à modifier",
      synced: "déjà synchronisé",
    },
    zones: {
      title: "Zones géographiques",
      auto_reconcile: "Réconciliation auto",
      select_placeholder: "Associer une zone…",
      access_denied:
        "Accès à la ressource /zones non autorisé sur la clé API PrestaShop — activez-la dans Paramètres avancés > Webservice.",
      saved: "Associations zones sauvegardées",
    },
    countries: {
      title: "Pays",
      select_placeholder: "Associer un pays…",
      access_denied:
        "Accès à la ressource /countries non autorisé sur la clé API PrestaShop.",
      saved: "Associations pays sauvegardées",
    },
    orderState: {
      etat: "État logistique",
      etatPaiement: "État paiement",
      etatExpedition: "État expédition",
      default: "État par défault",
      custom: "État custom",
      defaultsReadOnlyTooltip:
        "Configuré automatiquement par PrestaShop — non modifiable",
      duplicateError:
        "Plusieurs mappings pointent vers le même état PrestaShop",
      orphanError:
        "État PrestaShop introuvable (supprimé ou désactivé). Retirez ce mapping pour continuer.",
      flagsFixed:
        "#count état(s) PrestaShop corrigé(s) — génération automatique de factures/BL désactivée : #names",
    },
    auto_reconcile_partial:
      "#count élément(s) rapproché(s) — #left non assigné(s) : leurs suggestions pointaient des éléments JustUp déjà pris (à décider manuellement).",
    show_disabled: "Afficher les désactivés",
    show_disabled_tooltip:
      "Inclure les éléments désactivés côté PrestaShop (masqués par défaut)",
    ps_disabled: "Désactivé",
    ps_disabled_tooltip: "Cet élément est désactivé sur PrestaShop",
    duplicate_chip: "Doublon",
    duplicate_goto: "Cliquer pour aller à l'élément en conflit",
    duplicate_tooltip:
      "Le même élément Justup est aussi sélectionné pour « #other »",
    duplicate_tooltip_linked:
      "Cet élément Justup est déjà associé à « #other »",
    duplicate_snack:
      "#count sélection(s) en conflit : un même élément JustUp ne peut pas être associé à plusieurs éléments. Corrigez les lignes marquées « Doublon ».",
    syncProgress: {
      title: "Synchronisation en cours",
      current: "Élément en cours :",
      done: "Synchronisation terminée",
      finalizing: "Chargement des données mises à jour…",
      errors: "erreur(s) rencontrée(s)",
      close: "Fermer",
      waiting_server: "Préparation en cours…",
      elements: "éléments",
      step_prepare: "Préparation",
      step_prepare_connecting: "Envoi des éléments à #name…",
      step_prepare_starting: "Le traitement démarre…",
      step_sync: "Synchronisation des éléments",
      current_item: "Élément en cours : #item",
      step_finalize: "Finalisation",
      entity: {
        groups: "Groupes",
        customers: "Clients",
        addresses: "Adresses",
        products: "Articles",
        orders: "Commandes",
        taxes: "TVA",
        carriers: "Transporteurs",
        zones: "Zones",
        countries: "Pays",
        specificPrices: "Prix spécifiques",
        payments: "Paiements",
      },
    },
    ecommerce: "Connecteurs e-commerce",
    synchronisation: "Synchronisation des éléments",
    parametrage: "Paramétrage de la synchronisation",
    params: "Paramètres",
    recuperation: "Récupération des éléments",
    groups: "Groupes",
    clients: "Clients",
    addresses: "Adresses",
    articles: "Articles",
    orderStates: "États de Commande",
    orders: "Commandes",
    new: "Nouveau",
    select: "Sélectionner",
    ignore: "Ignorer",
    synced: "Synchronisé",
    notSynced: "Non synchronisé",
    justup: "JustUp",
    syncPending: "Synchronisation en cours...",
    cancelModification: "Annuler la modification",
    delete: "Supprimer le connecteur",
    deleteConfirm:
      "Cette action supprimera définitivement la configuration du connecteur et tous ses liens de synchronisation. Continuer ?",
    edit: "Modifier",
    defaultPrices: "Prix par défaut",
    tiersPrices: "Tiers",
    groupPrices: "Groupe",
    article_non_lie_chip: "Article non lié",
    article_non_lie_tooltip:
      "Cet article n'est pas encore lié à #connectorName — la commande ne pourra pas être transmise tant que l'article n'est pas lié",
    tiers_non_lie_chip: "Tiers non lié",
    tiers_non_lie_tooltip:
      "#tiersName n'est pas encore lié — ce tarif ne sera pas transmis à #connectorName",
    sync_doublon: "Référence déjà synchronisée",
    articleCard: {
      viewDetail: "voir le détail",
      remisesTitle: "Remises (entité séparée, créées en plus des tarifs)",
      suggestions_line: "#count correspondance(s) trouvée(s)",
      associate: "Associer un article",
      create: "Créer l'article",
      ignore: "Ignorer",
      change: "Changer",
      syncFailed: "Échec sync",
      tarif: "#count tarif",
      tarif_plural: "#count tarifs",
      remise: "#count remise",
      remise_plural: "#count remises",
      psTarifsTitle: "Tarifs #connectorType",
      resultTitle: "Ce qui sera synchronisé",
      target: "Cible",
      targetDefault: "Défaut",
      targetTiers: "Tiers",
      targetGroup: "Groupe",
      targetGroupUnmapped: "Groupe non apparié (#connectorType #id)",
      targetTiersUnmapped: "Client non apparié (#connectorType #id)",
      targetUnmappedHint:
        "Cette cible #connectorType n'est pas encore appairée à JustUp. Ce prix ne sera pas synchronisé tant que le groupe/client correspondant n'est pas associé (étape Groupes/Clients). Il le sera automatiquement dès l'appairage.",
      unit: "Unité",
      coef: "Coef",
      price: "Prix #connectorType",
      remiseCol: "Remise",
      editUg: "Modifier l'unité et le coefficient",
      editDone: "OK",
      coefHint: "Coefficient d'unité de gestion (ex. 1 carton = N unités)",
      modification: "#count modification",
      modification_plural: "#count modifications",
      viewDiff: "Voir le diff complet",
      update: "Mettre à jour",
      updateTooltip: "Inclure cette mise à jour dans la synchronisation",
      compare: "Comparer",
      synced: "Synchronisé",
      statusNew: "Nouvelle",
      statusDiff: "Diff",
      statusSync: "Sync",
      from: "Du",
      to: "au",
      ht: "HT",
      ttc: "TTC",
      tva: "TVA #rate%",
      tvaHint:
        "Les prix #connectorType sont stockés HT. Le TTC est calculé avec le taux de TVA de l'article (#taxName). Le front-office de la boutique affiche le TTC — d'où un écart possible avec le HT importé.",
      priceDialogTitle: "Tarifs & remises",
      noSellPrices: "Aucun tarif de vente pour cet article.",
      defaultPrice: "Prix par défaut",
    },
    suggestions_available:
      "#count correspondance(s) trouvée(s) — cliquez pour associer",
    sync_doublon_tooltip:
      "Tous les articles suggérés sont déjà liés à d'autres produits #connectorType. Ignorez cet élément, ou utilisez 'Associer' pour le lier manuellement à un article Justup différent.",
    sync_already_linked: "Déjà lié à #skuKey",
    validate: {
      title: "Test de la configuration",
      btn: "Tester la configuration",
      checking: "Test en cours...",
      success: "Configuration valide — tous les accès requis sont en place",
      failure: "Configuration incomplète — vérifiez les points en rouge",
      required_for_sync: "Testez d'abord la connexion pour activer ce bouton",
    },
    simple_save_title: "Paramètres avancés personnalisés",
    simple_save_message:
      "Certaines catégories ont des paramètres avancés qui ne sont pas visibles en mode simple. En enregistrant, ces paramètres seront conservés tels quels. Continuer ?",
    start_sync: "Commencer la synchronisation",
    info: {
      future_only:
        "La synchronisation s'applique aux <b>modifications futures</b>. Les entités déjà présentes dans Justup ne sont pas automatiquement exportées — le rapprochement initial se fait dans les étapes suivantes.",
      save_before_sync:
        "Enregistrez vos modifications avant d'accéder à la synchronisation.",
      taxes_hint:
        "Rapprochez les taux de TVA en premier : ils sont appliqués aux articles lors de leur import.",
      products_needs_taxes:
        "<b>#count taux de TVA</b> ne sont pas encore rapprochés — les articles importés qui les utilisent arriveront <b>sans TVA</b>. Retournez à l'étape TVA pour les traiter.",
      payments_needs_orders:
        "Un paiement ne peut être importé que si sa commande l'a été. Traitez l'étape Commandes en premier.",
      orders_needs_states:
        "Les statuts des commandes importées dépendent du mapping des états (étape précédente). Vérifiez-le avant de continuer.",
      carriers_hint:
        "Associez chaque transporteur de la boutique à un tiers transporteur Justup — leurs barèmes de livraison resteront ensuite à jour automatiquement.",
      specificPrices_hint:
        "Les prix spécifiques se gèrent depuis l'étape Articles : ouvrez le détail « prix » d'un article pour les consulter.",
    },
  },
  time: {
    instant: "À l'instant",
    ago: "Il y a #time",
    dayShort: "j",
    hourShort: "h",
    minuteShort: "min",
  },
  pdf: {
    generate: "Génération du PDF en cours...",
  },
  sso: {
    sync: "Synchroniser mon compte",
    configure_mail: "Configurer",
    no_mail_configured: "Aucun email n'est paramétré pour votre compte",
    refresh: "Rafraichir la connexion",
    active: "Actif",
    inactive: "Inactif",
    sendMail: "Envoi d'email",
    yes: "Oui",
    no: "Non",
    detail1:
      "Pour une configuration plus rapide et plus fiable, nous vous recommandons d’utiliser un compte Google ou Microsoft.",
    detail2:
      "Si vous préférez utiliser votre propre serveur mail, vous pouvez renseigner manuellement les paramètres SMTP ci-dessous.",
    alreadyConfigured:
      "Vous avez déjà une configuration mail, supprimez la pour en configurer une autre.",
  },
  "mouvements-stocks": {
    titre: "Mouvements de stocks",
    kpi: {
      entrees: "Entrées",
      sorties: "Sorties",
      "solde-net": "Solde Net",
      mouvements: "Mouvements",
      "derniere-activite": "Dernière activité",
      "sur-la-periode": "sur la période",
      "aucune-donnee": "Aucune donnée",
    },
    filtres: {
      "interval-date": "Interval de date",
      rechercher: "Rechercher par article, lot, lien...",
      "type-mouvement": "Type de mouvement",
      article: "Article",
      entrepot: "Entrepôt",
      "effacer-filtres": "Effacer les filtres",
    },
    periodes: {
      "1m": "1M",
      "3m": "3M",
      "12m": "12M",
      exercice: "Exercice en cours",
      "exercice-passe": "Exercice passé",
      tout: "Tout",
    },
    table: {
      type: "Type",
      date: "Date",
      article: "Article",
      lot: "Lot",
      lien: "Lien",
      quantite: "Quantité",
      valeur: "Valeur",
      entrepot: "Entrepôt",
      emplacement: "Emplacement",
      documents: "Doc.",
      "elements-par-page": "Éléments par page",
      sur: "sur",
      "aucun-mouvement": "Aucun mouvement trouvé",
      chargement: "Chargement des mouvements...",
    },
  },
  actions: {
    "exporter-csv": "Exporter CSV",
  },
  famille: {
    sousFamilles: "Sous-familles",
    sousFamille: "sous-famille",
    sousFamilles_plural: "sous-familles",
    addSousFamille: "Ajouter une sous-famille",
    noSousFamille: "Aucune sous-famille",
    emptyList: "Aucun élément",
    showDisabled: "Voir les éléments désactivés",
    disableWarn:
      "Cette famille est liée à des anciennes versions d'article, elle ne peut pas être supprimée définitivement.",
  },
  qualite: {
    cockpit_title: "Cockpit qualité",
    generer_rapport: "Générer le rapport",
    kpi_reponses: "Réponses de contrôle",
    kpi_conformite: "Conformité globale",
    kpi_nc_ouvertes: "NC ouvertes",
    kpi_alertes: "Alertes (7 j)",
    kpi_actions_retard: "Actions en retard",
    actions_retard: "Actions correctives en retard",
    pires_conformites: "Contrôles par conformité croissante",
    alertes_recentes: "Alertes récentes",
    reponses: "réponses",
    aucun_controle: "Aucune réponse de contrôle sur la période",
    aucune_alerte: "Aucune alerte émise",
  },
  /**
   * La NATURE d'une unité de gestion — ce qu'elle permet de convertir.
   *
   * ⚠️ Les libellés disent la CONSÉQUENCE, pas la catégorie. « Volume » seul n'apprend rien à qui
   * paramètre son référentiel ; « volume — conversion en poids par la densité » dit ce qu'il faudra
   * renseigner sur les articles comptés dans cette unité, et pourquoi un calcul les refusera sans elle.
   */
  unite: {
    /**
     * Les quatre CATÉGORIES de la carte des unités.
     *
     * ⚠️ Le titre nomme la catégorie, la ligne dessous en dit la **conséquence** : « volume » seul
     * n'apprend rien à qui paramètre son référentiel, « conversion par la densité » dit ce qu'il faudra
     * renseigner sur les articles et pourquoi un calcul les refusera sans elle.
     */
    groupe_MASSE: "Unités de poids",
    groupe_VOLUME: "Unités de volume",
    groupe_COMPTAGE: "Unités de comptage",
    groupe_LONGUEUR: "Unités de longueur",
    /**
     * ⚠️ « Autres unités », pas « non reconnues ».
     *
     * « Non reconnue » se lisait comme une **erreur** — un défaut de saisie à corriger — alors que la
     * plupart des unités qui atterrissent là sont parfaitement légitimes : une palette, un carton de six,
     * un mille ne mesurent aucune grandeur physique. Et le mot désignait en réalité l'état du *logiciel*
     * (« je ne sais pas classer ce code »), pas celui de la donnée. C'est aussi le même mot que la
     * catégorie « Autre » du formulaire : deux noms pour un seul groupe faisaient chercher la différence.
     */
    groupe_INCONNUE: "Autres unités",
    /**
     * ⚠️ **Les aides nomment la CONDITION et l'endroit où la remplir, pas la mécanique interne.**
     * « Pesables via la densité (kg/L) » décrivait le calcul du serveur — vrai, et inutile : il ne disait
     * ni qui doit faire quoi, ni où. « Si l'article a une densité renseignée dans sa fiche article » dit
     * les deux, et c'est la seule formulation qui transforme une explication en action.
     */
    /**
     * ⚠️ « Directement pesables » a été retiré : dire d'une unité de poids qu'elle pèse est une
     * tautologie, et elle occupait la place de la seule information actionnable — la condition de
     * conversion. Les quatre aides sont désormais parallèles : chacune n'énonce que ce qui est conditionnel.
     */
    groupe_aide_MASSE:
      "Converties en volume si l'article concerné a une densité (kg/L) renseignée dans sa fiche article.",
    groupe_aide_VOLUME:
      "Converties en poids si l'article concerné a une densité (kg/L) renseignée dans sa fiche article.",
    groupe_aide_COMPTAGE:
      "Converties en poids si l'article concerné a un poids net par unité (kg) renseigné dans sa fiche article.",
    /**
     * ⚠️ Calqué sur les trois autres sous-titres — « converties si … est renseigné » — parce que c'est la
     * même situation : **il manque une donnée sur la fiche article, pas une capacité au logiciel.** Les
     * versions « pas de conversion en poids » et « ne se pèsent pas » énonçaient une impossibilité, ce qui
     * est faux (un tuyau de 50 cm pèse 1 kg) et faisait passer un référentiel juste pour un cas non géré.
     */
    groupe_aide_LONGUEUR:
      "Converties entre elles, et en poids là où la taille fixe de l'article est déclarée — un tuyau de 50 cm qui pèse 1 kg.",
    /**
     * ⚠️ Resserré à la longueur des quatre autres sous-titres : le détail (« renseignez sa catégorie et son
     * coefficient ») est déjà dit dans le dialogue, à l'endroit où l'on agit. Un sous-titre de groupe annonce,
     * il n'explique pas.
     */
    groupe_aide_INCONNUE:
      "Aucune famille de mesure, donc aucune conversion en poids — normal pour un conditionnement (palette, carton).",
    groupe_vide: "aucune",
    /**
     * Les mêmes trois natures, vues depuis le FORMULAIRE — au moment où l'on tape un code.
     *
     * ⚠️ Le regroupement de la liste a rendu inutiles les pastilles par puce (`nature_MASSE`…) et la
     * ligne qui énumérait les codes non reconnus : le titre de catégorie porte l'information, et la
     * répéter sur chaque puce faisait du bruit. Ces clefs ont donc été **retirées** plutôt que laissées
     * mortes dans le fichier de traductions.
     */
    /**
     * ⚠️ « Sigle » et « Nom », pas « Dénomination » : le premier est la notation officielle (`m³`), le
     * second ce qu'on lit (« Mètre cube »). « Dénomination » désignait le code stocké, une notion
     * technique — et le mot ne disait pas laquelle des deux formes il portait.
     */
    sigle: "Sigle",
    categorie: "Catégorie",
    /**
     * ⚠️ Libellés COURTS, pour le sélecteur : « Unités de volume » y était tronqué en « Unités de volu… ».
     * Les titres de catégorie de la liste (`groupe_*`) restent au pluriel, parce qu'ils coiffent un groupe.
     */
    categorie_MASSE: "Poids",
    categorie_VOLUME: "Volume",
    categorie_COMPTAGE: "Comptage",
    categorie_LONGUEUR: "Longueur",
    /**
     * ⚠️ « Autre » existe pour les unités qui ne mesurent aucune grandeur physique — palette, carton,
     * mille. Les forcer dans une famille pour satisfaire un champ obligatoire ferait mentir la donnée, et
     * le coefficient inventé qui l'accompagne finirait dans un calcul de masse.
     */
    categorie_AUTRE: "Autre",
    reference_aucune: "aucune",
    /**
     * ⚠️ Employé dans le SÉLECTEUR, là où « Autre » n'apprenait rien : on n'y annonce pas une catégorie, on
     * aide à choisir. « sans conversion » dit ce qu'on obtient — la seule chose qui décide, à l'instant où
     * l'on rattache une unité à un article.
     */
    sans_conversion: "sans conversion",
    perso: "unité propre à votre référentiel",
    /**
     * ⚠️ **Le texte précédent énumérait les mauvaises tables** (« fiches articles, spécifications ou
     * matières ») : il datait d'un comptage limité à cinq colonnes. Or `CT6` est employée par 816 lignes
     * de commande client et 130 tarifs — aucune des trois citées. Un motif qui nomme les mauvais endroits
     * envoie chercher au mauvais endroit, ce qui est pire que ne rien nommer.
     *
     * ⚠️ **Les sources réelles sont donc INTERPOLÉES** (`#quoi`), pas écrites en dur : elles viennent du
     * comptage, donc elles suivent le produit sans qu'on y pense.
     */
    employee:
      "Suppression impossible : cette unité est employée par #n éléments (#quoi).",
    autre_aide:
      "Cette unité n'est rattachée à aucune famille : ses quantités ne seront pas convertibles en poids, et c'est normal pour un conditionnement (palette, carton). Vous pouvez lui donner un coefficient et une unité de référence si elle en a une.",
    /**
     * ⚠️ « Unité de gestion » ne disait rien à cet endroit — c'est le nom du référentiel entier, pas celui
     * de cette valeur. « Exprimé en » se lit avec le champ voisin : « Coefficient 2 · exprimé en L ».
     */
    reference: "Exprimé en",
    coefficient_vers: "pour 1 unité, exprimé en",
    native_lecture_seule:
      "Unité standard : son sigle, son nom et sa conversion sont ceux du système métrique — ils ne se modifient pas, et cette unité ne peut pas être supprimée.",
    nom: "Nom de l'unité",
    nom_placeholder: "ex. « Carton de 6 »",
    /*
     * ⚠️ **Le geste, pas le raisonnement** : la phrase dit à quoi sert le champ et où le nom apparaîtra.
     * Le pourquoi (cinq unités sans nom, `METRE` libellé « Millilitre » et personne pour le corriger)
     * vit en commentaire de code, pas à l'écran.
     */
    nom_aide: "C'est ce nom que les écrans affichent à côté des quantités — le code reste réservé aux intégrations.",
    nature_aide_MASSE:
      "Les poids exprimés dans cette unité pourront être convertis en volume si l'article concerné a une densité (kg/L) renseignée dans sa fiche article.",
    nature_aide_VOLUME:
      "Les volumes exprimés dans cette unité pourront être convertis en poids si l'article concerné a une densité (kg/L) renseignée dans sa fiche article.",
    nature_aide_COMPTAGE:
      "Les quantités exprimées dans cette unité pourront être converties en poids si l'article concerné a un poids net par unité (kg) renseigné dans sa fiche article.",
    /**
     * ⚠️ **Troisième rédaction, et les deux premières affirmaient la même chose de faux** : « il n'y a rien
     * à renseigner », puis « une longueur ne se convertit pas en poids ». Un bout de tuyau de 50 cm qui
     * pèse 1 kg se convertit très bien — le métier le fait tous les jours.
     *
     * ⚠️ **La limite est celle du CALCUL, pas du métier.** Ce qui manquerait pour l'automatiser est une
     * masse au mètre (kg/m) sur la fiche article — l'équivalent de la densité pour les volumes.
     *
     * ⚠️ **Mais cela NE SE DIT PAS à l'écran, et c'est une règle de destinataire.** Les trois autres
     * natures nomment un champ que l'utilisateur peut REMPLIR (densité, poids net par unité — tous deux
     * présents dans les formulaires d'article). La masse au mètre, elle, n'existe nulle part : l'annoncer
     * envoie chercher un champ introuvable et transforme une aide en énigme. Le texte affiché s'arrête donc
     * à ce qui est actionnable — le **coefficient de l'unité d'achat**, sur le tarif.
     *
     * ⚠️ Annoncer une impossibilité là où il ne manque qu'une donnée **décourage la seule saisie qui rend
     * ces articles exploitables** — et fait passer un référentiel juste pour un cas non géré.
     */
    nature_aide_LONGUEUR:
      "Les longueurs se convertissent entre elles, et vers un poids ou un comptage dès que l'article a une taille fixe : un tuyau de 50 cm qui pèse 1 kg. Cette correspondance se déclare dans le coefficient de l'unité d'achat, sur le tarif de l'article.",
    /**
     * ⚠️ Elle dit quoi FAIRE, pas ce qui est impossible. Les deux clefs précédentes
     * (`nature_saisie_inconnue`, `codes_reconnus`) affirmaient qu'aucune conversion ne serait possible et
     * listaient les codes métriques : c'est devenu faux dès que la catégorie et le coefficient ont permis
     * de rattacher n'importe quel code à sa famille. Elles ont été retirées plutôt que corrigées à moitié.
     */
    a_rattacher:
      "Renseignez la catégorie et le coefficient pour que les quantités exprimées dans cette unité soient convertibles.",
    /**
     * La **provenance** de la catégorie, quand elle n'a pas été lue mais calculée.
     *
     * ⚠️ **L'écran disait *quelle* catégorie, jamais *comment* elle avait été obtenue** — et les deux voies
     * n'ont pas la même solidité. `kg` est un poids par définition du système métrique ; `CT6` est un
     * comptage parce que quelqu'un a saisi « 6 » et « Unité » dans ce référentiel. Seule la seconde se
     * relit, se conteste et se corrige, encore faut-il savoir qu'elle existe.
     *
     * ⚠️ **Une catégorie DIRECTE reste muette** : répéter « déduit de » sous les quarante puces métriques
     * serait le bruit qui avait déjà fait retirer les pastilles de nature par puce.
     */
    deduite: "déduit de #regle",
    /**
     * Les **constats** sur le rattachement d'une unité — factuels, sans verdict ni correction proposée.
     *
     * ⚠️ **Ils nomment la donnée, ils ne jugent pas.** Un module de constat n'a pas à trancher : réactiver
     * une unité, la rattacher à une autre famille ou la retirer engage les articles qui la portent. Écrire
     * « incohérent » ou « à corriger » ferait passer une question ouverte pour une réponse.
     *
     * ⚠️ **Aucun jeton ne doit être le préfixe d'un autre** (`#sigle`, `#residu`, `#categorie`, `#evoquee`,
     * `#ref`, `#coef`) : l'interpolation remplace les jetons l'un après l'autre, et un couple
     * `#code`/`#coefficient` abîmerait le texte sans lever la moindre erreur.
     */
    constat_FAMILLE_CONTREDITE:
      "« #sigle » est rattaché à la catégorie #categorie ; « #residu » est une unité de #evoquee.",
    constat_COEFFICIENT_SANS_REFERENCE:
      "Coefficient #coef, sans unité de référence.",
    constat_REFERENCE_SANS_COEFFICIENT:
      "Unité de référence « #ref », sans coefficient.",
    constat_REFERENCE_INCONNUE:
      "L'unité de référence « #ref » n'existe pas dans le référentiel.",
    constat_DERIVEE_SANS_LIBELLE:
      "Aucun nom saisi : la catégorie ne repose que sur le coefficient.",
  },
  controle: {
    identifiant_manquant_titre: "Identifiant manquant",
    identifiant_manquant_message:
      "#count ligne(s) n'ont pas d'identifiant alors que ce contrôle le demande. Enregistrer quand même ?",
    nc_auto: "NC automatique",
    nc_auto_hint: "Une réponse non conforme ouvre automatiquement une fiche NC (déclarative, sans mouvement de stock)",
    nature: {
      organoleptique: "Caractéristiques organoleptiques",
      physico_chimique: "Caractéristiques physico-chimiques",
      microbiologique: "Caractéristiques microbiologiques",
      contaminants: "Contaminants",
      metrologie: "Métrologie",
      emballage: "Emballage & étiquetage",
      /*
       * ⚠️ **Forme courte pour le SÉLECTEUR, longue pour le titre de rubrique.** Un `<select>` de
       * la largeur d'une ligne tronque « Caractéristiques organoleptiques » en
       * « Caractéristiques orga… » — et les trois natures en « Caractéristiques » deviennent alors
       * indiscernables, ce qui est exactement l'information qu'on cherche à lire. Le titre de la
       * sous-carte, lui, a la place et garde la forme longue.
       */
      courte: {
        organoleptique: "Organoleptique",
        physico_chimique: "Physico-chimique",
        microbiologique: "Microbiologique",
        contaminants: "Contaminants",
        metrologie: "Métrologie",
        emballage: "Emballage & étiquetage",
      },
    },
    controlesGeneraux: "Contrôles généraux",
    scope: {
      title: "Périmètre d'application",
      all: "Ce contrôle s'applique à tous les éléments",
      filtered: "Ce contrôle s'applique aux éléments filtrés",
      famille: "Famille d'article",
      famille_plural: "Familles d'article",
      sousFamille: "Sous-famille d'article",
      sousFamille_plural: "Sous-familles d'article",
      tiers: "Tiers",
      allFeminin: "Toutes",
      allMasculin: "Tous",
      or: "ou",
      and: "et",
      selected: "#count sélectionnée",
      selected_plural: "#count sélectionnées",
      selectedMasculin: "#count sélectionné",
      selectedMasculin_plural: "#count sélectionnés",
      familleCategorie: "Familles et sous-familles",
    },
    applique_uniquement: "Appliqué uniquement pour:",
    form: {
      min: "Min",
      max: "Max",
      saisies: "saisies",
      minimumRequis: "minimum requis",
      ajouterSaisie: "Ajouter une saisie",
      realisePar: "Réalisé par",
      modifierRealisateur: "Modifier le réalisateur et la date",
      cible: "Cible",
      cible_hors_limites: "Le nominal doit être entre #min et #max",
    },
    matrix: {
      title: "Contrôles",
      controls_count: "contrôles",
    },
    history: {
      title: "Historique des réponses",
      empty: "Aucune réponse enregistrée pour ce contrôle",
      empty_filtre: "Aucune réponse sur la période ou les filtres choisis — élargis ou réinitialise les filtres",
      reponses: "#count réponse",
      reponses_plural: "#count réponses",
      conformes: "conformes",
      moyenne: "Moyenne",
      voir_courbe: "Voir la courbe",
      export: "Exporter l'historique vers Excel",
      du: "Du",
      au: "Au",
      nc_seulement: "NC seulement",
      etat_tous: "Tous",
      operateur_tous: "Tous les opérateurs",
      of_tous: "Tous les OF",
      hors_bornes: "Valeur hors des bornes du contrôle",
      lot: "Lot",
      identifiant: "Échantillon",
      ventilations_voir_tout: "Voir tout (+#count)",
      ventilations_replier: "Replier les ventilations",
      operateur: "Opérateur",
      reinitialiser: "Réinitialiser",
      surremplissage: "Surremplissage",
      cpk_hint: "Marge entre vos mesures et les bornes du contrôle, centrage compris. 1,33 et plus : process fiable · 1 : tout juste dans les bornes · moins de 1 : des mesures sortiront des bornes, même si les réponses passées sont marquées conformes. Se resserre quand la moyenne dérive de la cible ou que la dispersion (σ) augmente.",
      giveaway_hint: "Écart moyen vs nominal — produit donné en trop, valorisé au coût matière quand il est connu",
      par_operateur: "Par opérateur",
      par_of: "Par ordre de fabrication",
      charger_plus: "Charger plus (#shown / #total)",
      tronque: "#shown saisies affichées sur #total",
      etat: {
        CONFORME: "Conforme",
        NOT_CONFORME: "Non conforme",
        INCOMPLET: "Incomplet",
        EN_COURS: "En cours",
      },
    },
  },
  dynText: {
    // Categories
    general: "Généraux",
    variables: "Variables",
    documents: "Documents",
    // Mode toggle
    value: "Valeur",
    formula: "Formule",
    // Placeholders
    selectArticle: "Sélectionnez un article...",
    number: "Nombre",
    dragHere: "Glissez des éléments ici ou cliquez dessus...",
    selection: "Sélection :",
    addDynamicValue: "Ajouter une valeur dynamique",
    dynamicValues: "Valeurs dynamiques",
    // Expression labels
    ofQuantity: "Quantité OF",
    opeQuantity: "Quantité Opération",
    opeQuantityShort: "Quantité Opé",
    name: "Nom",
    reference: "Référence",
    code: "Code",
    quantity: "Quantité",
    quantityNomenclature: "Quantité nomenclature",
    unit: "Unité",
    unitManagement: "Unité de gestion",
    loss: "Perte",
    lossNormal: "Perte normale (%)",
    lossMax: "Perte max",
    lossMaxPercent: "Perte max. (%)",
    density: "Densité",
    photo: "Photo",
    // Short labels for formula display
    qtyOf: "Qté OF",
    qtyOpe: "Qté Opé",
    qty: "Qté",
    maxLoss: "P.max",
    // Validation errors
    errorParenthesisClose: "Parenthèse fermante sans ouvrante",
    errorStartOperator: "Ne peut pas commencer par un opérateur",
    errorConsecutiveOperators: "Deux opérateurs consécutifs",
    errorMissingOperator: "Opérateur manquant entre deux valeurs",
    errorOperatorAfterOpen: "Opérateur invalide après (",
    errorCloseAfterOperator: "Parenthèse fermante après opérateur",
    errorMissingOperatorAfterClose: "Opérateur manquant après )",
    errorMissingOperatorBeforeOpen: "Opérateur manquant avant (",
    errorUnbalancedParenthesis: "Parenthèses non équilibrées",
    errorEndOperator: "Ne peut pas finir par un opérateur",
    errorEndOpenParen: "Ne peut pas finir par (",
    selectElement: "Sélectionnez un élément",
    orSelectElement: "ou sélectionnez un élément",
  },
  bilanArticle: {
    declarations: "Déclarations",
    enregistrements: "enregistrement(s)",
    aideDeclarations:
      "Les enregistrements sont les déclarations de consommation saisies manuellement par les opérateurs sur la ligne.",
    tracaWarning: "⚠ Traçabilité activée mais aucune déclaration.",
    tracaWarningDetail:
      "Saisissez les consommations pour assurer la traçabilité.",
    theorique: "Théorique",
    aSaisir: "À saisir",
    aidePertes:
      "Écart entre la consommation réelle et la consommation théorique attendue par la nomenclature.",
    calculDesPertes: "Calcul des pertes",
    formulePertes: "Conso réelle − Conso théorique",
    soit: "Soit",
    dEcart: "d'écart",
    seuilsConfigures: "Seuils configurés",
    seuilNormal: "Normal",
    seuilAttention: "Attention",
    seuilCritique: "Critique",
    modeTheorique: "Mode théorique :",
    modeTheoriqueDetail: "pas d'écart calculé (conso = théorique).",
    enAttenteDeSaisie: "En attente de saisie.",
    completezLots: "Complétez les lots pour calculer les pertes.",
    entreePdc: "ENTRÉE PDC",
    aideEntreePdc:
      "Quantité totale transférée du stock vers le Pied de Chaîne pour cet OF. C'est la matière mise à disposition de la production.",
    mouvement: "Mouvement",
    stockVersPdc: "Stock → Pied de Chaîne",
    total: "Total",
    consommation: "CONSOMMATION",
    aideConso:
      "Quantité réellement consommée en production. Composée des déclarations opérateurs et du calcul automatique par lot.",
    repartition: "Répartition",
    declare: "Déclaré",
    calcule: "Calculé",
    theoriqueLabel: "Théorique",
    saisieIncomplete: "Saisie incomplète.",
    saisieIncompleteDetail: "Renseignez tous les lots pour obtenir le total.",
    theo: "théo.",
    retourPdc: "RETOUR PDC",
    aideRetourPdc:
      "Quantité non utilisée retournée du Pied de Chaîne vers le stock. Cette réintégration génère un mouvement 'Réintég. PDC' et permet de récupérer les matières excédentaires.",
    calcul: "Calcul",
    pdcVersStock: "Mouvement : PDC → Stock",
    formuleRetour: "Entrée − Conso = Retour",
    retourAttenteDetail:
      "Le retour sera calculé automatiquement une fois tous les lots renseignés.",
    reintegrationTitle: "Réintégration PDC → Stock",
    aideReintegration:
      "Pour chaque lot, saisissez la quantité réellement retournée au stock. L'écart avec l'entrée initiale déterminera la consommation réelle.",
    parLot: "Par lot",
    formuleConsoLot: "Conso = Entrée PDC − Retour",
    lotsASaisir: "lot(s) à saisir",
    pourCompleterBilan: "pour compléter le bilan.",
    saisieRetourPdc:
      "Saisissez la quantité retournée au stock depuis le Pied de Chaîne",
    ope: "Opé.",
    du: "du",
    qteProduite: "Qté produite:",
    qteConsommee: "Qté consommée",
    qteTheorique: "Qté théorique",
    lot: "lot",
    lot_plural: "lots",
  },
  bilanLot: {
    entreePdcLabel: "Entrée PDC",
    qteDisponible: "Qté disponible",
    aideQteInitialeReinte:
      "Quantité de ce lot transférée du stock vers le Pied de Chaîne au début de la production.",
    ceLot: "Ce lot",
    stockVersPdc: "Stock → PDC",
    aideQteInitiale:
      "Quantité disponible de ce lot au début de cette opération.",
    detail: "Détail",
    entreePdcDetail: "Entrée PDC :",
    reportPostePrec: "Report poste préc. :",
    total: "Total :",
    qteRestanteFinProd: "Quantité restante en fin de prod.",
    qteRestanteFinOpe: "Quantité restante en fin d'opé.",
    aideRestanteReinte:
      "Quantité non utilisée de ce lot, retournée au stock. Saisissez ce qui reste physiquement après production.",
    aideRestanteOpe:
      "Quantité non consommée de ce lot en fin d'opération. Ce restant sera reporté comme disponible pour l'opération suivante.",
    saisieAttendue: "Saisie attendue",
    entre: "Entre",
    et: "et",
    resultat: "Résultat",
    pdcVersStock: "PDC → Stock",
    retour: "Retour :",
    consommation: "Consommation",
    aideConsoLot:
      "Quantité réellement consommée pour ce lot, calculée automatiquement à partir de l'entrée et du retour.",
    calcul: "Calcul",
    formuleEntreeRetour: "Entrée − Retour = Conso",
    saisirRetourPourCalcul: "Saisissez le retour pour calculer",
    declarationsOperateur: "Déclarations opérateur",
    declaration_plural: "déclaration(s)",
    consosDifferentesDeclarations: "⚠ Conso différentes des déclarations",
    consoNegative: "Consommation négative",
    estDifferentDes: "est différent des",
    declaresEnCoursProd: "déclarés en cours de production",
    aideConsoDirecte:
      "Sans Pied de Chaîne, saisissez directement la quantité consommée de ce lot pendant la production.",
    declarations: "Déclarations",
    initiale: "INITIALE",
    disponible: "DISPONIBLE",
    restante: "RESTANTE",
    restanteFinOpe: "RESTANTE FIN OPÉ",
    consommee: "CONSOMMÉE",
    na: "N/A",
  },
  transfert_entrepot: {
    ajouterLigne: "Ajouter une ligne",
    modifierLigne: "Modifier la ligne",
    zoneDeTransfert: "Zone de transfert",
    palettes: "Unités d'expéditions",
    expedier: "Expédier",
    envoyerVersChargement: "Envoyer vers un chargement",
    creerNouvellePalette: "Ajouter une Unité d'Expédition",
    palettesEnPreparation: "Zone de préparation",
    ajouterLignesAvant: "Veuillez d'abord ajouter des lignes au transfert",
    ajouterDesLots: "Ajouter des lots",
    preparationVrac: "Préparation zone de transfert",
    enZone: "En zone",
    enZoneDeTransfert: "en zone",
    lignesDeTransfert: "Lignes de transfert",
    // Labels & headers
    article: "Article",
    quantite: "Quantité",
    numero: "Numéro",
    surPalette: "Sur unité d'expédition",
    expedie: "Expédié",
    demande: "Demandé",
    stockDispo: "Stock dispo",
    mode: "Mode",
    adresseDepart: "Adresse de départ",
    adresseArrivee: "Adresse d'arrivée",
    // Dialog titles
    nouveauTransfert: "Nouveau transfert d'entrepôt",
    modifierTransfert: "Modifier le transfert",
    creerPalette: "Créer une unité d'expédition",
    ajouterDesLotsTitle: "Ajouter des lots",
    voirAutresPalettes: "Voir les autres unités d'expédition",
    // Actions
    valider: "Valider",
    confirmerReception: "Confirmer réception",
    annuler: "Annuler",
    desannuler: "Désannuler",
    solder: "Solder",
    nouveauTransfertBtn: "Nouveau transfert",
    // Dates
    transfertDu: "Transfert du",
    // Filter
    etatsTransfert: "États du transfert",
    trierParModification: "Trier par modification",
    trierParCreation: "Trier par création",
    // Lot chooser
    choisirArticle: "Cliquez sur un article pour ajouter des lots",
    aide_expedition_maker:
      "Sélectionnez un article pour choisir les lots et quantités à ajouter sur l'unité d'expédition. La barre de progression indique l'avancement par rapport à la quantité demandée.",
    selectLotEtQuantite: "Sélectionnez un lot et une quantité à ajouter",
    quantiteAAjouter: "Quantité à ajouter",
    mouvementsEnAttente: "Mouvements en attente de sauvegarde",
    aucunMouvement: "Aucun mouvement à sauvegarder",
    sur: "sur",
    // Confirmation & errors
    annulationTitre: "Annulation du transfert",
    annulationMessage:
      "Vous êtes sur le point d'annuler le transfert #numero. Confirmez-vous ?",
    adressesIdentiques:
      "Les adresses de départ et d'arrivée ne peuvent pas être identiques",
    dluoDepassee:
      "La DLUO du lot #numero est dépassée, impossible de le sélectionner",
    modifNonSauvegardees:
      "Vous avez des modifications non sauvegardées, voulez-vous vraiment quitter la page ?",
    abandonnerModif: "Abandonner les modifications",
    resterSurPage: "Rester sur la page",
    attention: "Attention",
    paletteRequise:
      "L'unité d'expédition est requise pour ce mode de transfert",
    // Préparation
    preparation: "Préparation",
    // Bon de transfert
    imprimerBT: "Imprimer le BT",
    bonDeTransfert: "Bon de transfert",
    // Lot detail
    lot: "Lot",
    lots: "lots",
    expeditions: "Préparation du transfert",
    vrac: "Lots sans unités d'expéditions",
    // Dechargement transfert
    dechargement: "Déchargement",
    dechargementTitle: "Déchargement du transfert",
    nouveauDechargement: "Nouveau déchargement",
    terminerDechargement: "Terminer le déchargement",
    numeroBonTransfert: "N° bon de transfert",
    bonTransfert: "Bon de transfert",
    selectionnerBonTransfert: "Sélectionner un bon de transfert",
    // Reception transfert
    reception: "Réception",
    receptionTitle: "Réception du transfert",
    receptionsTitle: "Réceptions",
    quantiteAttendue: "Quantité attendue",
    quantitePresente: "Quantité présente",
    quantiteRecue: "Quantité reçue",
    conforme: "Conforme",
    nonConforme: "Non conforme",
    ecart: "Écart",
    recevoir: "Recevoir",
    recu: "Réceptionné",
    quantiteDechargee: "Quantité déchargée",
    // Warnings
    quantiteEnZone: "Quantité en zone de transfert",
    warningQuantiteSupZone:
      "La quantité saisie est supérieure à la quantité en zone de transfert",
    warningEcartQuantite:
      "Écart de quantité : #expected attendu, #actual présent",
    warningEcartDechargement:
      "Écart : #decharged déchargé, #present présent en zone",
    warningArticleManquant: "Article absent de la zone de transfert",
    warningArticleSupplementaire:
      "Article présent dans la zone de transfert mais non prévu dans le transfert",
    aucuneReception: "Aucune réception enregistrée",
    aucunDechargement: "Aucun déchargement enregistré",
    // Lot chooser refonte
    progression: "Progression",
    modificationsEnCours: "Modifications en cours",
    presentsSurPalette: "Présents sur l'unité d'expédition",
    choisirPalette: "Choisir une unité d'expédition",
    mettreSurPalette: "Mettre sur unité d'expédition",
    preparationDuTransfert: "Préparation",
    paletteDu: "Unité d'expédition ·",
    vracDu: "Vrac ·",
    titre: "Transferts entrepôt",
    titreDetail: "Transfert entrepôt",
    termine: "Terminé",
    enCours: "En cours",
    terminer: "Terminer",
    du: "Du",
    dechargementDuTransfert: "Déchargement du transfert",
    declarerLots: "Déclarer les lots",
    recevoirLigne: "Recevoir",
    terminerDechargementTitle: "Terminer le déchargement",
    terminerDechargementMessage: "Confirmez-vous la fin du déchargement ?",
    dechargementTermine: "Déchargement terminé",
    receptionsEnregistrees: "Réceptions enregistrées",
    receptionModifiee: "Réception modifiée",
    receptionSupprimee: "Réception supprimée",
    errorNoUserInfos: "Impossible de sauvegarder : utilisateur non chargé",
  },
  taux_horaire: {
    title: "Taux horaire",
    add: "Ajouter un taux horaire",
    date: "Date",
    rate: "Taux horaire (€/h)",
    override: "Saisie manuelle",
    no_data: "Aucun taux horaire enregistré.",
    delete_confirm: "Supprimer ce taux horaire ?",
    col_date: "Date",
    col_rate: "Taux (€/h)",
    col_override: "Type",
    col_actions: "Actions",
    type_manual: "Manuel",
    type_auto: "Automatique",
    edit: "Modifier le taux horaire",
    date_exists: "Un taux horaire existe déjà pour cette date.",
  },

  lot_stock_selector: {
    title: "Sélection du lot",
    titleStock: "Sélection du stock",
    entrepotsCommande: "Entrepôts de la commande",
    tousLesStocks: "Stock disponible",
    stocksDisponibles: "Lots disponibles pour cet article",
    ddm_depassee: "Périmé",
    ddm_force: "Durée de vie mini non tenue",
    consignation_badge: "Déjà vendu — à enlever",
    ddm_blocked_msg:
      "Ce lot ne respecte pas la durée de vie minimale exigée par le client (ou est périmé) — sélection bloquée.",
    ddm_warn_msg:
      "Ce lot est sous la durée de vie minimale exigée par le client : forçage motivé requis.",
    autres_entrepots_info:
      "Aussi disponible (autre entrepôt, non sélectionnable ici) :",
    placeholder: "Sélectionner un lot",
    placeholderStock: "Sélectionner un stock",
    selectionner: "Sélectionner",
    voirTousLesStocks: "Voir tous les stocks",
    quarantaine: "Quarantaine",
    dluoDepassee: "DDM dépassée",
    aucunLot: "Aucun lot disponible",
    aucunStock: "Aucun stock disponible",
    aideTitle: "Sélection",
    aideContent:
      "Les lots disponibles pour cet article sont affichés ci-dessous. Sélectionnez le lot souhaité puis validez.",
    aideAdresseFilter:
      "Seuls les stocks de l'adresse #adresse sont affichés. Activez le toggle pour voir tous les stocks.",
    lotUnique: "Lot unique",
    voirLesLots: "Voir les lots",
    scan: "Scanner",
    ajouter: "Ajouter",
    quantite: "Quantité",
    stockRestant: "Stock restant",
    reserveRestant: "Réservé restant",
    resteAExpedier: "Reste à expédier",
    resteAPlacer: "Reste à placer sur l'unité",
    prendre_entrepot: "Prendre ailleurs dans l'entrepôt",
    scanError: "Lot scanné introuvable dans les options disponibles",
    ddm: "DDM",
    rechercherLot: "Rechercher un lot",
    rechercherStock: "Rechercher un stock",
    dispo: "dispo",
    stocksFiltresAdresse: "Entrepôt de départ — lots disponibles ici",
    lotsEnStock: "lot(s) en stock",
    labelLot: "Lot",
    labelStock: "Stock",
    editTitle: "Modifier la quantité",
    currentOnPalette: "Sur unité d'expédition",
    modifier: "Modifier",
  },
  parametrage: {
    expedition_unit_types: "Unités d'expédition",
    commercial: "Commercial",
    territoire_taxes: "Territoire & Taxes",
    base_connaissance: "Base de connaissance",
  },
  frais_port_tva: {
    title: "TVA par défaut — frais de port",
    taux_label: "TVA appliquée automatiquement aux frais de port",
  },
  expedition_unit_type: {
    add: "Ajouter un type d'unité d'expédition",
    new_chip: "Nouveau type",
    edit: "Modifier le type d'unité d'expédition",
    denomination: "Dénomination",
    denomination_hint: "Ex : Palette Europe, Roll, Demi-palette",
    tare: "Tare (kg)",
    tare_hint: "Poids à vide de l'unité",
    valeur: "Valeur (€)",
    valeur_hint: "Valeur unitaire de l'unité",
    article: "Article lié",
    article_aide:
      "Lier un article permet de suivre le stock d'unités disponibles dans l'entrepôt.",
    icon: "Icône",
    gradient: "Couleur",
    stock_available: "en stock",
    no_article: "Aucun article lié",
    delete_confirm: "Supprimer ce type d'unité ?",
    disabled: "Désactivée",
  },
  tax_rate: {
    title: "Taux de TVA",
    add: "Ajouter un taux de TVA",
    edit: "Modifier le taux de TVA",
    denomination: "Dénomination",
    denomination_hint: "Ex : TVA 20%, TVA 5.5%",
    rate: "Taux (%)",
    rate_hint: "Taux de TVA en pourcentage",
    is_default: "Par défaut",
    select: "Taux de TVA",
    custom: "Personnalisé",
    back_to_list: "Revenir à la liste des taux",
  },
  palette_display: {
    status: {
      empty: "Vide",
      preparing: "En préparation",
      preparing_on_chargement: "En préparation · sur chargement",
      ready: "Prêt",
      waiting_shipment: "En attente d'expédition",
      shipped: "Expédiée",
    },
    articles: "article(s)",
    weight: {
      produits: "Brut Produits",
      tare: "Tare",
      brut: "Brut Total",
      missing: "Poids brut / UG non renseigné",
      edit_weight: "Éditer le poids brut / UG",
      enter_weight: "Saisir le poids brut / UG",
    },
    action: {
      mark_ready: "Déclarer Prêt",
      prepare: "Préparer",
      prepare_hint: "Cliquez sur un article pour ajouter des lots",
      edit_palette: "Modifier l'unité d'expédition",
      fiche: "Fiche d'expédition",
      print_sscc: "Imprimer SSCC",
      edit_unit: "Modifier l'unité",
      resume_preparation: "Reprendre préparation",
      expedier: "Vers expédition",
      change_expedition: "Changer d'expédition",
      remove_from_expedition: "Retirer de l'expédition",
      link_expedition: "Lier à une expédition",
      remove_from_bl: "Retirer",
    },
    alert: {
      dlc_expired: "DLC dépassée",
      dlc_warning: "DLC proche",
      weight_missing: "poids brut / UG manquant",
    },
    timeline: {
      created_by: "Créé par",
      empty: "En attente de préparation",
    },
    expired_badge: "PÉRIMÉ",
    shipped_on: "Expédiée le",
    no_articles: "Aucun article",
    lots: "Lots",
    expedition_pending: "Expé. à prévoir",
    delete_confirm:
      "Vous êtes sur le point de supprimer cette unité d'expédition. Confirmez-vous ?",
    delete_title: "Suppression",
    controle_pod: "Voir le contrôle POD",
    controles: "Contrôles",
    print_bl: "Imprimer le BL",
    print_bt: "Imprimer le BT",
    route_shipped: "Expédié",
    route_waiting: "En chargement",
    no_expedition: "Pas encore d'expédition prévue",
    departure_planned: "Départ prévu :",
  },
  chargements: {
    historique_empty: "Aucun chargement dans l'historique",
    filter_search: "Rechercher (destinataire, unité d'expédition, produit...)",
    filter_transporteur: "Transporteur",
    filter_etat: "État",
    filter_dates: "Date",
    filter_clear: "Effacer",
    clear_selection: "Effacer la sélection",
    show_more: "Afficher plus",
  },
  chargement_timeline: {
    palettes: "unités d'expédition",
    colis: "colis",
    poids: "poids total",
  },
  expedies_filters: {
    transporteur: "Transporteur",
    etat: "État",
  },
  status_timeline: {
    start: "Créé",
    no_event_yet: "Aucun événement pour cette étape",
  },
  cc_timeline: {
    title: "Historique",
    see_all: "Voir tout",
    empty: "Aucun événement enregistré pour cette commande.",
  },
  cf_timeline: {
    title: "Historique",
    empty: "Aucun événement enregistré pour cette commande.",
  },
  of_timeline: {
    title: "Historique",
    empty: "Aucun événement enregistré pour cet OF.",
  },
  integration_pf: {
    timeline: {
      candidate: "Nouvelle intégration — glisser pour positionner",
      date_label: "Date de l'intégration",
      en_cours: "En cours",
      maintenant: "maintenant",
      will_attach_to: "Sera rattachée à",
      no_ope: "Aucune opération démarrée sur cet OF.",
      existing_marker:
        "Glisser pour repositionner (borné par les intégrations voisines)",
      bounded_hint:
        "Glissez les puces sur la frise pour repositionner chaque intégration. Chacune est bornée par ses voisines : impossible de la déplacer avant la précédente ou après la suivante.",
      total_of: "Total OF",
      produit_sur_ope: "Produit sur l'opé",
      integre_sur_ope: "Intégré sur l'opé",
      restante_fin_ope: "Restante en fin d'opé",
      apres_derniere_ope: "Après la dernière opé",
      hors_ope_short: "aucune opé (baisse le PF sur ligne)",
      hors_ope_hint:
        "Après la fin de la dernière opé : cette intégration n'est rattachée à aucune opé, elle baisse seulement le produit fini resté sur la ligne (aucune quantité produite ajoutée).",
      preview_title: "Aperçu de l'impact sur les opérations",
    },
  },
  specification_timeline: {
    // Densification aside 09/07 : titre court + « Voir tout » integres a la ligne de titre.
    title_short: "Historique",
    see_all: "Voir tout",
    title: "Historique de l'article",
    empty: "Aucune version enregistrée pour cet article.",
  },
  /**
   * Spec-snapshot d'OF : fork, promote, propagation aux postes, journal de
   * modifications, archive des éléments retirés.
   *
   * ⚠️ Même oubli que `specification_history` / `specification_version_bar` :
   * le bloc n'existait qu'en EN / ES / PT.
   */
  of_spec_snapshot: {
    nomenclature_status_refreshed: "Composants mis à jour",
    rebind_conflict_retry: "La spécification a changé entre-temps. Vérifiez le nouvel impact avant de confirmer.",
    se_url_introuvable_fallback: "La version de spécification demandée n'existe plus. Affichage de la version courante.",
    update_model_confirm_button: "Confirmer",
    update_model_confirm_title: "Resynchroniser le modèle ?",
    update_model_delete_warning: "L'OF va être mis à jour avec le modèle référencé par la spécification privée. Si l'OF possédait une SE modèle privée, elle sera supprimée.",
    update_model_success: "Modèle de l'OF resynchronisé",
    variable_change_success: "Variable mise à jour et postes synchronisés",
    variable_impact_message: "Cette modification de variable change les opérations/contrôles des postes de cet OF. Vérifiez l'impact avant de confirmer.",
    variable_impact_title: "Impact sur les postes",
    changes_panel_apply_poste: "Appliquer sur ce poste",
    changes_panel_postes_outdated_title: "Postes en version antérieure",
    model_mismatch_error: "La spécification article privée utilise un modèle différent de celui de l'OF. Utilisez l'action ci-dessous pour resynchroniser.",
    nomenclature_status_refresh_cta: "Tout mettre à jour",
    nomenclature_status_title: "Composants dont la spécification a changé",
    poste_spec_out_of_sync_banner: "La spécification de ce poste diffère de la version courante de l'OF — pensez à propager",
    poste_spec_outdated_chip_tooltip: "La spécification de ce poste diffère de la version courante de l'OF",
    poste_spec_pending_changes_banner: "La spécification a été modifiée depuis la dernière propagation — les étapes de process peuvent être incomplètes",
    poste_terminee_old_spec_banner: "Ce poste terminé a été réalisé avec une version antérieure de la spécification de l'OF",
    preview_edit_bilans_invalidated_title: "Recalcul des bilans matière",
    preview_edit_bilans_invalidated_hint: "La nomenclature a changé : les bilans matière de cet OF seront invalidés et recalculés. Vérifiez les écarts après application.",
    preview_edit_process_added: "Procédés ajoutés",
    preview_edit_process_removed: "Procédés supprimés",
    preview_edit_requires_data_title: "Données désormais requises",
    preview_edit_requires_lot: "Suivi par lot désormais requis sur :",
    preview_edit_requires_traca: "Traçabilité à saisir sur ces composants :",
    preview_edit_vars_kept_title: "Réglages OF conservés",
    preview_edit_vars_kept_show: "Afficher",
    preview_edit_vars_kept_hide: "Masquer",
    preview_edit_vars_removed_title: "Réglages OF qui vont disparaître",
    preview_edit_vars_removed_hint: "Ces réglages OF disparaissent car la variable n'existe plus dans la nouvelle version.",
    spec_version_legacy_label: "Non migré",
    spec_version_legacy_tooltip: "Cet OF est antérieur à la gestion figée des spécifications. Aucune action de synchronisation n'est disponible.",
    update_model_cta: "Resynchroniser le modèle de l'OF",
    variables_dialog_substitute_label: "Composant à utiliser pour cet OF",
    variables_dialog_value_placeholder: "Valeur…",
    version_anterieure: "Version antérieure",
    config_changes_title: "Changements de configuration des composants",
    config_yes: "Oui",
    config_no: "Non",
    config_field_withTraca: "Traçabilité obligatoire",
    config_field_withLot: "Gestion par lot",
    config_field_withPiedDeChaine: "Stock en pied de chaîne",
    config_field_withStockPositif: "Stock positif forcé",
    config_field_withConsoTraca: "Quantité consommée (traçabilité)",
    remove_process_loss_title: "Retirer ce procédé ?",
    remove_process_loss_message:
      "Ce procédé porte #count saisie(s). Elles seront archivées (restaurables depuis l'historique) puis le procédé sera retiré de cet OF.",
    remove_process_loss_confirm: "Archiver et retirer",
    badge_forked: "Spécification privée (OF forké)",
    badge_outdated: "Spécification pas à jour",
    badge_uptodate: "Spécification à jour",
    badge_forked_tooltip:
      "Cet OF utilise une spécification privée, forkée depuis la version partagée. Vous pouvez la promouvoir pour qu'elle devienne la nouvelle version partagée.",
    badge_outdated_tooltip:
      "Une nouvelle version de la spécification est disponible. Cliquez pour mettre cet OF à jour.",
    rebind_cta: "Mettre à jour la spécification",
    rebind_confirm_title: "Synchroniser la spécification de l'OF ?",
    rebind_confirm_message:
      "Cet OF sera synchronisé avec la version courante de la spécification. Les saisies en cours seront préservées si possible.",
    promote_cta: "Publier cette version",
    promote_confirm_title: "Promouvoir en spécification partagée ?",
    promote_confirm_message:
      "La spécification privée de cet OF sera publiée comme nouvelle version partagée à approuver. Tant qu'elle n'est pas approuvée elle reste un brouillon ; les autres OF devront ensuite se resynchroniser pour en bénéficier.",
    promote_sync_confirm_title: "Synchroniser le modèle et promouvoir ?",
    promote_sync_confirm_message:
      "La spécification privée utilise un modèle différent de celui de l'OF. Le modèle de l'OF sera d'abord resynchronisé, puis la spécification sera promue en nouvelle version partagée à approuver.",
    promote_sync_confirm_button: "Synchroniser et promouvoir",
    promote_shared_draft_exists:
      "Une version partagée est déjà en rédaction — validez-la ou supprimez-la avant de promouvoir.",
    promote_error:
      "La promotion de la spécification a échoué. Réessayez ou contactez le support.",
    // P4 (promote éclairé) : écrans de décision appuyés sur le diff de version
    promote_never_approved_note:
      "La version promue restera une version en rédaction à approuver — rien n'est appliqué automatiquement.",
    promote_source_outdated_warning:
      "La version partagée a évolué depuis la création de cette spécification privée : votre promotion n'inclut pas ces changements.",
    promote_source_outdated_diff_title:
      "Différences entre votre version et la version courante :",
    promote_draft_exists_title: "Une version partagée en rédaction existe déjà",
    promote_draft_exists_message:
      "Une version partagée est déjà en rédaction sur cette spécification. Vous pouvez la supprimer pour promouvoir la vôtre à la place, ou annuler.",
    promote_draft_diff_title:
      "Différences entre votre version et la version en rédaction existante :",
    promote_discard_draft_button:
      "Supprimer la version en rédaction et promouvoir",
    promote_discard_draft_failed:
      "La version en rédaction existante n'a pas pu être supprimée (elle est peut-être déjà référencée) — promotion annulée.",
    promote_model_diff_title: "Ce qu'implique la synchronisation du modèle :",
    skipped_components_alert:
      "Certains composants ne sont pas suivis dans la spécification de cet OF (spécification archivée ou indisponible) : #components",
    fork_cta: "Modifier pour cet OF",
    fork_spec_article_cta: "Personnaliser la spécification article",
    fork_spec_model_cta: "Personnaliser le modèle",
    edit_spec_article_cta: "Modifier la spécification article",
    edit_spec_model_cta: "Modifier le modèle",
    apply_edit_cta: "Appliquer les changements aux postes",
    promote_spec_cta: "Publier la spécification privée",
    sync_cta: "Synchroniser la spécification",
    variables_cta: "Variables",
    edit_disabled_in_production:
      "Modification impossible pendant une production en cours",
    fork_confirm_title: "Personnaliser la spécification pour cet OF ?",
    fork_confirm_button: "Personnaliser",
    fork_confirm_message:
      "Une copie privée de la spécification sera créée pour cet OF. Vos modifications n'affecteront pas les autres OF.",
    fork_model_confirm_title: "Personnaliser la spécification pour cet OF ?",
    fork_model_confirm_message:
      "Une copie privée de la spécification (article + modèle) sera créée pour cet OF. Vos modifications n'affecteront pas les autres OF.",
    apply_edit_confirm_title: "Appliquer les changements aux postes ?",
    apply_edit_confirm_message:
      "Les changements de votre spécification privée seront propagés aux postes de cet OF.",
    apply_edit_success: "Changements appliqués aux postes",
    rebind_success: "Spécification synchronisée",
    rebind_error:
      "La synchronisation de la spécification a échoué. Réessayez ou contactez le support.",
    promote_confirm_button: "Promouvoir",
    promote_success: "Version v#version créée — à approuver",
    variables_dialog_title: "Variables de spécification",
    variables_dialog_empty: "Aucune variable sur cette spécification.",
    variables_dialog_readonly:
      "Production terminée : les variables sont en lecture seule et ne peuvent plus être modifiées.",
    variables_dialog_override_chip: "Personnalisé pour l'OF",
    variables_dialog_override_tooltip:
      "Valeur personnalisée pour cet OF — la spécification d'origine est ignorée",
    variables_dialog_edit_tooltip: "Modifier (crée une priorité pour cet OF)",
    variables_dialog_unset_tooltip:
      "Retirer la personnalisation de l'OF — retour à la valeur de la spécification",
    variables_dialog_exclude_step: "Exclure cette étape",
    variables_dialog_exclude_line: "Exclure cette ligne",
    variables_dialog_excluded: "Exclu",
    variables_dialog_included: "Inclus",
    variables_section_title: "Variables propres à cet OF",
    variables_empty: "Aucune variable spécifique",
    missing_variables_title: "Variables à renseigner",
    missing_variables_hint:
      "Le modèle exige ces paramètres. Renseignez-les avant de créer l'OF.",
    missing_variables_hint_update:
      "Le modèle exige ces paramètres. Renseignez-les pour appliquer la mise à jour.",
    preview_edit_title: "Impact de la modification",
    preview_edit_postes_affected: "Postes concernés",
    preview_edit_all_termines_title: "Aucune opération en cours ne peut recevoir ce changement.",
    preview_edit_all_termines_desc: "Les opérations terminées ne changent pas ; les nouvelles opérations appliqueront la spécification modifiée.",
    preview_edit_ope_added: "Procédés à ajouter",
    preview_edit_ope_removed: "Procédés à retirer",
    preview_edit_controles_expected:
      "Contrôles ajoutés (au prochain démarrage)",
    preview_edit_controles_removed: "Contrôles à retirer",
    preview_edit_saisies_loss_title: "⚠️ Saisies qui seront perdues",
    preview_edit_saisies_loss_ack: "Je confirme la perte de ces saisies",
    preview_edit_cancel: "Annuler",
    preview_edit_confirm: "Confirmer la modification",
    admin_backfill_title: "Backfill spec-snapshot",
    admin_backfill_ofs_stats: "OF sans IDs spec figés",
    admin_backfill_sems_stats: "SEM sans currentVersionId",
    admin_backfill_ofs_dryrun: "Simuler le backfill des OF",
    admin_backfill_ofs_apply: "Appliquer le backfill des OF",
    admin_backfill_sems_apply: "Backfill currentVersionId",
    admin_backfill_logs: "Rapport",
    admin_backfill_in_progress: "Backfill en cours…",
    admin_backfill_errors: "Erreurs",
    // H4 — SpecVersionButtonComponent
    spec_version_edit_article: "Modifier la spécification article",
    spec_version_edit_model: "Modifier le modèle",
    spec_version_view_history: "Voir l'historique",
    spec_version_update: "Mettre à jour vers la version courante",
    spec_version_update_then_edit: "Mettre à jour puis modifier",
    spec_version_edit_current: "Rester sur cette version et modifier",
    spec_version_continue_edit: "Continuer la modification",
    spec_version_revert: "Revenir à la spécification courante",
    spec_version_promote: "Promouvoir en spécification partagée",
    spec_article_label: "Spécification article",
    spec_model_label: "Spécification modèle",
    spec_version_navigate_article: "Aller à la spécification",
    spec_version_navigate_model: "Aller à la spécification modèle",
    // H8 — SpecChangesPanelComponent
    changes_panel_title:
      "Des modifications ont été apportées aux procédés et/ou aux contrôles",
    changes_panel_view_article_log: "Voir les modifications de l'article",
    changes_panel_view_model_log: "Voir les modifications du modèle",
    changes_panel_outdated_title:
      "La spécification de cet OF n'est plus à jour",
    changes_panel_outdated_cta: "Synchroniser avec la version courante",
    changes_panel_rebind_success: "Spécification synchronisée avec succès",
    poste_spec_outdated_tooltip:
      "Ce poste a été réalisé avec une version antérieure de la spécification",
    // H5 — SpecPendingChangesComponent
    pending_changes_label: "Modifications non appliquées",
    pending_changes_modified_at: "Modifiée le",
    pending_changes_tooltip:
      "Des modifications ont été apportées à la spécification depuis la dernière propagation aux postes.",
    applied_at_label: "Appliquée le",
    applied_at_tooltip:
      "Les modifications de la spécification ont été propagées aux postes.",
    // H6 — SpecChangeLogComponent
    change_log_title: "Journal des modifications",
    change_log_before: "Avant",
    change_log_after: "Après",
    change_log_empty_label: "(sans étiquette)",
    change_log_no_details: "Aucun détail enregistré pour cet événement.",
    change_log_no_changes: "Aucune modification",
    entity_type_process_etape: "Étape de procédé",
    entity_type_process_etape_ope: "Procédé",
    entity_type_controle: "Contrôle",
    // Formulaire des variables manquantes à la création d'un OF. Ces clés
    // étaient RÉFÉRENCÉES par le code sans exister dans aucune langue :
    // l'écran affichait « of_spec_snapshot.missing_var_include » en clair.
    missing_var_include: "Inclure",
    missing_var_exclude: "Exclure",
    missing_var_quantite_label: "Quantité",
    missing_var_document_hint: "Document à joindre pour cette variable",
    loss_archive_title: "Historique des éléments retirés",
    loss_archive_hint:
      "Éléments (procédés, contrôles) retirés des postes lors d'une modification de spécification. Les données affichées sont figées au moment du retrait — elles restent consultables même si l'élément n'existe plus dans la spécification courante.",
    loss_archive_empty: "Aucun élément retiré pour cet OF",
    loss_archive_poste: "Poste",
    loss_archive_conform: "conformes",
    loss_archive_saisie_by: "Saisi par",
    loss_archive_restore: "Restaurer",
    loss_archive_restored: "Restauré",
    loss_archive_restored_at: "Restauré le",
    entity_type_nomenclature_ligne: "Ligne de nomenclature",
    entity_type_specification_variable: "Variable de spécification",
    entity_type_se_itself: "Spécification",
    change_type_created: "Créé",
    change_type_updated: "Modifié",
    change_type_deleted: "Supprimé",
    // H7 — nomenclatureContext
    variables_dialog_nomenclature_article: "Article",
    variables_dialog_nomenclature_dynamic_var: "Variable d'article",
    // Libellés de version (spec-version-button versionLabel)
    version_private_label: "v#nb (privée)",
    version_outdated_label: "Pas à jour",
    // Dialogs d'aperçu rebind / revert
    rebind_preview_title: "Impact de la synchronisation",
    rebind_preview_message:
      "La spécification de cet OF sera synchronisée avec la version courante.",
    revert_preview_title: "Revenir à la spécification courante",
    revert_preview_message:
      "La spécification privée sera abandonnée. L'OF reviendra à la version partagée courante.",
  },
  /**
   * Barre de version du builder de spécification.
   *
   * ⚠️ Même oubli que `specification_history` : le bloc n'existait qu'en
   * EN / ES / PT. Le wording est celui validé le 09/07 (« En rédaction », pas
   * « Brouillon ») — les clés techniques (`draft_*`) restent inchangées.
   */
  specification_version_bar: {
    current_label: "Version courante",
    current_since: "appliquée depuis le #date",
    draft_label: "En rédaction",
    draft_modified: "modifiée le #date",
    modify: "Modifier",
    apply: "Appliquer",
    changes_label: "Modifications",
    discard: "Supprimer la version",
    history: "Historique",
    archived_label: "Archivée",
    archived_period: "en vigueur du #from au #to",
    archived_since: "en vigueur depuis le #from",
    back_current: "Revenir à la version courante",
    all_versions: "Toutes les versions",
    discard_confirm_title: "Supprimer cette version en rédaction ?",
    discard_confirm_message:
      "La version en rédaction et ses modifications seront définitivement supprimées. La version courante reste inchangée.",
  },
  /**
   * Historique unifié des versions de spécification.
   *
   * ⚠️ Ce bloc n'existait qu'en EN / ES / PT : le français — la langue par
   * défaut de l'application — avait été oublié à l'i18n-isation. Tout l'écran
   * d'historique affichait donc ses clés brutes
   * (« specification_history.yes » au lieu de « Oui »). Ajouté le 30/07/2026.
   */
  specification_history: {
    title: "Historique des versions",
    consult: "Consulter",
    consult_tooltip: "Consulter cette version",
    initial_version: "Version initiale",
    what_changed: "Ce qui a changé",
    no_changes: "Aucune différence avec la version précédente",
    journal: "Journal de version",
    journal_count: "Journal de version (#count)",
    journal_open: "Consulter le journal",
    produced_by: "Produite par #count OF",
    consumed_by: "Consommée par #count OF",
    model_ofs: "Modèle pour #count OF",
    private_forks: "#count fork(s) privé(s)",
    forked_by_of: "Forkée par l'OF ##of",
    legacy_banner: "+ #count OF anciens non rattachés aux versions",
    used_by_articles: "Utilisé par #count article(s)",
    period_from_to: "en vigueur du #from au #to",
    period_since: "appliquée depuis le #from",
    see_more: "Voir plus",
    view_ofs: "Consulter les OFs",
    ofs_dialog_title: "OFs liés à la version v#nb",
    ofs_producing: "OFs qui produisent cet article",
    ofs_consuming: "OFs qui consomment cet article",
    ofs_empty: "Aucun OF",
    replaced_entry_hint:
      "Remplacement automatique d'une version jamais utilisée",
    field_column: "Champ",
    yes: "Oui",
    no: "Non",
    section_process: "Procédés",
    section_controle: "Contrôles",
    section_nomenclature: "Nomenclature",
    section_infosArticle: "Infos article",
    section_variables: "Variables",
    fields: {
      label: "Nom",
      index: "Position",
      description: "Description",
      optional: "Optionnelle",
      timer: "Minuteur",
      lignes: "Lignes",
      type: "Type",
      denomination: "Nom",
      name: "Variable",
      payload: "Valeur",
      dontDo: "Désactivé",
      articleId: "Article (ID)",
      quantity: "Quantité",
      etat: "État",
      quantite: "Quantité",
      perteNormale: "Perte normale",
      perteMax: "Perte maximale",
      articleDynamic: "Article paramétrable",
      paramDynamic: "Quantité paramétrable",
      lotHeritier: "Lot héritier",
      tiersId: "Tiers (ID)",
      code: "Code",
      codeClient: "Code client",
      ean13: "EAN 13",
      ean14: "EAN 14",
      ean128: "EAN 128",
      ingredients: "Ingrédients",
      allergenes: "Allergènes",
      identifiantObligatoire: "Commentaire obligatoire",
      isPhoto: "Photo",
      isValeur: "Valeur",
      choix: "Choix",
      correctAnswer: "Bonne réponse",
      valeur1: "Valeur 1",
      valeur2: "Valeur 2",
      unite: "Unité",
      limiteHaute: "Limite haute",
      limiteBasse: "Limite basse",
      nominal: "Nominal",
      uniteGestion: "Unité de gestion",
      pcb: "PCB",
      // Contrôle (complétude vis-à-vis des CONTROLE_FIELDS du back)
      isEcart: "Calcul d'écart",
      limiteHauteString: "Limite haute",
      limiteBasseString: "Limite basse",
      nominalString: "Nominal",
      typeNominal: "Enregistrer la moyenne",
      moyenne: "Moyenne",
      tailleEchantillon: "Taille d'échantillon",
      tailleEchantillonString: "Nombre minimum d'enregistrements",
      echantillonParQuantite: "Quantité par échantillon (unité de gestion)",
      controleParQuantite:
        "La taille d'échantillon dépend de la quantité à contrôler",
      avecCourbe: "Graphique / courbe",
      valeurDynamique: "Limites en formule dynamique",
      ccp: "CCP (point critique pour la maîtrise)",
      prpo: "PrPO (programme prérequis opérationnel)",
      isJustupTodo: "Tâche liée à l'OF",
      ecartMinutes: "Fréquence (minutes)",
      toleranceMinutes: "Tolérance (minutes)",
      ecartMinutesString: "Fréquence (minutes)",
      toleranceMinutesString: "Tolérance (minutes)",
      consigneNC: "Consigne en cas de non-conformité",
      elementType: "Contexte d'application",
      event: "Événements",
      // Infos article (complétude vis-à-vis des INFOS_ARTICLE_FIELDS du back)
      typeFiche: "Format de fiche imprimable",
      ddmAsNumeroDeLot: "Utiliser la DDM comme numéro de lot",
      withTraca: "Exige de déclarer les lots consommés",
      withLot: "Suivi lot par lot",
      withPiedDeChaine: "Suivi précis des consommations",
      withStockPositif: "Garantir du stock disponible pour les mouvements",
      withConsoTraca: "Déclaration de consommation",
      withEmplacement: "Avec emplacement de stock",
      withSpecif: "Article fabriqué",
      withParallelOpe:
        "Autoriser plusieurs opérations de fabrication simultanées",
      withStock: "Article géré en stock",
      withValeursNutritionnelles: "Article avec valeurs nutritionnelles",
      isAlimentaire: "Article alimentaire",
      isPrestation: "Prestation",
      bio: "Article bio",
      secable: "Produit sécable",
      typeDateLimite: "Type de date limite",
      dluoJours: "DLUO (jours)",
      dluoMois: "DLUO (mois)",
      dluoAnnes: "DLUO (années)",
      dluoFormat: "Format de date",
      typePeriode: "Type de période",
      microbio: "Critères microbiologiques",
      infoFicheTechnique: "Fiche technique",
      quantiteParDimension: "Quantité par dimension",
      gross_weight_uc: "Poids brut / UG (kg)",
      net_weight_uc: "Poids net UC (kg)",
      longueur_uc: "Longueur UC (cm)",
      hauteur_uc: "Hauteur UC (cm)",
      largeur_uc: "Largeur UC (cm)",
      gross_weight_colis: "Poids brut colis (kg)",
      net_weight_colis: "Poids net colis (kg)",
      longueur_colis: "Longueur colis (cm)",
      hauteur_colis: "Hauteur colis (cm)",
      largeur_colis: "Largeur colis (cm)",
      weight_suremballage: "Poids du surremballage",
      epaisseur: "Épaisseur du produit",
      laize: "Laize",
      densite: "Densité (kg/L)",
      typeComptage: "Type de calcul",
      refCalcul: "Référence de calcul de quantité",
      uniteId: "Unité (ID)",
      uniteDeComptageId: "Unité de comptage (ID)",
      coefficientComptage: "Coefficient de comptage",
      valeurUnitaire: "Valeur unitaire par défaut",
      nbUnitePerPack: "Unités par pack",
      diametreMandrin: "Diamètre du mandrin",
      diametreComplet: "Diamètre du rouleau plein",
      stockMax: "Stock maximum",
      familleId: "Famille (ID)",
      categorieId: "Sous-famille (ID)",
      sousFamilleId: "Famille technique (ID)",
      proprietaireId: "Appartient à un tiers",
      coefficientVN: "Coefficient",
      labelBisVN: "Unité alternative",
      mlVN: "Base 100 ml (valeurs nutritionnelles)",
      // Journal (spec-change-log) — champs hors périmètre du diff de version
      version_nb: "Numéro de version",
      replacedSeId: "Version remplacée (ID)",
    },
  },
  facture_timeline: {
    title: "Historique",
    empty: "Aucun événement enregistré pour ce document.",
    facture_created: "Facture créée",
    avoir_created: "Avoir créé",
    facture_emitted: "Facture émise",
    avoir_emitted: "Avoir émis",
    paiement: "Paiement reçu",
    avoir_linked: "Avoir émis",
    facture_parent: "Facture d'origine",
    open_facture_tooltip: "Cliquer pour ouvrir le PDF de la facture",
    settled: "Facture soldée",
    cancelled: "Facture annulée",
  },
  bls_filters: {
    state: "État",
    period: "Période",
    tiers: "Client / Tiers",
    entrepot_source: "Entrepôt de départ",
    adresse_destination: "Adresse destination",
    chargement: "Chargement",
    facture: "Facture",
    include_archived: "Inclure les BLs annulés",
  },
  bls_list: {
    export_excel: "Exporter vers Excel",
    export_excel_tooltip:
      "Exporter tous les BLs filtrés dans un fichier Excel formaté (totaux, formules, branding JustUp)",
    bulk_print: "Télécharger PDF",
    bulk_print_tooltip:
      "Télécharge un ZIP contenant un PDF par BL sélectionné (un fichier nommé par numéro de BL)",
    bulk_print_select_first:
      "Sélectionnez au moins un BL dans la liste (cochez la case à gauche) pour activer le téléchargement groupé",
    bulk_print_success: "{count} BLs téléchargés dans le ZIP",
    bulk_print_error:
      "Échec du téléchargement groupé. Réessayez ou consultez les logs.",
    clear_selection: "Désélectionner",
    column_numero: "N° BL",
    column_date: "Date",
    column_tiers: "Client",
    column_adresse: "Destination",
    column_entrepot: "Entrepôt départ",
    column_commandes: "Commandes",
    column_chargement: "Chargement",
    column_facture: "Facture",
    column_etat: "État",
    column_poids: "Poids brut",
    column_palettes: "Unités",
    column_contenu: "Contenu",
    column_actions: "",
    empty: "Aucun bon de livraison",
    open_detail: "Voir le détail",
    print_pdf: "Imprimer le BL",
  },
  chargement_display: {
    title: "Chargement du",
    transporteur: "Transporteur:",
    no_transporteur: "Transporteur non défini",
    print_fiches: "Imprimer les fiches d'unités d'expédition",
    add_palettes: "Ajouter des unités d'expédition",
    export_pdf: "Exporter en PDF",
    print_bls: "Imprimer les BL",
    print_bls_all: "Imprimer tous les BL",
    print_bl_single: "#numero",
    print_bts: "Imprimer les BT",
    declare_all_palettes_ready: "Tout déclarer Prêt",
    declare_all_palettes_ready_success:
      "Toutes les unités d'expédition sont déclarées prêtes",
    empty_units_title: "Aucune unité d'expédition",
    empty_units_message:
      "Ce chargement ne contient aucune unité d'expédition. Ajoutez-en au moins une avant de pouvoir le charger.",
    declare_ready: "Déclarer comme prêt",
    ready_to_load: "Prêt à charger",
    load: "Charger",
    resume_load: "Reprendre le chargement",
    loading_in_progress: "Chargement en cours",
    no_palettes: "Aucune unité d'expédition associée",
    departure_planned: "Départ prévu :",
    add_palette_short: "Unité d'Expédition",
    no_entrepot: "Entrepôt non défini",
    view_detail: "Voir le détail",
    cancel: "Annuler le chargement",
    cancel_title: "Annuler le chargement #num",
    cancel_message:
      "Le chargement repassera en préparation. Les unités d'expédition restent attachées et prêtes, et les numéros de bon de livraison sont conservés. Les contrôles généraux et palettes scannées dans l'étape d'expédition seront effacés. Confirmer l'annulation ?",
    cancel_confirm: "Confirmer l'annulation",
    delete_empty: "Supprimer le chargement",
    delete_title: "Supprimer le chargement #num",
    delete_message:
      "Ce chargement est vide (aucune unité d'expédition). Il sera supprimé définitivement. Confirmer ?",
    delete_confirm: "Supprimer",
    pret_title: "Déclarer prêt le chargement #num",
    pret_message:
      'Le chargement va passer en état "Prêt à charger". Les numéros de bons de livraison seront générés. Confirmer ?',
    pret_message_with_pending:
      '#count unité(s) d\'expédition encore en préparation. En continuant, elles seront automatiquement marquées comme prêtes et le chargement passera en état "Prêt à charger".',
    pret_confirm: "Confirmer",
    pret_confirm_with_ready: "Marquer prêtes et continuer",
    livraisons_count: "#count livraisons",
    sites_count: "#count sites",
  },
  chargement_form: {
    title_new: "Nouvelle préparation d'expédition",
    title_edit: "Chargement du",
    today: "Aujourd'hui",
    tomorrow: "Demain",
    after_tomorrow: "Après-demain",
    next_monday: "Lundi",
    create_and_add: "Créer et ajouter des unités d'expédition",
    entrepot_source: "Entrepôt de départ",
    entrepot_readonly:
      "Retirez les unités d'expédition pour changer d'entrepôt",
    transporteur_suggestions:
      "Plusieurs transporteurs possibles — sélectionnez :",
  },
  navbar: {
    reconnect: "Veuillez vous reconnecter",
    reading_badge: "Lecture du badge en cours...",
    scan_badge: "Scanner un badge",
    dark_mode: "Mode sombre",
    light_mode: "Mode clair",
    all_warehouses: "Tous les entrepôts",
  },
  user_card: {
    my_profile: "Mon profil",
    report_problem: "Signaler un problème",
    bug_title: "Vous avez rencontré un bug ?",
    bug_desc1:
      "Nous faisons le maximum pour corriger les problèmes rapidement.",
    bug_desc2:
      "Pour cela, chaque information est précieuse. N'hésitez pas à nous signaler ce qui ne fonctionne pas.",
    recording_desc1: "Vous pouvez activer l'enregistrement de votre session",
    recording_desc2:
      "Une fois l'enregistrement activé, essayez de reproduire le bug : cela nous aidera à mieux comprendre ce qui s'est passé.",
    recording_desc3: "Ensuite, contactez-nous via le chat.",
    open_chat: "Ouvrir le chat de support",
  },
  substep_validation: {
    errors_title: "Vous avez des erreurs:",
    errors_blocking: "Les erreurs sont bloquante pour la validation de l'opé.",
    warnings_title: "Vous avez des avertissements:",
    warnings_not_blocking:
      "Les avertissements ne sont pas bloquants pour la validation de l'opé.",
    step_validation_title: "Validation de l'étape #name",
    no_record_for_control: "Aucun enregistrement pour le contrôle #name",
    control_matrix_in_progress:
      "La matrice de contrôle est toujours en cours, souhaitez vous la terminer ?",
    control_matrix_not_started: "La matrice de contrôle n'a pas été démarrée",
  },
  ips_input: {
    local: "locales",
    authorized: "autorisées",
    add_ip: "Ajouter une ip",
  },
  smtp_form: {
    delete_smtp: "Supprimer le SMTP",
  },
  search_bar: {
    no_result: "Aucun résultat",
    min_chars: "Saisir au moins 3 caractères",
  },
  qr_link: {
    creating: "Création du lien....",
  },
  arborescence_etape: {
    etapes: "Etapes",
  },
  tags: {
    add_placeholder: "Ajouter un tag ...",
  },
  user_infos_select: {
    label: "Utilisateurs:",
    add_user: "Ajouter un utilisateur",
  },
  widget_title: {
    theme: "Thème",
    remove: "Retirer",
  },
  week_loader: {
    week: "Semaine",
  },
  multi_date: {
    date_interval: "Interval de date",
  },
  multi_checkbox: {
    all: "Toutes",
    select_all: "Tout sélectionner",
    deselect_all: "Tout désélectionner",
  },
  input_search_dialog: {
    placeholder: "Rechercher...",
  },
  embedded_expression: {
    size: "Taille",
    alignment: "Alignement",
  },
  intext_embedded: {
    dynamic_values: "Valeurs dynamiques",
  },
  select_autocomplete: {
    suggestions: "Suggestions:",
  },
  chargement_selector: {
    title: "Sélectionner un chargement",
  },
  cursor_tooltip_dialog: {
    title: "Ajouter une valeur dynamique",
  },
  article_badges: {
    traca: "Traça",
    pdc: "PDC",
    consos: "Consos",
    auto: "Auto",
  },
  sankey: {
    zoom_in: "Zoom +",
    zoom_out: "Zoom -",
    recenter: "Recentrer",
    fullscreen: "Plein écran",
  },
  dynamic_text: {
    general_elements: "Éléments généraux",
    operators: "Opérateurs",
    nomenclature_elements: "Éléments liés à la nomenclature",
    nomenclature_variable_elements: "Éléments variables liés à la nomenclature",
    variables: "Variables",
    documents: "Documents",
    formula: "Formule",
    drop_in_formula: "Déposez l'élément dans la formule",
  },
  bon_livraison_display: {
    provisional: "Bon de livraison provisoire",
    provisional_short: "BL provisoire",
    print_bl: "Imprimer le BL",
    print_fiches: "Fiches",
  },
  bon_transfert_display: {
    provisional: "Bon de transfert provisoire",
    transfer_number: "Transfert N°",
    print_bt: "Imprimer le BT",
    print_fiches: "Fiches",
  },
  /*
   * 🔴 **`error.unknown` était appelée 33 fois et n'existait pas.** Le bloc de messages d'erreur
   * s'appelle `errors` (pluriel) et sert à traduire un CODE renvoyé par le serveur ; `error.unknown`,
   * lui, est le repli quand la réponse ne porte aucun message exploitable —
   * `e?.error?.message ?? translate("error.unknown")`.
   *
   * `TranslateUtils.translate` rendant la clé brute quand le libellé manque, l'utilisateur lisait
   * littéralement « error.unknown » au moment précis où quelque chose venait d'échouer : le seul
   * instant où un message doit être lisible.
   *
   * ⚠️ **La clé est créée, les 33 appels ne sont pas touchés.** Les renommer aurait été 33 occasions
   * d'en oublier un — et l'oubli ne se voit que sur l'écran concerné, au moment d'une erreur.
   */
  error: {
    unknown: "L'opération n'a pas abouti. Réessayez ; si cela se reproduit, signalez-le en précisant ce que vous faisiez.",
  },
  errors: {
    /*
     * **Les 63 codes d'erreur du module « Développement recette ».**
     *
     * 🔴 **Ils n'en avaient AUCUN** — trouvé à l'audit du 14/09/2026. `error.interceptor` retombe alors
     * sur `error.error.message`, c'est-à-dire le message brut du serveur : les utilisateurs lisaient
     * « Essai 5a0f9fbc-af48-4736-8011-4dd2c8c3e2da introuvable », et les non-francophones lisaient du
     * français. Les 50 messages qui portaient un identifiant ont été réécrits côté serveur en même temps
     * — l'identifiant reste dans la trace, il ne monte plus à l'écran.
     *
     * ⚠️ **`#detail` sur trois codes, et c'est délibéré** : `COMPOSANT_AMBIGU`, `PROMOTION_IMPOSSIBLE` et
     * `MO_INCOMPLETE` composent leur message au moment du refus, à partir des constats en cours. Une clef
     * ne peut pas porter ce contenu — elle porte l'encadrement, le paramètre porte ce qui bloque.
     */
    RECETTE: {
      ARTICLE_REQUIS: "Indiquez l’article fabriqué dont cette étape reprend la recette.",
      CASCADE_VIDE: "Aucune spécification exploitable sur « #nom » : il n'y a rien à importer.",
      CIQUAL_SANS_TENEUR:
        "Cet aliment ne porte aucune teneur mesurée pour les constituants suivis.",
      COMPOSANT_AMBIGU: "#detail",
      COMPOSANT_REQUIS:
        "Indiquez un article du référentiel, ou un nom pour créer une matière provisoire.",
      DENOMINATION_REQUISE:
        "Donnez un nom à cette étape : c’est la seule chose obligatoire pour la créer.",
      DUREE_VIE_INVALIDE: "Une durée de vie ne peut pas être négative.",
      ESSAI_INTROUVABLE:
        "Ce projet d'essai est introuvable : il a pu être supprimé depuis l'ouverture de l'écran.",
      ETAPE_CONSOMMEE:
        "Cette étape est consommée par la recette : retirez d’abord la ligne qui l’utilise.",
      ETAPE_DEJA_PRESENTE:
        "Cet article est déjà une étape de cet essai : ajustez sa quantité au lieu de l’ajouter deux fois.",
      ETAPE_DEJA_PROMUE: "Cette étape a déjà donné son article : il n'y a rien à créer.",
      ETAPE_EST_PRODUIT_FINI:
        "C’est le produit fini de cet essai : le retirer laisserait le projet sans recette.",
      ETAPE_INTROUVABLE:
        "Cette étape est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      ETAPE_NON_INVENTEE:
        "Cette étape a été importée d’un article existant : il n’y a rien à créer. Seule une étape inventée devient un article.",
      ETAPE_OPE_HORS_ESSAI:
        "Cette étape d'exécution appartient à un ordre de fabrication : on n'y attache pas un commentaire d'essai.",
      ETAPE_OPE_LISTE_DEJA_LIEE:
        "Cette étape porte déjà une liste de commentaires : la remplacer rendrait invisibles ceux déjà saisis.",
      ETAPE_OPE_NOT_FOUND:
        "Cette étape d'exécution est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      ETIQUETTE_SANS_LOT:
        "L'essai n'a pas de numéro de lot : l'étiquette ne désignerait aucun échantillon.",
      FAMILLE_HORS_ESSAI:
        "Ce nœud appartient à une spécification de production : sa famille technique se change sur la fiche article, pas depuis un essai.",
      FAMILLE_INTROUVABLE:
        "Cette valeur n'existe pas dans le référentiel « #referentiel » : écrire une référence morte rendrait le champ vide à la relecture, sans explication.",
      FICHE_ARTICLE_INTROUVABLE:
        "La fiche de cet article est introuvable : c'est elle qui manque, et sans elle le composant n'a ni unité ni teneurs.",
      LIGNE_DU_MODELE:
        "« #nom » vient de la spécification modèle, partagée : sa quantité ne se modifie pas depuis un essai.",
      LIGNE_INTROUVABLE:
        "Cette ligne de composition est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      LOT_EMPLOYE_DEJA_DECLARE: "Ce lot est déjà déclaré pour ce composant dans cette opération.",
      LOT_EMPLOYE_INTROUVABLE: "Cette déclaration n’existe pas ou a déjà été retirée.",
      LOT_EMPLOYE_SANS_COMPOSANT:
        "Cette déclaration ne désigne aucun composant : elle ne pourrait pas être rattachée.",
      LOT_EMPLOYE_SANS_DESIGNATION:
        "Indiquez un lot du stock, ou le numéro lu sur l’étiquette : une déclaration qui ne désigne rien n’est pas une trace.",
      MARGE_CIBLE_INVALIDE:
        "La marge s'exprime en pourcentage du prix de vente : entre 0 et 100 (exclu), ou vide.",
      MATIERE_INTROUVABLE:
        "Ce composant provisoire est introuvable : il a pu être retiré depuis l'ouverture de l'écran.",
      MO_INCOMPLETE: "Main-d’œuvre : #detail",
      MP_ESSAI_NOT_FOUND:
        "Cette matière provisoire est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      NOEUD_INTROUVABLE:
        "Cette étape est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      NOMENCLATURE_ABSENTE: "Cette spécification n’a pas de nomenclature : rien à composer.",
      NUTRISCORE_CATEGORIE_INVALIDE: "Cette catégorie Nutri-Score est inconnue.",
      NUTRISCORE_PART_INVALIDE:
        "Le pourcentage de fruits, légumes et légumineuses doit être compris entre 0 (exclu) et 100. Pour « rien ne compte », répondez « non ».",
      NUTRITION_VIDE:
        "Aucune teneur exploitable : indiquez au moins un constituant, ou retirez la surcharge.",
      OPERATION_AUTRE_ESSAI:
        "Ce nœud appartient à un autre essai : une opération ne peut pas dérouler le procédé d'un essai voisin.",
      OPERATION_HORS_ESSAI:
        "Ce nœud appartient à une spécification de production : on ne lance pas une opération d'essai dessus.",
      OPERATION_LISTE_DEJA_LIEE:
        "Cette opération porte déjà une liste de commentaires : la remplacer rendrait invisibles ceux déjà saisis.",
      OPERATION_NOEUD_INTROUVABLE:
        "Cette étape est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      OPERATION_NOT_FOUND:
        "Cette opération est introuvable : elle a pu être supprimée depuis l'ouverture de l'écran.",
      OPERATION_SANS_ETAPE:
        "Ce nœud n'a aucune étape de procédé : définissez le mode opératoire avant de lancer une opération.",
      PERTE_INVALIDE:
        "Une perte se situe entre 0 et 100 % (exclus) : à 100 %, la quantité à engager serait infinie.",
      PESEE_INVALIDE: "Une densité et un poids net doivent être des nombres strictement positifs.",
      PHOTO_REQUISE: "Aucun document à rattacher.",
      POURCENTAGE_MODELE_ABSORBE:
        "Renormaliser demanderait de modifier une ligne venue de la spécification modèle, partagée : l'opération est refusée plutôt que d'écrire une recette de production depuis un essai.",
      PRIX_CIBLE_INVALIDE: "Le prix de vente doit être strictement positif, ou vide.",
      PRIX_INVALIDE: "Un prix ne peut pas être négatif.",
      PRODUIT_FINI_DEJA_DEFINI:
        "Cet essai porte déjà des étapes : son produit fini est celui que rien ne consomme. Ajoutez les suivantes depuis le schéma de fabrication.",
      PROJET_CHAMP_NON_MODIFIABLE: "Ces champs ne se modifient pas après la création du projet.",
      PROJET_INTROUVABLE:
        "Ce projet est introuvable : il a pu être supprimé depuis l'ouverture de l'écran.",
      PROMOTION_IMPOSSIBLE: "#detail",
      QUANTITE_INVALIDE: "Une quantité doit être un nombre positif.",
      RENDEMENT_INVALIDE:
        "Un rendement doit être un nombre strictement positif (1 = aucune perte), et une quantité obtenue aussi.",
      SEMI_FINI_DEJA_DANS_LE_PROJET:
        "« #nom » vient déjà de ce projet : il est dans ses nœuds disponibles.",
      SEMI_FINI_INTROUVABLE:
        "Ce semi-fini provisoire est introuvable : il a pu être retiré depuis l'ouverture de l'écran.",
      SENS_PERTE_INVALIDE:
        "Le sens de saisie vaut « UTILISE » (ce qui entre dans le produit) ou « ENGAGE » (ce qui sort du stock).",
      SPECIFICATION_INTROUVABLE:
        "Cette spécification est introuvable : elle a pu être retirée depuis l'ouverture de l'écran.",
      SPECIFICATION_PARTAGEE:
        "Cette spécification est partagée : elle sert en production et ne se modifie pas depuis la formulation. Travaillez sur un essai.",
      SPECIFICATION_SANS_VERSION: "« #nom » n'a aucune version : il n'y a rien à importer.",
      TAILLE_LOT_INVALIDE: "La taille de lot doit être strictement positive : c’est un diviseur.",
      UNITE_INCONNUE: "Cette unité n'existe pas dans le référentiel des unités.",
      VOLUME_ANNUEL_INVALIDE:
        "Le volume annuel s'exprime dans l'unité de gestion du produit fini : un nombre strictement positif, ou vide.",
    },
    ARTICLE_FOURNI: {
      DUPLICATE_TARIF:
        "Un tarif existe déjà pour cet article et « #cible » (unité : #unite). Deux tarifs identiques ne peuvent pas coexister : rien ne dirait lequel s'applique. Ajoutez une tranche à ce tarif existant (quantité minimum ou période de validité différente), ou modifiez le coefficient d'unité de gestion s'il s'agit d'un autre conditionnement.",
      COEFFICIENT_REQUIS:
        "Renseignez le coefficient d'unité de gestion : c'est le nombre d'unités de l'article contenues dans une unité d'achat (25 pour un sac de 25 kg si l'article se compte en kg). Sans lui, le prix n'est pas convertible en coût. Mettez 1 si le prix est déjà celui d'une unité.",
    },
    INFO_PRIX: {
      DUPLICATE_TRANCHE:
        "Deux tranches de prix ont la même quantité minimum et la même période de validité. Modifiez la quantité mini ou les dates de validité.",
    },
    ARTICLE: {
      HAS_MOUVEMENTS:
        "Impossible de supprimer cet article : il possède des mouvements de stock",
    },
    EXPEDITION_UNIT_TYPE: {
      SAVE_FAILED: "Impossible de sauvegarder le type d'unité d'expédition",
      DELETE_FAILED: "Impossible de supprimer le type d'unité d'expédition",
      DENOMINATION_REQUIRED: "La dénomination est obligatoire",
      NOT_FOUND: "Type d'unité d'expédition introuvable",
    },
    PAYS: {
      ISOCODE_DUPLICATE: "Ce code ISO est déjà utilisé par un autre pays",
    },
    FACTURE: {
      NOT_FOUND: "Facture introuvable",
      ALREADY_ISSUED: "Cette facture est déjà émise.",
      EMPTY: "Impossible de figer une facture vide.",
      ADDRESS_REQUIRED: "Veuillez sélectionner l'adresse de facturation",
      RIB_REQUIRED: "Veuillez sélectionner un RIB pour cette facture",
      CHRONOLOGICAL_INCONSISTENCY:
        "Incohérence chronologique (#kind) : la date d'émission #emissionDate est antérieure à celle du dernier document émis #lastNumero (#lastDate).",
      BL_INVALID: "BL invalide",
      BL_ID_MISSING: "BL id manquant",
      RECOMPUTE_LOCKED:
        "Impossible de recalculer une facture qui n'est plus en rédaction",
    },
    COMMANDE_CLIENT: {
      FRAIS_PORT_FIGE:
        "Les frais de port sont figés (synchronisés depuis le connecteur) et ne peuvent pas être modifiés manuellement.",
    },
    QR: {
      MISSING_EAN14:
        "Impossible de construire le code barre car il manque l'EAN14 dans l'article",
      MISSING_LOT_NUMBER:
        "Impossible de construire le code barre car il manque le numéro de lot",
      MISSING_PROD_DATE:
        "Impossible de construire le code barre car il manque la date de fabrication du lot",
      MISSING_DLUO:
        "Impossible de construire le code barre car il manque la DLUO du lot",
      MISSING_COUNT:
        "Impossible de construire le code barre car il manque la quantité contenue par l'unité d'expédition",
      MISSING_NET_WEIGHT:
        "Impossible de construire le code barre car il manque le poids net de l'article",
      MISSING_LOGISTIC_WEIGHT:
        "Impossible de construire le code barre car il manque le poids logistique de l'article",
    },
    PALETTE: {
      NOT_FOUND_SSCC: "Aucune unité d'expédition n'a été trouvée pour ce SSCC",
      ALREADY_IN_BL: "L'unité d'expédition est déjà présente dans un autre BL",
      EMPTY:
        "Impossible d'éditer la fiche d'expédition d'une unité d'expédition vide",
      SELECT_AT_LEAST_ONE:
        "Veuillez sélectionner au moins une unité d'expédition",
      TRANSFERT_NOT_FOUND: "Le transfert entrepôt est introuvable",
      ALREADY_SHIPPED: "L'unité d'expédition #sscc a déjà été expédiée",
    },
    TRANSFERT_ENTREPOT: {
      DUPLICATE_ARTICLE: "L'article (et le lot) est déjà dans le transfert",
      VALIDATION_DATE_REQUIRED: "Veuillez saisir la date de validation",
      EMPTY: "Le transfert doit contenir au moins une ligne",
      MUST_BE_AWAITING_SHIPMENT:
        "Le transfert doit être en attente d'expédition",
      MUST_BE_SHIPPED_OR_PARTIAL:
        "Le transfert doit être en état 'Expédiée' ou 'Transférée partiellement'",
      NO_RECEPTION:
        "Aucune réception saisie. Veuillez effectuer le déchargement et la réception avant de confirmer.",
      QUANTITY_REQUIRED: "Veuillez saisir une quantité pour chaque ligne",
      MISMATCH: "Erreur de transfert",
      NOT_FOUND: "Transfert introuvable",
      NO_MOVEMENTS: "Aucun mouvement à traiter",
      NO_LOTS_TO_SHIP: "Aucun lot à expédier",
      ZONE_NOT_FOUND: "Zone de transfert introuvable pour ce transfert",
      STOCK_NOT_FOUND: "Stock introuvable",
      ARTICLE_NOT_RESOLVED: "Impossible de déterminer l'article du stock",
    },
    CHARGEMENT_EMPTY:
      "Impossible de déclarer le chargement prêt : aucune unité d'expédition associée.",
    CHARGEMENT_PALETTES_NOT_READY:
      "Impossible de déclarer le chargement prêt : certaines unités d'expédition ne sont pas prêtes.",
    EXPEDITION_COMMANDE: {
      NOT_FOUND: "Commande client introuvable",
      NO_MOVEMENTS: "Aucun mouvement à traiter",
      NO_LOTS_TO_SHIP: "Aucun lot à expédier",
      COMMANDE_NOT_FOUND: "Commande client introuvable",
      STOCK_NOT_FOUND: "Stock introuvable",
    },
    LOT: {
      NOT_FOUND: "Lot introuvable",
      INVALID_PAYLOAD: "Aucun lot fourni pour la sauvegarde",
      DUPLICATE_NUMBER:
        "Un lot existe déjà avec ce numéro, veuillez en saisir un autre",
      ARTICLE_NOT_FOUND_BY_EAN:
        "Aucun article n'a pu être identifié avec cet EAN14",
      SPECIFICATION_NOT_FOUND:
        "La spécification pour cet article est introuvable",
      FICHE_PALETTE_NO_LOT:
        "Impossible de construire la fiche d'unité d'expédition, car aucun lot n'a été fourni",
    },
    LOT_MOUVEMENT: {
      NULL_MOVEMENT_LIST: "Aucun mouvement à traiter",
      NULL_MOVEMENT: "Il y a un mouvement NULL",
      MISSING_DESTINATION_ADDRESS: "Adresse de destination manquante",
      MISSING_LOT: "Lot manquant pour la réception",
      MISSING_ARTICLE: "Article manquant pour la réception",
      ZERO_QUANTITY: "Veuillez renseigner une quantité non nulle",
      MISSING_DEPARTURE_STOCK: "Stock de départ manquant",
      DEPARTURE_STOCK_NOT_FOUND: "Stock de départ introuvable",
      MISSING_ARTICLE_REGROUPEMENT: "Article manquant pour le regroupement",
      MISSING_STOCK_OR_ADDRESS:
        "Stock ou adresse + lot manquant pour l'inventaire",
      MISSING_OF_REFERENCE: "Référence OF manquante pour la fabrication",
      MISSING_ARTICLE_OR_LOT:
        "Article ou lot manquant pour la création/fabrication",
      MISSING_LOT_SELECTION: "Lot non sélectionné pour le transfert",
      MISSING_PALETTE_OR_ADDRESS:
        "Unité d'expédition ou adresse d'arrivée manquante",
      MISSING_COMMANDE_CLIENT:
        "Commande client manquante pour la préparation d'expédition",
      SAME_STOCK_TRANSFER: "Impossible de transférer vers le même stock",
      INSUFFICIENT_DEPARTURE_STOCK:
        "Stock de départ insuffisant pour le transfert",
      MISSING_DESTRUCTION_PARAMS: "Paramètres manquants pour la destruction",
      INSUFFICIENT_STOCK_DESTRUCTION: "Stock insuffisant pour la destruction",
      MISSING_DELIVERY_PARAMS: "Paramètres manquants pour la livraison",
      INSUFFICIENT_STOCK_DELIVERY: "Stock insuffisant pour la livraison",
      MISSING_SUPPLIER_RETURN_PARAMS:
        "Paramètres manquants pour le retour fournisseur",
      INSUFFICIENT_STOCK_SUPPLIER_RETURN:
        "Stock insuffisant pour le retour fournisseur",
      MISSING_CLIENT_RETURN_PARAMS:
        "Paramètres manquants pour le retour client",
      MISSING_CONSO_FAB_PARAMS:
        "Paramètres manquants pour la consommation fabrication",
      CONSO_FAB_STOCK_NOT_FOUND:
        "Stock de consommation fabrication introuvable",
      INSUFFICIENT_STOCK_CONSO_FAB:
        "Stock insuffisant pour la consommation fabrication",
      MISSING_EXPEDITION_PARAMS: "Paramètres manquants pour l'expédition",
      INSUFFICIENT_STOCK_EXPEDITION: "Stock insuffisant pour l'expédition",
      MISSING_EXPEDITION_TRANSFERT_PARAMS:
        "Paramètres manquants pour l'expédition transfert",
      MISSING_TRANSFER_ZONE_ADDRESS: "Adresse de zone de transfert manquante",
      MISSING_OWNER_CHANGE_QUANTITY:
        "Quantité manquante pour le changement de propriétaire",
      MISSING_NEW_OWNER:
        "Nouveau propriétaire manquant pour le changement de propriétaire",
      LOT_NOT_FOUND: "Lot introuvable",
      ARTICLE_NOT_FOUND: "Article introuvable pour ce lot",
      INSUFFICIENT_ARTICLE_STOCK: "Stock insuffisant pour cet article",
      MISSING_DEPARTURE_STOCK_FOR_LOT:
        "Stock de départ requis pour un article géré par lot",
      LOT_STOCK_NOT_FOUND: "Aucun stock trouvé pour ce lot à cette adresse",
      INSUFFICIENT_LOT_STOCK: "Stock insuffisant pour ce lot",
    },
    AUTH: {
      TOO_MANY_ATTEMPTS:
        "Vous avez dépassé le nombre de tentatives autorisées. Veuillez réessayer dans #remainingSeconds secondes",
      IP_NOT_ALLOWED: "Adresse IP non autorisée",
      LOGIN_REQUIRED: "Veuillez vous connecter avec vos identifiants",
      CREDENTIALS_REQUIRED: "Login et mot de passe requis",
    },
    TIERS: {
      IMPORT_MAINTENANCE: "Import ARTICLE FOURNI en maintenance",
      GROUPS_NOT_FOUND: "Groupe(s) introuvable(s)",
      CODE_INVALID_FORMAT:
        "Référence tiers invalide : seuls les lettres et les chiffres sont acceptés",
      CODE_DUPLICATE: "Cette référence est déjà utilisée par un autre tiers",
    },
    TRANSACTION: {
      NOT_FOUND: "Transaction introuvable",
      PAYMENT_NOT_FOUND: "Paiement introuvable",
      FACTURE_NOT_FOUND: "Facture introuvable",
      INVALID_AMOUNT: "Le montant de la transaction doit être supérieur à 0",
      INVALID_PAIEMENT_AMOUNT:
        "Le montant de chaque paiement doit être supérieur à 0",
    },
    BON_TRANSFERT: {
      NOT_FOUND: "Bon de transfert introuvable",
    },
    BON_LIVRAISON: {
      NOT_FOUND: "Bon de livraison introuvable",
    },
    COMMANDE: {
      DUPLICATE_ARTICLE: "L'article est déjà dans la commande",
      ERROR: "Erreur de commande",
      LIGNES_VERROUILLEES:
        "Les lignes de la commande #numero ne sont plus modifiables : elle est déjà confirmée auprès du fournisseur (état #etat).",
    },
    POSTE: {
      CANNOT_DELETE_COMPLETED: "Impossible de supprimer un poste terminé",
    },
    INVENTAIRE: {
      EMPTY: "L'inventaire est vide",
    },
    INTEGRATIONS: {
      NOT_FOUND: "L'intégration n'existe pas",
    },
    CRENEAU: {
      NO_SCHEDULE_FOUND: "Aucun horaire trouvé",
    },
    TRAVAIL: {
      DURATION_REQUIRED: "Veuillez saisir la durée pour #label",
    },
  },
  stats: {
    ca_cumule: "CA cumulé",
    ca_par_periode: "CA par période",
    repartition_ventes: "Répartition des ventes par client",
    repartition_achats: "Répartition des achats par fournisseur",
    repartition_marges_client: "Répartition des marges par client",
    repartition_marges_article: "Répartition des marges par article",
    repartition_consos: "Répartitions des consommations par produit fabriqué",
    voir_fiche_produit: "Voir la fiche produit",
    voir_fiche_tiers: "Voir la fiche du tiers",
    synthese_mouvements: "Synthèse des mouvements",
    heures_par_user: "Heures travaillées par utilisateur",
    liste_ofs: "Liste des OFs",
    stock_actuel: "Stock actuel",
    quick_wins: "Quick wins",
    evolution_prix_vente: "Évolution prix de vente (moyenne pondérée)",
    evolution_prix_achat: "Évolution prix d'achat (moyenne pondérée)",
    repartition_articles_achetes:
      "Répartition des articles achetés (par tiers)",
    date_interval: "Interval de date",
    no_article_matching: "Aucun article correspondant",
    commande_placeholder: "Commande n°…",
    client_placeholder: "Nom client…",
    fournisseur_placeholder: "Nom fournisseur…",
    reference_placeholder: "Référence / libellé…",
    of_placeholder: "OF n°, article, lot…",
  },
  evolution_marge: {
    titre: "Évolution de la marge",
    mode: "Vue",
    mode_facture: "Facturé",
    mode_produit: "Produit",
    mode_compare: "Comparé",
    mode_facture_help:
      "Marge réellement réalisée sur les ventes facturées, reconnue à la date de facture. C'est la vue qui fait foi.",
    mode_produit_help:
      "Marge projetée à la fabrication : production valorisée au prix de vente de référence de chaque article, reconnue à la date de fin d'OF. C'est une projection, pas un résultat comptable.",
    mode_compare_help:
      "Superpose les deux vues. Production au-dessus de la facturation = vous fabriquez du stock, la marge est immobilisée. En dessous = vous déstockez. Les deux courbes ne s'additionnent jamais : ce serait compter deux fois la même marchandise.",
    granularite: "Granularité",
    part: "Part",
    reglages: "Réglages",
    main_oeuvre: "Main d'œuvre",
    inclure_mo: "Inclure la MO",
    du_ca: "du CA",
    periodes_actives: "Périodes actives",
    g_abbr_jour: "J",
    g_abbr_semaine: "S",
    g_abbr_mois: "M",
    g_abbr_trimestre: "T",
    g_abbr_annee: "A",
    calcul_help_avec_mo:
      "Main d'œuvre INCLUSE : le coût retenu est matière + main d'œuvre. C'est le coût de revient complet.",
    calcul_help_sans_mo:
      "Main d'œuvre EXCLUE : seul le coût matière est déduit. Utile pour isoler la performance achat/matière, mais la marge affichée est mécaniquement plus élevée que la marge réelle.",
    calcul_help_facture:
      "Marge facturée = chiffre d'affaires net encaissé − coût du lot expédié.\nLe coût retenu est celui figé à la date d'expédition (matière + main d'œuvre, transport d'achat inclus). Les avoirs sont déduits et les remises de pied de facture répercutées.\nC'est un résultat comptable : c'est cette vue qui fait foi.",
    calcul_help_produit:
      "Marge produite = production valorisée au prix de vente de référence − coût de revient de l'OF.\nLe prix de référence est le prix moyen réellement encaissé sur l'article ces 12 derniers mois ; à défaut, son tarif catalogue.\nC'est une PROJECTION, pas un résultat : la marchandise n'est pas encore vendue. Les semi-finis en sont exclus (pas de prix de vente).",
    g_jour: "Jour",
    g_semaine: "Semaine",
    g_mois: "Mois",
    g_trimestre: "Trimestre",
    g_annee: "Année",
    dimension: "Découpage",
    d_total: "Total",
    d_famille: "Par famille",
    d_article: "Par article",
    d_tiers: "Par client",
    d_tiers_indispo: "Le découpage par client n'existe pas à la fabrication : il n'y a pas encore de client.",
    unite: "Unité",
    en_euro: "En euros",
    en_pourcent: "En %",
    avec_mo: "Avec main d'œuvre",
    sans_mo: "Hors main d'œuvre",
    ecart_mo: "L'écart entre les deux courbes est le poids de la main d'œuvre dans la marge.",
    remise_pied: "Déduire les remises de pied de facture",
    couverture_titre: "Couverture de la valorisation",
    couverture_valorise: "Production valorisée",
    couverture_intermediaire: "Production intermédiaire",
    couverture_intermediaire_help:
      "Semi-finis re-consommés en fabrication. Normal : ils n'ont pas de prix de vente, et leur coût est déjà compté dans celui du produit fini.",
    couverture_sans_prix: "Sans prix de référence",
    couverture_sans_prix_help:
      "Production sans vente observée ni tarif catalogue : impossible à valoriser. À corriger en renseignant un tarif.",
    couverture_of: "#nb OF",
    series_tronquees: "#nb série(s) masquée(s) — les plus faibles. Le total les inclut.",
    aucune_donnee: "Aucune marge sur cette période",
    marge_euro: "Marge",
    taux_marge: "Taux de marge",
    ca: "Chiffre d'affaires",
    ca_projete: "CA projeté",
    cout_matiere: "Coût matière",
    cout_mo: "Coût main d'œuvre",
    periode: "Période",
    export_excel: "Exporter en Excel",
  },
  dashboard_ui: {
    parametres: "Paramètres",
    actualiser: "Actualiser les widgets",
    ca_cumule_info:
      "Somme du total HT signé de toutes les factures émises (avoirs déduits). Les factures provisoires (non figées) sont exclues. Inclut les lignes saisies hors expédition (frais de port, prestations).",
    repartition_ca_info:
      "Le CA inclut toutes les lignes facturées (avoirs déduits). La marge n'est calculée que sur les lignes rattachées à une expédition (lot + coût traçables). Si une partie du CA vient de lignes hors expédition, le label marge précise la couverture.",
    modules: "Modules",
    ajoute: "ajouté",
    ajouter_widget: "ajouter",
    par_defaut: "Dashboard par défaut",
    mon_dashboard: "Mon Dashboard",
    reinitialiser: "Réinitialiser mon dashboard",
    specifications: "Spécifications",
    productivite: "Productivité",
    heures_travaillees: "Heures travaillées",
    ofs_en_cours: "OFs en cours",
    ca_par_periode: "CA par période",
    ofs_en_attente: "OFs en attente",
    valeur_stock: "Valeur stock:",
    ca_cumule: "CA cumulé",
    ca_nmoins: "CA N vs N-1",
    delta_treso: "Delta tréso",
    repartition_ca: "CA",
    commandes_clients: "Commandes clients",
    commandes_fournisseurs: "Commandes fournisseurs",
    status_ofs: "Status des OFs",
    lots_par_etat: "Lots par état",
    repartition_defauts: "Répartition des défauts",
    repartition_temps: "Répartition du temps",
    granularite: "Granularité",
    echelle: "Échelle",
    comparaison: "Comparaison",
    aucune: "Aucune",
    n_moins_1: "N-1",
    periode_precedente: "Préc.",
    jour: "j",
    semaine: "s",
    mois: "m",
    annee: "a",
    quantite_produite_periode: "Quantité produite par période",
    toutes_lignes: "Toutes",
    ligne: "Ligne",
    cf_envoi: "À envoyer",
    cf_confirmation: "À confirmer",
    cf_livraison: "À livrer",
    cf_livree_partiel: "Livrée partiel.",
    cc_traitement: "À traiter",
    cc_preparation: "À préparer",
    cc_partielle: "Expédition partielle",
    of_prod: "Prod.",
    of_en_prod: "En cours",
    of_planif: "Planification",
    of_validation: "Validation",
    of_bilan_matiere: "Bilan matière",
    of_reintegration: "Réintégration",
    spec_redigees: "Rédigées",
    spec_validees: "Validées",
    valeur_totale: "Valeur totale",
    concentration: "Concentration 80%",
    concentration_desc: "représentent 80% de la valeur",
    moyenne: "Moy.",
    top: "Top",
    export_excel: "Exporter vers Excel",
    cat_production: "Production",
    cat_financier: "Financier",
    cat_commandes: "Commandes",
    cat_stock: "Stock",
    cat_supervision: "Supervision",
    cat_ligne: "Ligne",
    cat_qualite: "Qualité",
    w_productivite_moyenne: "Productivité moyenne",
    w_productivite_moyenne_desc:
      "Score de productivité moyen par ligne, calculé sur les OFs réalisés",
    w_ofs_en_cours: "OFs en cours",
    w_ofs_en_cours_desc:
      "Ordres de fabrication en production, regroupés par ligne",
    w_heures_travaillees: "Heures travaillées",
    w_heures_travaillees_desc:
      "Heures cumulées par utilisateur, avec détail par travail",
    w_quantite_ofs: "OFs par état",
    w_quantite_ofs_desc: "Nombre d'ordres de fabrication par état et par mois",
    w_ofs: "Liste des OFs",
    w_ofs_desc:
      "Liste filtrable des OFs (en attente, planif, validation, réintégration…)",
    w_specifications: "Spécifications",
    w_specifications_desc:
      "Liste filtrable des spécifications (rédigées, validées)",
    w_quantite_produite_periode: "Quantité produite par période",
    w_quantite_produite_periode_desc:
      "Volumes produits par ligne et par période, avec comparaison N-1",
    w_ca_cumule: "CA cumulé",
    w_ca_cumule_desc:
      "Chiffre d'affaires HT cumulé sur la période sélectionnée",
    w_ca_par_periode: "CA par période",
    w_ca_par_periode_desc:
      "CA HT par période, comparable à N-1 ou à la période précédente",
    w_ca_delta: "Évolution CA (delta)",
    w_ca_delta_desc:
      "Variation du CA d'une période à l'autre (positif/négatif)",
    w_ca_n_moins_1: "Comparaison CA N vs N-1",
    w_ca_n_moins_1_desc:
      "Évolution du CA cumulé cette année comparé à l'année précédente",
    w_repartition_par_client: "Répartition par client",
    w_repartition_par_client_desc:
      "Part du CA par client (top clients en doughnut)",
    w_commandes_fournisseurs: "Commandes fournisseurs",
    w_commandes_fournisseurs_desc:
      "Liste des commandes fournisseurs filtrable par état",
    w_commandes_clients: "Commandes clients",
    w_commandes_clients_desc: "Liste des commandes clients filtrable par état",
    w_couverture_commandes: "Couverture des commandes",
    w_couverture_commandes_desc:
      "Ai-je assez de stock pour servir les commandes en attente ? (manque / à réserver / couvert)",
    w_valeur_par_famille: "Valeur stock par famille",
    w_valeur_par_famille_desc:
      "Répartition de la valeur stock par famille d'articles",
    w_evolution_valeur: "Évolution valeur stock",
    w_evolution_valeur_desc:
      "Suivi de la valeur globale du stock dans le temps (jour, semaine, mois)",
    evolution_valeur_title: "Évolution valeur stock",
    evolution_valeur_delta_30j: "Variation 30j :",
    evolution_valeur_partial_history: "Données disponibles depuis le #since",
    evolution_valeur_single_point:
      "Un seul snapshot pour l'instant — la courbe se construira dans les prochains jours",
    evolution_valeur_empty: "Pas encore de données d'évolution",
    evolution_valeur_empty_hint:
      "Cliquez sur l'icône Actualiser pour créer le premier snapshot.",
    evolution_valeur_refreshed: "Snapshot enregistré (#count lignes)",
    evolution_valeur_backfilled:
      "Historique reconstruit sur #days jours (#count lignes, #mouvements mouvements pris en compte)",
    evolution_valeur_refresh:
      "Recalculer l'historique sur la plage sélectionnée",
    evolution_valeur_partial_warning:
      "L'historique stocké est incomplet sur cette plage.",
    evolution_valeur_recompute: "Recalculer",
    evolution_valeur_field_range: "Plage",
    evolution_valeur_field_granularity: "Granularité",
    evolution_valeur_field_dimension: "Dimension",
    evolution_valeur_field_filter: "Filtrer",
    evolution_valeur_col_dim: "Dimension",
    evolution_valeur_col_current: "Valeur actuelle",
    evolution_valeur_col_delta: "Variation période",
    evolution_valeur_export:
      "Exporter en Excel (toutes dimensions, multi-feuilles)",
    w_lot_par_etat: "Lots par état",
    w_lot_par_etat_desc:
      "Lots non conformes et lots avec DLUO dépassée, avec stocks restants",
    w_planning: "Planning (Activité à venir)",
    w_planning_desc:
      "Vue calendaire transversale : commandes fournisseurs/clients, OF, réceptions, expéditions…",
    w_live_production: "Live Production",
    w_live_production_desc:
      "Supervision atelier en temps réel : état des lignes, cadence, OF, conducteurs. Bouton fenêtre détachée pour 2e écran.",
    w_quantite_produite: "Quantité produite",
    w_quantite_produite_desc:
      "Volumes produits par cette ligne au fil du temps",
    w_main_oeuvre: "Main d'œuvre",
    w_main_oeuvre_desc:
      "Heures de main d'œuvre par utilisateur sur cette ligne",
    w_productivite_ligne: "Productivité",
    w_productivite_ligne_desc:
      "Évolution mensuelle du score de productivité de la ligne",
    w_couts_matiere_personnel: "Coûts matière / personnel",
    w_couts_matiere_personnel_desc:
      "Décomposition mensuelle des coûts matière et personnel",
    w_ofs_ligne: "OFs de la ligne",
    w_ofs_ligne_desc: "Liste triable des OFs réalisés sur cette ligne",
    w_articles_produits: "Articles produits",
    w_articles_produits_desc:
      "Top 12 des articles produits sur cette ligne (en quantité)",
    w_nc_stats: "Non-conformités (synthèse)",
    w_nc_stats_desc:
      "Volume des NC, origine (fournisseur / interne) et gravité",
    w_nc_closure_metrics: "Taux de clôture NC",
    w_nc_closure_metrics_desc:
      "Taux de clôture, délai moyen et SLA J+30 des non-conformités",
    w_nc_supplier_compliance: "Conformité fournisseurs",
    w_nc_supplier_compliance_desc:
      "Top des fournisseurs avec le plus de NC sur la période",
    w_certificat_bio_echeance: "Certificats bio",
    w_certificat_bio_echeance_desc: "Certificats bio arrivant à échéance",
    nc_kpi_ouvertes: "Ouvertes",
    nc_kpi_critiques: "Critiques ouvertes",
    nc_kpi_total: "Total période",
    nc_kpi_cout: "Coût période",
    nc_kpi_taux_echappees: "Taux d'échappées",
    nc_kpi_taux_echappees_hint:
      "% NC détectées par un tiers externe — KPI ISO 9001",
    nc_kpi_taux_cloture: "Taux de clôture",
    nc_kpi_delai_moyen: "Délai moyen",
    nc_kpi_sla_j30: "SLA J+30",
    nc_kpi_sla_j30_hint: "% NC clôturées sous 30 jours sur le total créé",
    nc_kpi_taux_conformite: "Taux conformité",
    nc_origine_interne: "Interne",
    nc_origine_externe: "Externe",
    nc_origine_mixte: "Mixte",
    nc_origine_non_renseigne: "Non renseigné",
    nc_titre_origine: "Origine des NC",
    nc_titre_type_gravite: "Type × Gravité",
    nc_titre_evolution: "Évolution mensuelle",
    nc_titre_top_tiers: "Top tiers",
    nc_titre_top_articles: "Top articles",
    nc_titre_critiques: "NC critiques ouvertes",
    nc_titre_par_decision: "Délai par décision",
    nc_titre_pires: "Pires fournisseurs",
    nc_titre_meilleurs: "Meilleurs fournisseurs",
    nc_unite_jours: "j",
    nc_decision_acceptation_derogation: "Acceptation / dérogation",
    nc_decision_retouche: "Retraitement",
    nc_decision_rebut: "Rebut",
    nc_decision_retour_fournisseur: "Retour fournisseur",
    nc_decision_non_renseignee: "Non renseignée",
    col_article: "Article",
    col_ecart: "Écart",
    col_manquant: "Manquant",
    col_manque: "Manque",
    col_seuil: "Seuil",
    col_stock_libre: "Stock libre",
    cout_inconnu_tip:
      "Coût unitaire inconnu : plus aucun lot de cet article en stock, le manque n'est pas chiffrable.",
    phys: "phys.",
    // Badges et tooltips du défaut de paramétrage (widget Alertes de stock).
    stock_issue_deux: "Sécu + réappro",
    stock_issue_deux_tip:
      "Aucun seuil renseigné : cet article ne déclenchera jamais d'alerte.",
    stock_issue_incoherent: "Incohérent",
    stock_issue_incoherent_tip:
      "Le seuil de réappro est inférieur au stock de sécurité : l'alerte de réappro arriverait après la critique.",
    stock_issue_reappro: "Réappro",
    stock_issue_reappro_tip:
      "Seuil de réapprovisionnement absent : pas d'alerte anticipée.",
    stock_issue_secu: "Sécu",
    stock_issue_secu_tip:
      "Stock de sécurité absent : pas d'alerte critique possible.",
    stock_libre_detail:
      "Stock physique #physique · dont #reserve réservé à une commande client · dont #nc en zone non conforme",
    stock_lignes_criticite: "#count article(s) · trié par criticité.",
    stock_lignes_param: "#count article(s) sans paramétrage exploitable.",
    stock_manque_total: "#valeur € à réapprovisionner",
    stock_param: "Paramétrage à revoir",
    stock_param_tip:
      "Seuil manquant, à zéro, ou incohérent (réappro inférieur à la sécurité) — angle mort du pilotage",
    stock_sous_reappro: "Sous seuil de réappro",
    stock_sous_reappro_tip:
      "Le stock libre est sous le seuil de réapprovisionnement mais encore au-dessus de la sécurité — à commander ou à produire",
    // Badges et tooltips du défaut de paramétrage (widget Alertes de stock).
    stock_sous_secu: "Sous stock de sécurité",
    stock_sous_secu_tip:
      "Le stock libre est passé sous le stock de sécurité — risque de rupture, arbitrage immédiat",
    stock_sous_seuil: "Alertes de stock",
    stock_sous_seuil_vide: "Aucun article dans ce périmètre.",
    stock_sous_seuil_voir_tout_tip:
      "Ouvrir la vue détaillée (pivot famille / mode d'appro + export Excel)",
    stock_taux_param: "taux de paramétrage #taux %",
    stock_top_criticite:
      "Top #shown sur #total · trié par criticité — voir tout pour la liste complète.",
    stock_top_param:
      "Top #shown sur #total · trié par valeur de stock — voir tout pour la liste complète.",
    voir_tout: "Voir tout",
    w_evolution_marge: "Évolution de la marge",
    w_evolution_marge_desc:
      "Marge dans le temps, en euros et en pourcentage, avec et sans main d'œuvre — vue facturée, produite ou comparée",
    w_stock_sous_seuil: "Alertes de stock",
    w_stock_sous_seuil_desc:
      "Articles sous stock de sécurité ou sous seuil de réapprovisionnement, et articles sans seuil paramétré",
  },
  stats_ligne: {
    aucune_ligne: "Aucune ligne correspondante",
    interval_date: "Intervalle de date",
    aucune_donnee: "Aucune donnée",
    stats_ligne_exercice_en_cours: "Exercice en cours",
    stats_ligne_exercice_passe: "Exercice passé",
    stats_ligne_tout: "Tout",
    utilisateur: "Utilisateur",
    duree: "Durée",
    part: "Part",
    of: "OF",
    article: "Article",
    quantite: "Quantité",
    date_fin: "Date fin",
    score: "Score",
    quantite_produite_ligne: "Quantité produite",
    main_oeuvre_ligne: "Main d'œuvre",
    productivite_ligne: "Productivité",
    couts_ligne: "Coûts matière / personnel",
    ofs_ligne: "OFs de la ligne",
    articles_produits_ligne: "Articles produits",
    aide_quantite_produite:
      "Somme des <span class='bold'>quantités produites</span> de chaque OF clôturé sur la ligne, agrégée par période et regroupée par <span class='bold'>unité de gestion</span> de l'article.",
    aide_quantite_produite_formule:
      "Pour chaque période :<br/><span class='aide-formula'>Total = Σ quantiteProduite (OFs)</span><br/>Utilisez le sélecteur d'unité pour filtrer par type de produit (Kg, L, etc.).",
    aide_main_oeuvre:
      "Répartition du <span class='bold'>temps de travail</span> saisi par opérateur sur les OFs de la ligne.",
    aide_main_oeuvre_formule:
      "Pour chaque opérateur :<br/><span class='aide-formula'>Durée = Σ durées des travaux</span><br/>Le pourcentage est calculé sur le total cumulé.",
    aide_productivite:
      "Score de productivité par mois, calculé en <span class='bold'>moyenne pondérée</span> par les quantités produites. Filtrable par unité de gestion.",
    aide_productivite_formule:
      "<span class='aide-formula'>Score mois = Σ(score × qté) / Σ(qté)</span><br/>" +
      "Les OFs avec de gros volumes pèsent davantage.<br/>" +
      "<span class='valid bold'>Vert</span> ≥ 95% · " +
      "<span class='warning bold'>Orange</span> 85–95% · " +
      "<span class='error bold'>Rouge</span> &lt; 85%",
    aide_couts:
      "Coûts <span class='bold'>matière</span> et <span class='bold'>personnel</span> cumulés par mois, empilés.",
    aide_couts_formule:
      "<span class='aide-formula'>Matière = Σ coûtMatièreRéel (ou théorique)<br/>Personnel = Σ coûtPersonnelRéel (ou théorique)</span><br/>" +
      "Les coûts réels sont prioritaires ; si absents, les coûts théoriques sont utilisés.",
    aide_ofs:
      "Liste de tous les <span class='bold'>OFs clôturés</span> sur la ligne dans la période sélectionnée.",
    aide_ofs_formule:
      "Triable par date de fin, quantité produite ou score. Cliquez sur une ligne pour accéder aux statistiques de l'OF.",
    aide_articles:
      "Répartition des <span class='bold'>quantités produites</span> par article sur la ligne (top 12).",
    aide_articles_formule:
      "<span class='aide-formula'>Part article = Σ quantitéProduite (article) / Σ total</span>",
    toutes: "Toutes",
    calcul: "Calcul",
    score_moyen: "Score moyen (%)",
    quantite_produite_chart: "Quantité produite",
    n_moins_1: "N-1",
    prec: "Préc.",
    label_quantite: "Quantité",
    label_matiere: "Matière",
    label_personnel: "Personnel",
    tri_date: "Date",
    tri_quantite: "Quantité",
    tri_score: "Score",
    tri_desc: "Desc",
    tri_asc: "Asc",
    tri_heures: "Tri Heures",
    tri_alpha: "Tri Alpha",
    inconnu: "Inconnu",
    autres: "Autres",
    heures: "Heures",
  },
  stats_user_infos: {
    aucun_operateur: "Aucun opérateur correspondant",
    heures_travaillees: "Heures travaillées",
    total: "Total",
    repartition_travail: "Répartition par type de travail",
    ofs_realises: "OFs réalisés",
    ligne: "Ligne",
    productivite: "Productivité",
    lignes_travaillees: "Lignes travaillées",
    articles_produits: "Articles produits",
  },
  expedition_commande: {
    maj_client: "Mettre à jour dans le client",
    lots_prepares: "Lots préparés",
    lots_reintegres: "Lots retirés de l'expédition",
    presta_restantes_titre: "Prestations de cette expédition",
    presta_restantes_consigne:
      "Ces prestations partent avec ce bon de livraison et seront facturées avec. Décochez celles à reporter sur une prochaine expédition.",
    presta_restantes_tout_reporter: "Tout reporter",
    presta_restantes_expedier_avec: "Expédier avec (#count)",
    presta_restantes_expedier_sans: "Expédier sans prestation",
  },
  horaires_form: {
    lundi: "Lundi",
    mardi: "Mardi",
    mercredi: "Mercredi",
    jeudi: "Jeudi",
    vendredi: "Vendredi",
    samedi: "Samedi",
    dimanche: "Dimanche",
    planning_perso: "Planning personnalisé",
    planning_defaut: "Planning par défaut",
    annuler_modification: "Annuler la modification",
    modifier: "Modifier",
  },
  logs_ui: {
    enregistrement_en_cours: "Enregistrement en cours...",
    enregistrement_info:
      "L'enregistrement de la session peut servir à des fins de debugage",
    arreter_enregistrement: "Arreter l'enregistrement",
  },
  lignes_progress: {
    expedie: "Expédié",
    en_expedition: "En attente d'expédition",
    // 2026-08-03 — ex-« En cours de préparation » : collision frontale avec
    // le vocabulaire de l'onglet Préparation (où « préparer » = réserver).
    // Le segment orange = posé sur une UE pas encore expédiée.
    sur_palette: "Sur unité d'expédition",
    sur_autres_unites: "sur d'autres unités",
    sur_cette_unite: "sur cette unité",
    sur_unite_en_attente: "sur unité · en attente d'expédition",
    reservee: "Réservé (à placer)",
    reserve_a_placer: "réservé(s) · à placer",
    a_preparer: "à préparer",
    a_inclure: "à inclure",
    rupture: "Rupture de stock",
    stock_limite: "Stock limité",
    sur_entrepot: "sur",
    voir_autres_stocks: "Voir les autres stocks",
    ddm_depassee: "DDM/DLC dépassée",
    over_expedition: "Sur-expédition : +#quantite excédentaire(s)",
  },
  stock_entrepot: {
    title: "Stock par entrepôt",
    depart: "Entrepôt de départ",
    aucun_stock: "Aucun stock disponible",
    total: "Stock total",
    rupture_warning: "Rupture dans cet entrepôt.",
  },
  enums: {

    /**
     * ⚠️ **Les libellés des énumérations NATIVES vivent ici, et pas en base.** Une native est un
     * vocabulaire externe — un virement, un kilogramme, un nettoyage : sa dénomination stockée est
     * l'identité, son libellé AFFICHÉ doit pouvoir se traduire comme tout le reste de l'interface.
     * Jusqu'ici les noms d'unités venaient du back en français dur (`LIBELLES` de `masse.utils`),
     * donc intraduisibles : les fichiers `message-en/es/pt.utils.ts` ne pouvaient rien en faire.
     *
     * ⚠️ **La clef est un SLUG dérivé de la dénomination canonique** (`slugNatif` côté back), jamais
     * de la valeur en base : un client qui renomme quoi que ce soit ne casse aucune clef.
     *
     * ⚠️ **Repli sur la dénomination stockée si la clef manque.** Un vocabulaire qui grandit sans que
     * ces blocs suivent doit afficher le français de la base, pas une clef brute — un libellé muet est
     * invisible, une clef affichée est un bug visible mais laid ; le repli évite les deux.
     */
    reglement_natif: {
      VIREMENT: "Virement",
      PRELEVEMENT_SEPA: "Prélèvement SEPA",
      CHEQUE: "Chèque",
      CARTE_BANCAIRE: "Carte bancaire",
      ESPECES: "Espèces",
      LETTRE_DE_CHANGE_RELEVE: "Lettre de change relevé",
      TIP: "TIP",
      COMPENSATION: "Compensation",
    },
    travail_natif: {
      NETTOYAGE: "Nettoyage",
      CHANGEMENT_DE_FORMAT: "Changement de format",
      RETRAITEMENT_PRODUITS_NON_CONFORMES: "Retraitement produits non conformes",
      RANGEMENT: "Rangement",
      ATTENTE_ARRET_SUBI: "Attente / arrêt subi",
      RECEPTION_MARCHANDISE: "Réception marchandise",
      EXPEDITION_CHARGEMENT: "Expédition / chargement",
      INVENTAIRE: "Inventaire",
      MAINTENANCE_PREVENTIVE: "Maintenance préventive",
      INTERVENTION_TECHNIQUE: "Intervention technique",
      CONTROLE_QUALITE_PRELEVEMENT: "Contrôle qualité / prélèvement",
      AUDIT_OU_VISITE: "Audit ou visite",
      FORMATION: "Formation",
      REUNION: "Réunion",
      TRAVAIL_ADMINISTRATIF: "Travail administratif",
    },
    /**
     * Les natures de non-conformité **natives** — ce qui est constaté, pas où ni pourquoi.
     *
     * ⚠️ **Aucune ne redit un axe déjà codé du module** (`TypeNc` = où, `Cause5M` = pourquoi,
     * `GraviteNc` = combien c'est grave, `DecisionNc` = ce qu'on en fait). Un doublon ferait saisir la
     * même information à deux endroits, puis les deux divergeraient — et le « Récap par nature » de
     * l'export deviendrait faux sans que rien ne le signale. Un test le vérifie côté serveur.
     *
     * ⚠️ **Les natures propres à un atelier ne sont PAS ici** et n'ont pas à l'être : `CAPSULE NON
     * PRISE` porte 24 des 26 non-conformités de la base Bloc, elle reste une entrée maison, modifiable,
     * affichée telle que le client l'a écrite.
     */
    allergene_natif: {
      CEREALES_A_GLUTEN: "Céréales à gluten",
      BLE: "Blé",
      SEIGLE: "Seigle",
      ORGE: "Orge",
      AVOINE: "Avoine",
      EPEAUTRE: "Épeautre",
      KAMUT: "Kamut",
      CRUSTACES: "Crustacés",
      UFS: "Œufs",
      POISSONS: "Poissons",
      ARACHIDES: "Arachides",
      SOJA: "Soja",
      LAIT: "Lait",
      FRUITS_A_COQUE: "Fruits à coque",
      AMANDE: "Amande",
      NOISETTE: "Noisette",
      NOIX: "Noix",
      NOIX_DE_CAJOU: "Noix de cajou",
      NOIX_DE_PECAN: "Noix de pécan",
      NOIX_DU_BRESIL: "Noix du Brésil",
      PISTACHE: "Pistache",
      NOIX_DE_MACADAMIA: "Noix de macadamia",
      CELERI: "Céleri",
      MOUTARDE: "Moutarde",
      SESAME: "Sésame",
      SULFITES: "Sulfites",
      LUPIN: "Lupin",
      MOLLUSQUES: "Mollusques",
    },
    nature_nc_natif: {
      CORPS_ETRANGER: "Corps étranger",
      ALLERGENE_NON_DECLARE: "Allergène non déclaré",
      NON_CONFORMITE_MICROBIOLOGIQUE: "Non-conformité microbiologique",
      NON_CONFORMITE_PHYSICO_CHIMIQUE: "Non-conformité physico-chimique",
      DEFAUT_ORGANOLEPTIQUE: "Défaut organoleptique",
      POIDS_OU_VOLUME_NON_CONFORME: "Poids ou volume non conforme",
      CORPS_PROPRE_OU_DEBRIS_DE_FABRICATION:
        "Corps propre ou débris de fabrication",
      DEFAUT_DE_CONDITIONNEMENT: "Défaut de conditionnement",
      DEFAUT_D_ETIQUETAGE: "Défaut d’étiquetage",
      DATE_DE_DURABILITE_ERRONEE: "Date de durabilité erronée",
      DEFAUT_DE_TRACABILITE: "Défaut de traçabilité",
      ERREUR_DE_REFERENCE_OU_DE_LOT: "Erreur de référence ou de lot",
      QUANTITE_LIVREE_NON_CONFORME: "Quantité livrée non conforme",
      RUPTURE_DE_LA_CHAINE_DU_FROID: "Rupture de la chaîne du froid",
      DEFAUT_DE_PALETTE_OU_DE_SUPPORT: "Défaut de palette ou de support",
      RETARD_DE_LIVRAISON: "Retard de livraison",
      DOCUMENT_MANQUANT_OU_NON_CONFORME: "Document manquant ou non conforme",
      DEFAUT_D_HYGIENE_OU_DE_NETTOYAGE: "Défaut d’hygiène ou de nettoyage",
    },
    unite_natif: {
      MG: "Milligramme",
      CG: "Centigramme",
      DG: "Décigramme",
      G: "Gramme",
      DAG: "Décagramme",
      HG: "Hectogramme",
      KG: "Kilogramme",
      Q: "Quintal",
      T: "Tonne",
      OZ: "Once",
      LB: "Livre",
      ML: "Millilitre",
      CL: "Centilitre",
      DL: "Décilitre",
      L: "Litre",
      DAL: "Décalitre",
      HL: "Hectolitre",
      M3: "Mètre cube",
      FL_OZ: "Once liquide (US)",
      PT: "Pinte (US)",
      QT: "Quart (US)",
      GAL: "Gallon (US)",
      UNITE: "Unité",
      DZ: "Douzaine",
      MM: "Millimètre",
      CM: "Centimètre",
      DM: "Décimètre",
      M: "Mètre",
      DAM: "Décamètre",
      HM: "Hectomètre",
      KM: "Kilomètre",
      IN: "Pouce",
      FT: "Pied",
      YD: "Yard",
      MI: "Mile",
    },
    remise_type: {
      POURCENTAGE: "Pourcentage (%)",
      MONTANT_UNITAIRE: "Montant / unité (€/u)",
      MONTANT: "Montant fixe (€)",
    },
    type_controle: {
      QCM: "QCM",
      CNC: "Conforme / Non conforme",
      QUANTITATIF: "Quantitatif",
      MARQUAGE: "Marquage ou comparaison",
      ENREGISTREMENT: "Enregistrement",
      TRACABILITE: "Traçabilité",
    },
    element_type_controle: {
      LOT_RECEPTION: "Réception d'un lot",
      PREPARATION_EXPE: "Préparation d'une expé.",
      DECHARGEMENT: "Déchargement",
      EXPEDITION: "Chargement",
      LIBERATOIRE: "Libératoire",
      CHARGEMENT: "Chargement",
      PRODUCTION: "Production",
      START_OPE: "Démarrage d'une opé.",
      END_OPE: "Fin d'une opé.",
      START_OPE_GENERAUX: "Au démarrage de chaque opé.",
      END_OPE_GENERAUX: "A la fin de chaque opé.",
      LOT_RECEPTION_GENERAUX: "A chaque réception de lot",
      PREPARATION_EXPE_GENERAUX: "A chaque préparation d'expé.",
      DECHARGEMENT_GENERAUX: "A chaque déchargement",
      DECHARGEMENT_ARTICLE_GENERAUX: "A chaque article au déchargement",
      EXPEDITION_GENERAUX: "A chaque chargement",
      LIBERATOIRE_GENERAUX: "A chaque libération",
      CHARGEMENT_GENERAUX: "A chaque chargement",
      CHARGEMENT_ARTICLE_GENERAUX: "A chaque article au chargement",
      CHARGEMENT_UNITE_GENERAUX: "A chaque unité d'expédition au chargement",
      PRODUCTION_GENERAUX: "A chaque production",
      PROCESS: "Procédé",
      SUIVI_LOT: "Suivi de la vie du lot",
      CHECKLIST: "Checklist",
    },
    type_nominal: {
      SUP: "Supérieur à",
      INF: "Inférieur à",
      BETWEEN: "Entre minimum et maximum",
    },
    etat_specification: {
      ARCHIVED: "Archivée",
      EN_COURS: "Courante",
      EN_REDACTION: "En rédaction",
      REDIGEE: "Rédigée",
      VALIDEE: "Validée",
      APPROUVEE: "Approuvée",
    },
    specification_variable_type: {
      NUMBER: "Nombre",
      STRING: "Texte",
      DOCUMENT: "Document / Photo",
      NOMENCLATURE_ARTICLE: "Nomenclature optionnel",
      PROCESS_OPTIONAL: "Procédé optionnel",
    },
    transfert_entrepot_etat: {
      EN_REDACTION: "En rédaction",
      EN_ATTENTE_EXPEDITION: "En attente d'expédition",
      EXPEDIEE: "Expédiée",
      TRANSFEREE_PARTIELLEMENT: "Transférée partiellement",
      TERMINEE: "Terminée",
      ANNULEE: "Annulée",
      SOLDEE: "Soldée",
    },
    transfert_entrepot_mode: {
      SIMPLE: "Simple",
      AVEC_EXPEDITION: "Avec expédition",
    },
    commande_etat: {
      EN_REDACTION: "En rédaction",
      EN_ATTENTE_ENVOI: "En attente d'envoi",
      EN_ATTENTE_CONFIRMATION: "En attente de confirmation",
      EN_ATTENTE_LIVRAISON: "En attente de livraison",
      ANNULEE: "Annulée",
      LIVREE_PARTIELLEMENT: "Livrée partiellement",
      TERMINEE: "Terminée",
      SOLDEE: "Terminée (Soldée)",
    },
    commande_client_etat: {
      EN_REDACTION: "En rédaction",
      EN_ATTENTE_CONFIRMATION: "En attente de confirmation",
      EN_ATTENTE_CONFIRMATION_MODIFIED: "En attente de confirmation",
      EN_COURS_DE_TRAITEMENT: "En cours de traitement",
      EN_COURS_DE_PREPARATION: "En cours de préparation",
      PRETE: "Prête",
      EXPEDIE: "Expédiée",
      EXPEDIE_PARTIELLEMENT: "Expédiée partiellement",
      FACTURE_PARTIELLEMENT: "Facturée partiellement",
      FACTURE: "Facturée",
      ANNULEE: "Annulée",
      // Phase 4 : `TERMINE` = lifecycle clôturé. Règlement / facturation
      // sont des axes parallèles, ne pas mélanger dans le libellé.
      TERMINE: "Terminée",
    },
    etat_facture: {
      EN_REDACTION: "En rédaction",
      EMISE: "Émise",
      PARTIELLEMENT_REGLEE: "Partiellement réglée",
      REGLEE: "Réglée",
      ANNULEE: "Annulée",
      LITIGE: "Litige",
      PARTIELLEMENT_CONSOMME: "Partiellement consommé",
      CONSOMME: "Consommé",
    },
    etat_transaction: {
      NON_AFFECTE: "Non affecté",
      PARTIELLEMENT_AFFECTE: "Partiellement affecté",
      AFFECTE: "Affecté",
    },
    type_transaction: {
      CB: "Carte",
      VIREMENT: "Virement",
      CHEQUE: "Chèque",
      ESPECES: "Espèces",
      AUTRE: "Autre",
    },
    famille_article: {
      FINI: "Fini",
      SEMIFINI: "Semi-fini",
    },
    unite_gestion: {
      KG: "Kilo",
      G: "Gramme",
      T: "Tonne",
      ML: "Millilitre",
      L: "Litre",
      M3: "Mètre cube",
      UNITE: "Unité",
      M: "Mètre",
    },
    numero_complement: {
      BIS: "Bis",
      TER: "Ter",
      QUATER: "Quater",
    },
    periode_type: {
      FIN_DE_PERIODE: "Fin de période",
      NET: "Net",
    },
    type_tiers: {
      CLIENT: "Client",
      FOURNISSEUR: "Fournisseur",
      CLIENT_FOURNISSEUR: "Client et Fournisseur",
    },
    type_reglement: {
      ESPECE: "Espèce",
      VIREMENT: "Virement",
      PRELEVEMENT: "Prélèvement",
      TRAITE: "Traite",
      LCR: "LCR",
    },
    delai_reglement: {
      LIVRAISON: "A la livraison",
      COMMANDE: "A la commande",
      JOUR: "jours",
      FDM: "fin de mois",
      FDD: "fin de décade",
    },
    of_etat: {
      ANNULE: "Annulé",
      EN_ATTENTE_PLANNIF: "En attente de planification",
      EN_ATTENTE_PROD: "En attente de production",
      EN_ATTENTE_BILAN_MATIERE: "En attente du bilan matière",
      EN_PROD: "En production",
      EN_ATTENTE_REINTE_PDC: "En attente de réintégration",
      EN_ATTENTE_VALIDATION: "En attente de validation",
      VALIDE: "Terminé",
      EN_DEFAUT: "En défaut",
    },
    etat_poste: {
      CALAGE: "En cours de calage",
      EN_COURS: "En cours",
      TERMINEE: "Terminée",
      PENDING: "En attente",
      EN_ATTENTE_BILAN_MATIERE: "En attente du bilan matière",
    },
    type_mouvement: {
      RECEPTION: "Réception",
      RECEPTION_LOT_UNIQUE: "Réception (lot unique)",
      RECEPTION_NON_CONFORME: "Réception non conforme",
      INVENTAIRE: "Inventaire",
      FABRICATION_LOT_UNIQUE: "Fabrication (lot unique)",
      FABRICATION: "Fabrication",
      TRANSFERT: "Transfert",
      LIBERATION: "Libération",
      CREATION: "Création",
      LIBERATION_RECEPTION: "Libération réception",
      DESTRUCTION: "Destruction",
      LIVRAISON: "Livraison",
      RETOUR_FOURNISSEUR: "Retour fournisseur",
      RETOUR_CLIENT: "Retour client",
      CONSO_FAB: "Conso. fabrication",
      PIED_DE_CHAINE: "Mise en pied de chaîne",
      REINTE_PIED_DE_CHAINE: "Réintégration pied de chaîne",
      PREPA_EXPEDITION: "Préparation à l'expédition",
      REINTE_EXPEDITION: "Réintégration expédition",
      EXPEDITION: "Expédition",
      PREPA_TRANSFERT: "Préparation au transfert",
      REINTE_TRANSFERT: "Réintégration transfert",
      EXPEDITION_TRANSFERT: "Expédition transfert",
      REGROUPEMENT: "Regroupement",
      RAZ: "Remise à zéro",
      CHANGEMENT_PROPRIO: "Changement de propriétaire",
      INTEGRATION_EN_FABRICATION_LOT_UNIQUE:
        "Intégration en cours de fab. (Lot unique)",
      INTEGRATION_EN_FABRICATION: "Intégration en cours de fab.",
      REFUS_RECEPTION: "Refus de réception",
      CONSO_SUPPORT: "Consommation support expédition",
      REINTE_SUPPORT: "Réintégration support expédition",
      ENGAGEMENT_PREPA: "Réservation prépa",
      LIBERATION_PREPA: "Libération prépa",
      NC_TRANSFERT: "Mise en zone non conforme",
      NC_PDC: "Mise en zone NC (pied de chaîne)",
      FABRICATION_NC: "Fabrication non conforme",
      LIBERATION_NC: "Libération zone non conforme",
    },
    type_mouvement_short: {
      RECEPTION: "Réception",
      RECEPTION_LOT_UNIQUE: "Réception",
      RECEPTION_NON_CONFORME: "Réception NC",
      INVENTAIRE: "Inventaire",
      FABRICATION_LOT_UNIQUE: "Fabrication",
      FABRICATION: "Fabrication",
      TRANSFERT: "Transfert",
      LIBERATION: "Libération",
      CREATION: "Création",
      LIBERATION_RECEPTION: "Libération réception",
      DESTRUCTION: "Destruction",
      LIVRAISON: "Livraison",
      RETOUR_FOURNISSEUR: "Retour fournisseur",
      RETOUR_CLIENT: "Retour client",
      CONSO_FAB: "Conso. fab.",
      PIED_DE_CHAINE: "PDC",
      REINTE_PIED_DE_CHAINE: "Réinté. PDC",
      PREPA_EXPEDITION: "Prép. l'expé.",
      REINTE_EXPEDITION: "Réinté. expé.",
      EXPEDITION: "Expé.",
      PREPA_TRANSFERT: "Prép. transfert",
      REINTE_TRANSFERT: "Réinté. transfert",
      EXPEDITION_TRANSFERT: "Expé. transfert",
      REGROUPEMENT: "Regroupement",
      RAZ: "RAZ",
      CHANGEMENT_PROPRIO: "Changement de propriétaire",
      INTEGRATION_EN_FABRICATION_LOT_UNIQUE: "Inté. en fab.",
      INTEGRATION_EN_FABRICATION: "Inté. en fab.",
      REFUS_RECEPTION: "Refus réception",
      CONSO_SUPPORT: "Conso. support",
      REINTE_SUPPORT: "Réinté. support",
      ENGAGEMENT_PREPA: "Réservation",
      LIBERATION_PREPA: "Libération",
      NC_TRANSFERT: "Mise en zone NC",
      NC_PDC: "Zone NC (PDC)",
      FABRICATION_NC: "Fabrication NC",
      LIBERATION_NC: "Libération NC",
    },
    etat_palette: {
      PRETE: "Prête",
      EXPEDIEE: "Expédiée",
      EN_PREPARATION: "En cours de préparation",
    },
    etat_chargement: {
      EN_PREPARATION: "En cours de préparation",
      PRET: "Prêt pour le chargement",
      CHARGE: "Chargé",
    },
    sexe: {
      HOMME: "Homme",
      FEMME: "Femme",
    },
    type_comptage: {
      POIDS_SURREMBALLAGE: "Poids avec surremballage",
      DIMENSION: "Dimension / Poids",
      COMPTABLE_PACK: "Comptable (pack/colis)",
      SIMPLE: "Simple",
      ROULEAU_SURFACE: "Surface de rouleau",
      ROULEAU_LONGUEUR: "Longueur de rouleau",
    },
    ref_calcul: {
      LONGUEUR_UC: "Longueur (Unité conditionnée)",
      LARGEUR_UC: "Largeur (Unité conditionnée)",
      HAUTEUR_UC: "Hauteur (Unité conditionnée)",
      LONGUEUR_COLIS: "Longueur (Colis)",
      LARGEUR_COLIS: "Largeur (Colis)",
      HAUTEUR_COLIS: "Hauteur (Colis)",
      POIDS_NET_COLIS: "Poids net (Colis)",
      POIDS_BRUT_COLIS: "Poids brut (Colis)",
      POIDS_NET_UC: "Poids net (Unité conditionnée)",
      POIDS_BRUT_UC: "Poids brut (Unité de gestion)",
    },
    jour: {
      LUNDI: "Lundi",
      MARDI: "Mardi",
      MERCREDI: "Mercredi",
      JEUDI: "Jeudi",
      VENDREDI: "Vendredi",
      SAMEDI: "Samedi",
      DIMANCHE: "Dimanche",
    },
    type_list: {
      MOIS: "Par jour",
      JOUR: "Par heure",
    },
    type_quick_inventaire: {
      RAZ_OTHER: "Remettre à 0 les autres lots",
      ONLY_THOSE: "Compter seulement ces lots",
      ONLY_THOSE_ARTICLES: "Compter seulement ces articles",
    },
    date_schema: {
      JJMMAA: "JJ/MM/AA",
      JJMMAAAA: "JJ/MM/AAAA",
      MMAA: "MM/AA",
      MMAAAA: "MM/AAAA",
    },
    type_marquage_elem: {
      NUM_LOT: "Numéro de lot du produit fabriqué",
      DLUO: "DLUO du produit fabriqué",
      NUMBER: "Chiffres",
      VARIABLE_STRING: "Caractères variables",
      DATE_FAB: "Date de fabrication du produit fabriqué",
      SPACE: "Espace",
      EAN13: "EAN 13 du produit fabriqué",
      EAN14: "EAN 14 du produit fabriqué",
      EAN128: "EAN 128 du produit fabriqué",
    },
    ai: {
      GTIN: "GTIN (01)",
      BATCH_LOT: "Lot (10)",
      PROD_DATE: "Date de production (11)",
      BEST_BEFORE: "Meilleur avant (15)",
      USE_BY: "A consommer avant (17)",
      NET_WEIGHT: "Poids net (3100)",
      LOGISTIC_WEIGHT: "Poids brut (3300)",
      REF: "Référence (240)",
    },
    role: {
      ADMIN: "Administrateur",
      UTILISATEUR: "Utilisateur",
    },
    type_fiche: {
      STANDARD: "Fiche Unité d'Expédition",
      CARTON: "Carton",
      CARTON_EAN13: "Carton EAN 13",
      LONG: "Barquette",
    },
    type_connector: {
      PRESTASHOP: "PrestaShop",
    },
    state_connector: {
      WAITING_SYNC: "Configuration validée — prêt à synchroniser",
      SYNCING: "Synchronisation en cours",
      SYNC_ERROR: "Erreur de synchronisation",
      OK: "Synchronisé",
    },
    sync_direction: {
      BOTH: "Deux sens",
      PULL: "Réception",
      PUSH: "Envoi",
      NONE: "Aucune",
    },
    validation_specif: {
      VALIDATION: "Approbation et Validation",
      NONE: "Sans Validation",
    },
    validation_of: {
      SIMPLE: "Validation",
      DOUBLE: "Validation production et qualité",
      NONE: "Sans validation",
    },
    validation_facture: {
      NUMEROTATION_AUTO: "Numérotation automatique à l'expédition",
      CREATION_SANS_NUMEROTATION: "Création de la facture sans numérotation",
      NONE: "Facturation manuelle",
    },
    type_idea: {
      BUG: "Bug",
      IDEA: "Idée",
    },
    /**
     * Les valeurs de `travail.defaultType`, jusqu'ici affichées BRUTES.
     *
     * ⚠️ **L'utilisateur lisait `CONGES` en capitales**, valeur d'enum sortie telle quelle du template
     * (`{{ elem.defaultType }}`). Aucune clef n'existait, donc les fichiers `message-en/es/pt` ne
     * pouvaient rien en faire non plus : un écran de saisie d'heures affichait du code.
     *
     * ⚠️ **`OF` reste `OF`** — c'est le terme métier employé partout dans le produit (ordre de
     * fabrication), pas un identifiant technique. Le traduire en « Production » créerait deux noms pour
     * la même chose.
     *
     * ⚠️ **Une valeur ajoutée à `TypeTravail` sans sa clef ICI s'affiche en capitales** et non vide :
     * `libelleTypeTravail` se replie volontairement sur la valeur brute, parce qu'un libellé absent
     * passe inaperçu alors qu'un code affiché se corrige.
     */
    type_travail: {
      OF: "OF",
      CONGES: "Congés",
      RTT: "RTT",
      MALADIE: "Maladie",
      ACCIDENT_TRAVAIL: "Accident du travail",
      RETARD: "Retard",
      AUTRE: "Autre",
    },
    /**
     * Le titre du dialogue, PAR TYPE — parce que le français a des genres.
     *
     * ⚠️ **On lisait « Modifier une Travail »**, et « Modifier une Mode de règlement » : le titre était
     * composé de `« une » + libellé du type`, ce qui marche pour une famille et une unité, jamais pour un
     * travail ni un mode. Une phrase par type coûte onze lignes et supprime la faute d'accord — la régler
     * par une table de genres coûterait le même effort en laissant le lecteur deviner la règle.
     *
     * ⚠️ **`consulter_*` sert les NATIVES**, où rien n'est modifiable : afficher « Modifier » au-dessus
     * d'un texte qui dit « ne se modifie pas » se contredit dans le même dialogue.
     */
    dialog_titre: {
      ajouter_FAMILLE_ARTICLE: "Ajouter une famille",
      modifier_FAMILLE_ARTICLE: "Modifier une famille",
      ajouter_CATEGORIE_ARTICLE: "Ajouter une sous-famille",
      modifier_CATEGORIE_ARTICLE: "Modifier une sous-famille",
      ajouter_SOUS_FAMILLE_ARTICLE: "Ajouter une famille technique",
      modifier_SOUS_FAMILLE_ARTICLE: "Modifier une famille technique",
      ajouter_UNITE: "Ajouter une unité de gestion",
      modifier_UNITE: "Modifier une unité de gestion",
      consulter_UNITE: "Unité de gestion standard",
      ajouter_TRAVAIL: "Ajouter un type de travail",
      modifier_TRAVAIL: "Modifier un type de travail",
      consulter_TRAVAIL: "Type de travail standard",
      ajouter_TYPE_REGLEMENT: "Ajouter un mode de règlement",
      modifier_TYPE_REGLEMENT: "Modifier un mode de règlement",
      consulter_TYPE_REGLEMENT: "Mode de règlement standard",
      ajouter_NC_NATURE: "Ajouter une nature de non-conformité",
      modifier_NC_NATURE: "Modifier une nature de non-conformité",
      consulter_NC_NATURE: "Nature de non-conformité standard",
      reactiver: "Réactiver #type",
    },
    type_custom_enum: {
      FAMILLE_ARTICLE: "Famille",
      CATEGORIE_ARTICLE: "Sous-famille",
      SOUS_FAMILLE_ARTICLE: "Famille technique",
      UNITE: "Unité de gestion",
      UNITE_PROD: "Unité",
      EMPLACEMENT_STOCK: "Emplacement en stock",
      POSTE: "Poste",
      TITRE: "Titre",
      TRAVAIL: "Travail",
      TYPE_REGLEMENT: "Mode de règlement",
      NC_NATURE: "Nature de NC",
      ALLERGENE: "Allergène",
    },
    dyn_text: {
      OF_QUANTITY_: "Quantité OF",
      OPE_QUANTITY_: "Quantité Opé.",
      FAB_DENOMINATION_: "Dénomination article fabriqué",
      FAB_UNITE_: "Unité article fabriqué",
      FAB_REF_: "Référence article fabriqué",
      NAME_: "Dénomination",
      DENSITY_: "Densité",
      REF_: "Référence",
      QUANTITY_: "Quantité nomenclature",
      VAR_QUANTITY_: "Quantité nomenclature",
      LOSS_: "Perte normale (%)",
      MAXLOSS_: "Perte max. (%)",
      UNIT_: "Unité de gestion",
      VAR_NAME_: "Dénomination",
      VAR_DENSITY_: "Densité",
      VAR_REF_: "Référence",
      VAR_LOSS_: "Perte normale (%)",
      VAR_MAXLOSS_: "Perte max. (%)",
      VAR_UNIT_: "Unité de gestion",
      VAR_STRING_: "Variable texte",
      VAR_N_: "Variable nombre",
      N_: "Quantité",
      X_: "×",
      DOC_: "Document",
      VAR_DOC_: "Variable document",
      ARTICLE_DOC_: "Document article",
      VAR_ARTICLE_DOC_: "Document article",
    },
  },
  training: {
    start_btn: "Me guider",
    quit_btn: "Quitter la formation",
    completed_badge: "Complété",
    step_counter: "Étape #current / #total",
    prev: "Précédent",
    next: "Suivant",
    finish: "Terminer",
    demo_title: "Comment voulez-vous suivre cette formation ?",
    demo_fake_title: "Données fictives",
    demo_fake_desc:
      "Apprenez avec un exemple pâtisserie sans toucher à vos données",
    demo_real_title: "Mes données réelles",
    demo_real_desc:
      "Suivez la formation sur vos propres données (+20 XP bonus)",
    xp_toast: "+#xp XP — Niveau #level",
    rating_title: "Comment évaluez-vous ce parcours ?",
    rating_subtitle: "Votre retour améliore la formation",
  },
  cost_impact: {
    title: "Simulation de coûts",
    description:
      "Simulez l'impact d'une variation de prix sur vos matières premières et visualisez la répercussion sur vos produits finis et vos marges clients.",
    scenario: "Scénario",
    depth: "Profondeur",
    depth_help: "Nombre de niveaux de nomenclature à remonter (∞ = illimité)",
    include_sf: "Semi-finis",
    include_sf_help: "Inclure les semi-finis dans les résultats",
    include_mo: "Main-d'œuvre",
    include_mo_help: "Inclure les coûts de main-d'œuvre dans la simulation",
    add_component: "Ajouter un composant",
    articles_impacted: "articles impactés",
    without_cost: "sans coût",
    col_component: "Composant",
    col_type: "Type",
    col_unit: "Unité",
    col_method: "Méthode",
    col_sim_mode: "Mode sim.",
    col_value: "Valeur",
    col_base_cost: "Coût base",
    col_sim_cost: "Coût sim.",
    col_delta_unit: "Δ unit.",
    col_delta_pct: "Δ %",
    tab_cascade: "Impact cascade",
    tab_direct: "Impact direct",
    tab_margin: "Impact marge",
    col_article: "Article",
    col_delta_combined: "Δ unit. combiné",
    col_drivers: "Composants",
    col_depth: "Prof.",
    no_impact: "Aucun article impacté — ajoutez un composant et une variation",
    col_driver: "Composant",
    col_qty_u: "Qté/u",
    col_delta_driver: "Δ unit. composant",
    col_contribution: "Contribution",
    combined_impact: "Impact unitaire combiné",
    sum_contributions: "Somme de #count contribution(s)",
    of_impact: "de l'impact",
    step_cost_varies: "Le coût du composant varie",
    step_qty_used: "Quantité utilisée dans la nomenclature",
    per_unit_of: "par unité de",
    step_impact: "Impact sur le coût unitaire",
    total_impact: "Impact total",
    drivers_combined: "drivers cumulés",
    simu_date_label:
      "Simuler au",
    simu_date_tooltip:
      "Date à laquelle raisonner. Les prix de vente sont résolus à cette date : un tarif déjà programmé n'apparaîtra plus comme une hausse à demander. Vide = aujourd'hui.",
    simu_date_clear:
      "Aujourd'hui",
    col_margin_at:
      "Marge au #date",
    col_taux_at:
      "Taux au #date",
    tooltip_prix_projete_inconnu:
      "Aucun tarif de vente valide à la date de simulation : la projection retombe sur le prix du jour.",
    margin_period: "Période",
    clients: "clients",
    loading_margins: "Chargement des données marges...",
    no_client_data: "Aucune donnée client pour les articles impactés",
    total_all_clients: "TOTAL TOUS CLIENTS",
    load_scenario: "Charger un scénario",
    save_scenario: "Sauvegarder",
    new_scenario: "Nouveau",
    empty_scenario:
      "Ajoutez un composant pour simuler l'impact d'une variation de coût",
    calculate: "Calculer l'impact",
    // ─── Page container ───
    draft_unsaved: "Brouillon non sauvegardé",
    tooltip_mo_badge:
      "La MO n'est pas intégrée au scénario : les coûts, taux de marge et impacts affichés sont tous calculés sur la seule matière. Activez la MO dans le scénario pour inclure la main-d'œuvre.",
    tooltip_unsaved: "Modifications non enregistrées",
    badge_unsaved: "Non enregistré",
    btn_reset: "Reset",
    reset_confirm_title: "Réinitialiser le scénario ?",
    reset_confirm_message:
      "Tous les composants, la variation MO et le périmètre seront effacés. Les scénarios enregistrés ne sont pas affectés.",
    btn_save: "Enregistrer",
    btn_scenarios: "Scénarios",
    scenario_active: "Actif",
    components_count: "#count composant",
    components_count_plural: "#count composants",
    draft: "Brouillon",
    mo_short: "MO",
    no_scenario_loaded: "Aucun scénario chargé",
    btn_save_as: "Enregistrer sous…",
    btn_rename: "Renommer…",
    rename: "Renommer",
    btn_delete: "Supprimer",
    btn_save_draft: "Enregistrer le brouillon…",
    btn_new_scenario: "Nouveau scénario",
    saved_count: "Enregistrés (#count)",
    calculating: "Calcul de l'impact en cours…",
    entity_nomenclatures: "nomenclatures traitées",
    loading_client_margins: "Chargement des marges clients…",
    entity_clients_analyzed: "clients analysés",
    empty_title: "Aucun résultat pour ce scénario",
    empty_desc:
      "La simulation a tourné mais aucun article impacté n'a été trouvé.",
    empty_scope_hint:
      "Le périmètre que vous avez défini est probablement trop restrictif.",
    empty_no_scope_hint:
      "Vérifiez que les composants choisis sont bien utilisés dans au moins une nomenclature.",
    scope_active: "Périmètre actif :",
    targeted_article: "article ciblé",
    targeted_article_plural: "articles ciblés",
    targeted_client: "client ciblé",
    targeted_client_plural: "clients ciblés",
    targeted_group: "groupe ciblé",
    targeted_group_plural: "groupes ciblés",
    possible_causes: "Causes possibles :",
    cause_intersection:
      "Les articles ciblés n'ont pas été vendus aux clients/groupes ciblés sur les 12 derniers mois (intersection vide).",
    cause_articles:
      "Les articles ciblés ne contiennent aucun des composants modifiés dans leur nomenclature.",
    cause_clients:
      "Les clients ciblés n'ont acheté aucun article impacté par vos drivers sur la période.",
    phase_analyze_bom: "Analyse de la nomenclature…",
    phase_cascade: "Calcul de la cascade de coûts…",
    phase_mo: "Estimation de la main-d'œuvre…",
    phase_consolidate: "Consolidation des impacts…",
    snack_saved: "Scénario enregistré",
    scenario_placeholder: "Mon scénario",
    copy_suffix: "(copie)",
    rename_title: "Renommer le scénario",
    snack_renamed: "Scénario renommé",
    delete_title: "Supprimer le scénario",
    delete_message: "Le scénario « #name » sera définitivement supprimé.",
    cancel: "Annuler",
    snack_deleted: "Scénario supprimé",
    snack_reset: "Scénario réinitialisé",
    // ─── Scenario builder ───
    builder_title: "Scénario d'impact",
    builder_step_tooltip: "#hint — cliquer pour modifier",
    builder_optional: "optionnel",
    builder_help_aria: "Aide : #label",
    builder_open_help: "Ouvrir l'aide",
    builder_col_variation: "Variation simulée",
    builder_col_current_cost: "Coût actuel",
    builder_col_sim_cost: "Coût simulé",
    builder_col_impact_unit: "Impact / unité",
    builder_current: "Actuel",
    builder_simulated: "Simulé",
    builder_impact: "Impact",
    builder_combine_title: "Combinaison des drivers",
    builder_intersection: "Intersection (ET)",
    builder_union: "Union (OU)",
    builder_strict_label: "Mode strict",
    builder_strict_desc_1:
      "seuls les produits dont la nomenclature contient les",
    builder_strict_drivers: "#count drivers",
    builder_strict_desc_2:
      "sont affichés. Idéal pour repérer les recettes cumulant toutes les hausses simulées.",
    builder_large_label: "Mode large",
    builder_large_desc_1: "un produit est inclus dès qu'il contient",
    builder_at_least_one: "au moins un",
    builder_large_desc_2:
      "des #count drivers (comportement par défaut, panorama large).",
    builder_combine_tooltip:
      "Bascule entre union (au moins un driver) et intersection (tous les drivers requis).",
    builder_all_required: "Tous requis",
    builder_add_another: "Ajouter un autre composant",
    builder_include_fab_tooltip:
      "Inclure les articles fabriqués (semi-finis avec nomenclature) dans la liste de sélection.",
    builder_include_fab: "Inclure articles fabriqués",
    builder_from_product: "Depuis un produit (nomenclature)",
    builder_import_tooltip:
      "Importe en une fois tous les composants matières/emballages de la nomenclature d'un produit fini. Les composants déjà présents sont ignorés.",
    builder_empty_title: "Composez votre scénario",
    builder_empty_desc:
      "Sélectionnez un premier composant ci-dessous. Vous pourrez simuler une hausse, une baisse ou un prix fixe, et lancer le calcul d'impact quand vous êtes prêt.",
    builder_import_evolutions:
      "Importer les évolutions fournisseurs",
    builder_import_evolutions_tooltip:
      "Crée un composant par tarif d'achat qui change entre aujourd'hui et la date de simulation — à la hausse comme à la baisse — avec sa variation calculée. Les composants déjà présents ne sont pas modifiés.",
    snack_evolutions_sans_date:
      "Choisissez d'abord une date de simulation : sans elle, il n'y a rien à comparer.",
    snack_evolutions_aucune:
      "Aucun tarif d'achat ne change au #date (#examines article(s) daté(s) examiné(s)).",
    snack_evolutions_deja:
      "Les #total évolution(s) trouvée(s) sont déjà présentes comme composants.",
    snack_evolutions_ok:
      "#added composant(s) ajouté(s) depuis les tarifs fournisseurs au #date (#examines article(s) daté(s) examiné(s)).",
    snack_evolutions_palier:
      "Attention : la variation de #liste vient d'un palier de quantité supérieur. Le calcul raisonne à l'unité, l'impact réel dépend donc des quantités achetées.",
    builder_or_import: "ou importer depuis un produit",
    builder_import_tooltip_2:
      "Charge en une fois tous les composants (matières + emballages) de la nomenclature d'un produit fini.",
    builder_mo_cta_desc:
      "Inclure ou exclure la MO dans le coût de revient, et appliquer une variation globale.",
    builder_mo_fold_tooltip: "Replier — désactive la prise en compte MO",
    builder_mo_toggle_tooltip:
      "Intègre la main-d'œuvre dans le coût de revient (historique OFs, famille technique, bilan productivité…)",
    builder_include_mo_label: "Inclure la main-d'œuvre",
    builder_global_variation: "Variation globale",
    builder_reset_zero: "Remettre à 0%",
    builder_mo_activate_hint:
      "Activez le toggle pour intégrer la MO au calcul d'impact.",
    builder_scope_title: "Périmètre",
    builder_scope_cta_desc:
      "Focaliser l'analyse sur un sous-ensemble d'articles, de clients, ou une intersection des deux.",
    builder_scope_header: "Périmètre d'analyse",
    builder_scope_fold_tooltip: "Replier — repart sur le catalogue complet",
    builder_scope_hint_1:
      "Activez une ou plusieurs dimensions pour focaliser l'analyse. Plusieurs dimensions actives ⇒",
    builder_scope_intersection: "intersection",
    builder_dim_articles: "Articles",
    builder_dim_articles_desc:
      "Focus sur un produit fini ou une famille, descendants inclus.",
    builder_dim_clients: "Clients",
    builder_dim_clients_desc:
      "Concentre l'analyse sur les articles vendus à ce(s) client(s).",
    builder_dim_groups: "Groupes de clients",
    builder_dim_groups_desc:
      "Tous les clients appartenant à un ou plusieurs groupes (cumul avec clients individuels).",
    builder_scope_empty_hint:
      "Cliquez sur une carte pour activer cette dimension d'analyse.",
    builder_zone_articles: "Articles ciblés",
    builder_zone_clients: "Clients ciblés",
    builder_zone_groups: "Groupes ciblés",
    builder_add_article: "Ajouter un article",
    builder_add_client: "Ajouter un client",
    builder_add_group: "Ajouter un groupe",
    builder_remove: "Retirer",
    builder_zone_empty_article:
      "Ajoutez au moins un article pour activer ce filtre.",
    builder_zone_empty_client:
      "Ajoutez au moins un client pour activer ce filtre.",
    builder_zone_empty_group:
      "Ajoutez au moins un groupe pour activer ce filtre.",
    builder_scope_reset_tooltip:
      "Vider la sélection — l'analyse repartira sur tout le catalogue",
    builder_reset: "Réinitialiser",
    builder_scope_footer_note:
      "La cascade interne reste complète ; l'affichage couvre les articles scopés et leurs SF descendants.",
    builder_impact_tooltip:
      "Nombre d'articles du catalogue dont le coût varie dans ce scénario",
    builder_mode_pct_tooltip: "Variation relative en % du coût actuel",
    builder_mode_eur_tooltip: "Ajout absolu en € sur le coût unitaire",
    builder_mode_price: "Prix",
    builder_mode_price_tooltip: "Imposer un nouveau prix fixe",
    builder_mo_not_included: "non incluse",
    builder_mo_included_no_var: "incluse · sans variation",
    builder_mo_included_var: "incluse · #v\u202f%",
    builder_scope_full_catalog:
      "catalogue complet (plus complet, mais plus long)",
    unit_article: "article",
    unit_client: "client",
    unit_group: "groupe",
    builder_cta_disabled: "Ajoutez un composant ou une variation MO",
    help_driver_title: "Composants — à quoi ça sert ?",
    help_driver_what:
      "Le composant est la matière première (ou semi-fini) dont vous voulez simuler la variation de coût. C'est le point de départ du scénario : la cascade calculera ensuite l'impact sur tous les articles fabriqués qui contiennent ce composant, directement ou indirectement.",
    help_driver_how:
      "Cliquez sur « Ajouter un composant », recherchez par nom (ex. « huile colza », « bocal 200ml »). Pour chaque composant, choisissez le mode de variation : % (relatif), € (absolu) ou Prix (nouveau prix fixe). Vous pouvez empiler plusieurs composants dans un même scénario.",
    help_mo_title: "Main-d'œuvre — à quoi ça sert ?",
    help_mo_what:
      "La main-d'œuvre représente le coût humain de fabrication, estimé à partir de l'historique des OFs (bilans de productivité). Par défaut elle n'est pas intégrée au calcul ; vous pouvez l'inclure pour avoir un coût de revient complet, et appliquer une variation globale (ex. +3% pour une hausse salariale).",
    help_mo_how:
      "Activez « Inclure la main-d'œuvre » pour l'ajouter au coût de revient affiché. Une fois activée, le champ « Variation » permet d'appliquer un % global (positif ou négatif) à tous les coûts MO. Laisser à 0% conserve les valeurs historiques.",
    help_scope_title: "Périmètre — à quoi ça sert ?",
    help_scope_what:
      "Par défaut, l'analyse couvre tout votre catalogue. Si vous savez déjà sur quel(s) produit(s) ou client(s) vous voulez décider, le périmètre filtre les résultats à cette zone — plus rapide à calculer, plus simple à lire.",
    help_scope_how:
      "3 dimensions composables (intersection si plusieurs actives) : « Articles » (focus sur des produits finis précis et leur fabrication), « Clients » (uniquement les ventes à ces clients), « Groupes de clients » (étend par groupe). Cliquez sur une carte pour activer la dimension, puis ajoutez les éléments à cibler.",
    help_compute_title: "Lancer le calcul — à quoi ça sert ?",
    help_compute_what:
      "Lance la simulation côté serveur : applique vos variations de composants, propage la cascade dans toutes les nomenclatures, recalcule les coûts de revient des articles, et croise avec les prix de vente clients pour estimer l'impact sur la marge.",
    help_compute_how:
      "Cliquez quand vous êtes prêt — vous pouvez relancer autant de fois que nécessaire. Les résultats apparaîtront en 3 onglets : Impact direct (par driver), Cascade (par article fabriqué) et Impact marge (par client × article).",
    builder_hint_components: "#count composant configuré",
    builder_hint_components_plural: "#count composants configurés",
    builder_hint_add_component: "Ajoutez au moins un composant",
    builder_hint_mo_default: "Par défaut : non incluse",
    builder_hint_mo_included_var: "Incluse · #v %",
    builder_hint_mo_included: "Incluse, sans variation",
    builder_hint_scope_default: "Par défaut : catalogue complet",
    builder_step_compute: "Lancer le calcul",
    builder_hint_results_ready: "Résultats prêts — relancer pour actualiser",
    builder_hint_waiting: "En attente d’un composant ou d’une variation MO",
    builder_hint_ready: "Tout est prêt — lancez quand vous voulez",
    builder_mo_variation_tooltip:
      "Ajuste globalement tous les coûts de main-d'œuvre des OFs utilisés dans le calcul (ex: +3% pour une augmentation de 3% des taux horaires).",
    builder_reset_analysis_title: "Réinitialiser l'analyse ?",
    builder_reset_analysis_message:
      "Modifier ce paramètre va effacer les résultats du calcul d'impact actuel. Vous devrez relancer le calcul pour voir les nouveaux impacts.",
    builder_reset_analysis_confirm: "Modifier et réinitialiser",
    snack_no_bom: "Aucune nomenclature trouvée pour « #name »",
    snack_all_present:
      "Tous les composants de « #name » sont déjà présents (#total)",
    snack_added: "#count composant ajouté depuis « #name »#skipped",
    snack_added_plural: "#count composants ajoutés depuis « #name »#skipped",
    skipped_hint: " (#count déjà présent)",
    skipped_hint_plural: " (#count déjà présents)",
    unknown_error: "erreur inconnue",
    snack_import_error: "Impossible de charger la nomenclature : #detail",
    // ─── Onglet Impact direct ───
    tooltip_excel_direct:
      "Export Excel complet (composants du scénario + impacts + graphique)",
    col_cost_current_new: "Coût actuel → nouveau",
    tooltip_article_type: "Voir pourquoi cet article est classé #type",
    label_qty_unit: "Qté / unité",
    label_mat: "mat.",
    label_mo: "mo.",
    tooltip_contribution: "Δ #driver × #qty = contribution",
    mo_only_title: "Simulation MO uniquement",
    mo_only_desc_1:
      "Aucun composant matière dans ce scénario. L'impact de la variation MO (#v) est réparti sur chaque article, consultez l'onglet",
    mo_only_cascade: "Cascade complète",
    mo_only_desc_2: "pour le détail par produit fini.",
    lot_number: "lot n°#n",
    last_lot: "dernier lot",
    tooltip_real: "Réel (#lot) : #v",
    tooltip_real_simple: "Réel : #v",
    tooltip_theoretical: "Théorique : #v",
    retained_suffix: "  ← retenu",
    tooltip_gap: "Écart : #v € (#dir)",
    real_more_expensive: "réel plus cher",
    real_less_expensive: "réel moins cher",
    tooltip_source_retained: "Source retenue : #s",
    not_estimated: "Non estimée",
    // ─── Onglet Impact cascade ───
    hidden_direct: "(#count direct masqué)",
    hidden_direct_plural: "(#count directs masqués)",
    tooltip_excel_cascade:
      "Export Excel complet (synthèse + articles + composants du scénario + graphiques)",
    coverage_estimated: "/ #total articles fabriqués ont un coût MO estimé",
    coverage_missing:
      "Les #count autres n'apparaissent pas dans les résultats faute d'historique de production exploitable (pas d'OFs, pas de bilan productivité, pas de Cost MO enregistré).",
    label_impact_unit: "Impact / unité",
    cascade_only: "Cascade uniquement",
    include_directs: "Inclure les directs",
    tooltip_hidden_direct:
      'Articles directement touchés par un driver — déjà visibles dans l\'onglet "Impact direct". Bascule sur "Inclure les directs" pour les afficher ici aussi.',
    tooltip_theoretical_cascade: "Théorique (cascade) : #v",
    tooltip_real_of_history: "Réel (historique OFs / dernier lot) : #v",
    tooltip_theoretical_family:
      "Théorique (famille · ligne / bilan / catalogue) : #v",
    // ─── Onglet Impact marge ───
    margin_priority_title: "Source du coût retenu",
    margin_priority_desc: "(priorité descendante) :",
    badge_ventes: "◈ ventes",
    margin_priority_invoices: "(≥ 3 factures sur #n mois)",
    margin_priority_real: "● réel (moyenne 3 derniers lots)",
    margin_priority_theoretical: "○ théorique (cascade composants)",
    margin_priority_catalog: "catalogue",
    margin_priority_note:
      "Les remises et avoirs sont intégrés automatiquement dans la source ventes.",
    margin_mo_off_title: "Coûts et marges calculés hors main-d'œuvre.",
    margin_mo_off_desc:
      "La MO n'est pas intégrée à ce scénario : les coûts de revient, les marges en € et les taux de marge affichés ci-dessous sont tous matière seule. Activez la MO dans le scénario pour ajouter la main-d'œuvre au calcul.",
    margin_mo_on_title: "Coûts et marges calculés MO incluse.",
    margin_mo_on_desc:
      "La main-d'œuvre estimée (colonne dédiée) est additionnée au coût matière pour constituer le coût de revient total. Les marges et taux affichés sont donc",
    margin_mo_deducted: "MO déduite",
    tooltip_no_sales: "Produits sans aucune vente sur la période sélectionnée",
    badge_no_sales: "Sans ventes",
    tooltip_no_impact_filter:
      "Produits dont le coût n'est pas modifié par le scénario (Δ = 0)",
    filter_no_impact: "Non impactés",
    tooltip_excel_margin:
      "Export Excel complet (synthèse, top impacts, une feuille par client avec formules recalculables)",
    niveau_client:
      "Tarif client",
    niveau_groupe:
      "Tarif groupe",
    niveau_general:
      "Tarif général",
    niveau_facture:
      "Dernière facture",
    tooltip_niveau_prix:
      "Niveau d'où vient le prix actuel. Seul un tarif client peut être modifié depuis ce parcours : un tarif groupe ou général s'appliquerait aussi à d'autres clients.",
    btn_ouvrir_grille: "Ouvrir la grille",
    tooltip_ouvrir_grille:
      "Ouvrir la grille tarifaire de cet article : paliers, fenêtres de validité, DDM mini contractuelle et délai de livraison — ces deux derniers ne sont pas modifiés par la mise à jour tarifaire, qui les reprend tels quels.",
    btn_editer_niveau:
      "Modifier à ce niveau",
    tooltip_editer_niveau:
      "Ouvrir la grille tarifaire à son niveau réel (groupe ou général), avec ses dates de validité",
    creer_tarif_client_label:
      "Créer un tarif propre au client si absent",
    creer_tarif_client_hint:
      "Sinon, les articles dont le prix vient d'un groupe ou du tarif général sont laissés intacts. Attention : créer un tarif client détache ce client de la grille de groupe pour cet article.",
    badge_tarif_cree:
      "Tarif client créé",
    badge_tarif_applique:
      "Tarif enregistré",
    badge_tarif_envoye:
      "Tarif envoyé",
    badge_tarif_avenir:
      "Programmé le #date",
    tooltip_tarif_avenir:
      "Tarif de #prix € enregistré le #quand, applicable à partir du #date. Inutile de le réenregistrer.",
    tooltip_tarif_divergent:
      "Tarif de #annonce € enregistré le #quand, mais le prix appliqué aujourd'hui est de #actuel € : il a changé depuis.",
    badge_tarif_perime:
      "Enregistré le #quand",
    tooltip_tarif_quand:
      "Enregistré le #quand.",
    badge_tarif_ignore:
      "Non modifié",
    tooltip_tarif_applique:
      "Nouveau tarif écrit dans la grille de ce client (#prix). Aucun mail envoyé.",
    tooltip_tarif_envoye:
      "Nouveau tarif écrit dans la grille de ce client (#prix) et courrier envoyé.",
    tooltip_tarif_jusquau:
      " Applicable jusqu'au #fin inclus.",
    unit_product: "produit",
    unit_product_plural: "produits",
    margin_kpi_unchanged: "Impact marge si prix inchangés",
    tooltip_kpi_unchanged:
      "Variation de marge totale si les prix de vente ne sont pas modifiés. Rouge = perte, vert = gain ponctuel (coûts en baisse).",
    margin_kpi_repriced: "Impact marge si prix actualisés (taux figé)",
    tooltip_kpi_repriced:
      "Variation de marge en € si les prix sont actualisés pour conserver le même taux %. Positif = gain lié à la revalorisation, négatif = marge absolue réduite par la baisse de prix.",
    tooltip_send_client: "Envoyer le nouveau tarif à ce client uniquement",
    btn_mail_tarif: "Mail tarif",
    col_product: "Produit",
    tooltip_excel_product:
      "Télécharger l'analyse Excel détaillée pour ce produit (cascade de coûts, formules de maintien de marge)",
    tooltip_no_sales_badge: "Aucune vente sur la période sélectionnée",
    badge_no_impact: "Non impacté",
    tooltip_no_impact_badge:
      "Le coût n'est pas modifié par le scénario (driver présent dans la cascade mais variation × multiplicateur = 0)",
    badge_in_scope: "Dans le périmètre",
    tooltip_in_scope:
      "Article dans le périmètre du driver. Augmente la variation simulée pour calculer un impact réel.",
    units: "unités",
    tooltip_sell_price: "Prix de vente unitaire actuel",
    col_sell_price: "Prix vente /u",
    no_price: "Pas de prix",
    tooltip_no_price:
      "Aucun prix trouvé (ni fiche tiers/groupe/générique, ni historique facture)",
    tooltip_price_group: "Prix issu du groupe tiers",
    tooltip_price_generic: "Prix issu de la fiche générique",
    tooltip_price_invoice: "Dernier prix facturé (aucune fiche article-fourni)",
    price_level_group: "↳ groupe",
    price_level_generic: "↳ générique",
    price_level_invoiced: "↳ facturé",
    tooltip_col_mat:
      "Coût matière : règle réel prioritaire (dernier lot) sinon théorique (cascade depuis les composants). Survoler la cellule pour voir les deux valeurs et l'écart. Cliquer pour la décomposition complète.",
    col_mat_unit: "Matière /u",
    badge_ventes_scope_client: "client",
    badge_ventes_scope_article: "article",
    badge_manual: "✎ saisi manuellement (#count lot)",
    badge_manual_plural: "✎ saisi manuellement (#count lots)",
    tooltip_manual:
      "Le coût des lots de cette moyenne a été SAISI manuellement (valeur entrée à la création du lot ou éditée) — ce n'est pas un coût calculé depuis la production. À vérifier avant d'appuyer une décision tarifaire dessus.",
    badge_real_avg: "● réel (moy. #n lot)",
    badge_real_avg_plural: "● réel (moy. #n lots)",
    badge_manual_partial: "— dont #count saisi ✎",
    badge_manual_partial_plural: "— dont #count saisis ✎",
    tooltip_manual_partial:
      "#a des #b lots de la moyenne porte(nt) un coût saisi manuellement, pas calculé depuis la production.",
    badge_theoretical: "○ théorique",
    badge_realized: "● réalisé",
    badge_calculated: "● calculé",
    badge_partial: "⚠ partiel (#count sans coût)",
    tooltip_col_mo:
      "Main-d'œuvre profonde estimée à l'unité. Règle réel prioritaire : historique OFs article (≥3) → dernier lot produit → moyenne famille·ligne → bilan productivité → catalogue. Survoler pour voir la valeur retenue + l'alternative.",
    col_mo_unit: "Main-d'œuvre /u",
    badge_real: "● réel",
    tooltip_no_mo_history: "Pas d'historique MO exploitable pour cet article",
    tooltip_col_total:
      "Coût de revient total (matière + MO). Sert de base au calcul de la marge MO déduite.",
    col_total_unit: "Coût total /u",
    tooltip_col_margin: "Marge unitaire actuelle en € (Prix - Coût)",
    col_margin_unit: "Marge /u",
    tooltip_col_rate: "Taux de marge actuel",
    col_rate: "Taux",
    tooltip_col_realized:
      "Marge moyenne réellement dégagée sur les 12 derniers mois facturés (en €/UG, cohérent avec 'Marge /u' au-dessus).\nPrix = prix encaissé net de toutes remises (ligne + pied de facture).\nCoût = coût du lot au moment de l'expédition (historique).\nAvoirs déduits.\nÀ comparer à 'Marge /u' (prospective) qui utilise le prix catalogue actuel.",
    col_realized_margin: "Marge réalisée /u",
    tooltip_no_history:
      "Pas de facturation historique pour ce couple article × client sur les 12 derniers mois.",
    tooltip_col_new_margin:
      "Marge unitaire après application du scénario (prix inchangé)",
    col_sim_margin: "Marge simulée /u",
    tooltip_col_new_rate:
      "Taux de marge après application du scénario (prix inchangé)",
    col_new_rate: "Nv. taux",
    tooltip_col_impact:
      "Variation de marge totale sur la période si le prix n'est pas actualisé (signe négatif = perte, positif = gain ponctuel lié à une baisse de coût)",
    col_margin_impact: "Impact marge",
    tooltip_col_keep_val:
      "Nouveau prix de vente à appliquer pour conserver la même marge absolue en €. Affiché en €/UG et en €/UV (conditionnement de vente).",
    col_keep_val: "Prix maintien €",
    tooltip_price_variation: "Variation du prix de vente côté client",
    tooltip_price_variation_abs: "Variation absolue du prix unitaire",
    tooltip_price_per: "Prix par #u (= #coef UG)",
    tooltip_col_keep_pct:
      "Nouveau prix de vente à appliquer pour conserver le même taux de marge %. Affiché en €/UG et en €/UV (conditionnement de vente).",
    col_keep_pct: "Prix maintien taux",
    phase_prices: "Récupération des prix de vente clients…",
    phase_sim_costs: "Simulation des coûts par article…",
    phase_volumes: "Chargement des volumes réalisés…",
    phase_margins: "Consolidation des marges…",
    period_3m: "3 mois",
    period_6m: "6 mois",
    period_12m: "12 mois",
    tooltip_realized_lines: "Marge réalisée sur #count ligne(s) de facture",
    tooltip_realized_scope_mo:
      "Périmètre : matière + MO (cohérent avec le scénario)",
    tooltip_realized_scope_mat:
      "Périmètre : matière seule (MO exclue, cohérent avec le scénario)",
    tooltip_realized_price: "· Prix moyen encaissé net : #v",
    tooltip_realized_remise: "· dont remise pied moyenne : #v %",
    tooltip_realized_cost_mo: "· Coût moyen lot (mat + MO) : #v",
    tooltip_realized_cost_mat: "· Coût moyen lot (matière seule) : #v",
    tooltip_realized_margin: "· = Marge unitaire : #v (#p %)",
    tooltip_realized_qty: "· Sur qty nette : #qty u",
    ventes_scope_tiers: "ce client spécifiquement",
    ventes_scope_all: "tous clients confondus (fallback)",
    tooltip_ventes_title: "Coût retenu depuis les ventes réelles — #s",
    tooltip_ventes_lines: "· #count ligne(s) de facture sur #months mois",
    tooltip_ventes_price: "· Prix moyen encaissé : #v €",
    tooltip_ventes_remise: "· Remise pied moyenne : #v %",
    tooltip_ventes_mat: "· Coût matière : #v €",
    tooltip_ventes_mo: "· Coût MO : #v €",
    tooltip_ventes_note:
      "Avoirs compensés (qty + coût négatifs), seuil min 3 lignes.",
    snack_mail_sent: "Mail tarifaire envoyé à #name.",
    snack_no_products: "#name n'a aucun produit impacté — rien à envoyer.",
    snack_export_error:
      "L'export Excel a échoué. Réessayez ou contactez le support si le problème persiste.",
    // ─── Dialog Détail marge ───
    dlg_margin_title: "Détail marge produit",
    dlg_margin_client: "Client : #name",
    dlg_margin_no_mo_title: "Calcul hors main-d'œuvre.",
    dlg_margin_no_mo_desc:
      "Les coûts et marges ci-dessous portent uniquement sur la matière.",
    dlg_margin_comp_cost: "Coût de revient",
    dlg_margin_comp_mat: "matière",
    dlg_margin_comp_mo_deducted: "La MO est déduite de la marge.",
    dlg_margin_no_price_title: "Aucun prix de vente trouvé",
    dlg_margin_no_price_desc:
      "— ni fiche article-fourni (tiers / groupe / générique), ni historique de facturation. Les marges et propositions de repricing ne sont pas calculables.",
    dlg_margin_price_invoiced: "Dernier prix facturé",
    dlg_margin_price_hint_group: "pas de prix spécifique au tiers.",
    dlg_margin_price_hint_invoice: "aucune fiche article-fourni avec prix.",
    dlg_margin_remise_title: "Remise appliquée",
    dlg_margin_remise_line: "prix catalogue #cat → prix net #net",
    dlg_margin_remise_tiers_article: "Remise tiers (article)",
    dlg_margin_remise_tiers_global: "Remise tiers (globale)",
    dlg_margin_remise_groupe_article: "Remise groupe (article)",
    dlg_margin_remise_groupe_global: "Remise groupe (globale)",
    dlg_margin_remise_default: "Remise",
    dlg_margin_cc_tooltip:
      "Indicateur de contrôle : moyenne entre le dernier lot et la moyenne pondérée des lots récents. Il sert uniquement à détecter une divergence avec le coût retenu (chip Δ affichée au-delà de 5 %) — ce n'est PAS le coût utilisé dans les calculs de marge.",
    dlg_margin_cc_label: "Coût moyen constaté",
    dlg_margin_col_matiere: "Matière",
    dlg_margin_col_total: "Total",
    dlg_margin_used_badge: "utilisé",
    dlg_margin_retained: "Coût retenu",
    dlg_margin_src_ventes: "ventes réelles",
    dlg_margin_src_manual: "saisi manuellement",
    dlg_margin_src_real_partial: "réel — incl. coûts saisis",
    dlg_margin_src_real: "réel",
    dlg_margin_src_theoretical: "théorique",
    dlg_margin_src_cascade: "cascade composants",
    dlg_margin_src_calculated: "calculé",
    dlg_margin_src_partial: "partiel",
    dlg_margin_src_none: "source indisponible",
    dlg_margin_retained_tooltip_base:
      "Règle : le dernier coût réellement observé (réel) est retenu en priorité ; la cascade théorique prend le relais quand aucun réel n'est disponible. Cliquer pour ouvrir le détail de la nomenclature.",
    dlg_margin_retained_tooltip_real:
      'Ici, la source retenue est "réel" : moyenne pondérée des 3 derniers lots produits / reçus (fenêtre différente de la ligne « Moyenne pondérée » ci-dessous, qui élargit jusqu\'à 10 lots).',
    dlg_margin_retained_tooltip_theo:
      'Ici, la source retenue est "théorique" (aucun réel exploitable pour cet article).',
    dlg_margin_theo_bom: "Nomenclature théorique",
    dlg_margin_theo_bom_tooltip:
      "Recomposition de la nomenclature à partir des composants (coûts actuels des MP). Cliquer pour voir le détail.",
    dlg_margin_last_lot: "Dernier lot",
    dlg_margin_manual_badge: "✎ saisi",
    dlg_margin_manual_tooltip:
      "Coût saisi manuellement (valeur entrée à la création du lot ou éditée) — pas un coût calculé depuis la production.",
    dlg_margin_avg_weighted: "Moyenne pondérée",
    dlg_margin_avg_lots: "#count dernier lot — 10 max",
    dlg_margin_avg_lots_plural: "#count derniers lots — 10 max",
    dlg_margin_avg_qty: ", #qty u produites",
    dlg_margin_avg_override: ", dont #count au coût saisi ✎",
    dlg_margin_avg_tooltip_1: "Moyenne pondérée par la quantité de chaque lot.",
    dlg_margin_avg_tooltip_2:
      "#count lot(s) retenu(s) · total #qty u produites.",
    dlg_margin_avg_tooltip_row: "• #num · #date · #qty u · mat #mat · MO #mo",
    dlg_margin_lot_num: "n°#n",
    dlg_margin_lot_no_num: "(sans numéro)",
    dlg_margin_lot_history_title: "Historique des coûts de production",
    dlg_margin_loading: "Chargement…",
    dlg_margin_no_history: "Aucun historique disponible.",
    dlg_margin_col_lot: "Lot",
    dlg_margin_col_date: "Date",
    dlg_margin_col_qty: "Quantité",
    dlg_margin_col_qty_tooltip:
      "Quantité produite/reçue sur le lot — sert de pondération dans le calcul de la moyenne.",
    dlg_margin_col_source: "Source",
    dlg_margin_src_of: "Fabrication",
    dlg_margin_src_achat: "Achat",
    dlg_margin_src_mvt: "Mvt stock",
    dlg_margin_divergence_warning:
      "L'écart entre le coût simulé et le coût constaté dépasse 5 %. Vérifiez que les prix composants sont à jour.",
    dlg_margin_ventes_title: "Coût retenu depuis les ventes réelles",
    dlg_margin_ventes_scope_tiers: "Granularité : ce client (V1)",
    dlg_margin_ventes_scope_article:
      "Granularité : tous clients confondus (V2 — fallback)",
    dlg_margin_ventes_lines: "#count ligne(s) facturée(s) sur #months mois",
    dlg_margin_ventes_pedago:
      "Priorité à la marge constatée : lorsque ≥ 3 lignes de facture sont disponibles sur la fenêtre, le coût retenu est la moyenne pondérée €/UG effectivement payée pour produire ces ventes (coût du lot au moment de l'expédition). Les remises et avoirs sont déjà intégrés. Sans données suffisantes, la chaîne classique reprend la main (moyenne 3 derniers lots → cascade composants → catalogue).",
    dlg_margin_ventes_price_label:
      "Prix moyen UV encaissé (net toutes remises)",
    dlg_margin_per_u: "/ u",
    dlg_margin_remise_pied: "dont remise pied moyenne",
    dlg_margin_ventes_mat_label: "Coût matière moyen €/UG",
    dlg_margin_ventes_mo_label: "Coût MO moyen €/UG",
    dlg_margin_ventes_total_label: "= Coût total retenu",
    dlg_margin_ventes_no_lot:
      "#count ligne(s) sans lot (prestations pures — coût 0 €).",
    dlg_margin_ventes_no_cost:
      "#count ligne(s) avec lot mais sans Cost historique (coût 0 €).",
    dlg_margin_realized_title: "Marge réalisée sur 12 mois",
    dlg_margin_realized_subtitle:
      "#count ligne(s) facturée(s) · #qty u vendues (net d'avoirs)",
    dlg_margin_realized_pedago:
      "Calcul à partir des factures réelles des 12 derniers mois (hors brouillons et annulées, avoirs déduits). Prix = prix HT effectivement encaissé, net de toutes les remises (ligne ET pied de facture). Coût = coût du lot expédié au moment de la vente (snapshot historique).",
    dlg_margin_realized_price_label: "Prix moyen encaissé net",
    dlg_margin_per_uv: "/ UV",
    dlg_margin_soit_ug: "soit #v / UG",
    dlg_margin_realized_cost_label: "Coût moyen lot (à la date d'expédition)",
    dlg_margin_realized_margin_label: "= Marge unitaire réelle",
    dlg_margin_realized_no_lot:
      "#count ligne(s) sans lot physique (prestations pures — coût considéré à 0 €).",
    dlg_margin_realized_no_cost:
      "#count ligne(s) avec lot mais sans coût enregistré.",
    dlg_margin_hero_label: "Impact coût unitaire",
    dlg_margin_cost_realized: "● Coût réalisé",
    dlg_margin_cost_calculated: "● Coût calculé",
    dlg_margin_cost_partial: "⚠ Coût partiel (#count sans coût)",
    dlg_margin_cost_theoretical: "○ Coût théorique",
    dlg_margin_per_unit: "/ unité",
    dlg_margin_new_cost: "Nouveau coût",
    dlg_margin_breakdown_title: "Décomposition de l'impact",
    dlg_margin_section_margin: "Impact sur la marge",
    dlg_margin_current_margin: "Marge actuelle",
    dlg_margin_hint_sell_price: "prix vente",
    dlg_margin_hint_cost: "coût",
    dlg_margin_sim_margin: "Marge simulée",
    dlg_margin_price_unchanged: "(prix inchangé)",
    dlg_margin_hint_new_cost: "nv. coût",
    dlg_margin_pts: "pts",
    dlg_margin_section_total: "Impact total sur la période",
    dlg_margin_term_delta: "Δ coût unitaire",
    dlg_margin_term_volume: "Volume vendu",
    dlg_margin_term_impact: "Impact marge total",
    dlg_margin_section_repricing: "Propositions de repricing",
    dlg_margin_keep_val_title: "Maintien marge €",
    dlg_margin_keep_val_desc: "Conserver la même marge absolue en €",
    dlg_margin_new_price: "Nouveau prix",
    dlg_margin_variation: "Variation",
    dlg_margin_keep_pct_title: "Maintien taux %",
    dlg_margin_keep_pct_desc: "Conserver le même taux de marge",
    dlg_margin_tip_1: "Le scénario modifie le coût de revient de",
    dlg_margin_tip_2: "de #delta par unité.",
    dlg_margin_tip_no_price_1:
      "Aucun prix de vente n'a été trouvé pour ce produit chez",
    dlg_margin_tip_no_price_2:
      "— les marges et propositions de repricing ne sont pas calculables.",
    dlg_margin_tip_volume_1: "Sur #vol unités vendues à",
    dlg_margin_tip_volume_2: ", cela représente un impact de #v sur la marge.",
    dlg_margin_tip_keep:
      "Pour maintenir la marge, le prix de vente doit passer de #from à #to.",
    // ─── Dialog Coût de revient (breakdown) ───
    dlg_breakdown_loading: "Calcul en cours…",
    dlg_breakdown_hero_label: "Coût de revient unitaire",
    dlg_breakdown_badge_partial: "⚠ partiel",
    dlg_breakdown_per_unit: "par unité",
    dlg_breakdown_unit_cost: "Coût unitaire",
    dlg_breakdown_source: "source : #s",
    dlg_breakdown_dec_title: "Décomposition du coût matière",
    dlg_breakdown_dec_real: "Réalisé",
    dlg_breakdown_dec_theo: "Théorique",
    dlg_breakdown_theo_hint: "recomposé depuis les composants (coûts actuels)",
    dlg_breakdown_retained_badge: "retenu",
    dlg_breakdown_gap: "Écart",
    dlg_breakdown_gap_pos: "réel plus cher que théorique",
    dlg_breakdown_gap_neg: "réel moins cher que théorique",
    dlg_breakdown_real_hint_lot: "lot n°#n — dernière production / réception",
    dlg_breakdown_real_hint: "dernière production / réception",
    dlg_breakdown_components: "Composants (#count)",
    dlg_breakdown_no_components:
      "Aucun composant dans la nomenclature de cet article.",
    dlg_breakdown_total_per_unit: "Total par unité",
    dlg_breakdown_no_data:
      "Aucune nomenclature ni coût direct pour cet article.",
    dlg_breakdown_src_last: "Dernier coût enregistré",
    dlg_breakdown_src_valunit: "Valeur unitaire catalogue",
    dlg_breakdown_src_cascade: "Calculé via sa propre nomenclature",
    dlg_breakdown_src_none: "Aucun coût connu",
    // ─── Dialog Cascade (décomposition de l'impact) ───
    dlg_cascade_no_mo_desc:
      "Les valeurs ci-dessous portent uniquement sur le coût matière de l'article.",
    dlg_cascade_contribs: "#count composant impactant",
    dlg_cascade_contribs_plural: "#count composants impactants",
    dlg_cascade_mo_chip_tooltip:
      "Variation globale de la main-d'œuvre appliquée à cette simulation",
    dlg_cascade_depth: "profondeur #n",
    dlg_cascade_mat_title: "Matière — Réel vs théorique",
    dlg_cascade_mo_title: "MO — Réel vs théorique",
    dlg_cascade_real: "Réel",
    dlg_cascade_wf_title: "Répartition de l'impact",
    dlg_cascade_wf_hint: "axe zoomé sur la plage des variations",
    dlg_cascade_hide_zero_tooltip:
      "Cacher les composants sans impact pour gagner en lisibilité",
    dlg_cascade_show_zero_tooltip:
      "Inclure les composants présents mais sans variation simulée",
    dlg_cascade_hide_zero: "Masquer les 0 €",
    dlg_cascade_show_all: "Afficher tous (#count cachés)",
    dlg_cascade_detail_both: "Détail par composant & main-d'œuvre",
    dlg_cascade_detail_mo: "Détail main-d'œuvre",
    dlg_cascade_detail_comp: "Détail par composant",
    dlg_cascade_pct_total: "% du total",
    dlg_cascade_before: "Avant",
    dlg_cascade_after: "Après",
    dlg_cascade_term_delta_comp: "Δ prix composant",
    dlg_cascade_per_comp_unit: "par unité de composant",
    dlg_cascade_qty_in: "Quantité dans 1 #type",
    dlg_cascade_comp_per_pf: "composant / produit fini",
    dlg_cascade_per_pf_unit: "par unité de PF",
    dlg_cascade_mo_current: "Coût MO actuel",
    dlg_cascade_mo_sim: "Coût MO simulé",
    dlg_cascade_mo_estimated: "estimé depuis les OFs historiques",
    dlg_cascade_mo_variation: "Variation MO",
    dlg_cascade_mo_applied: "appliquée au scénario",
    dlg_cascade_mo_contribution: "Contribution MO",
    dlg_cascade_sum_label: "Somme des contributions",
    dlg_cascade_mo_contrib_tooltip: "Contribution main-d'œuvre",
    dlg_cascade_tip_both_1: "subit deux effets :",
    dlg_cascade_tip_mat_label: "• Matière",
    dlg_cascade_tip_mat_desc:
      "— utilise directement ou via semi-finis les composants ci-dessus. Chaque composant contribue à hauteur de",
    dlg_cascade_tip_formula: "Δ prix × quantité par produit fini",
    dlg_cascade_tip_mo_label: "• Main-d'œuvre",
    dlg_cascade_tip_mo_desc:
      "— le coût MO estimé sur les OFs historiques est multiplié par la variation globale du scénario.",
    dlg_cascade_tip_both_total: "Le total est la somme des deux.",
    dlg_cascade_tip_mo_only_1:
      "Aucun composant matière de ce scénario n'entre dans la nomenclature de",
    dlg_cascade_tip_mo_only_2: ". L'impact vient uniquement de la",
    dlg_cascade_tip_mo_only_strong: "variation globale de la main-d'œuvre",
    dlg_cascade_tip_mo_only_3:
      ", appliquée au coût MO estimé depuis les OFs historiques.",
    dlg_cascade_tip_comp_1:
      "utilise directement ou via semi-finis les composants ci-dessus dans sa nomenclature. Chaque composant contribue au coût de revient à hauteur de",
    dlg_cascade_tip_comp_2: ", puis les contributions sont sommées.",
    // ─── Dialog Impact direct (détail du calcul) ───
    dlg_direct_title: "Détail du calcul",
    dlg_direct_no_mo_desc:
      "Le détail ci-dessous porte uniquement sur le coût matière.",
    dlg_direct_qty_eyebrow: "Quantité par unité",
    dlg_direct_qty_title: "Consommation unitaire",
    dlg_direct_qty_lead_1: "Pour fabriquer",
    dlg_direct_qty_lead_2: "de #name",
    dlg_direct_qty_unit: "#u de #name",
    dlg_direct_result_eyebrow: "Impact sur le coût de revient",
    dlg_direct_result_title: "Par #u de produit fini",
    dlg_direct_tip_1: "L'article",
    dlg_direct_tip_2: "consomme directement",
    dlg_direct_tip_3:
      "de #driver par #u produit. Toute variation se répercute proportionnellement.",
    dlg_direct_unit_fallback: "unité",
    // ─── Dialog Classification article ───
    dlg_article_type_title: "Classification article",
    dlg_article_type_rule_title: "Règle de classification",
    dlg_article_type_rule_1: "Un article est un",
    dlg_article_type_rule_2:
      "s'il apparaît comme composant dans la nomenclature d'au moins un autre article du catalogue. Un",
    dlg_article_type_rule_3:
      "est vendu directement aux clients via un tarif (ArticleFourni). Un article à la fois consommé ET vendu porte le badge",
    dlg_article_type_consumed_by: "Consommé par #count article",
    dlg_article_type_consumed_by_plural: "Consommé par #count articles",
    dlg_article_type_hidden: "… et #count autre non affiché",
    dlg_article_type_hidden_plural: "… et #count autres non affichés",
    dlg_article_type_no_parent:
      "Classé comme consommé mais aucun parent n'a pu être résolu dans la simulation en cours — l'article est référencé ailleurs dans le catalogue mais hors périmètre de calcul.",
    dlg_article_type_pf_prefix: "Un tarif client actif",
    dlg_article_type_pf_mixed:
      "existe pour cet article — il est donc vendu directement, en plus d'être utilisé comme composant.",
    dlg_article_type_pf_pure:
      "existe pour cet article et aucune autre nomenclature ne le consomme. Il est vendu tel quel en tant que produit fini.",
    dlg_article_type_other:
      "Article sans classification claire — ni produit fini ni semi-fini identifié sur la base des nomenclatures actives et des tarifs clients.",
    dlg_article_type_pf_label: "Produit fini",
    dlg_article_type_sf_label: "Semi-fini",
    dlg_article_type_mixed_label: "Produit fini ET semi-fini",
    dlg_article_type_default_label: "Article",
    dlg_article_type_pf_tagline:
      "Article fabriqué et vendu tel quel — n’entre dans aucune autre nomenclature.",
    dlg_article_type_sf_tagline:
      "Article fabriqué qui entre comme composant dans au moins une autre nomenclature, sans être vendu directement aux clients.",
    dlg_article_type_mixed_tagline:
      "Article à double rôle : vendu directement aux clients ET utilisé comme composant dans la nomenclature d’autres articles.",
    dlg_article_type_other_tagline:
      "Article non classifié — ni produit fini ni semi-fini identifié.",
    // ─── Dialog Méthode de calcul MO ───
    dlg_mo_title: "Méthode de calcul MO",
    dlg_mo_hero_label: "MO retenue (€ / u. produit fini)",
    dlg_mo_source_label: "Source :",
    dlg_mo_tier: "niveau #tier",
    dlg_mo_cmp_title: "Réel vs théorique",
    dlg_mo_no_real_source: "aucune source réelle exploitable",
    dlg_mo_no_theo_source: "aucune source théorique exploitable",
    dlg_mo_no_data_title: "Pas assez de données pour estimer la MO",
    dlg_mo_no_data_desc:
      "Pour peupler l'historique : valider un OF sur cet article, saisir un coût MO sur un lot produit, ou alimenter le bilan productivité de la ligne.",
    dlg_mo_retained_detail: "Détail de la source retenue",
    dlg_mo_kpi_nb_ofs: "Nb OFs analysés",
    dlg_mo_kpi_total_qty: "Qté totale produite",
    dlg_mo_kpi_total_mo: "MO totale",
    dlg_mo_kpi_bps: "Bilans productivité",
    dlg_mo_samples_title: "Échantillons (les plus récents)",
    dlg_mo_col_of: "OF",
    dlg_mo_col_ligne: "Ligne",
    dlg_mo_col_date_fin: "Date fin",
    dlg_mo_col_qty: "Qté",
    dlg_mo_col_mo_unit: "MO / u.",
    dlg_mo_others_title: "Autres sources considérées",
    dlg_mo_real_sources: "Sources réelles",
    dlg_mo_real_sources_hint: "observées sur la production de cet article",
    dlg_mo_theo_sources: "Sources théoriques",
    dlg_mo_theo_sources_hint:
      "extrapolation depuis des articles similaires ou une valeur catalogue",
    dlg_mo_not_usable: "Non utilisable",
    dlg_mo_fallback_help:
      "L'estimation teste les sources du plus fiable au moins fiable, en privilégiant toujours le réel observé : historique OFs article (réel agrégé) → dernier lot produit (réel ponctuel) → moyenne famille · ligne (théorique) → bilan productivité (théorique) → valeur catalogue (théorique). La première qui renvoie une valeur exploitable est retenue.",
    dlg_mo_src_of_history: "Historique OFs article",
    dlg_mo_src_family_line: "Moyenne famille technique · ligne",
    dlg_mo_src_bp: "Bilan productivité ligne",
    dlg_mo_src_last_lot: "Dernier lot produit",
    dlg_mo_src_last_lot_num: "Dernier lot produit — n°#n",
    dlg_mo_src_catalog: "Valeur catalogue article",
    dlg_mo_src_none: "Aucune source fiable",
    // ─── Dialog nom de scénario + bannière de progression ───
    dlg_scenario_name_label: "Nom du scénario",
    progress_default_entity: "éléments",
    progress_init: "Initialisation…",
    // ─── Dialog Mail tarifaire (UI) ───
    mail_title: "Mail tarifaire — #name",
    mail_mode_label: "Mode de recalage",
    mail_apply_date_label: "Application au",
    mail_reason_label: "Motif",
    mail_reason_hint: "— italique dans le mail si renseigné",
    mail_reason_placeholder: "ex: hausse du coût des matières premières 2026",
    mail_field_label: "Destinataire par défaut",
    mail_display_label: "Affichage dans le mail & le PDF",
    mail_toggle_current: "Tarif actuel",
    mail_toggle_current_tooltip:
      "Afficher le tarif actuel aux côtés du nouveau",
    mail_toggle_evolution: "Évolution %",
    mail_toggle_evolution_tooltip: "Afficher le taux d'évolution en %",
    mail_toggle_round: "Arrondi à 0,01 €",
    mail_toggle_round_tooltip: "Arrondit les nouveaux tarifs à 2 décimales",
    mail_reset_template: "Réinitialiser le modèle",
    mail_reset_template_tooltip:
      "Réinitialise sujet et corps depuis les variables actuelles",
    mail_prices_title: "Prix appliqués (#count produit)",
    mail_prices_title_plural: "Prix appliqués (#count produits)",
    mail_prices_hint_1: "Choix par défaut selon le mode",
    mail_prices_hint_2:
      ". Personnalise un prix dans l'input ou via les chips de proposition — marge et évolution se recalculent automatiquement.",
    mail_mode_taux: "Maintien du taux",
    mail_custom_count: "#count prix personnalisé",
    mail_custom_count_plural: "#count prix personnalisés",
    mail_reset_all: "Tout réinitialiser",
    mail_reset_all_tooltip:
      "Reprend le prix par défaut du mode courant sur toutes les lignes",
    mail_col_current_price: "Prix actuel",
    mail_col_suggestions: "Propositions",
    mail_col_price: "Prix retenu",
    mail_col_margin: "Marge / unité",
    mail_col_evolution: "Évolution",
    mail_taux_tooltip: "Maintient le taux de marge initial · #v €",
    mail_marge_tooltip: "Maintient la marge € initiale · #v €",
    mail_custom_input_tooltip:
      "Saisis un prix personnalisé. Vide → reprend la proposition selon le mode global.",
    mail_reset_row_tooltip:
      "Annule la personnalisation et reprend la proposition par défaut",
    mail_recipient_label: "Destinataire",
    mail_cc_label: "Copie (CC)",
    mail_body_label: "Corps du mail",
    mail_body_placeholder: "Rédigez votre message…",
    mail_pdf_title: "Courrier tarifaire formel",
    mail_pdf_desc:
      "Joint automatiquement au mail — format PDF, tableau détaillé, en-tête entreprise, signature.",
    mail_edit_courrier: "Modifier le texte",
    mail_edit_courrier_tooltip:
      "Personnaliser le texte du courrier (comme pour les relances clients) — le tableau des tarifs reste généré automatiquement",
    mail_view_courrier: "Voir le courrier",
    mail_view_courrier_tooltip:
      "Ouvre le courrier dans un nouvel onglet pour relecture",
    mail_courrier_body_hint:
      "Corps de la lettre — avant le tableau des tarifs. Un paragraphe par bloc (ligne vide entre deux paragraphes). La date d'application et le nombre de produits sont déjà intégrés au texte proposé.",
    mail_courrier_closing_hint: "Formule de clôture — après le tableau.",
    mail_courrier_reset: "Revenir au texte automatique",
    mail_skip: "Passer",
    mail_skip_tooltip: "Ne pas envoyer à ce client et passer au suivant",
    mail_field_facturation: "Facturation",
    mail_field_commercial: "Commercial",
    mail_field_commande: "Commande",
    mail_field_general: "Général",
    mail_snack_no_recipient: "Aucun destinataire renseigné.",
    mail_snack_send_failed:
      "L'envoi du mail tarifaire a échoué — le client n'a rien reçu. Réessayez ou vérifiez la configuration mail.",
    // ─── Vague 3 — Exports Excel : clés communes ───
    xls_sheet_scenario_components: "Composants du scénario",
    xls_sheet_impacted_articles: "Articles impactés",
    xls_sheet_synthesis: "Synthèse",
    xls_stat_up: "#count en hausse",
    xls_stat_down: "#count en baisse",
    xls_stat_types: "#pf PF · #sf SF",
    xls_stat_types_mixed: "#pf PF · #sf SF · #mixed PF/SF",
    xls_stat_articles: "#count articles",
    xls_type_mixed: "PF/SF",
    xls_total: "TOTAL",
    xls_col_code: "Code",
    xls_col_client: "Client",
    xls_col_products: "Produits",
    xls_col_volume: "Volume",
    xls_col_delta_eur_u: "Δ €/u",
    xls_col_direction: "Sens",
    xls_dir_up: "↑ hausse",
    xls_dir_down: "↓ baisse",
    xls_col_qty_per_u: "Qté / u",
    xls_col_mat_current: "Coût mat. actuel",
    xls_col_mo: "Coût MO",
    xls_col_mo_disabled: "Coût MO (désactivé)",
    xls_col_mo_off: "Coût MO (off)",
    xls_col_total_current: "Coût total actuel",
    xls_col_delta_component_price: "Δ prix composant",
    xls_col_contribution_u: "Contribution /u",
    xls_col_total_sim: "Coût total simulé",
    xls_col_contribution_eur_unit: "Contribution €/u",
    xls_col_delta_mat_u: "Δ Mat. /u",
    xls_col_delta_mo_u: "Δ MO /u",
    xls_col_delta_total_u: "Δ total /u",
    xls_col_nb_components: "Nb composants",
    xls_col_price_source: "Source prix",
    xls_col_price_u: "Prix/u",
    xls_col_cost_u: "Coût/u",
    xls_col_delta_cost_u: "Δ coût/u",
    xls_col_impact_eur: "Impact €",
    xls_col_ug: "UG",
    xls_col_coef: "Coef",
    xls_col_coef_ug: "Coef UG",
    xls_col_mat_u: "Coût mat. /u",
    xls_col_mo_u: "Coût MO /u",
    // ─── Vague 3 — Export Excel Impact direct ───
    xls_direct_title_impacts:
      "Impact direct — articles dont la recette contient un composant du scénario",
    xls_direct_stat_lines: "#count lignes d'impact",
    xls_direct_stat_sum: "Somme Δ : #v €",
    xls_direct_total_contributions: "TOTAL CONTRIBUTIONS",
    xls_direct_sheet_top: "Top contributions",
    xls_direct_title_top: "Top 15 articles par contribution absolue",
    // ─── Vague 3 — Export Excel Impact cascade ───
    xls_cascade_sheet_overview: "Vue d'ensemble",
    xls_cascade_title_overview: "Impact cascade — synthèse",
    xls_cascade_stat_impacted: "#count articles impactés",
    xls_cascade_stat_avg: "Δ moyen : #v%",
    xls_cascade_title_impacts: "Articles impactés (cascade complète)",
    xls_cascade_stat_sum_base: "Σ coût actuel : #v €",
    xls_cascade_stat_sum_delta: "Σ Δ : #v € (#p%)",
    xls_cascade_title_drivers: "Composants du scénario — synthèse",
    // ─── Vague 3 — Export Excel Impact marge ───
    xls_price_level_tiers: "tarif client",
    xls_margin_chart_gain: "Gain si prix actualisé (maintien taux)",
    xls_margin_title_synthesis: "Impact marge — synthèse par client (#period)",
    xls_margin_stat_clients: "#clients clients · #products produits",
    xls_margin_stat_loss: "Perte si inchangé : #v €",
    xls_margin_stat_gain: "Gain si actualisé : #v €",
    xls_margin_col_loss: "Perte si prix inchangé",
    xls_margin_col_gain: "Gain si prix actualisé",
    xls_margin_col_ratio: "Ratio",
    xls_margin_col_priority: "Priorité",
    xls_margin_priority_high: "● élevée",
    xls_margin_priority_medium: "● moyenne",
    xls_margin_priority_low: "● faible",
    xls_margin_sheet_top: "Top impacts",
    xls_margin_title_top: "Top 30 produits × clients par valeur d'impact",
    xls_margin_col_gain_margin: "+Marge €",
    xls_margin_col_margin_u: "Marge/u",
    xls_margin_col_new_margin_u: "Nv marge/u",
    xls_margin_col_new_rate: "Nv taux",
    xls_margin_col_price_uv: "Prix /UV",
    xls_margin_col_keep_val_uv: "Prix maintien € /UV",
    xls_margin_col_keep_pct_uv: "Prix maintien taux /UV",
    xls_margin_title_client: "#name — impact marge (#period)",
    xls_margin_stat_products: "#count produit(s)",
    xls_margin_stat_loss_client: "Perte si prix inchangé : #v €",
    xls_margin_stat_gain_client: "Gain si prix actualisé (taux) : #v €",
    xls_margin_note_formulas:
      "Astuce : modifier le Δ coût/u (colonne #delta) ou le Prix/u (colonne #price) recalcule automatiquement marge, taux, impact et prix de maintien via les formules.",
    xls_margin_sheet_raw: "Données brutes",
    xls_margin_sheet_client_fallback: "Client",
    xls_margin_title_raw: "Données brutes — tous clients × produits (#period)",
    xls_margin_col_delta_mat_lc: "Δ mat. /u",
    xls_margin_col_new_margin_raw: "Nv marge /u",
    xls_margin_col_rate_pct: "Taux marge %",
    xls_margin_col_new_rate_pct: "Nv taux %",
    xls_margin_col_gain_keep_val: "Gain maintien €",
    xls_margin_col_gain_keep_pct: "Gain maintien taux",
    xls_margin_col_last_lot_mat: "Dern. lot mat.",
    xls_margin_col_last_lot_mo: "Dern. lot MO",
    xls_margin_col_last_lot_date: "Dern. lot date",
    xls_margin_col_avg_lot_mat: "Moy. lots mat.",
    xls_margin_col_avg_lot_mo: "Moy. lots MO",
    xls_margin_col_avg_lot_count: "Nb lots moy.",
    // ─── Vague 3 — Export Excel produit : synthèse ───
    xls_product_title_synthesis: "Fiche produit — #name",
    xls_product_grp_identity: "Identité",
    xls_product_lbl_article_code: "Code article",
    xls_product_lbl_prod_unit: "Unité de production",
    xls_product_unit_abbrev: "u.",
    xls_product_lbl_ug: "Unité de gestion (facturation)",
    xls_product_lbl_coef: "Nombre d'unités de production par unité de gestion",
    xls_product_lbl_period: "Période analysée",
    xls_product_lbl_mat_source: "D'où vient le coût matière",
    xls_product_lbl_mo_source: "D'où vient le coût main-d'œuvre",
    xls_product_grp_unit_costs:
      "Coûts unitaires détaillés (par unité produite)",
    xls_product_col_component: "Composante",
    xls_product_col_current: "Actuel (€/u.)",
    xls_product_col_variation: "Variation scénario (€)",
    xls_product_col_after: "Après scénario (€/u.)",
    xls_product_col_how: "Comment c'est calculé",
    xls_product_lbl_mat_cost: "Coût matière",
    xls_product_how_mat:
      "Somme des composants descendus jusqu'aux matières premières",
    xls_product_lbl_mo_cost: "Coût main-d'œuvre",
    xls_product_how_mo:
      "Estimé via historique des OFs, bilan productivité ligne ou dernier lot produit",
    xls_product_lbl_total_cost: "Coût de revient total",
    xls_product_how_total: "Coût matière + coût main-d'œuvre",
    xls_product_grp_prices: "Prix de vente et marges",
    xls_product_col_indicator: "Indicateur",
    xls_product_col_today: "Aujourd'hui",
    xls_product_col_if_unchanged: "Si le prix ne bouge pas",
    xls_product_lbl_sell_price: "Prix de vente unitaire",
    xls_product_how_sell_price:
      "Tarif facturé au client (inchangé dans ce scénario)",
    xls_product_lbl_margin_unit: "Marge par unité (€)",
    xls_product_how_margin: "Prix de vente − Coût de revient",
    xls_product_lbl_margin_rate: "Taux de marge (%)",
    xls_product_how_rate: "Marge ÷ Prix de vente × 100",
    xls_product_grp_period_impact: "Impact sur la période",
    xls_product_lbl_volume_period: "Volume vendu sur #period",
    xls_product_how_volume: "Quantité réellement vendue au client",
    xls_product_lbl_total_impact: "Impact total sur la marge",
    xls_product_how_impact: "Variation du coût × volume (négatif = perte)",
    // ─── Vague 3 — Export Excel produit : détail des coûts ───
    xls_product_sheet_cost_detail: "Détail des coûts",
    xls_product_title_cost_detail: "Détail des coûts — #name",
    xls_product_grp_drivers:
      "Composants du scénario — impact sur le coût du produit fini",
    xls_product_col_current_u: "Coût actuel /u",
    xls_product_col_after_u: "Coût après scénario /u",
    xls_product_col_comp_variation: "Variation du composant (€)",
    xls_product_col_qty_per_pf: "Quantité pour 1 produit fini",
    xls_product_col_contribution: "Contribution au coût produit",
    xls_product_lbl_total_mat_variation:
      "Total variation matière par unité produite",
    xls_product_lbl_mo_variation:
      "Main-d'œuvre (variation globale appliquée au coût MO actuel)",
    xls_product_grp_paths:
      "Parcours dans la recette — de la matière première au produit fini",
    xls_product_col_step: "Étape",
    xls_product_col_goes_into: "Entre dans",
    xls_product_col_which_into: "Qui entre dans",
    xls_product_col_qty_used: "Quantité utilisée",
    xls_product_path_unavailable: "Détail du parcours indisponible",
    xls_product_path_label: "#name · parcours #i/#n",
    xls_product_path_total: "Total parcours",
    xls_product_path_total_hint:
      "Quantité totale pour 1 produit fini (multiplication des étapes)",
    // ─── Vague 3 — Export Excel produit : nouveau tarif ───
    xls_product_sheet_new_price: "Nouveau tarif",
    xls_product_title_new_price: "Quel tarif appliquer ? — #name",
    xls_product_grp_inputs: "Données de départ",
    xls_product_col_data: "Donnée",
    xls_product_col_value: "Valeur",
    xls_product_in_sell_price: "Prix de vente actuel",
    xls_product_in_sell_price_hint: "Tarif facturé au client aujourd'hui",
    xls_product_in_base_cost: "Coût de revient actuel",
    xls_product_in_mat: "   dont coût matière",
    xls_product_in_mat_hint: "Somme cascade des composants",
    xls_product_in_mo: "   dont coût main-d'œuvre",
    xls_product_in_mo_hint: "Historique OFs, bilan productivité ou dernier lot",
    xls_product_in_delta: "Variation du coût unitaire",
    xls_product_in_delta_hint:
      "Variation matière + main-d'œuvre issue du scénario",
    xls_product_in_delta_mat: "   dont variation matière",
    xls_product_in_delta_mat_hint: "Somme des contributions des composants",
    xls_product_in_delta_mo: "   dont variation main-d'œuvre",
    xls_product_in_delta_mo_hint: "Coût MO actuel × variation globale MO",
    xls_product_in_new_cost: "Coût de revient après scénario",
    xls_product_in_new_cost_hint: "Coût actuel + variation",
    xls_product_in_rate: "Taux de marge initial",
    xls_product_in_rate_hint:
      "(Prix de vente − Coût actuel) ÷ Prix de vente × 100",
    xls_product_grp_strategy1:
      "Stratégie 1 — Garder la même marge en € par unité",
    xls_product_strategy1_desc:
      "Objectif : garder la même marge en € par unité vendue. On répercute exactement la hausse du coût sur le prix de vente.",
    xls_product_lbl_new_price: "Nouveau prix de vente",
    xls_product_strategy1_formula: "Prix de vente actuel + Variation du coût",
    xls_product_lbl_price_var_eur: "   Variation du prix en €",
    xls_product_hint_price_var_eur: "Nouveau prix − Prix de vente actuel",
    xls_product_lbl_price_var_pct: "   Variation du prix en %",
    xls_product_hint_price_var_pct:
      "(Nouveau prix − Prix actuel) ÷ Prix actuel × 100",
    xls_product_lbl_check_margin: "Vérification : marge après recalage",
    xls_product_hint_check_margin:
      "Doit être identique à la marge initiale (Nouveau prix − Coût après scénario)",
    xls_product_grp_strategy2:
      "Stratégie 2 — Garder le même taux de marge en %",
    xls_product_strategy2_desc:
      "Objectif : garder le même pourcentage de marge. La hausse est répercutée ET le taux initial est préservé (la marge en € augmente donc proportionnellement).",
    xls_product_strategy2_formula:
      "Coût après scénario ÷ (1 − Taux initial ÷ 100)",
    xls_product_lbl_check_new_margin: "Vérification : nouvelle marge en €",
    xls_product_hint_check_new_margin: "Nouveau prix − Coût après scénario",
    xls_product_lbl_check_new_rate: "Vérification : nouveau taux de marge",
    xls_product_hint_check_new_rate: "Doit être identique au taux initial",
    xls_product_grp_projection: "Projection sur la période (#period)",
    xls_product_lbl_volume: "Volume vendu",
    xls_product_hint_volume: "Quantité réellement vendue sur la période",
    xls_product_lbl_impact_unchanged:
      "Impact marge si les prix restent inchangés",
    xls_product_lbl_gain1: "Gain récupéré avec la stratégie 1",
    xls_product_hint_gain1: "On récupère exactement la hausse du coût",
    xls_product_lbl_gain2: "Gain récupéré avec la stratégie 2",
    xls_product_hint_gain2: "On récupère la hausse ET on ré-valorise la marge",
    // ─── Vague 3 — Export Excel produit : sources MO ───
    xls_product_mo_src_of_history: "Historique des OFs article",
    xls_product_mo_src_family:
      "Moyenne famille technique sur la ligne de production",
    xls_product_mo_src_catalog: "Valeur unitaire catalogue",
    xls_product_mo_src_sales: "Ventes réelles (historique factures)",
    xls_product_mo_src_none: "Aucune source fiable — 0 € par défaut",
    xls_product_mo_desc_of_history:
      "Coût MO réel constaté sur les ordres de fabrication passés qui ont produit cet article exact.",
    xls_product_mo_desc_family:
      "À défaut d’historique direct, on prend la moyenne des OFs d’articles de la même famille technique produits sur la même ligne.",
    xls_product_mo_desc_bp:
      "Coût horaire MO de la ligne (bilan productivité) multiplié par le temps standard théorique de fabrication pour cet article.",
    xls_product_mo_desc_last_lot:
      "Coût MO unitaire du dernier lot produit, tel qu'enregistré après clôture de l’OF.",
    xls_product_mo_desc_catalog:
      "Valeur unitaire saisie manuellement dans la fiche article (paramétrage de secours).",
    xls_product_mo_desc_sales:
      "Coût MO moyen €/UG constaté sur les factures récentes. Seuil : ≥ 3 lignes pour fiabilité. Intègre remises et avoirs. Tier prioritaire — applique directement la marge constatée.",
    xls_product_mo_desc_none:
      "Aucune des sources précédentes n’a retourné une valeur exploitable. On retient 0 € par défaut et la simulation MO n’aura pas d’impact sur cet article.",
    // ─── Vague 3 — Export Excel produit : méthode de calcul ───
    xls_product_sheet_method: "Méthode de calcul",
    xls_product_title_method: "Méthode de calcul des coûts — #name",
    xls_product_method_intro:
      "Cette feuille explique comment le coût matière et le coût main-d'œuvre ont été déterminés pour cet article, afin de permettre l'audit du calcul.",
    xls_product_grp_mat_method: "Coût matière unitaire — méthode de calcul",
    xls_product_method_avg: "Moyenne pondérée sur 30 jours",
    xls_product_method_last: "Dernier coût connu",
    xls_product_method_avg_desc:
      "Pour chaque composant, on prend la moyenne pondérée des prix d'achat sur les 30 derniers jours. Lisse les variations ponctuelles.",
    xls_product_method_last_desc:
      "Pour chaque composant, on prend le prix du dernier lot réceptionné (le plus récent en base). Réactif aux dernières négociations fournisseurs.",
    xls_product_alg_method: "Méthode de valorisation",
    xls_product_alg_principle: "Principe général",
    xls_product_alg_principle_desc:
      "On part de la nomenclature active de l'article, puis on descend récursivement jusqu'aux matières premières. Pour chaque feuille (composant non fabriqué), on applique la méthode de valorisation ci-dessus.",
    xls_product_alg_sf: "Gestion des semi-finis (SF)",
    xls_product_alg_sf_value: "Réel prioritaire, théorique en secours",
    xls_product_alg_sf_desc:
      "Pour chaque SF on calcule à la fois le réel observé (lastCost du dernier OF clos) et le théorique recomposé (cascade depuis les MP). On retient le réel quand il existe, sinon le théorique. L'écart entre les deux est exposé pour signaler une dérive (dernier OF antérieur à une hausse de MP par ex.).",
    xls_product_alg_fallback: "Fallback si coût manquant",
    xls_product_alg_fallback_value: "lastCost → valeur catalogue → 0 €",
    xls_product_alg_fallback_desc:
      "Sur une feuille (MP) sans lastCost, on retombe sur la valeurUnitaire catalogue de la fiche article. En dernier recours, la feuille est comptabilisée à 0 € et signalée comme composant manquant dans les totaux.",
    xls_product_alg_qty: "Quantité par produit fini",
    xls_product_alg_qty_value: "Quantité ligne ÷ taille de lot",
    xls_product_alg_qty_desc:
      "La quantité consommée de chaque composant = quantité de la ligne de nomenclature ÷ taille de lot de production. Pour la cascade à travers les SF, les quantités se multiplient étape par étape.",
    xls_product_alg_final: "Valeur finale",
    xls_product_alg_final_desc:
      "par #unit — coût matière retenu qui alimente les calculs de marge et d'impact.",
    xls_product_unit_produced: "unité produite",
    xls_product_col_element: "Élément",
    xls_product_col_value_retained: "Valeur retenue",
    xls_product_col_explanation: "Explication",
    xls_product_hdr_figures: "Synthèse chiffrée",
    xls_product_col_detail: "Détail",
    xls_product_fig_mat_before: "Coût matière avant scénario",
    xls_product_fig_mat_before_desc:
      "Total de la cascade matière, méthode de valorisation indiquée ci-dessus.",
    xls_product_fig_mat_var: "Variation scénario matière",
    xls_product_fig_mat_var_desc:
      "Somme des contributions de tous les composants dont le prix est modifié par le scénario (voir feuille #sheet).",
    xls_product_fig_mat_after: "Coût matière après scénario",
    xls_product_fig_mat_after_desc: "Coût matière avant + variation scénario.",
    xls_product_grp_mo_method:
      "Coût main-d'œuvre unitaire — sources testées dans l'ordre",
    xls_product_mo_disclaimer:
      "⚠️ La main-d'œuvre N'EST PAS intégrée au scénario courant. Les valeurs ci-dessous sont affichées à titre d'information : elles ne sont PAS utilisées dans le coût de revient, les marges ou le tarif de cette feuille (tous hors MO).",
    xls_product_mo_alg_principle_desc:
      "Le coût MO unitaire n'est jamais saisi directement. On interroge plusieurs sources dans un ordre strict de fiabilité, et on retient la première qui renvoie une valeur exploitable. Les sources suivantes ne sont pas consultées.",
    xls_product_mo_alg_order: "Ordre de fiabilité",
    xls_product_mo_alg_order_value:
      "Ventes prioritaires, puis réel, puis théorique",
    xls_product_mo_alg_order_desc:
      "1 ) Ventes réelles — moyenne €/UG sur factures (≥3 lignes) — tier prioritaire, intègre remises et avoirs · 2 ) Historique des OFs de l'article (réel agrégé, ≥3 OFs) · 3 ) Dernier lot produit (réel ponctuel) · 4 ) Moyenne famille/ligne (théorique analogique) · 5 ) Bilan productivité ligne (théorique prédictif) · 6 ) Valeur unitaire catalogue (théorique saisi) · 7 ) 0 € par défaut (aucune source exploitable).",
    xls_product_mo_alg_depth: "Profondeur MO",
    xls_product_mo_alg_depth_value: "Inclut la MO des semi-finis",
    xls_product_mo_alg_depth_desc:
      "La valeur retournée par les sources 1 à 4 est déjà un coût MO « profond » : elle inclut la MO des semi-finis consommés dans la recette, calculée récursivement depuis leurs propres OFs.",
    xls_product_mo_alg_impact: "Impact scénario",
    xls_product_mo_alg_impact_value: "Variation MO × coût MO retenu",
    xls_product_mo_alg_impact_desc:
      "La variation MO globale du scénario (en %) est appliquée au coût MO retenu pour obtenir la variation unitaire MO. Les articles sans source MO exploitable (0 €) ne sont pas impactés par la simulation MO.",
    xls_product_mo_alg_retained: "Source retenue pour cet article",
    xls_product_mo_alg_retained_unit: " · par #unit",
    xls_product_grp_mo_candidates:
      "Sources MO testées dans l'ordre pour cet article",
    xls_product_col_tier: "Tier (ordre de fiabilité)",
    xls_product_col_status: "Statut · Raison",
    xls_product_status_retained: "✓ SOURCE RETENUE",
    xls_product_status_tested_beaten: "· testé, écarté par un tier supérieur",
    xls_product_status_tested_no_value: "· testé, pas de valeur exploitable",
    xls_product_status_not_tested: "· non testé (tier supérieur déjà validé)",
    xls_product_grp_of_samples: "OFs utilisés pour l'estimation (#count)",
    xls_product_col_of_num: "N° OF",
    xls_product_col_mo_unit: "MO unitaire (€)",
    xls_product_of_line: "Ligne : #v",
    xls_product_of_closed: "Clôturé le #date",
    xls_product_of_qty: "Quantité produite : #v",
    xls_product_of_mo_total: "Coût MO profond total : #v €",
    // ─── Vague 3 — Export Excel produit : sources de coût ───
    xls_product_src_ventes:
      "Marge constatée sur les ventes (≥ 3 lignes factures)",
    xls_product_src_realise: "Moyenne pondérée des 3 derniers lots produits",
    xls_product_src_theorique: "Cascade théorique depuis les composants",
    xls_product_src_nomenclature:
      "Cascade depuis les composants enfants (fallback)",
    xls_product_src_calcule: "Cascade complète (composants + MO simulée)",
    xls_product_src_partiel: "Cascade partielle (composants manquants)",
    xls_product_src_none: "Aucune source exploitable — coût à 0 €",
    xls_product_legacy_src_last: "Dernier coût lot",
    xls_product_legacy_src_valunit: "Valeur catalogue",
    xls_product_legacy_src_cascade: "Calculé via nomenclature",
    xls_product_legacy_src_none: "Aucun coût connu",
    xls_product_enr_real_lot: "● Réel (lot n°#n)",
    xls_product_enr_real_last: "● Réel (dernier lot)",
    xls_product_enr_theo_cascade: "○ Théorique (cascade)",
    xls_product_enr_theo_catalog: "○ Théorique (catalogue)",
    xls_product_enr_theo: "○ Théorique",
    xls_product_enr_none: "⚠ Aucun coût connu",
    // ─── Vague 3 — Export Excel produit : coût de revient théorique ───
    xls_product_col_qty_per_unit: "Qté pour 1 u",
    xls_product_col_unit_cost: "Coût unit. (€)",
    xls_product_col_cost_source: "Source du coût",
    xls_product_col_contribution_eur: "Contribution (€)",
    xls_product_col_mat_pct: "% matière",
    xls_product_link_sf_tooltip: "Aller au détail de ce semi-fini",
    xls_product_note_real: "Réel (#lot) : #v",
    xls_product_note_theo: "Théorique : #v",
    xls_product_note_gap: "Écart : #v (#p %)",
    xls_product_sf_lot_hint: "Lot n°#n — dernière production",
    xls_product_sf_last_prod: "Dernière production",
    xls_product_sf_realise: "  Réalisé",
    xls_product_sf_theorique: "  Théorique",
    xls_product_sf_theo_hint: "Cascade depuis les composants",
    xls_product_retained_suffix: " · RETENU",
    xls_product_gap_pos: "Réel plus cher que théorique (#p %)",
    xls_product_gap_neg: "Réel moins cher que théorique (#p %)",
    xls_product_sf_gap: "  Écart réel − théorique",
    xls_product_sheet_theoretical: "Coût de revient théorique",
    xls_product_title_theoretical: "Coût de revient théorique — #name",
    xls_product_grp_decomposition: "Décomposition complète du coût de revient",
    xls_product_theo_intro:
      "Cette feuille liste chaque composant entrant dans la fabrication du produit avec la quantité utilisée, le coût unitaire retenu et sa contribution au coût matière total. Le coût MO est ajouté en dessous pour obtenir le coût de revient théorique. Les semi-finis de la recette sont détaillés en cascade plus bas.",
    xls_product_batch_normalized:
      "Quantités normalisées pour 1 unité produite (taille de lot : #n, issue de la spécification)",
    xls_product_batch_simple: "Quantités pour 1 unité produite",
    xls_product_grp_mat_components: "Composants matière — #info",
    xls_product_total_mat: "Total coût matière",
    xls_product_grp_mat_no_bom:
      "Composants matière — article sans nomenclature",
    xls_product_grp_mo_unit: "Coût main-d'œuvre unitaire",
    xls_product_mo_off_label: "Simulation sans main-d'œuvre",
    xls_product_mo_unavailable: "Non disponible",
    xls_product_mo_off_desc:
      "La MO n'est pas intégrée à ce scénario (toggle \"Main-d'œuvre\" désactivé). Elle n'entre ni dans le coût de revient total, ni dans la marge.",
    xls_product_mo_estimated_desc:
      'Coût MO unitaire estimé — voir feuille "#sheet" pour le détail des candidats testés',
    xls_product_mo_none_desc:
      "Aucun coût MO estimable sur cet article (pas d'historique OF, famille · ligne, bilan ou lot exploitable)",
    xls_product_grp_theo_total: "Coût de revient théorique unitaire",
    xls_product_mat_lot_hint: "Lot n°#n — dernière production / réception",
    xls_product_mat_last_hint: "Dernière production / réception",
    xls_product_mat_realise: "Coût matière — Réalisé",
    xls_product_retained_for_calc: " · RETENU pour les calculs",
    xls_product_mat_theorique: "Coût matière — Théorique",
    xls_product_mat_theo_hint: "Cascade depuis les composants (coûts actuels)",
    xls_product_mat_gap_label: "Écart réel − théorique",
    xls_product_mat_gap_pos:
      "Réel plus cher que théorique (#p %) — dernière production antérieure à la hausse des MP",
    xls_product_mat_gap_neg:
      "Réel moins cher que théorique (#p %) — dernière production antérieure à la baisse des MP",
    xls_product_mat_retained: "Coût matière retenu",
    xls_product_mat_retained_real: "= Réalisé (réel prioritaire sur théorique)",
    xls_product_mat_retained_theo: "= Théorique (aucun réalisé disponible)",
    xls_product_no_source: "Aucune source exploitable",
    xls_product_mat_sum_hint: "Somme des contributions ci-dessus",
    xls_product_mo_real: "Coût MO — Réel",
    xls_product_mo_real_hint: "Historique OFs article ou dernier lot",
    xls_product_mo_theo: "Coût MO — Théorique",
    xls_product_mo_theo_hint:
      "Famille · ligne / bilan productivité / catalogue",
    xls_product_mo_gap_label: "Écart MO réel − théorique",
    xls_product_mo_retained: "Coût MO retenu",
    xls_product_mo_retained_real: "= Réel (source la plus fiable disponible)",
    xls_product_mo_retained_theo:
      "= Théorique (aucune source réelle disponible)",
    xls_product_mo_from_above: "Valeur reprise de la section MO ci-dessus",
    xls_product_total_hint: "Matière retenue + MO retenue",
    xls_product_grp_cascade:
      "Cascade — composition détaillée de chaque semi-fini utilisé dans la recette",
    xls_product_cascade_intro:
      "Chaque semi-fini de la recette est développé ici avec sa propre nomenclature et son coût matière unitaire. Multiplier ce coût matière par la quantité utilisée dans le produit parent donne la contribution au coût matière du produit fini. Les lignes surlignées dans les tableaux renvoient ici par hyperlien.",
    xls_product_sf_batch:
      " — normalisé sur 1 unité produite (taille de lot : #n, issue de la spécification)",
    xls_product_sf_group: "Semi-fini : #name#code#info",
    xls_product_total_mat_sf: "Total matière — #name",
    xls_product_sf_no_bom:
      "Nomenclature non détaillable — coût repris depuis la fiche article",
    // ─── Vague 3 — Export Excel produit : marge réalisée ───
    xls_product_sheet_realized: "Marge réalisée",
    xls_product_title_realized: "Marge réalisée sur #period — #name · #client",
    xls_product_realized_banner_mo:
      "Marge réalisée sur les factures historiques — coûts du snapshot Cost (matière + MO profondes). La synthèse expose matière et MO séparément pour audit.",
    xls_product_realized_banner_nomo:
      "Marge réalisée sur les factures historiques — coûts du snapshot Cost (matière + MO confondues, telles qu'enregistrées au moment de l'expédition). Le scénario actif est en mode hors MO mais cette feuille reste sur la donnée comptable réelle ; la décomposition matière/MO est exposée dans la synthèse pour lecture audit.",
    xls_product_period_generic: "la période",
    xls_product_period_one_month: "1 mois",
    xls_product_period_months: "#n mois",
    xls_product_rm_src: "Source des ventes",
    xls_product_rm_src_desc:
      "Factures réelles des #period pour ce couple article × client. Exclut les brouillons (EN_REDACTION) et les annulées (ANNULEE).",
    xls_product_rm_price: "Prix retenu par unité",
    xls_product_rm_price_desc:
      "Prix HT réellement encaissé, net de toutes les remises : remise ligne (déjà appliquée sur le PU) ET remise pied de facture (répartie au prorata des lignes concernées par la commande cible).",
    xls_product_rm_cost: "Coût retenu par unité",
    xls_product_rm_cost_desc:
      "Coût du lot (matière profonde + MO profonde) issu de la table Cost, à la date la plus récente antérieure à l'expédition. Snapshot historique cohérent avec la réalité comptable.",
    xls_product_rm_units: "Unités utilisées",
    xls_product_rm_units_desc:
      "Le prix est en €/#uv (unité de vente, ce qui est facturé). Le coût lot est stocké en €/#ug (unité de production). La marge totale en € est homogène ; les ratios unitaires sont annotés explicitement.",
    xls_product_rm_nolot: "Lignes sans lot",
    xls_product_rm_nolot_desc:
      "Prestations pures (frais facturés sans article stocké) : coût considéré à 0 € → marge = 100 % du prix.",
    xls_product_rm_avoirs: "Avoirs (credit notes)",
    xls_product_rm_avoirs_desc:
      "Traités en négatif : ils réduisent la quantité vendue et la marge dégagée. Un avoir annule partiellement ou totalement la facture d'origine.",
    xls_product_rm_formula: "Formule",
    xls_product_rm_formula_desc:
      "Marge totale = CA net − Coût total · Marge % = Marge totale ÷ CA net × 100. Les moyennes sont pondérées par les quantités.",
    xls_product_col_param: "Paramètre",
    xls_product_grp_rm_synth:
      "Synthèse sur la période — moyenne pondérée par les quantités",
    xls_product_scope_mat_mo: "matière + MO",
    xls_product_scope_mat_only: "matière seule (MO exclue)",
    xls_product_rm_qty: "Quantité nette vendue",
    xls_product_rm_qty_desc: "Expéditions − avoirs sur #n mois distinct.",
    xls_product_rm_qty_desc_plural:
      "Expéditions − avoirs sur #n mois distincts.",
    xls_product_rm_ca_brut: "Chiffre d'affaires brut (avant remise pied)",
    xls_product_rm_ca_brut_desc: "Prix ligne (net de remise ligne) × quantité.",
    xls_product_rm_remise: "Remise pied moyenne appliquée",
    xls_product_rm_remise_desc:
      "Part du CA brut perdue via la remise pied de facture.",
    xls_product_rm_ca_net: "Chiffre d'affaires net encaissé",
    xls_product_rm_ca_net_desc:
      "Après remise pied — c'est le montant facturé réellement.",
    xls_product_rm_cout_mat: "Coût matière (profond)",
    xls_product_rm_cout_mat_desc:
      "Σ coût matière unitaire (€/#ug) × quantité produite. Coûts profonds prioritaires sur unitaires en fallback.",
    xls_product_rm_cout_mo: "Coût main-d'œuvre (profond)",
    xls_product_rm_cout_mo_desc:
      "Σ coût MO unitaire (€/#ug) × quantité produite. 0 € si MO non historisée.",
    xls_product_rm_cout_retenu: "Coût retenu (#scope)",
    xls_product_rm_cout_retenu_desc_mo:
      "Coût matière + Coût MO. Aligné sur le scénario en mode MO incluse.",
    xls_product_rm_cout_retenu_desc_nomo:
      "Uniquement la part matière. Aligné sur le scénario en mode MO exclue (la MO est exposée à titre informatif au-dessus mais n'est pas comptée).",
    xls_product_rm_marge_totale: "Marge totale dégagée (#scope)",
    xls_product_rm_marge_totale_desc: "CA net − Coût retenu.",
    xls_product_rm_prix_moyen: "Prix moyen encaissé par #uv",
    xls_product_rm_prix_moyen_desc: "CA net ÷ quantité nette vendue.",
    xls_product_rm_cout_moyen_uv: "Coût moyen retenu par #uv",
    xls_product_rm_cout_moyen_uv_desc:
      "Coût retenu (#scope) ÷ quantité nette vendue. Homogène avec le prix moyen.",
    xls_product_rm_cout_moyen_ug: "Coût moyen retenu par #ug (production)",
    xls_product_rm_cout_moyen_ug_desc:
      "Coût retenu (#scope) ÷ quantité produite. Cohérent avec les coûts Cost stockés en €/#ug.",
    xls_product_rm_dont_mat: "dont matière par #ug",
    xls_product_rm_dont_mat_desc: "Part matière du coût moyen unitaire.",
    xls_product_rm_dont_mo: "dont MO par #ug",
    xls_product_rm_dont_mo_desc: "Part main-d'œuvre du coût moyen unitaire.",
    xls_product_rm_dont_mo_desc_excl:
      "Part main-d'œuvre du coût moyen unitaire — non comptée dans la marge retenue (MO exclue).",
    xls_product_rm_marge_moyenne: "Marge moyenne par #uv (#scope)",
    xls_product_rm_marge_moyenne_desc: "Marge retenue ÷ quantité nette vendue.",
    xls_product_rm_taux: "Taux de marge moyen (#scope)",
    xls_product_rm_taux_desc: "Marge retenue ÷ CA net × 100.",
    xls_product_rm_coef: "Coefficient multiplicateur (#scope)",
    xls_product_rm_coef_desc:
      "CA net ÷ Coût retenu. Ratio sans unité, indépendant des conversions.",
    xls_product_rm_note_nolot:
      "#count ligne(s) sans lot (prestations pures — coût 0 €)",
    xls_product_rm_note_nocost:
      "#count ligne(s) avec lot mais sans Cost historique exploitable (coût 0 €)",
    xls_product_grp_monthly: "Détail mois par mois — coût #scope",
    xls_product_col_qty_uv: "Quantité (#uv)",
    xls_product_col_ca_brut: "CA brut",
    xls_product_col_ca_net: "CA net",
    xls_product_col_cost_scope: "Coût (#scope)",
    xls_product_col_margin_scope: "Marge € (#scope)",
    xls_product_col_margin_pct: "Marge %",
    xls_product_col_lines: "Lignes",
    xls_product_grp_lines: "Détail ligne par ligne — #count ligne de facture",
    xls_product_grp_lines_plural:
      "Détail ligne par ligne — #count lignes de factures",
    xls_product_lines_intro:
      "Toutes les lignes de factures retenues sur la période, du plus récent au plus ancien. Les avoirs apparaissent en rouge avec quantités et marges signées (négatives).",
    xls_product_col_invoice: "Facture",
    xls_product_col_qty_uv_short: "Qté (#uv)",
    xls_product_col_net_price: "Prix net /#uv",
    xls_product_col_cost_ug: "Coût /#ug (#scope)",
    xls_product_col_cost_total: "Coût total (#scope)",
    xls_product_avoir_suffix: " (avoir)",
    xls_product_lot_suffix: " · lot #n",
    xls_product_draft_label: "(brouillon)",
    // ─── Vague 3 — Courrier tarifaire PDF ───
    courrier_meta_title: "Courrier tarifaire — #name",
    courrier_meta_subject:
      "Notification d'évolution tarifaire à #name (application #date)",
    courrier_meta_keywords:
      "JustUp, justup.io, tarif, courrier, évolution prix",
    courrier_salutation: "Madame, Monsieur,",
    courrier_para_thanks:
      "Nous tenons avant toute chose à vous remercier pour la confiance que vous nous accordez et pour la qualité de notre collaboration.",
    courrier_para_commitment:
      "Conscients que la pérennité de notre partenariat repose autant sur la constance de nos services que sur la justesse de nos tarifs, nous nous efforçons en permanence de vous proposer les conditions les plus compétitives et les plus optimisées possibles. Nos équipes travaillent au quotidien à maîtriser nos coûts d'approvisionnement, de production et de logistique afin de préserver le meilleur équilibre entre qualité et prix. Lorsque les conditions s'améliorent, nous veillons également à vous en faire bénéficier en ajustant nos tarifs à la baisse.",
    courrier_evolution_up:
      "Les évolutions récentes de nos intrants nous conduisent aujourd'hui à revoir nos tarifs à la hausse sur un certain nombre de vos références, malgré tous nos efforts pour les contenir. Fidèles à notre engagement à vos côtés, nous avons tenu à absorber la plus grande part possible de cette hausse afin d'en limiter la répercussion sur vos conditions. Cette mise à jour, qui prendra effet à compter du #date, porte sur #count de vos produits et résulte d'une analyse rigoureuse visant à maintenir la qualité de notre offre tout en préservant l'équilibre de notre relation commerciale.",
    courrier_evolution_down:
      "Les conditions récemment observées sur nos intrants nous permettent aujourd'hui de vous proposer une actualisation à la baisse de nos tarifs, que nous avons tenu à vous faire bénéficier sans attendre. Cette mise à jour prendra effet à compter du #date et porte sur #count de vos produits.",
    courrier_evolution_mixed:
      "Les évolutions observées sur nos intrants nous conduisent aujourd'hui à actualiser nos tarifs sur #count de vos références — à la hausse pour certaines, à la baisse pour d'autres lorsque cela est possible. Sur les hausses, nous avons tenu à absorber une part significative afin d'en atténuer au maximum l'impact pour vous. Cette mise à jour, qui prendra effet à compter du #date, résulte d'une analyse rigoureuse visant à maintenir la qualité de notre offre tout en préservant l'équilibre de notre relation commerciale.",
    courrier_para_periode:
      "Cette actualisation s'applique du #debut au #fin inclus. Au-delà de cette période, notre tarif précédent s'appliquera de nouveau, sans démarche de votre part.",
    courrier_para_details:
      "Vous trouverez ci-dessous le détail exhaustif des nouveaux tarifs applicables à cette date. Soyez assurés que nous continuerons, comme nous l'avons toujours fait, à tout mettre en œuvre pour vous accompagner avec exigence, transparence et bienveillance.",
    courrier_closing_1:
      "Nous demeurons naturellement à votre entière disposition pour échanger sur cette évolution, étudier ensemble vos contraintes et répondre à toutes vos interrogations.",
    courrier_closing_2:
      "Vous remerciant chaleureusement pour votre compréhension et pour la confiance renouvelée que vous nous témoignez, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations les plus cordiales.",
    courrier_object_label: "Objet :",
    mail_body_periode_ouverte:
      "à compter du #date",
    mail_body_periode_bornee:
      "du #debut au #fin inclus",
    mail_body_periode_note:
      "Au-delà de cette période, notre tarif précédent s'appliquera de nouveau.",
    courrier_object_periode:
      "Évolution tarifaire applicable du #debut au #fin inclus",
    courrier_application_periode:
      "Application du #debut au #fin",
    courrier_meta_apply_until:
      "Fin d'application",
    courrier_conditions_apply_periode:
      "Nouveaux tarifs applicables du #debut au #fin inclus. Au-delà, le tarif précédent s'applique de nouveau.",
    applyuntil_clear:
      "Effacer",
    applyuntil_clear_tooltip:
      "Retirer la date de fin — le changement redevient permanent",
    applyuntil_avant_debut:
      "La fin d'application ne peut pas précéder le début (#debut).",
    applydate_requise: "Choisissez une date d'application.",
    applyuntil_label:
      "Application jusqu'au",
    applyuntil_hint:
      "Facultatif — laisser vide pour un changement permanent",
    col_ddm_mini:
      "DDM mini",
    tooltip_ddm_mini:
      "DDM/DLC mini contractuelle en jours, à respecter à la livraison. Laisser vide pour conserver celle du tarif remplacé.",
    ddm_mini_placeholder:
      "repris",
    col_qty_min:
      "Qté min.",
    tooltip_qty_min:
      "Palier de quantité (unité de gestion) sur lequel écrire ce tarif. 0 = pas de quantité mini.",
    acte_tarif_avis_periode:
      "À l'envoi — ou en enregistrant seulement — le nouveau tarif s'appliquera du #debut au #fin (inclus) pour ce client. Au-delà, le tarif précédent reprend automatiquement : il n'est donc pas clos. Les articles sans tarif propre au client, ou soumis à une remise, ne sont pas modifiés.",
    courrier_object: "Évolution tarifaire applicable à compter du #date",
    courrier_doc_title: "COURRIER TARIFAIRE",
    courrier_issued: "Émis le #date",
    courrier_application: "Application au #date",
    courrier_attention: "À L'ATTENTION DE",
    courrier_siret: "SIRET : #v",
    courrier_tva: "N° TVA : #v",
    courrier_meta_apply_date: "Date d'application",
    courrier_meta_nb_products: "Nombre de produits",
    courrier_meta_tva: "TVA émetteur",
    courrier_meta_ref: "Référence interne",
    courrier_col_product: "PRODUIT",
    courrier_col_current: "TARIF ACTUEL",
    courrier_col_new: "NOUVEAU TARIF",
    courrier_col_evolution: "ÉVOLUTION",
    courrier_footer_legal: "INFORMATIONS LÉGALES",
    courrier_capital: "Capital #v",
    courrier_rcs: "RCS #city #num",
    courrier_generated_by: "Document généré par justup.io",
    courrier_footer_conditions: "CONDITIONS",
    courrier_conditions_apply:
      "Nouveaux tarifs applicables à compter du #date.",
    courrier_conditions_informative: "Document à valeur informative.",
    courrier_footer_contact: "CONTACT",
    courrier_page: "Page #current / #total",
    // ─── Vague 3 — Corps du mail tarifaire par défaut ───
    mail_body_subject: "ÉVOLUTION TARIFAIRE — {periode}",
    mail_body_greeting: "Bonjour,",
    mail_body_intro:
      "Vous trouverez en pièce jointe un courrier officiel vous présentant une évolution de nos tarifs applicable <strong>{periode}</strong>{periode_note}, concernant <strong>{nb_produits}</strong> de vos références.",
    mail_body_partnership:
      "Nous sommes très attachés à la qualité du partenariat qui nous unit et vous invitons à prendre connaissance du détail présenté dans ce courrier.",
    mail_body_availability:
      "Nous restons naturellement à votre entière disposition pour en échanger et répondre à toutes vos interrogations.",
    mail_body_thanks: "Vous remerciant par avance pour votre confiance,",
    acte_sans_envoi:
      "Acter sans envoyer",
    acte_sans_envoi_tooltip:
      "Enregistrer le nouveau tarif dans les grilles, sans envoyer de mail",
    acte_tarif_avis:
      "À l'envoi — ou en actant sans envoyer — le nouveau tarif s'appliquera à partir du #debut pour ce client, et l'ancien sera clos au #fin (inclus). Les articles sans tarif propre au client, ou soumis à une remise, ne sont pas modifiés : le détail est affiché ensuite.",
    btn_maj_tarifs:
      "Mettre à jour les tarifs",
    btn_save_tarifs:
      "Enregistrer les nouveaux tarifs",
    btn_send_and_save:
      "Envoyer et enregistrer",
    ignore_af_absent:
      "pas de tarif propre au client",
    ignore_echec:
      "échec",
    ignore_prix_invalide:
      "prix invalide",
    ignore_remises:
      "remise applicable",
    notifier_hint:
      "Les tarifs sont enregistrés dans les deux cas. Le mail n'est qu'un moyen de les communiquer.",
    notifier_label:
      "Notifier le client par mail ?",
    notifier_non:
      "Non, enregistrer seulement",
    notifier_oui:
      "Oui, envoyer un mail",
    snack_mail_not_sent:
      "Mail NON envoyé à #name — #reason",
    snack_no_impact:
      "#name n'a aucun produit impacté — rien à acter.",
    snack_tarif_applied:
      "Tarif acté pour #name (sans envoi de mail).",
    snack_tarifs_actes:
      "#n tarif(s) écrits, applicables au #date.",
    snack_tarifs_ignores:
      "#n article(s) non modifié(s) : #causes.",
    tooltip_maj_tarifs:
      "Régler la date d'application et les prix, puis enregistrer — avec ou sans envoi de mail",
    btn_send: "Envoyer",
    snack_batch_done:
      "Procédure tarifaire terminée — #sent mail(s) envoyé(s), #skipped client(s) passé(s).",
    tooltip_send_all:
      "Lancer la procédure d'envoi du nouveau tarif à tous les clients impactés (un dialog par client)",
  },
  excel_branding: {
    mo_scope:
      "Périmètre de calcul : coûts matière uniquement — la main-d'œuvre n'est pas intégrée à ce scénario. Tous les coûts, taux et marges affichés sont donc hors MO.",
    unit_scope_col:
      "Les coûts, marges et prix sont exprimés en €/unité de gestion (UG, ex: kilo, unité produite). Pour obtenir le prix en €/unité de vente (UV, carton…) : multiplier par le coefficient de la colonne #col (= #col). Quand le coefficient vaut 1, UG ≡ UV.",
    unit_scope:
      "Les coûts, marges et prix sont exprimés en €/unité de gestion (UG, ex: kilo, unité produite). Pour obtenir le prix en €/unité de vente (UV, carton…) : multiplier par le coefficient UG/UV de l'article. Quand le coefficient vaut 1, UG ≡ UV.",
  },
  relance: {
    dialog_title: "Relancer — facture",
    btn_send: "Envoyer",
    btn_record: "Enregistrer la relance",
    btn_cancel: "Annuler",
    btn_next: "Suivant",
    btn_preview: "Aperçu courrier",
    btn_relancer: "Relancer",
    mail_dialog_title: "Rédiger la relance —",
    step1_title: "Choisir le modèle",
    step2_title: "Canal d'envoi",
    field_canal: "Canal d'envoi",
    field_destinataires: "Destinataires",
    field_cc: "Copie (CC)",
    field_objet: "Objet",
    field_corps: "Message",
    edit_target_mail: "E-mail",
    edit_target_courrier: "Courrier",
    edit_target_hint:
      "Le texte de l'e-mail et du courrier peuvent être personnalisés indépendamment. Utilisez le sélecteur ci-dessus pour basculer.",
    field_commentaire: "Note interne (non envoyée au client)",
    canal_mail: "Mail",
    canal_courrier: "Courrier papier",
    canal_both: "Mail + Courrier",
    niveau_rappel: "Rappel cordial",
    niveau_rappel_desc: "Envoyé 1 semaine avant l'échéance — ton très courtois",
    niveau_1: "1re relance",
    niveau_1_desc: "J+7 après échéance — courtois mais ferme",
    niveau_2: "2e relance",
    niveau_2_desc: "J+21 — ferme, demande un engagement",
    niveau_3: "3e relance",
    niveau_3_desc: "J+45 — pressant, annonce procédure",
    niveau_demeure: "Mise en demeure",
    niveau_demeure_desc: "J+60 — formel juridique, mentions légales",
    delai_label: "J",
    suggested_badge: "Suggéré",
    history_title: "Historique des relances",
    no_history: "Aucune relance envoyée",
    sent_success: "Relance envoyée",
    destinataires_required:
      "Au moins un destinataire est requis pour l'envoi par mail",
    courrier_preview_tooltip: "Ouvrir l'aperçu du courrier PDF",
    courrier_only_hint:
      "Le courrier PDF sera généré et téléchargeable. La relance sera enregistrée dans l'historique pour assurer le suivi.",
    warn_not_due:
      "Cette facture n'est pas encore échue (échéance dans #days jour(s)). Le niveau de relance sélectionné est normalement destiné aux factures en retard de paiement.",
    warn_already_overdue:
      "Cette facture est en retard de #days jour(s). Un simple rappel pourrait ne pas suffire — un niveau supérieur serait peut-être plus adapté.",
  },
  non_conformite: {
    save_reception_first: "Enregistrez d'abord la réception pour pouvoir y rattacher une non-conformité.",
    cause_racine: "Cause racine",
    cause_racine_optional: "Cause racine (optionnel)",
    cause_racine_ph: "Analyse : pourquoi le défaut s'est-il produit ?",
    actions: {
      title: "Actions correctives & préventives",
      ajouter: "Ajouter une action",
      description: "Action",
      echeance: "Échéance",
      responsable: "Responsable",
      empty: "Aucune action — la boucle HACCP attend une action corrective.",
      demarrer: "Démarrer",
      terminer: "Terminée",
      verifier: "Vérifier l'efficacité",
      verification_ph: "Constat d'efficacité (obligatoire pour clore la boucle)",
      kind: { CORRECTIVE: "Corrective", PREVENTIVE: "Préventive" },
      etat: { A_FAIRE: "À faire", EN_COURS: "En cours", FAITE: "Faite", VERIFIEE: "Vérifiée" },
    },
    /*
     * 🔴 **Ce message sortait EN BRUT** : la clé n'existait pas. Qui cliquait « Déclarer une
     * non-conformité » sur une réception non enregistrée lisait littéralement
     * « non_conformite.save_reception_first » dans un bandeau d'avertissement.
     *
     * ⚠️ Le libellé dit le GESTE, pas la contrainte technique : la vraie raison — une NC doit pointer
     * un `receptionId`, sinon le lien arrière est perdu — n'apprend rien à qui doit juste enregistrer.
     */
    title: "Non-conformités",
    title_singular: "Non-conformité",
    list_title: "Liste des non-conformités",
    new: "Nouvelle non-conformité",
    declarer: "Déclarer une NC",
    declarer_short: "Déclarer",
    builder_create: "Déclarer une non-conformité",
    builder_edit: "Modifier une non-conformité",
    type: "Type",
    gravite: "Gravité",
    cause_5m: "Cause (5M)",
    cause_5m_optional: "Cause (5M) — optionnel",
    description: "Description du défaut observé",
    description_placeholder:
      "Décrivez précisément l'écart constaté (état, quantité, mesure, photos déjà jointes...).",
    cout_estime: "Coût supplémentaire (€)",
    cout_estime_hint:
      "Surcoûts induits par la NC en plus de la valeur du stock : gestion, retraitement, pertes indirectes… S'ajoute au coût matière.",
    cout_nc_total: "Coût total NC",
    scope_label: "Portée",
    scope_tout: "Tout le lot",
    scope_partiel: "Quantité précise",
    scope_quantite: "Quantité concernée",
    heures_retouche: "Heures de retraitement",
    heures_retouche_hint:
      "Temps total passé à retraiter la NC. Multiplié par le taux horaire moyen de l'OF pour estimer le coût personnel imputé à la NC.",
    aide_move_zone_nc:
      "Si coché et qu'un lot/palette est lié à la NC, le stock concerné est immédiatement déplacé en zone NC PDC dédiée à cet OF (ou en zone NC entrepôt si pas en PDC). À la clôture, le mouvement final dépend de la décision : rebut → destruction, retour fournisseur → mouvement de retour, retraitement/dérogation → retour vers le PDC. À la réintégration finale de l'OF, tout résidu en zone NC PDC est apuré vers la zone NC entrepôt globale.",
    aide_scope:
      "Indique si la NC concerne tout le lot/palette lié, ou seulement une quantité précise. La quantité partielle est utilisée pour le mouvement de stock et les agrégats coût NC.",
    aide_nature:
      "Catégorie macro de la non-conformité, complémentaire de la description libre. Sert à grouper et filtrer les NC dans la liste et les statistiques.<br><br>Exemples : <em>Casse, Manquant, Salissure, Hors-spec, DLUO trop courte, Étiquette manquante, Mauvaise référence, Conditionnement abîmé</em>.<br><br>La liste est personnalisable depuis les paramètres custom enums, ou directement via le bouton + du sélecteur.",
    nature_hint: "Ex. Casse, Manquant, Salissure, Hors-spec, DLUO trop courte…",
    aide_decision:
      "Décision finale qui détermine le mouvement de stock à la clôture. Pour un PF (produit fini de l'OF), la décision RETOUR_FOURNISSEUR n'a pas de sens et est masquée.",
    aide_heures_retouche:
      "Saisir le temps réel passé à retraiter la non-conformité. Utilisé pour estimer le coût MO induit. Le taux horaire moyen est dérivé du `coutPersonnelTotalReel / heuresProfondesTotalReel` de l'OF.",
    aide_pf_quantite_nc:
      "Si une ou plusieurs NC ont déjà été déclarées sur le PF de cet OF, leur quantité est rappelée ici. La quantité saisie correspond uniquement aux pièces conformes — la quantité fabriquée totale = conforme + NC est calculée automatiquement.",
    aide_reception_nc_button:
      "Cliquez pour déclarer une non-conformité sur cette ligne de réception. La NC est liée à la ligne via <code>receptionCommandeId</code> et le mouvement de stock part en zone NC de l'entrepôt. Quantité totale reçue = saisie conforme + somme des NC déclarées.",
    reception_nc_will_prompt:
      "Une non-conformité sera demandée pour cette ligne à la validation de la réception.",
    reception_nc_required_message:
      "Validation annulée — chaque ligne avec une quantité NC doit être documentée par une non-conformité.",
    controle_ko_title: "Contrôle qualité échoué",
    controle_ko_message:
      "Un ou plusieurs contrôles qualité ont échoué sur cette réception. Souhaitez-vous déclarer une non-conformité (recommandé) ?",
    abandon_provision_action: "Abandonner la provision",
    abandon_provision_title: "Abandonner la provision avoir ?",
    abandon_provision_warning:
      "Le fournisseur n'émettra pas l'avoir attendu. La provision sera basculée en charge définitive : coutNcRecuperable est décrémenté et le coût NC reste imputé. Action idempotente (peut être posée une seule fois).",
    abandon_provision_success:
      "Provision abandonnée — charge définitive imputée",
    provision_abandoned_label: "Provision abandonnée le",
    decision: "Décision",
    decision_optional: "Décision — optionnel",
    move_to_zone_nc: "Déplacer le lot en zone non conforme après validation",
    move_to_zone_nc_hint:
      "Le lot reste comptabilisé au stock total mais n'est plus disponible pour la production ou la préparation d'expédition.",
    move_zone_nc_confirm_title: "Déplacer en zone non conforme ?",
    move_zone_nc_confirm_message:
      "Cette non-conformité est mineure. Voulez-vous déplacer le stock concerné en zone non conforme ? Il restera comptabilisé au stock total mais ne sera plus disponible pour la production ou l'expédition.",
    move_zone_nc_auto_info:
      "Non-conformité majeure ou critique : le stock concerné va être déplacé en zone non conforme.",
    source_stock_label: "Emplacement source",
    source_stock_hint:
      "Ce lot a du stock à plusieurs endroits — choisissez d'où prélever les unités non conformes.",
    cout_unitaire_matiere_label: "Coût matière unitaire (€)",
    cout_unitaire_matiere_hint:
      "Coût pris en compte pour valoriser la non-conformité (pré-rempli depuis le lot, ajustable).",
    cout_unitaire_matiere_close_hint:
      "Coût matière unitaire appliqué au rebut. Ajustez-le si besoin — il recalcule le coût NC.",
    cout_nc_matiere_total: "Coût matière NC total",
    cout_nc_mo_total: "Coût main-d'œuvre NC total",
    go_to_lot: "Ouvrir la fiche du lot",
    whole_lot_warning:
      "Tout le lot : #count emplacement(s) seront mis en zone non conforme (total #total).",
    type_RECEPTION: "Réception",
    type_PRODUCTION: "Production",
    type_EXPEDITION: "Expédition",
    type_RETOUR_CLIENT: "Retour client",
    type_RECLAMATION_CLIENT: "Réclamation client",
    type_AUDIT_INTERNE: "Audit interne",
    origine_label: "Origine — qui est responsable ?",
    origine_hint: "Pré-rempli selon le type, modifiable",
    origine_INTERNE: "Interne",
    origine_EXTERNE: "Externe",
    origine_MIXTE: "Mixte",
    origine_non_renseigne: "Non renseignée",
    origine_repartition_title: "Origine ce mois",
    detection_label: "Détection — où / par qui détectée ?",
    detection_hint:
      "Détection externe = échappée du filet qualité (indicateur ISO 9001)",
    detection_INTERNE: "Interne",
    detection_EXTERNE: "Externe (client / autorité)",
    date_occurrence_label: "Date d'occurrence",
    date_occurrence_hint:
      "Quand le défaut s'est réellement produit (production, livraison...). Optionnel.",
    date_constatation_label: "Date de constatation",
    date_constatation_hint:
      "Quand le défaut a été observé pour la première fois (contrôle, plainte). Optionnel.",
    aide_dates_metier:
      "Trois dates différentes :<br/>• <b>Occurrence</b> : quand le défaut s'est produit<br/>• <b>Constatation</b> : quand vous l'avez observé<br/>• <b>Enregistrement</b> : quand vous le saisissez (auto)<br/><br/>Les écarts entre ces dates servent à mesurer la réactivité qualité (ISO 9001).",
    delai_detection_kpi: "Délai détection moyen",
    delai_enregistrement_kpi: "Délai enregistrement moyen",
    echappee: "Échappée",
    echappee_tooltip:
      "Cette NC a été détectée par un tiers externe — échappée du système qualité",
    taux_echappees: "Taux d'échappées",
    taux_echappees_tooltip:
      "% NC détectées en externe sur le mois (ISO 9001). Cible : < 5%.",
    gravite_MINEURE: "Mineure",
    gravite_MAJEURE: "Majeure",
    gravite_CRITIQUE: "Critique",
    cause_MATIERE: "Matière",
    cause_MACHINE: "Machine",
    cause_METHODE: "Méthode",
    cause_MAIN_OEUVRE: "Main-d'œuvre",
    cause_MILIEU: "Milieu",
    cause_MESURE: "Mesure",
    etat_OUVERTE: "Ouverte",
    etat_EN_TRAITEMENT: "En traitement",
    etat_CLOTUREE: "Clôturée",
    etat_ANNULEE: "Annulée",
    decision_ACCEPTATION_DEROGATION: "Acceptation par dérogation",
    decision_RETOUCHE: "Retraitement",
    decision_REBUT: "Rebut",
    decision_RETOUR_FOURNISSEUR: "Retour fournisseur",
    close_title: "Clôturer la non-conformité",
    close_decision: "Décision finale",
    close_comment: "Commentaire de clôture (optionnel)",
    close_action: "Clôturer",
    cancel_title: "Annuler cette non-conformité",
    cancel_comment: "Raison de l'annulation",
    cancel_action: "Annuler la NC",
    filter_search_placeholder: "Rechercher (n°, description, lot, fournisseur)",
    filter_etat: "État",
    filter_type: "Type",
    filter_gravite: "Gravité",
    filter_nature: "Nature",
    filter_origine: "Origine",
    filter_detection: "Détection",
    filter_period: "Période",
    no_data: "Aucune non-conformité",
    saved_success: "Non-conformité créée",
    updated_success: "Non-conformité mise à jour",
    closed_success: "Non-conformité clôturée",
    cancelled_success: "Non-conformité annulée",
    link_required:
      "Au moins un lien est requis (lot, palette, commande, chargement ou tiers).",
    description_required: "Une description est requise.",
    link_label:
      "Lien (article, lot, palette, commande, chargement, fournisseur)",
    required_missing: "Champs obligatoires manquants :",
    related_nc: "NC liées",
    related_count: "#count NC",
    stats_open: "NC ouvertes",
    stats_critical_open: "NC critiques ouvertes",
    stats_count_month: "NC ce mois",
    stats_cost_month: "Coût NC ce mois",
    aside_kpi_title: "Indicateurs",
    aside_warehouse_title: "Stock NC par entrepôt",
    aside_warehouse_count: "entrepôts",
    aside_warehouse_empty: "Aucun stock non conforme actif.",
    aside_warehouse_empty_title: "Aucun stock non conforme",
    aside_warehouse_empty_sub: "Tout est en zone conforme.",
    aside_warehouse_no_stock_title: "Aucun stock NC actif actuellement",
    aside_warehouse_no_stock_sub:
      "Les NC ouvertes ne sont pas rattachées à un stock courant (lot consommé, NC sur commande/article sans lot…).",
    aside_warehouse_nc: "NC",
    aside_warehouse_critical: "critiques",
    aside_warehouse_emplacements: "emplacements",
    aside_hero_label: "NC ouvertes",
    aside_hero_ok: "Aucune NC ouverte — bon état !",
    aside_hero_critical: "#count critique(s) à traiter",
    aside_hero_no_critical: "Aucune NC critique en cours",
    aside_short_month: "ce mois",
    aside_short_cost: "coût mois",
    aside_short_cost_short: "coût",
    aside_short_escapees: "taux échap.",
    aside_period_label: "Période",
    aside_count_period_tooltip: "Nombre de NC créées #period",
    aside_cost_period_tooltip: "Coût total des NC créées #period",
    aside_origine_title: "Origine — #period",
    aside_title: "Non-conformités",
    aside_count_singular: "NC liée",
    aside_count_plural: "NC liées",
    aside_loading: "Chargement…",
    aside_empty: "Aucune non-conformité liée.",
    aside_kpi_open: "Ouvertes",
    aside_kpi_critical: "Critiques",
    aside_kpi_cost: "Coût estimé",
    aside_no_numero: "(brouillon)",
    aside_expand: "Voir #count autres",
    aside_collapse: "Masquer",
    aside_see_all: "Voir tout",
    badge_open: "Ouverte",
    badge_in_progress: "En traitement",
    badge_closed: "Clôturée",
    badge_cancelled: "Annulée",
    no_lien_suggested: "Aucun élément lié",
    save: "Enregistrer",
    cancel: "Annuler",
    section_express: "Déclaration",
    section_detail: "En détail",
    section_detail_hint: "Gravité, cause, coût (optionnel)",
    section_operational: "Et maintenant ?",
    section_operational_hint: "Actions à appliquer après création",
    section_comments_attachments: "Commentaires & pièces jointes",
    section_comments_attachments_hint:
      "Discussion qualité, photos, rapports d'audit, mails fournisseur…",
    print_fiche_nc: "Imprimer fiche NC",
    download_rapport_nc: "Télécharger rapport PDF",
    download_rapport_nc_tooltip:
      "Rapport complet (description, photos, dates, décision, historique) — pour le dossier qualité, un audit ou une réclamation fournisseur",
    linked_to: "À quoi est-ce lié ?",
    linked_from_context:
      "Contexte pré-rempli depuis l'écran d'origine. Ajoutez d'autres éléments si nécessaire.",
    search_placeholder:
      "Tapez le n° du lot, palette, commande, chargement ou un nom de fournisseur (2 lettres min.)",
    search_hint:
      "Cliquez un résultat dans la liste pour l'attacher à la NC. Le texte tapé seul n'est pas enregistré.",
    search_no_results:
      "Aucun élément correspondant — vérifiez le numéro saisi.",
    actions_phase2_hint:
      "Blocage du lot, sortie en rebut et retour fournisseur arrivent en Phase 2.",
    lot: "Lot",
    other_link: "Autre lien",
    other_link_hint: "Palette, commande, chargement, fournisseur (optionnel)",
    link_optional_audit:
      "Pour un audit interne, le lien est facultatif. Vous pouvez juste décrire l'écart observé.",
    lot_pick_article_first: "Sélectionnez un article pour choisir un lot",
    detail_title: "Non-conformité",
    detail_history: "Historique",
    detail_links: "Éléments liés",
    detail_no_links: "Aucun élément lié",
    detail_section_what: "Caractéristiques",
    detail_section_when: "Chronologie",
    detail_creator: "Créée par",
    detail_closed_by: "Clôturée par",
    detail_close_action: "Clôturer la NC",
    detail_cancel_action: "Annuler la NC",
    detail_edit_action: "Modifier",
    detail_edit_restricted_action: "Modifier (limité)",
    detail_edit_restricted_tooltip:
      "Édition limitée : description, photos, coût, cause… La décision et les liens lot/palette/article restent verrouillés. Pour les modifier, réouvrez la NC.",
    detail_reopen_action: "Réouvrir",
    reopen_title: "Réouvrir cette non-conformité ?",
    reopen_warning:
      "La NC repassera en EN_TRAITEMENT — vous pourrez modifier la décision et les liens. ⚠️ Si un mouvement de stock a été émis à la clôture (libération zone NC, destruction ou retour fournisseur), il restera tel quel : la réouverture ne le défait pas. Vous devrez créer un transfert manuel pour aligner la position physique du stock après modification.",
    reopen_success:
      "NC réouverte — vous pouvez maintenant modifier la décision",
    reopen_success_with_movement:
      "NC réouverte. Un mouvement de stock avait été émis à la clôture et n'a PAS été annulé — pensez à aligner manuellement la position physique si vous changez la décision. En cas de retour fournisseur, la marchandise a quitté le site : le stock affiché ne la compte plus, et la réouverture ne la fait pas revenir.",
    restricted_banner_title: "Édition limitée — NC clôturée",
    restricted_banner_desc:
      "Vous pouvez corriger description, photos, coût, cause, dates. Les champs verrouillés (gravité, type, décision, liens) nécessitent de Réouvrir la NC depuis la fiche.",
    confirm_close_title: "Clôturer cette non-conformité",
    confirm_close_decision: "Quelle décision finale ?",
    confirm_close_comment: "Commentaire (optionnel)",
    confirm_close_cout: "Coût supplémentaire (€)",
    confirm_cancel_title: "Annuler cette non-conformité",
    confirm_cancel_warning:
      "L'annulation est utilisée quand la NC a été créée par erreur. Elle reste tracée dans l'historique.",
    confirm_cancel_comment: "Raison de l'annulation",
    cant_close_already_closed: "NC déjà clôturée ou annulée",
    nature: "Nature",
    detail_close_dialog: "Fermer",
    analytics_title: "Analyse des non-conformités",
    analytics_btn: "Analyse",
  },
  prepreparation: {
    tab_label: "Préparation",
    aide_intro:
      "Réservation logique des lots avant placement physique sur palette. L'algorithme respecte la DDM mini contractuelle paramétrée sur le tarif client (cascade Tier > Groupe > Générique).",
    auto_affecter_commande: "Pré-réserver toutes les lignes",
    valider_toutes: "Valider toutes (#count)",
    liberer_toutes: "Libérer toutes (#count)",
    preparer_ligne: "Pré-préparer cette ligne",
    reaffecter: "Pré-réserver à nouveau",
    affecter_manuel: "Pré-réserver manuellement",
    valider: "Valider",
    liberer: "Libérer",
    consommes: "consommés",
    lots_affectes: "Lots pré-réservés",
    lignes_total: "Lignes",
    nb_couvert: "Couvertes",
    nb_a_revoir: "À revoir",
    a_revoir_chip: "Reliquat",
    a_revoir_tooltip:
      "Le stock disponible n'a pas suffi à couvrir la quantité demandée — ajuster la commande, attendre une réception, créer un OF, ou demander un transfert.",
    alerte_ddm_chip: "Alerte DDM",
    alerte_ddm_tooltip:
      "Au moins un lot a été pré-réservé avec une DDM < contrainte contractuelle. À valider avec le commerce.",
    aucun_lot_dispo:
      "Aucun lot disponible respectant la contrainte DDM contractuelle.",
    statut_aucune: "Aucune pré-prépa",
    presta_en_attente: "#count prestation non expédiée",
    presta_en_attente_plural: "#count prestations non expédiées",
    lot_ddm_alerte: "DDM < contrat",
    journal_show: "Voir l'historique",
    journal_hide: "Masquer l'historique",
    empty: "Aucune ligne dans cette commande à pré-préparer.",
    consommer: "Consommer #qty",
    consommer_tooltip:
      "Marque ce lot comme placé physiquement en palette. Le statut de la pré-prépa passe en CONSOMMEE_PARTIELLE ou CONSOMMEE selon la couverture.",
  },
  cout_theorique: {
    title: "Coût théorique",
    open_btn: "Coût théorique",
    open_btn_hint:
      "Projeter le coût de revient de l'article selon sa nomenclature (matière + main d'œuvre) — avec export Excel.",
    empty:
      "Cet article n'a pas de nomenclature — aucun coût théorique à projeter.",
    matiere: "Matière",
    mo: "Main d'œuvre",
    mo_none: "Non estimée (pas d'historique)",
    mo_source_hint:
      "Source de l'estimation MO. Réel = constaté sur ventes/production ; Théorique = analogique (famille · ligne) ou bilan de productivité.",
    mo_realise_lots: "Moyenne #n derniers lots",
    mo_realise_lots_hint:
      "Le réalisé MO affiché est la moyenne pondérée des #n derniers lots produits — le même échantillon que la matière réalisée, pour un coût réalisé cohérent.",
    total: "Coût total / u",
    total_mat_mo: "Matière + main d'œuvre",
    total_mat: "Matière seule (MO exclue)",
    ecart: "Écart matière",
    ecart_hint: "Réalisé − théorique (dérive de la nomenclature)",
    reel: "réel",
    theorique_tag: "Théorique",
    realise_tag: "Réalisé",
    toggle_mo: "Inclure la MO",
    toggle_mo_hint:
      "Inclure (ou non) la main d'œuvre estimée dans le coût total affiché.",
    missing:
      "#count composant(s) sans coût exploitable — le coût théorique est partiel.",
    col_composant: "Composant",
    col_qty: "Qté / u",
    col_cout_unit: "Coût retenu",
    col_theorique: "Théorique",
    col_source: "Source",
    col_contribution: "Contribution",
    src_cascade: "Théorique (cascade)",
    src_catalogue: "Catalogue",
    tooltip_lot_item: "n°#n #v",
    tooltip_real_avg: "Coût réel — moyenne de #count lots :  #detail",
    tooltip_real_lot: "Coût réel — lot n°#n (#v)",
    tooltip_real_last_lot: "Coût réel — dernier lot n°#n",
    tooltip_real_last: "Coût réel — dernière réception / production",
    tooltip_theo_cascade:
      "Théorique — recomposé depuis la nomenclature (somme des composants)",
    tooltip_theo_purchase:
      "Théorique — prix d'achat fournisseur (tarif ArticleFourni)",
    tooltip_theo_catalog:
      "Théorique — valeur unitaire catalogue de la fiche article",
    tooltip_none: "Aucun coût connu pour ce composant",
  },
  preparateur: {
    title: "Préparateur de commandes",
    refresh_tooltip: "Actualiser la liste",
    search_placeholder: "Rechercher commande, client…",
    horizon_today: "Aujourd'hui",
    horizon_tomorrow: "Demain",
    horizon_week: "Cette semaine",
    horizon_all: "Toutes",
    urg_no_date: "Sans date",
    urg_today: "Aujourd'hui",
    urg_tomorrow: "Demain",
    urg_retard: "Retard #count j",
    urg_jplus: "J+#count",
    empty: "Aucune commande à préparer",
    empty_filter: "sur ce filtre",
    lignes_engagees: "ligne(s) engagée(s)",
    a_revoir: "à revoir",
    loading: "Chargement…",
    empty_lots: "Aucun lot engagé pour cette commande.",
    ref: "Réf.",
    lot: "Lot",
    ddm: "DDM",
    lots_count: "lot(s)",
    scan_tooltip: "Scanner un lot avec la caméra",
    scan: "Scanner",
    action_title: "Action sur le lot",
    mettre_ue: "Mettre sur unité d'expédition",
    mettre_ue_sub: "Palette existante ou nouvelle",
    livrer_directe: "Livrer directement",
    livrer_directe_sub: "Sortie de stock réelle, BL immédiat (retrait)",
    garder_stock: "Garder en stock",
    garder_stock_sub: "Facturé sans sortie (départ plus tard)",
    mode_label: "Mode de livraison",
    mode_expedition: "Avec expédition",
    mode_directe: "Directe",
    mode_garde: "Gardé en stock",
    directe_confirm_title: "Livrer directement ce lot ?",
    directe_confirm_message:
      "Le lot #lot va sortir physiquement du stock (livraison directe) et un bon de livraison sera créé.",
    directe_confirm_ok: "Livrer",
    garde_confirm_title: "Garder en stock ?",
    garde_confirm_message:
      "Le lot #lot sera facturé sans sortie physique (mis en attente de facturation). Le départ se fera plus tard via un bon de transfert.",
    garde_confirm_ok: "Garder en stock",
    snack_lot_reconnu: "Lot #lot reconnu",
    snack_lot_inattendu: "Lot « #lot » non attendu pour cette commande",
    snack_lot_palette: "Lot #lot placé sur l'unité d'expédition",
    snack_livraison_directe: "Vente enregistrée (facturé — gardé en stock)",
    placed_badge: "Sur unité d'expédition",
    placed_on: "Sur #unite",
    partially_placed: "#count déjà placé(s) sur unité",
    qty_todo_label: "à placer",
    qty_placed_label: "placé",
    retirer_ue: "Retirer de l'unité d'expédition",
    retirer_ue_sub: "Renvoie le lot en zone de préparation",
    retirer_ue_sub_named: "Renvoie le lot depuis #unite vers la préparation",
    retirer_confirm_title: "Retirer de l'unité d'expédition ?",
    retirer_confirm_title_prete:
      "⚠️ Unité prête — la repasser en préparation ?",
    retirer_confirm_message:
      "Le lot #lot sera retiré de l'unité d'expédition et redeviendra « à placer ».",
    retirer_confirm_message_prete:
      "Le lot #lot est sur une unité d'expédition déclarée prête. Elle sera d'abord repassée « en préparation », puis le lot sera retiré et redeviendra « à placer ».",
    retirer_confirm_ok: "Retirer",
    retirer_confirm_cancel: "Annuler",
    print_picking: "Fiche de picking",
    print_bl_provisoire: "BL provisoire",
    no_bl_provisoire:
      "Aucune unité en préparation à inclure dans le BL provisoire",
    unites_section: "Unités d'expédition utilisées",
    unite_lots: "#count lot(s)",
    unite_expediee: "Expédiée",
    unite_prete: "Prête",
    unite_en_prepa: "En préparation",
    unite_vide: "Unité vide",
    unite_associee_a: "Expédition #expe",
    unite_expediee_le: "Expédiée le",
    a_preparer_title: "À préparer — choisir les lots",
    preparer_auto: "Préparer automatiquement",
    choisir_lots: "Choisir les lots",
    aucun_lot_dispo: "Aucun lot en stock",
    snack_lots_engages: "Lots sélectionnés et engagés en préparation",
    snack_prepare_auto: "Commande préparée automatiquement",
    declarer_prete: "Déclarer prête",
    associer_expe: "Associer à une expédition",
    print_fiche: "Fiche",
    snack_unite_prete: "Unité d'expédition déclarée prête",
    snack_unite_associee: "Unité associée à l'expédition #chargement",
  },
  preparation_pilotage: {
    total_commandes: "Commandes",
    couvertes: "Couvertes",
    a_revoir: "À revoir",
    alertes_ddm: "Alertes DDM",
    en_retard: "En retard",
    sans_date: "Sans date",
    empty:
      "Aucune commande dans cet horizon. Modifier le filtre ou créer / dater une commande.",
    search_placeholder: "Rechercher une commande, un client, un article…",
    total_produits: "Produits",
    couverts: "Couverts",
    rupture: "Rupture / partiel",
    besoin_total: "Besoin total",
    stock_dispo: "Stock dispo / total",
    reserve: "Réservé",
    premier_depart: "Premier départ",
    // Refonte 30/05/2026 — KPI badge produit
    kpi_aucun_lot_eligible_label: "Aucun lot éligible",
    kpi_aucun_lot_eligible_tooltip:
      "Le stock physique brut couvrirait le besoin, mais aucun lot n'est éligible pour les commandes consommatrices (DDM/DLC trop courte ou lots hors des entrepôts assignés). Ouvre la grille produit pour arbitrer — un forçage motivé reste possible.",
    // Refonte 30/05/2026 — Toggle UV non fractionnée (grille pilotage produit)
    uv_non_fractionnee_label: "UV non fractionnée",
    uv_non_fractionnee_tooltip_active:
      "Affectation auto par unité de vente entière uniquement. Un lot dont la quantité disponible restante est inférieure à une unité de vente complète est ignoré.\n\nDécocher : autorise les queues partielles d'un lot court — utile pour vider les lots à DDM courte sans gaspiller, principe juste-assez.",
    uv_non_fractionnee_tooltip_inactive:
      "Fractionnement autorisé : un lot court avec une queue résiduelle inférieure à une unité de vente complète sera affecté pour préserver la logique juste-assez DDM.\n\nCocher pour repasser en mode strict (unités de vente entières uniquement).",
    // 2026-05-30 — Confirmation reset avant auto + spinner
    auto_running: "Pré-réservation automatique en cours…",
    auto_confirm_title: "Lancer la pré-réservation automatique ?",
    auto_confirm_message:
      "Toutes les pré-réservations courantes des commandes non réservées seront remises à zéro avant d'appliquer le calcul automatique. Les commandes déjà réservées ne sont pas impactées.",
    auto_confirm_btn: "Lancer la pré-réservation auto",
    // 2026-05-30 — Saisie cell over-allocated
    cell_max_warn: "Quantité ramenée au max dispo restant du lot.",
  },
  achatAnalysis: {
    title: "Analyse achats",
    btn: "Analyse achats",
    btn_tooltip:
      "Analyse achats : KPIs réceptions, distribution prix payé, comparaison fournisseurs vs catalogue, conformité, délais et simulateur d'économie.",
    excel_tooltip: "Télécharger l'analyse complète en Excel",
    loading: "Chargement de l'analyse…",
    period_label: "Période :",
    period_3m: "3 mois",
    period_6m: "6 mois",
    period_12m: "12 mois",
    period_24m: "24 mois",
    view_label: "Vue :",
    view_prix: "Prix",
    view_qty: "Quantités",
    inclure_nc: "Inclure non conformes",
    inclure_nc_tooltip:
      "Par défaut le coût et le volume comptent uniquement la part conforme.",
    inclure_annulees: "Inclure annulées",
    filter_fournisseur_placeholder: "Filtrer fournisseur / groupe…",
    perimetre:
      "#nbReceptions réception(s) · #nbCommandes commande(s) · #nbFournisseurs fournisseur(s) · #nbLots lot(s) fournisseur. Périmètre : #scope. Tous les agrégats prix sont en €/#unit pour rester homogènes entre fournisseurs (l'unité d'achat varie).",
    perimetre_scope_all: "tous reçus (conforme + NC)",
    perimetre_scope_conforme: "conforme uniquement",
    kpi_volume_recu: "Volume reçu",
    kpi_volume_dont_nc: "dont #qty #unit non-conforme",
    kpi_volume_100_conforme: "100 % conforme",
    kpi_cout_total: "Coût d'achat total",
    kpi_cout_tous_recus: "tous reçus : #value €",
    kpi_cout_nb_receptions: "#nb réception(s)",
    kpi_prix_moyen: "Prix moyen pondéré",
    kpi_prix_minmax: "min #min · max #max · méd #med",
    kpi_dispersion: "Dispersion prix",
    kpi_dispersion_sub: "écart-type sur le prix payé €/#unit",
    kpi_evolution: "Évolution prix",
    kpi_evolution_dernier_mois: "dernier mois : #value €/#unit",
    kpi_evolution_too_short: "période trop courte",
    kpi_conformite: "Conformité",
    kpi_conformite_nc: "#qty #unit NC",
    kpi_conformite_aucune_nc: "aucune NC sur la période",
    kpi_delai: "Délai livraison moyen",
    kpi_delai_sub: "commande → 1ʳᵉ réception",
    kpi_delai_unit: "jours",
    kpi_frequence: "Fréquence d'appro",
    kpi_frequence_unit: "cmd/mois",
    kpi_frequence_sub: "#nb commande(s) sur la période",
    kpi_volume_moyen_cmd: "Volume moyen / commande",
    kpi_volume_moyen_par_rec: "par réception : #qty #unit",
    kpi_taux_realisation: "Taux de réalisation",
    kpi_taux_realisation_sub: "qty reçue / qty commandée",
    kpi_concentration: "Concentration sourcing",
    kpi_concentration_top: "top : #name · indice #herfindahl",
    kpi_concentration_indice: "indice #herfindahl",
    kpi_tarif_generique: "Tarif catalogue (générique)",
    kpi_tarif_ecart_paye: "écart payé : #ecart",
    kpi_tarif_no_data: "fiche article — pas de tarif saisi",
    chart_evolution_prix: "Évolution mensuelle du prix payé (€/#unit)",
    chart_evolution_prix_sub:
      "Prix moyen pondéré par mois (par qty conforme) avec min/max observés. Permet de détecter une dérive de prix (inflation ou négociation).",
    chart_evolution_volumes: "Évolution mensuelle des volumes (#unit)",
    chart_evolution_volumes_sub:
      "Total achats par mois (pointillés noirs) + une courbe par top 6 fournisseurs. Permet de comparer les tendances et détecter une disparition / un nouveau fournisseur.",
    nego_title: "Simulateur d'économie",
    nego_sub: "Volume annualisé estimé : #volume #unit/an.",
    nego_prix_cible: "Prix cible €/#unit :",
    nego_btn_meilleur: "meilleur",
    nego_btn_meilleur_tooltip:
      "Aligner sur le meilleur fournisseur observé : #name",
    nego_btn_catalogue: "catalogue",
    nego_btn_catalogue_tooltip: "Aligner sur le tarif catalogue générique",
    nego_economie: "Économie annuelle estimée",
    nego_surcout: "Surcoût annuel estimé",
    nego_compare: "(#actuel €/#unit actuel → #cible €/#unit cible)",
    nego_constats: "Constats :",
    consommateurs_title: "Principaux produits utilisateurs",
    consommateurs_mode_direct: "Direct",
    consommateurs_mode_final: "Final",
    consommateurs_mode_client: "Par client",
    consommateurs_sub_direct:
      "Produits finis qui consomment ce composant sur la période (source : pied de chaîne). Cliquer sur une ligne pour voir, en cascade, qui consomme ce produit.",
    consommateurs_sub_final:
      "Produits finis « feuilles » — articles consommateurs (in)directs qui ne sont eux-mêmes consommés par personne. Le pourcentage est propagé sur toute la chaîne (profondeur max 5).",
    consommateurs_sub_client:
      "Clients qui reçoivent ce composant via la chaîne de production. Le pourcentage est propagé : composant → cascade conso → produits feuilles → expéditions au client.",
    consommateurs_empty: "Aucune consommation enregistrée sur la période.",
    consommateurs_empty_final:
      "Aucun produit fini consommateur identifié sur la période.",
    consommateurs_empty_client:
      "Aucun client expéditaire identifié sur la période.",
    consommateurs_loading_final: "Calcul de la cascade…",
    consommateurs_loading_client: "Calcul des consommateurs clients…",
    consommateurs_loading_kids: "Chargement…",
    consommateurs_kids_empty: "Aucun produit utilisateur sur la période.",
    consommateur_meta_count: "#nb OF · #qty #unit",
    consommateur_meta_qty: "#qty #unit",
    consommateur_pct_parent_tooltip:
      "Part dans la consommation du produit parent",
    consommateur_pct_root_tooltip:
      "Part dans la consommation totale du composant",
    consommateur_pct_propage_tooltip:
      "Part propagée dans la consommation totale du composant",
    consommateur_pct_client_tooltip: "Part dans le total client",
    consommateur_client_meta: "#nb produit(s) · #qty #unit",
    consommateurs_total_label: "Total consommé sur la période",
    consommateurs_total_valeur: "#qty #unit",
    consommateurs_total_produits: "#nb produit(s) au total",
    consommateurs_total_tooltip:
      "Consommation réelle du composant sur la période, tous produits confondus — indépendante du nombre de lignes affichées.",
    consommateurs_reste:
      "Les #nbLignes ligne(s) ci-dessus en couvrent #qty #unit (#pct) — le reste est consommé par des produits hors de cette liste.",
    consommateur_client_total_tooltip:
      "Part propagée totale attribuée à ce client",
    distribution_title: "Distribution du prix payé (€/#unit)",
    distribution_sub:
      "Volume reçu par tranche de prix unitaire payé. Identifie les paliers tarifaires effectifs et les anomalies (réceptions à prix outliers).",
    repartition_volume_title: "Répartition du volume par fournisseur",
    repartition_volume_sub:
      "Part de chaque fournisseur dans le volume conforme total (#unit). Permet de visualiser la concentration sourcing.",
    comparaison_prix_title: "Comparaison des prix par fournisseur",
    comparaison_prix_sub:
      "Prix moyen payé vs tarif catalogue, top 8 fournisseurs triés du moins cher au plus cher (€/#unit).",
    groupes_title: "Comparaison par groupe de tiers",
    groupes_col_groupe: "Groupe",
    groupes_col_volume: "Volume #unit",
    groupes_col_cout: "Coût",
    groupes_col_prix_moyen: "Prix moyen €/#unit",
    groupes_col_min: "Min",
    groupes_col_max: "Max",
    groupes_col_fournisseurs: "Fournisseurs",
    groupes_col_receptions: "Réceptions",
    fournisseurs_title: "Détail par fournisseur — vue #mode",
    fournisseurs_mode_prix: "prix",
    fournisseurs_mode_qty: "quantités",
    fournisseurs_sub:
      "Cliquer sur une ligne pour déplier le détail des réceptions et conditions commerciales.",
    fournisseurs_sub_prix_help:
      "Hausse de prix vs catalogue = rouge (mauvais), baisse = vert (bon).",
    fournisseurs_col_fournisseur: "Fournisseur",
    fournisseurs_col_prix_moyen: "Prix moyen",
    fournisseurs_col_tarif_cat: "Tarif catalogue",
    fournisseurs_col_ecart_cat: "Écart cat.",
    fournisseurs_col_evolution: "Évolution",
    fournisseurs_col_cout_part: "Coût (part)",
    fournisseurs_col_volume: "Volume #unit",
    fournisseurs_col_cmd_recu: "Cmd / Reçu",
    fournisseurs_col_vol_moy_cmd: "Vol moy/cmd",
    fournisseurs_col_freq: "Fréq.",
    fournisseurs_col_n_rec: "N° rec.",
    fournisseurs_col_conformite: "Conformité",
    fournisseurs_col_delai: "Délai",
    fournisseurs_rang_tooltip: "Rang volume ##rang",
    fournisseurs_score_fiabilite_tooltip:
      "Score fiabilité (conformité × délai)",
    fournisseurs_fiab: "fiab. #pct",
    fournisseurs_cmd_count: "#nb cmd · #lots lot(s)",
    fournisseurs_freq_unit: "cmd/mois",
    fournisseurs_vol_par_rec: "/rec : #qty",
    fournisseurs_empty_period: "Aucune réception sur la période sélectionnée.",
    conditions_title: "Conditions commerciales :",
    conditions_reduction: "Réduction #pct %",
    conditions_franco_qty: "Franco qty : #value",
    conditions_franco_prix: "Franco prix : #value €",
    conditions_delai_jours: "(#nb j)",
    conditions_multiple: "Multiple achat : #value",
    conditions_coef: "1 #ua = #coef #ug",
    receptions_col_date: "Date",
    receptions_col_commande: "Commande",
    receptions_col_bl: "BL",
    receptions_col_lot: "Lot fournisseur",
    receptions_col_qty: "Qty #unit",
    receptions_col_conforme: "Conforme",
    receptions_col_prix_unit: "€/#unit",
    receptions_col_cout: "Coût",
    receptions_nc_badge: "−#qty",
  },
  fabricationAnalysis: {
    title: "Analyse fabrication",
    btn: "Analyse fabrication",
    btn_tooltip:
      "Analyse fabrication : KPIs coûts matière+MO unitaires, écart théorique vs réel pied de chaîne, composants matière, distribution coût par OF, simulateur d'amélioration.",
    excel_tooltip: "Télécharger l'analyse fabrication complète en Excel",
    loading: "Chargement de l'analyse fabrication…",
    perimetre:
      "#nbOfs OF(s) · #nbLots lot(s) produits · #qty #unit produit(s). Périmètre : lots fabriqués sur la période avec coût enregistré.",
    period_label: "Période :",
    period_3m: "3 mois",
    period_6m: "6 mois",
    period_12m: "12 mois",
    period_24m: "24 mois",
    granularite_day: "Jour",
    granularite_week: "Semaine",
    granularite_month: "Mois",
    granularite_jour: "jour",
    granularite_semaine: "semaine",
    granularite_mois: "mois",
    kpi_volume_produit: "Volume produit",
    kpi_volume_lots: "#nb lot(s)",
    kpi_cout_total: "Coût total unitaire",
    kpi_cout_total_sub: "cumul #value",
    kpi_cout_matiere: "Coût matière unitaire",
    kpi_cout_matiere_sub: "composants directs",
    kpi_cout_mo: "Coût MO unitaire",
    kpi_cout_mo_sub: "MO profonde cascade",
    kpi_evolution: "Évolution coût total",
    kpi_evolution_sub: "1ᵉʳ → dernier mois",
    kpi_ecart_conso: "Écart conso matière",
    kpi_ecart_conso_sub: "réel vs théorique BOM",
    kpi_productivite_mo: "Productivité MO",
    kpi_productivite_mo_tooltip:
      "Unités produites par heure de main-d'œuvre cumulée (cascade complète). Si plusieurs ouvriers travaillent en parallèle, leurs heures s'additionnent — ce n'est pas la cadence machine.",
    kpi_concentration_ligne: "Concentration ligne",
    kpi_concentration_ligne_sub: "part du top 1",
    chart_evolution_couts: "Évolution mensuelle des coûts unitaires",
    chart_evolution_couts_sub:
      "Coûts matière (orange) et MO (teal) par mois, avec total en pointillés noirs.",
    chart_distribution_couts: "Distribution coûts unitaires par OF",
    chart_distribution_couts_sub:
      "10 tranches de coût total unitaire — repérez les OFs hors tendance (incidents, recettes modifiées).",
    chart_volume: "Évolution du volume produit",
    chart_volume_sub:
      "Volume produit (#unit) par #gran. Les périodes sans production apparaissent à zéro pour préserver la continuité visuelle.",
    chart_repartition_lignes: "Répartition production par ligne",
    chart_repartition_lignes_sub:
      "Poids en volume de chaque ligne de production sur la période. Permet de visualiser la concentration ligne et la rotation entre équipements.",
    chart_evolution_productivite: "Évolution de la productivité MO",
    chart_evolution_productivite_sub:
      "Unités produites par heure de main-d'œuvre cumulée (#unit/h MO), mois après mois. Une pente positive révèle une amélioration ; une pente négative signale une dérive à investiguer.",
    chart_evolution_productivite_warn:
      "À ne pas confondre avec la cadence ligne (vitesse machine) — ici c'est bien la MO qui compte.",
    composants_title: "Composants matière",
    composants_mode_direct: "Direct",
    composants_mode_profond: "Profond",
    composants_mode_fournisseur: "Par fournisseur",
    composants_sub_direct:
      "Composants directs de la nomenclature, agrégés sur la période. Comparaison théorique BOM vs réel pied-de-chaîne — l'écart mesure les pertes matière.",
    composants_sub_profond:
      "Composants feuilles de la cascade ascendante (matières achetées en bout de chaîne).",
    composants_sub_fournisseur:
      "Agrégation par fournisseur d'origine — quel tiers amont influence le plus le coût matière.",
    composants_empty: "Aucun composant identifié sur la période.",
    composants_empty_fournisseurs:
      "Aucun fournisseur identifié sur la période.",
    composants_col_composant: "Composant",
    composants_col_qty_theo: "Qty théo. #unit",
    composants_col_qty_reelle: "Qty réelle #unit",
    composants_col_ecart: "Écart",
    composants_col_cout_mat: "Coût mat. €/UG",
    composants_col_cout_mo: "MO profond €/UG",
    composants_col_cout_total: "Coût total",
    composants_col_part: "Part %",
    composants_col_feuille: "Composant feuille",
    composants_col_qty_propagee: "Qty propagée",
    composants_col_cout_propage: "Coût propagé",
    composants_col_fournisseur: "Fournisseur amont",
    composants_col_nb_composants: "Nb composants",
    composants_meta: "#code · #nbOfs OF",
    of_link_tooltip: "Ouvrir l'OF",
    simulator_title: "Simulateur d'amélioration",
    simulator_sub:
      "Volume annualisé estimé : #volume #unit/an. Coût annuel actuel : #cout. Activez un ou plusieurs leviers pour estimer l'économie.",
    simulator_lever_pertes: "Réduire les pertes matière (%)",
    simulator_lever_pertes_hint:
      "Aligner le réel sur le théorique pour les composants à fort écart.",
    simulator_lever_productivite: "Améliorer la productivité MO (%)",
    simulator_lever_productivite_hint:
      "Plus d'unités produites par heure-homme (réorganisation, automatisation partielle, formation).",
    simulator_lever_prix_top: "Négocier #composant (% prix)",
    simulator_lever_prix_top_default: "Négocier composant principal (%)",
    simulator_lever_prix_top_hint:
      "Top 1 composant en valeur — levier négociation amont.",
    simulator_lever_prix_mo: "Réduire prix MO (%)",
    simulator_lever_prix_mo_hint:
      "Optimisation atelier ou changement de barème salarial.",
    simulator_economie: "Économie annuelle estimée",
    simulator_reset: "Remettre à zéro",
    simulator_constats: "Constats :",
    ofs_title: "Détail OFs — #nb OF(s)",
    ofs_sub:
      "Triés par coût total unitaire décroissant. Les OFs marqués <strong>!</strong> sont des outliers (coût hors tendance).",
    ofs_outlier_tooltip:
      "Cet OF a un coût atypique : il s'écarte significativement de la fourchette des autres OFs (méthode IQR de Tukey). À investiguer (incident, recette modifiée, petit lot, saisie incomplète…).",
    ofs_col_of: "OF",
    ofs_col_ligne: "Ligne",
    ofs_col_date_fin: "Date fin",
    ofs_col_qty: "Qty #unit",
    ofs_col_lots: "Lots",
    ofs_ouvrir_of: "Ouvrir le suivi de cet ordre de fabrication",
    ofs_ouvrir_lot: "Ouvrir la fiche de ce lot",
    ofs_col_heures_mo: "Heures MO",
    ofs_col_cout_mat: "Coût mat €/UG",
    ofs_col_cout_mo: "Coût MO €/UG",
    ofs_col_cout_total: "Coût total €/UG",
  },
  articleMargeAnalysis: {
    btn_tooltip:
      "Analyse marge & prix : indicateurs de vente, distribution des prix, comparaison client vs. catalogue et aide tarifaire pour un nouveau client.",
    title: "Analyse marge & prix",
    title_with_article: "Analyse marge & prix — #name",
    excel_tooltip:
      "Télécharger l'analyse complète en Excel (synthèse, clients, distribution, évolution, détail factures, aide tarifaire)",
    excel_success: "Export Excel téléchargé",
    excel_error: "Échec de l'export Excel",
    period_label: "Période :",
    period_3m: "3 mois",
    period_6m: "6 mois",
    period_12m: "12 mois",
    toggle_inclure_mo: "Inclure la MO",
    toggle_inclure_remise_pied: "Inclure remise pied",
    filter_client_placeholder: "Filtrer client / groupe…",
    loading: "Chargement de l'analyse…",
    error_load: "Erreur lors du chargement",
    perimetre_mat_mo: "matière + MO",
    perimetre_mat_seule: "matière seule",
    perimetre:
      "Périmètre #scope · Données issues des factures sur #nbFactures facture(s) · #nbLignes ligne(s) · #nbClients client(s) distinct(s).",
    kpi_volume: "Volume vendu",
    kpi_volume_sub: "= #qty #unit (unité de vente, coef ×#coef)",
    kpi_ca_net: "CA net",
    kpi_ca_remise_pied: "dont #pct de remise pied",
    kpi_cout_retenu: "Coût retenu total (#scope)",
    kpi_cout_detail: "dont matière #mat € · MO #mo €",
    kpi_cout_unitaire: "soit #cout €/#unit (moyenne sur le volume vendu)",
    kpi_marge_totale: "Marge totale",
    kpi_marge_sub: "#pct · coef ×#coef",
    kpi_marge_fiabilite: "Dont #n ligne(s) sans coût connu (#ca €) — le taux est surestimé",
    kpi_taux_marge: "Taux de marge",
    kpi_taux_mat_seule: "Matière seule",
    kpi_taux_mat_mo: "Matière + MO",
    kpi_taux_mo_impact: "MO #mo € → impact #pct sur le taux",
    kpi_taux_mo_na: "— MO non historisée",
    kpi_taux_mo_none:
      "Pas de coût MO trouvé sur les ventes — taux mat. seule = taux total",
    kpi_prix_moyen: "Prix encaissé moyen",
    kpi_prix_minmax: "médiane #median · min #min · max #max",
    kpi_dispersion: "Dispersion prix",
    kpi_dispersion_sub: "écart-type /#unit sur le PU encaissé",
    nc_section_title: "Non-conformités sur cet article",
    distribution_title: "Distribution des prix encaissés",
    distribution_sub:
      "Volume vendu par tranche de PU encaissé (€/#unit). Permet de positionner un nouveau client.",
    chart_volume_label: "Volume vendu (#unit)",
    chart_tooltip_lignes: "#nb ligne(s) facture",
    chart_tooltip_clients: "#nb client(s) distinct(s)",
    chart_tooltip_ca_cumule: "CA cumulé : #value €",
    chart_prix_label: "Prix moyen €/#unit",
    chart_cout_label: "Coût moyen €/#unit",
    chart_marge_label: "Marge €/#unit",
    pricing_title: "Aide à la décision tarifaire",
    pricing_sub: "Pour fixer un prix à un nouveau client.",
    pricing_cout_actuel: "Coût retenu actuel",
    pricing_cout_matiere: "Matière",
    pricing_cout_mo: "Main-d'œuvre",
    pricing_detail_cascade: "Détail cascade",
    pricing_detail_mo: "Détail MO",
    pricing_detail_cascade_tooltip:
      "Voir la cascade complète des composants, sources matière (réel / théorique / cascade), dernier lot retenu, écart réel/théorique.",
    pricing_detail_mo_tooltip:
      "Voir la chaîne de fallback MO : les 6 tiers évalués (historique OFs, dernier lot, famille×ligne, bilan productivité, catalogue) avec leur valeur et la source retenue.",
    pricing_detail_mo_none: "Aucune source disponible",
    pricing_prix_observes: "Prix observés",
    pricing_prix_observes_unit: "(€/#unit)",
    pricing_prix_observes_tooltip:
      "Stats agrégées sur toutes les lignes de vente. Affichées en €/UG car l'unité de vente peut varier entre clients (carton 6 vs carton 12 vs palette) — comparer des €/UV mélange des choses non-comparables.",
    pricing_stats_min: "Min",
    pricing_stats_p25: "P25",
    pricing_stats_median: "Médiane",
    pricing_stats_p75: "P75",
    pricing_stats_max: "Max",
    pricing_stats_eq: "= #value €/#unit",
    pricing_calculator: "Calculateur prix par marge",
    pricing_cout_retenu_inline: "Coût retenu : #value €/#unit",
    pricing_pre_filled:
      "Pré-rempli avec le taux observé sur la période (#pct %).",
    pricing_reset: "Reset",
    pricing_reset_tooltip: "Revenir au taux de marge effectivement constaté",
    pricing_marge_label: "Marge cible",
    pricing_marge_input_tooltip: "Saisis directement le taux de marge en %",
    pricing_prix_propose: "Prix de vente proposé",
    pricing_prix_marge_sub: "marge #marge €/#unit · coef ×#coef",
    pricing_tarif_generique: "Tarif générique catalogue",
    pricing_tarif_value_eq: "= #value €/#unit",
    aide_tarif_origine_title: "D'où vient ce prix ?",
    aide_tarif_origine_html:
      "Du <b>tarif catalogue générique</b> (fiche <i>ArticleFourni</i> sans tiers ni groupe associé). C'est le prix appliqué par défaut à tout client qui n'a pas de tarif spécifique configuré.",
    aide_tarif_calcul_title: "Comment il est calculé",
    aide_tarif_calcul_html:
      "On prend le <b>premier palier</b> du tarif catalogue (trié par quantité minimale) — c'est le prix de base avant remises quantitatives.<br><br><b>€/#unitUV</b> : prix brut tel que stocké en catalogue.<br><b>€/#unitUG</b> = €/#unitUV ÷ coefficient UV→UG (×#coef), pour comparer au coût retenu exprimé par unité de gestion.",
    aide_tarif_hierarchie_title: "Hiérarchie de résolution",
    aide_tarif_hierarchie_html:
      "Pour un client donné, le prix retenu suit cette priorité :<ol style='margin: 4px 0 0 16px; padding: 0'><li>Tarif spécifique au tiers</li><li>Tarif du groupe tiers</li><li><b>Tarif générique</b> (fallback)</li></ol>",
    clients_title: "Détail par client (#count)",
    clients_sub:
      "Trié par CA décroissant. Cliquer sur un en-tête pour trier autrement (à venir).",
    clients_empty_no_sales:
      "Aucune vente sur cette période. L'aide pricing ci-dessus s'appuie sur le coût théorique (production / cascade composants) pour proposer un prix.",
    clients_empty_no_match: "Aucun client ne correspond à la recherche.",
    clients_col_client: "Client",
    clients_col_qty: "Qté",
    clients_col_prix: "Prix encaissé",
    clients_col_tarif_cat: "Tarif cat.",
    clients_col_ecart: "Écart",
    clients_col_cout_moy: "Coût moy.",
    clients_col_marge_unit: "Marge /u",
    clients_col_taux: "Taux",
    clients_col_factures: "Factures",
    clients_btn_show_detail: "Voir le détail des factures",
    clients_btn_hide_detail: "Masquer le détail",
    clients_qty_eq: "= #qty #unit",
    clients_value_eq: "= #value €/#unit",
    clients_tarif_none_tooltip:
      "Aucune fiche tarif active pour ce client (ni tiers-spécifique, ni groupe, ni générique). Le client paie au prix décidé manuellement à chaque commande.",
    aide_qty_title: "Quantité vendue",
    aide_qty_html:
      "Volume cumulé sur la période, exprimé sur deux unités :<br><br><b>Unité de gestion (UG)</b> : unité de production / stockage (ex. sachets individuels).<br><b>Unité de vente (UV)</b> : unité commerciale facturée (ex. cartons, palettes).<br><br>Coefficient article : 1 UV = #coef UG.",
    aide_prix_title: "Prix encaissé",
    aide_prix_html:
      "<b>Prix moyen pondéré net</b> facturé à ce client sur la période, après remise pied de facture.<br><br>Calcul : Σ CA net ÷ Σ quantité.<br>Les avoirs sont compensés (signe négatif).",
    aide_tarif_client_highlight_title:
      "Pourquoi des valeurs différentes selon le client ?",
    aide_tarif_client_highlight_html:
      "Le tarif catalogue est résolu <b>par client</b> selon une hiérarchie à 3 niveaux. La pastille sous la valeur indique le niveau retenu.",
    aide_tarif_client_hierarchie_html:
      "<ol style='margin: 4px 0 0 16px; padding: 0'><li><b>Tarif client spécifique</b> — fiche <i>ArticleFourni</i> rattachée au tiers</li><li><b>Tarif groupe</b> — rattachée au groupe tiers du client</li><li><b>Tarif générique</b> — fallback catalogue sans tiers ni groupe</li></ol>Si aucun tarif n'est trouvé, \"—\" s'affiche : le prix a été décidé manuellement à la commande.",
    aide_tarif_client_calcul_html:
      "Premier palier de prix (trié par quantité minimale) de la fiche tarif retenue. Affiché en €/#unitUG (÷ coef ×#coef) et en €/#unitUV (brut catalogue).",
    aide_ecart_title: "Écart prix encaissé / tarif catalogue",
    aide_ecart_html:
      "(Prix encaissé − Tarif catalogue) ÷ Tarif catalogue.<br><br><b style='color: var(--color-on-valid)'>+%</b> = on facture plus cher que le tarif applicable (sur-encaissement : marge bonus, mais peut signaler une réclamation possible si le client le découvre).<br><b style='color: var(--color-on-error)'>−%</b> = on facture moins cher que le tarif (remise consentie ou oubli de mise à jour).<br><b>0%</b> = pratique conforme au catalogue.",
    aide_cout_title: "Coût moyen retenu",
    aide_cout_html:
      "Coût unitaire <b>réel</b> à la date d'expédition de chaque facture (figé à la vente, pas le coût courant).<br><br><b>Source</b> : matière profonde + MO profonde du lot expédié, à défaut coût plat (<i>COALESCE(profond, plat)</i>).<br>La MO est incluse uniquement si le toggle \"main-d'œuvre\" est activé en haut du dialog.",
    aide_marge_title: "Marge unitaire",
    aide_marge_html:
      "Prix encaissé − Coût moyen retenu, par unité.<br><br><b style='color: var(--color-on-valid)'>Vert</b> : marge positive (on gagne de l'argent).<br><b style='color: var(--color-on-error)'>Rouge</b> : marge négative (on vend à perte).<br><br>Cohérent avec le périmètre choisi en haut du dialog (matière seule ou matière + MO).",
    aide_taux_title: "Taux de marge",
    aide_taux_html:
      "Marge totale ÷ CA net.<br><br>Calculé sur le périmètre actif (matière seule ou matière + MO).<br><br><i>Différent du coefficient multiplicateur</i> (CA / coût) — ici c'est bien la marge ramenée au chiffre d'affaires.",
    lignes_empty: "Aucune ligne facture pour ce client.",
    lignes_col_date: "Date",
    lignes_col_facture: "Facture",
    lignes_col_qty: "Qté (#unit)",
    lignes_col_prix_net: "Prix net /#unit",
    lignes_col_ca_net: "CA net",
    lignes_col_cout_unit: "Coût /#unit",
    lignes_col_cout_total: "Coût total",
    lignes_col_marge: "Marge €",
    lignes_brouillon: "(brouillon)",
    lignes_avoir: "avoir",
    lignes_lot: "· lot #num",
    evolution_title: "Évolution mensuelle",
    evolution_sub:
      "Prix moyen encaissé, coût moyen et marge unitaire ramenés à l'#unit.",
    groupes_title: "Comparaison par groupe de tiers",
    groupes_sub:
      "Aide à comparer un nouveau client à son segment. Plus le groupe a de clients, plus la moyenne est statistiquement fiable.",
    groupes_col_groupe: "Groupe",
    groupes_col_clients: "Clients",
    groupes_col_volume: "Volume",
    groupes_col_prix_moyen: "Prix moyen",
    groupes_col_mediane: "Médiane",
    groupes_col_marge_moy: "Marge moy.",
    groupes_col_taux: "Taux",
    groupes_sub_col_client: "Client",
    groupes_sub_col_volume: "Volume",
    groupes_sub_col_ca_net: "CA net",
    groupes_sub_col_prix_moyen: "Prix moyen",
    groupes_sub_col_marge: "Marge",
    groupes_sub_col_taux: "Taux",
    groupes_sub_col_factures: "Factures",
    tarif_level_tiers: "tarif client spécifique",
    tarif_level_groupe: "tarif groupe",
    tarif_level_generique: "tarif générique",
    tarif_level_none: "aucun tarif catalogue",
    mo_source_unknown: "Source MO inconnue",
    btn: "Analyse marge",
  },
  tiersMargeAnalysis: {
    title: "Analyse marge & ventes",
    title_with_tiers: "Analyse marge & ventes — #name",
    excel_tooltip:
      "Télécharger l'analyse complète en Excel (synthèse, articles, distribution paniers, évolution, détail factures, conditions tarifaires)",
    excel_export_title: "Exporter — Analyse marge & ventes",
    excel_success: "Export Excel téléchargé",
    excel_error: "Échec de l'export Excel",
    excel_toggle_mo: "Inclure la main-d'œuvre dans le coût",
    excel_toggle_mo_hint:
      "Décocher pour ne retenir que le coût matière dans le calcul de marge.",
    excel_toggle_remise: "Inclure la remise pied de facture",
    excel_toggle_remise_hint:
      "Décocher pour exclure la remise globale de la facture du calcul.",
    period_label: "Période :",
    period_3m: "3 mois",
    period_6m: "6 mois",
    period_12m: "12 mois",
    period_24m: "24 mois",
    period_ytd: "YTD",
    period_exercice: "Exercice",
    period_exerciceN1: "Exercice passé",
    toggle_inclure_mo: "Inclure la MO",
    toggle_inclure_remise_pied: "Inclure remise pied",
    filter_article_placeholder: "Filtrer article / famille…",
    loading: "Chargement de l'analyse…",
    error_load: "Erreur lors du chargement",
    perimetre_mat_mo: "matière + MO",
    perimetre_mat_seule: "matière seule",
    perimetre:
      "Périmètre #scope · Données issues des factures sur #nbFactures facture(s) · #nbLignes ligne(s) · #nbArticles article(s) distinct(s).",
    perimetre_dates: " · Du #from au #to.",
    perimetre_no_sales:
      "Aucune vente détectée sur cette période. Élargis la période ci-dessus, ou consulte les conditions tarifaires applicables au tiers ci-dessous (toujours utiles pour préparer une prochaine commande).",
    no_sales_section_title: "Conditions tarifaires applicables",
    no_sales_section_sub:
      "Réduction, franco, règlement, groupes, remises actives — synchronisés avec la fiche tiers.",
    no_sales_empty_title: "Pas de ventes sur cette période",
    no_sales_empty_msg:
      "Aucune facture n'a été émise pour #name sur les #months derniers mois (périmètre #scope). Essaie une période plus large, ou télécharge l'Excel pour récupérer les conditions tarifaires applicables.",
    cond_reduction_label: "Réduction tiers",
    cond_franco_label: "Franco port",
    cond_franco_qty_unit: "u",
    cond_reglement_label: "Règlement",
    cond_groupes_label: "Groupes tarifaires",
    remises_active_label: "Remises actives applicables",
    remises_empty: "Aucune remise active applicable au tiers.",
    remise_no_label: "(sans libellé)",
    insights_label: "Insights",
    kpi_ca_net: "CA net",
    kpi_ca_remise_pied: "dont #pct de remise pied",
    kpi_cout_retenu: "Coût retenu total (#scope)",
    kpi_cout_detail: "dont matière #mat € · MO #mo €",
    kpi_marge_totale: "Marge totale",
    kpi_marge_sub: "#pct · coef ×#coef",
    kpi_taux_marge: "Taux de marge",
    kpi_taux_mat_seule: "Matière seule",
    kpi_taux_mat_mo: "Matière + MO",
    kpi_taux_mo_impact: "MO #mo € → impact #pct sur le taux",
    kpi_taux_mo_na: "— MO non historisée",
    kpi_volume_factures: "Volume de factures",
    kpi_volume_unit: "factures",
    kpi_volume_sub: "dont #nbAvoirs avoir(s) · #nbLignes ligne(s)",
    kpi_panier_moyen: "Panier moyen",
    kpi_panier_marge_sub: "marge moy. #value €/facture",
    kpi_frequence: "Fréquence d'achat",
    kpi_frequence_unit: "factures/mois",
    kpi_frequence_sub: "#nb article(s) distinct(s) achetés",
    nc_section_title: "Non-conformités liées au tiers",
    panier_title: "Distribution des paniers",
    panier_sub:
      "Nombre de factures par tranche de montant — pour comprendre la structure du portefeuille.",
    chart_panier_label: "Nombre de factures",
    chart_panier_axis_y: "Nb factures",
    chart_panier_tooltip_factures: "#nb facture(s)",
    chart_panier_tooltip_ca: "CA cumulé : #value €",
    pricing_title: "Conditions tarifaires",
    pricing_sub:
      "Réduction, franco, règlement, remises actives applicables au tiers.",
    chart_evolution_ca: "CA net (€)",
    chart_evolution_marge: "Marge (€)",
    chart_familles_tooltip: "#name : #value € (#pct %)",
    familles_title: "Mix produit (#count famille#plural)",
    familles_sub:
      "Répartition du CA par famille article. Trié par CA décroissant.",
    familles_col_famille: "Famille",
    familles_col_articles: "Articles",
    familles_col_ca: "CA net",
    familles_col_part: "Part",
    familles_col_marge: "Marge",
    familles_col_taux: "Taux",
    familles_sub_col_article: "Article",
    familles_sub_col_qty: "Qty",
    familles_sub_col_ca: "CA net",
    familles_sub_col_marge: "Marge",
    familles_sub_col_taux: "Taux",
    familles_sub_col_factures: "Factures",
    familles_sans_famille: "Sans famille",
    familles_viz_title: "Visualisation mix produit",
    familles_viz_sub:
      "Top 8 familles par CA. Concentration vs diversification.",
    articles_title: "Détail par article (#count)",
    articles_sub:
      "Cliquer une ligne pour déplier le détail des factures · cliquer l'en-tête d'une colonne pour trier.",
    articles_empty_no_sales: "Aucune vente sur cette période.",
    articles_empty_no_match: "Aucun article ne correspond à la recherche.",
    articles_col_article: "Article",
    articles_col_qty: "Qté",
    articles_col_prix: "Prix encaissé",
    articles_col_tarif_cat: "Tarif cat.",
    articles_col_ecart: "Écart",
    articles_col_cout_moy: "Coût moy.",
    articles_col_marge_unit: "Marge /u",
    articles_col_taux: "Taux",
    articles_col_factures: "Factures",
    articles_btn_show_detail: "Voir le détail des factures",
    articles_btn_hide_detail: "Masquer le détail",
    articles_qty_eq: "= #qty #unit",
    articles_value_eq: "= #value €/#unit",
    articles_tarif_none_tooltip:
      "Aucune fiche tarif active pour ce client / cet article (ni tiers-spécifique, ni groupe, ni générique).",
    articles_ecart_tooltip:
      "Écart entre le prix moyen encaissé et le tarif catalogue, calculé en €/UG.",
    articles_factures_dern: "dern. #date",
    lignes_empty: "Aucune ligne facture pour cet article.",
    lignes_col_date: "Date",
    lignes_col_facture: "Facture",
    lignes_col_qty: "Qté",
    lignes_col_prix_net: "Prix net",
    lignes_col_ca_net: "CA net",
    lignes_col_cout_retenu: "Coût retenu",
    lignes_col_marge: "Marge €",
    lignes_brouillon: "(brouillon)",
    lignes_avoir: "avoir",
    lignes_lot: "· lot #num",
    evolution_title: "Évolution mensuelle",
    evolution_sub: "CA net et marge totale par mois sur la période.",
    btn_close: "Quitter",
    tarif_level_tiers: "tarif client spécifique",
    tarif_level_groupe: "tarif groupe",
    tarif_level_generique: "tarif générique",
    tarif_level_none: "aucun tarif catalogue",
    remise_scope_tiers: "tiers global",
    remise_scope_tiers_article: "tiers · article",
    remise_scope_groupe: "groupe global",
    remise_scope_groupe_article: "groupe · article",
    remise_value_pct: "#value %",
    remise_value_unitaire: "#value €/u",
    remise_value_eur: "#value €",
    unit_default: "u.",
    btn: "Analyse marge client",
  },
  frais_port_dialog: {
    title: "Frais de port",
    mode: "Mode de calcul",
    montant: "Montant HT",
    tva: "TVA appliquée",
    tva_none: "Aucune TVA",
    tva_bareme_hint:
      "TVA définie par le barème — passez en Manuel pour choisir un autre taux.",
    bareme_info:
      "Les frais de port sont calculés automatiquement depuis le barème applicable (négocié tiers, groupe, ou barème du transporteur).",
    bareme_aucun:
      "Aucun barème applicable — saisissez le montant en Manuel ou créez un barème.",
    select_transporteur:
      "Sélectionnez un transporteur pour utiliser un barème (sinon, saisissez le montant en Manuel).",
    choisir_bareme: "Plusieurs barèmes applicables — choisissez :",
    bareme_sans_tranche:
      "Le tarif sera calculé d'après le montant de la commande — aucune tranche ne correspond pour l'instant.",
    bareme_sans_tranche_poids:
      "Le tarif sera calculé d'après le poids de la commande — ajoutez des articles (avec poids renseigné sur la fiche article).",
    creer_bareme: "Créer un barème",
    source_tiers: "Tarif négocié",
    source_groupe: "Tarif groupe",
    source_transporteur: "Barème transporteur",
  },
  bareme_transporteur: {
    title: "Barèmes de transport",
    tab_label: "Barèmes",
    add: "Ajouter un barème",
    edit: "Modifier le barème",
    name: "Nom du barème",
    type: "Type",
    sens: "S'applique aux",
    sens_vente: "Commandes client",
    sens_achat: "Commandes fournisseur",
    sens_vente_court: "Vente",
    sens_achat_court: "Achat",
    sens_requis: "Sélectionnez au moins un sens (vente et/ou achat).",
    tva: "TVA du barème",
    tva_defaut_org: "Défaut organisation",
    lie_a: "Lié à",
    saved: "Barème enregistré",
    deleted: "Barème supprimé",
    scope_public_chip: "Public",
    negocie_pour: "Négocié :",
    link_default_hint:
      "Barème public du transporteur — s'applique à toutes les commandes sans tarif négocié.",
    link_tiers_hint:
      "Tarif négocié pour un tiers — prioritaire sur les barèmes du groupe et du transporteur.",
    link_group_hint:
      "Tarif négocié pour un groupe de tiers — prioritaire sur le barème du transporteur.",
    negocies_title: "Frais de port négociés",
    negocies_hint:
      "Barèmes propres à ce tiers — prioritaires sur les barèmes du groupe et du transporteur lors du calcul des frais de port de ses commandes.",
    groupe_title: "Frais de port négociés du groupe",
    groupe_hint:
      "Barèmes appliqués aux tiers de ce groupe — prioritaires sur les barèmes transporteur, mais pas sur un barème propre au tiers.",
    type_poids: "Au poids (kg)",
    type_prix: "Au prix (€ HT)",
    tranches: "Tranches",
    tranche_min: "Borne min",
    tranche_max: "Borne max (vide = illimité)",
    tranche_tarif_base: "Tarif de base (€)",
    add_tranche: "Ajouter une tranche",
    tarifs_zone: "Tarifs par zone",
    add_zone: "Ajouter une zone...",
    unit_kg: "kg",
    unit_eur: "€",
    tarif_base: "base",
  },
  support: {
    title: "Support",
    subtitle: "Vos demandes auprès de l'équipe JustUp",
    new_ticket: "Nouvelle demande",
    empty: "Aucune demande pour le moment",
    empty_open: "Aucune demande en cours",
    filter: {
      all: "Toutes",
      open: "En cours",
    },
    card: {
      last_activity: "Dernière activité",
      handled_by: "Suivi par",
      news: "Réponse de JustUp",
      open: "Ouvrir la demande",
    },
    status: {
      OUVERT: "Ouverte",
      EN_COURS: "En cours de traitement",
      EN_ATTENTE_CLIENT: "En attente de votre réponse",
      RESOLU: "Résolue",
      FERME: "Fermée",
    },
    // Libellés d'IMPACT et non de priorité interne : « Haute » invite chacun à
    // se juger prioritaire, « Une partie de l'activité est bloquée » se vérifie.
    priority: {
      BASSE: "Question, sans urgence",
      NORMALE: "Gênant, mais je peux travailler",
      HAUTE: "Une partie de mon activité est bloquée",
      CRITIQUE: "Production à l'arrêt",
    },
    category: {
      BUG: "Anomalie",
      QUESTION: "Question",
      DEMANDE_EVOLUTION: "Demande d'évolution",
      FACTURATION: "Facturation",
      AUTRE: "Autre",
    },
    new: {
      title: "Décrire votre demande",
      hint: "L'équipe JustUp vous répond directement dans cette demande. La page sur laquelle vous êtes et votre navigateur sont joints automatiquement.",
      subject: "Objet",
      category: "Type de demande",
      priority: "Urgence",
      body: "Description",
      body_placeholder:
        "Ce que vous faisiez, ce que vous attendiez, ce qui s'est passé.",
      send: "Envoyer",
      sent: "Demande #reference envoyée à l'équipe JustUp",
      context: "Page concernée :",
    },
    attachments: {
      add: "Joindre un fichier",
      upload_failed: "Envoi impossible : #name",
    },
    panel: {
      back: "Retour à la liste",
      close: "Fermer le support",
    },
    thread: {
      justup: "Équipe JustUp",
      you: "Vous",
      system: "Suivi de la demande",
      empty: "Aucun message pour le moment",
      send: "Répondre",
      send_shortcut: "Entrée : envoyer · Maj + Entrée : retour à la ligne",
      refresh: "Actualiser",
      first_response_by: "Réponse de l'équipe attendue avant le",
      reply_placeholder: "Votre réponse…",
      reply_placeholder_resolved: "Ce n'est pas réglé ? Expliquez-nous…",
      resolved_hint:
        "L'équipe a marqué cette demande comme résolue. Si ce n'est pas le cas, répondez ici : elle sera rouverte.",
      close_ticket: "Clore la demande",
      close_confirm:
        "Cette demande sera close définitivement. Pour un nouveau sujet, ouvrez une nouvelle demande.",
      closed_hint:
        "Cette demande est close. Ouvrez-en une nouvelle pour un autre sujet.",
    },
  },
  justy: {
    prefill: {
      titre: "Préparé par Justy — vérifiez avant d'enregistrer",
      vider: "Repartir de zéro",
    },
  },
  live_production: {
    attente_connexion: "Connexion au flux de production en cours…",
  },
};
